import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import OrderDesign from './OrderDesign';
import ordersStyles from './OrdersStyles';
import "./BattleUI.css";
import './GeneralStatus.css';
import './OrderDesignMobile.css';
import GeneralStatus from './GeneralStatus';
import GridOverlay from './GridOverlay';
import DebugOverlay from './DebugOverlay';
import TargetedDebugOverlay from './TargetedDebugOverlay';
import SimpleElementSelector from './SimpleElementSelector';
import EditableElementOverlay from './EditableElementOverlay';
import { DeveloperConsole, useDeveloperMode, handleElementSelected, handleElementMoved, handleElementResized, handleSaveDesign } from './devmode';
import { getTokenExpiry, needsTokenRefresh, refreshToken } from './auth';
import { getAvatarImage, getAvatarDirection, shouldMirrorAvatar, getOpponentAvatarImage, shouldMirrorOpponentAvatar } from './avatars';
import { useTurnTimer, TurnTimerDisplay } from './timer';
import { useDamageFlash, getDamageFlashStyles, DamageAnimationProvider, useDamageAnimationContext, DamageAnimation, useSlotDeathDetection, SlotDeathAnimations, useGameStartAnimations, getSlideInStyles, useUnitDeploymentAnimations, useDroneDeploymentAnimations, useShieldGrantAnimations, useStrategemSoundEffects, useDeployEffectSounds, useUnitRepositionAnimations, RepositioningProvider, RepositioningOverlay, useStrategemGeneralDamage, useDecommissionAnimations, DecommissionOverlay, ShockwaveEffect, useStrategemShockwave, InfernoNapalmEffect, useInfernoDropNapalm, ExplosionEffect } from './effects';
import { CounterStrikeDialog, OpponentCounterStrikeDialog, useCounterStrike } from './counterstrike';
import { StrategicForecastDialog } from './strategicforecast';
import { MulliganDialog, useMulligan } from './mulligan';
import StateSyncMonitor from './components/StateSyncMonitor';
import StateSyncToggle from './components/StateSyncToggle';
import RevealedOrderModal from './RevealedOrderModal';
import { TargetingCancelDialog, useTargetingCancel } from './cancel';
import { useTranslation } from 'react-i18next';
import { updateDiscordPresence, clearDiscordPresence } from './hooks/useDiscordPresence';
import BattleActionLog from './BattleActionLog/BattleActionLog';
import { useUnitRegistry, updateUnitRegistry } from './UnitRegistry';
import { detectActions } from './actionDetection';
import { useContainerScale } from './hooks/useViewportScale';
import {
  MobilePlayerAvatar,
  MobilePlayerStats,
  MobilePlayerAdvisor,
  MobilePlayerDrones,
  MobilePlayerBonuses,
  MobileOpponentAvatars,
  MobileOpponentStats,
  MobileOpponentDrones,
  MobileOpponentBonuses,
  MobileOrderCarousel
} from './components/mobile';
import useTouchDragDeploy from './hooks/useTouchDragDeploy';
import TouchDragOverlay from './components/TouchDragOverlay';
import useTouchDragAttack from './hooks/useTouchDragAttack';
import AttackDragOverlay from './components/AttackDragOverlay';
import { useAutobattlerMode } from './hooks/useAutobattlerMode';
import { useFriends } from './friends';
import {
  extractBattleGameIdFromPath,
  resolveExpectedSessionGameId,
  isWsMessageForDifferentSession as isWsMessageForDifferentSessionId,
  shouldDiscardStaleGameFetch,
} from './utils/battleWsSessionGuard';
import { useSpectateSpeedControl } from './hooks/useSpectateSpeedControl';
import { SpectateSpeedControl } from './components/SpectateSpeedControl';

import { getTitleColorHex } from './constants/titleColors';

// Faction colors for health orbs (matching Strategy.js)
const factionColors = {
  'Remnants of Humanity': '#556B2F',
  'Exolot Dominion': '#4B0082',
  'League of Shadows': '#1A1A1A',
  'Ra\'s Children': '#F4A460',
  'Asrah': '#8B0000',
  'The Sentients': '#ADD8E6',
  'Combined': '#FFA500'
};

/** Legendary-unique deploy ray with sparkling orbs (e.g. Samuel Johnson). Orbs randomly distributed throughout the ray; sibling so not blurred. */
const LegendaryUniqueDeployRay = () => (
  <>
    <div className="legendary-unique-deploy-ray" aria-hidden="true" />
    <div className="legendary-unique-deploy-ray-orbs" aria-hidden="true">
      {Array.from({ length: 126 }, (_, i) => (
        <span
          key={i}
          className="legendary-deploy-glow-orb"
          style={{
            left: `${12 + (i * 13) % 76}%`,
            top: `${4 + (i * 19) % 92}%`,
            animationDelay: `${(i * 0.07) % 2}s`,
          }}
        />
      ))}
    </div>
  </>
);

/** Legendary deploy glow with flickering orbs (non-unique legendaries e.g. t-rex). Orbs are a sibling layer so they are not blurred by the glow's filter. */
const LegendaryDeployGlow = () => (
  <>
    <div className="legendary-deploy-glow" aria-hidden="true" />
    <div className="legendary-deploy-glow-orbs" aria-hidden="true">
      {Array.from({ length: 42 }, (_, i) => (
        <span
          key={i}
          className="legendary-deploy-glow-orb"
          style={{
            left: `${8 + (i * 11) % 84}%`,
            top: `${8 + (i * 17) % 84}%`,
            animationDelay: `${(i * 0.08) % 2}s`,
          }}
        />
      ))}
    </div>
  </>
);

// Get faction orb background color/gradient (matching Strategy.js implementation)
const getFactionOrbBackground = (factionName) => {
  if (!factionName || factionName === 'Unknown') {
    return '#ccc';
  }

  // Check if it's a combined faction and return gradient with smooth blending
  // Using 45% and 55% stops creates a smooth transition zone in the middle
  if (factionName === 'The Conspiracy') {
    return 'linear-gradient(to right, #8B0000 45%, #1A1A1A 55%)';
  } else if (factionName === 'The Corrupted Core') {
    return 'linear-gradient(to right, #4B0082 45%, #8B0000 55%)';
  } else if (factionName === 'The Covenant') {
    return 'linear-gradient(to right, #F4A460 45%, #1A1A1A 55%)';
  } else if (factionName === 'The Divine Syndicate') {
    return 'linear-gradient(to right, #F4A460 45%, #4B0082 55%)';
  } else if (factionName === 'The Forsaken Pact') {
    return 'linear-gradient(to right, #556B2F 45%, #8B0000 55%)';
  } else if (factionName === 'The Hollow') {
    return 'linear-gradient(to right, #F4A460 45%, #8B0000 55%)';
  } else if (factionName === 'The Iron Alliance') {
    return 'linear-gradient(to right, #556B2F 45%, #4B0082 55%)';
  } else if (factionName === 'The Last Crusade') {
    return 'linear-gradient(to right, #F4A460 45%, #556B2F 55%)';
  } else if (factionName === 'The Shadow Consortium') {
    return 'linear-gradient(to right, #1A1A1A 45%, #4B0082 55%)';
  } else if (factionName === 'The Silent Accord') {
    return 'linear-gradient(to right, #556B2F 45%, #1A1A1A 55%)';
  }

  // Return base faction color
  return factionColors[factionName] || '#ccc';
};

/**
 * Convert server pendingDeployAnimations to viewer-relative slots and register deploy VFX.
 * Removes gameStateFragment.pendingDeployAnimations after processing (same as main WS handler).
 */
function consumePendingDeployAnimationsForViewer(gameStateFragment, viewerUserId, deployHook) {
  if (!gameStateFragment?.pendingDeployAnimations || !Array.isArray(gameStateFragment.pendingDeployAnimations)) {
    return;
  }
  if (!deployHook?.registerAnimation) {
    throw new Error('consumePendingDeployAnimationsForViewer: deployHook.registerAnimation is missing');
  }
  const pending = gameStateFragment.pendingDeployAnimations;
  pending.forEach((animationData) => {
    const fromPlayerId = String(animationData.fromSlot.playerId) === String(viewerUserId) ? 'your' : 'opponent';
    const toPlayerId = String(animationData.toSlot.playerId) === String(viewerUserId) ? 'your' : 'opponent';

    const fromZone = animationData.fromSlot.zone;
    const toZone = animationData.toSlot.zone;

    let fromZoneFull = fromZone.startsWith('opponent_') || fromZone.startsWith('your_')
      ? fromZone
      : (fromPlayerId === 'your' ? `your_${fromZone}` : `opponent_${fromZone}`);

    if (fromPlayerId === 'your' && fromZoneFull.startsWith('opponent_')) {
      fromZoneFull = `your_${fromZoneFull.slice('opponent_'.length)}`;
    } else if (fromPlayerId === 'opponent' && fromZoneFull.startsWith('your_')) {
      fromZoneFull = `opponent_${fromZoneFull.slice('your_'.length)}`;
    }

    const toZoneFull = toZone === 'general'
      ? 'general'
      : (toZone.startsWith('opponent_') || toZone.startsWith('your_')
        ? toZone
        : (toPlayerId === 'your' ? `your_${toZone}` : `opponent_${toZone}`));

    const toSlotConverted = {
      ...animationData.toSlot,
      zone: toZoneFull,
      playerId: toPlayerId
    };
    if (toZoneFull === 'opponent_drone' || toZoneFull === 'your_drone') {
      const droneIndex = animationData.toSlot.index;
      const droneEl = typeof document !== 'undefined' && document.querySelector(
        `[data-drone-zone="${toZoneFull}"][data-drone-index="${droneIndex}"]`
      );
      if (droneEl) {
        const rect = droneEl.getBoundingClientRect();
        toSlotConverted.cachedTargetPixel = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        };
      }
    }
    deployHook.registerAnimation({
      ...animationData,
      fromSlot: {
        ...animationData.fromSlot,
        zone: fromZoneFull,
        playerId: fromPlayerId
      },
      toSlot: toSlotConverted
    });
  });
  delete gameStateFragment.pendingDeployAnimations;
}

/**
 * Session-guarded consume for any handler path. Logs failures; does not throw (state can still be applied).
 */
function tryConsumePendingDeployAnimationsForViewer(
  gameStateFragment,
  viewerUserId,
  deployHook,
  msgGameId,
  isDifferentSession
) {
  if (!gameStateFragment?.pendingDeployAnimations?.length) {
    return;
  }
  if (typeof isDifferentSession === 'function' && msgGameId != null && isDifferentSession(msgGameId)) {
    return;
  }
  if (!deployHook?.registerAnimation) {
    console.error('🎬 tryConsumePendingDeployAnimationsForViewer: deployHook.registerAnimation is missing');
    return;
  }
  try {
    consumePendingDeployAnimationsForViewer(gameStateFragment, viewerUserId, deployHook);
  } catch (err) {
    console.error('🎬 tryConsumePendingDeployAnimationsForViewer failed', err);
  }
}

// Calculate base factions and faction name from strategy orders (matching server logic)
// This is needed because getStrategyWithOrders() doesn't add faction_name to the strategy object
const getBaseFactionsFromStrategy = (strategy) => {
  if (!strategy || !strategy.orders || strategy.orders.length === 0) {
    return [];
  }

  const combinedFactions = {
    'The Conspiracy': ['League of Shadows', 'Asrah'],
    'The Corrupted Core': ['Exolot Dominion', 'Asrah'],
    'The Covenant': ['Ra\'s Children', 'League of Shadows'],
    'The Divine Syndicate': ['Ra\'s Children', 'Exolot Dominion'],
    'The Forsaken Pact': ['Remnants of Humanity', 'Asrah'],
    'The Hollow': ['Ra\'s Children', 'Asrah'],
    'The Iron Alliance': ['Remnants of Humanity', 'Exolot Dominion'],
    'The Last Crusade': ['Ra\'s Children', 'Remnants of Humanity'],
    'The Shadow Consortium': ['League of Shadows', 'Exolot Dominion'],
    'The Silent Accord': ['Remnants of Humanity', 'League of Shadows']
  };

  const baseFactionNames = ['Asrah', 'Exolot Dominion', 'League of Shadows', 'Ra\'s Children', 'Remnants of Humanity'];
  const combinedFactionNames = Object.keys(combinedFactions);

  // Get unique factions from orders
  const uniqueFactions = [...new Set(strategy.orders.map(order => order.faction_name).filter(Boolean))];

  // Get base factions (excluding The Sentients and combined faction names)
  const baseFactions = uniqueFactions
    .filter(f => f && f !== null && f.trim() !== '')
    .filter(f => !combinedFactionNames.includes(f)) // Remove combined faction names
    .filter(f => baseFactionNames.includes(f) || f === 'The Sentients'); // Only keep base factions (and Sentients)

  // Remove The Sentients (no dedicated background mapping)
  const baseFactionsOnly = baseFactions.filter(f => f !== 'The Sentients');

  return baseFactionsOnly;
};

const calculateFactionFromStrategy = (strategy) => {
  if (!strategy || !strategy.orders || strategy.orders.length === 0) {
    return null;
  }

  // If strategy already has faction_name, use it
  if (strategy.faction_name) {
    return strategy.faction_name;
  }

  const combinedFactions = {
    'The Conspiracy': ['League of Shadows', 'Asrah'],
    'The Corrupted Core': ['Exolot Dominion', 'Asrah'],
    'The Covenant': ['Ra\'s Children', 'League of Shadows'],
    'The Divine Syndicate': ['Ra\'s Children', 'Exolot Dominion'],
    'The Forsaken Pact': ['Remnants of Humanity', 'Asrah'],
    'The Hollow': ['Ra\'s Children', 'Asrah'],
    'The Iron Alliance': ['Remnants of Humanity', 'Exolot Dominion'],
    'The Last Crusade': ['Ra\'s Children', 'Remnants of Humanity'],
    'The Shadow Consortium': ['League of Shadows', 'Exolot Dominion'],
    'The Silent Accord': ['Remnants of Humanity', 'League of Shadows']
  };

  const baseFactionsOnly = getBaseFactionsFromStrategy(strategy);

  if (baseFactionsOnly.length === 0) {
    return 'Unknown';
  } else if (baseFactionsOnly.length === 1) {
    // Single base faction
    return baseFactionsOnly[0];
  } else {
    // Check if it matches a combined faction
    const sortedBaseFactions = [...baseFactionsOnly].sort();

    // Try to find a matching combined faction
    let matchedFaction = null;
    for (const [combinedFactionName, requiredFactions] of Object.entries(combinedFactions)) {
      const sortedRequired = [...requiredFactions].sort();
      const baseSet = new Set(sortedBaseFactions);
      const requiredSet = new Set(sortedRequired);

      if (baseSet.size === requiredSet.size &&
          sortedBaseFactions.every(f => requiredSet.has(f)) &&
          sortedRequired.every(f => baseSet.has(f))) {
        matchedFaction = combinedFactionName;
        break;
      }
    }

    if (matchedFaction) {
      return matchedFaction;
    }

    // No combined faction match; use the first base faction
    return baseFactionsOnly[0];
  }
};

// Map base factions to background images for BattleUI
const getBackgroundForBaseFactions = (baseFactions) => {
  if (!Array.isArray(baseFactions) || baseFactions.length === 0) {
    throw new Error('BattleUI background selection failed: no base factions provided.');
  }

  const factionBackgrounds = {
    'League of Shadows': ['alps.jpg'],
    Asrah: ['ganges.jpg'],
    "Ra's Children": ['pyramid.jpg'],
    'Exolot Dominion': ['lavender.jpg'],
    'Remnants of Humanity': ['citycenter.jpg', 'refinery.jpg', 'snow.jpg'],
  };

  const pool = baseFactions.reduce((acc, faction) => {
    const backgrounds = factionBackgrounds[faction];
    if (backgrounds && backgrounds.length > 0) {
      acc.push(...backgrounds);
    }
    return acc;
  }, []);

  if (pool.length === 0) {
    throw new Error(`BattleUI background selection failed: no background mapping for base factions: ${baseFactions.join(', ')}`);
  }

  const randomIndex = Math.floor(Math.random() * pool.length);
  return pool[randomIndex];
};
import CompletedOrdersModal from './CompletedOrdersModal';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './music/MusicContext';
import { getWebSocketAuthMessage, API_URL, WS_URL } from './config/apiConfig';
import CustomCursor from './components/CustomCursor';

// Action history limit - maximum number of actions to display
// Storage limit: Store up to 50 actions for swipe navigation
// Action log: server sends up to 50 entries; BattleActionLog shows 5 at a time with arrows
import StrategyModal from './StrategyModal';
import { useAttackAnimations } from './hooks/useAttackAnimations';
import { useGameStateSync } from './hooks/useGameStateSync';
import AttackAnimation from './components/AttackAnimation';
import { DeployTargetAnimation } from './components/DeployTargetAnimation';
import { useDeployTargetAnimations } from './hooks/useDeployTargetAnimations';
import { StrategemTargetAnimation } from './components/StrategemTargetAnimation';
import { doesStrategemDealDamageById, doesStrategemDestroyUnitsById, doesStrategemShrinkUnitsById } from './utils/strategemUtils';
import { useStrategemTargetAnimations } from './hooks/useStrategemTargetAnimations';
import { useOpponentStrategemAnimations } from './hooks/useOpponentStrategemAnimations';
import { useBattleMusic } from './hooks/useBattleMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { 
  needsTargetingOnDeploy, 
  getAvailableTargets, 
  targetDeploy, 
  useTargetingState,
  getTargetingMode,
  getTargetingBorderStyle,
  getTargetingImageBorderStyle,
  getTargetingMessage,
  handleTargetingClick,
  isTarget,
  attachWeapon,
  weaponDeployTarget,
  targetDrone,
  targetImmobilize,
  targetConfuse,
  targetStrategem,
  targetSpecial,
  getZoneTargetingStyles,
  handleZoneClick,
  shouldHighlightZone,
  getZoneTargetingMessage,
  droneNeedsTargeting,
  droneCanTargetOpponents,
  droneNoTargeting,
  unitNeedsTargetingOnDeploy,
  strategemNeedsTargeting,
  weaponNeedsTargeting,
  specialUnitNeedsTargeting,
  getDeployTargetingRules,
  getStrategemTargetingRules,
  getValidTargets,
  canTargetUnit,
  TargetingCursor
} from './targeting';
import { getTargetingBorderStyles, getTargetingBorderClassName } from './targeting/TargetingBorder';

/** When tutorial overlay restricts strategem to specific units (data-tutorial-overlay-strategem-restrict), return true if this unit is in the allow list so we can show the yellow target border. */
function isTutorialOverlayStrategemTarget(unit, selectedStrategemId) {
  if (!unit || selectedStrategemId == null || typeof document === 'undefined' || !document.body) return false;
  try {
    const attr = document.body.getAttribute('data-tutorial-overlay-strategem-restrict');
    if (!attr) return false;
    const overlay = JSON.parse(attr);
    const sid = Number(selectedStrategemId) || selectedStrategemId;
    const allowed = overlay[String(sid)] ?? overlay[sid];
    if (!Array.isArray(allowed) || allowed.length === 0) return false;
    const orderId = unit.id ?? unit.orderId ?? unit.order_id;
    if (orderId == null) return false;
    return new Set(allowed.map(a => Number(a))).has(Number(orderId));
  } catch (_) { return false; }
}

// WebSocket URL - imported from apiConfig (uses environment variable, falls back to localhost for development)
const DEBUG = false;

// ✅ FIX: Wrapper component for attack animations
// NOTE: Not memoized because internal state (damageAnimationTrigger) needs to trigger re-renders
const MemoizedAttackAnimation = ({ 
  attackId, 
  attacker, 
  outcome,
  attackerPlayerId,
  targetPlayerId,
  attackAnimations,
  attackInProgressRef,
  setDamageAnimationTrigger, // ✅ FIX: Pass parent's state setter
  damageAnimationTriggeredRef, // ✅ BUG FIX: Pass ref to track triggered animations
  animationSpeedMultiplier = 1 // ✅ SPEED CONTROL: Animation speed multiplier
}) => {
  // Memoize slot objects to prevent recreation on every render
  const fromSlot = useMemo(() => ({
    zone: outcome.attackerZone,
    index: outcome.attackerIndex,
    playerId: attackerPlayerId
  }), [outcome.attackerZone, outcome.attackerIndex, attackerPlayerId]);
  
  const toSlot = useMemo(() => ({
    zone: outcome.targetZone,
    index: outcome.targetIndex,
    playerId: targetPlayerId
  }), [outcome.targetZone, outcome.targetIndex, targetPlayerId]);
  
  // Memoize callbacks to prevent recreation on every render
  const onComplete = useCallback(() => {
    attackAnimations.completeAnimation(attackId);
    attackInProgressRef.current = false;
  }, [attackId, attackAnimations, attackInProgressRef]);
  
  // ✅ FIX: onDiveComplete callback that stores damage animation in parent state
  // This ensures the animation persists even if this component unmounts
  const onDiveComplete = useCallback((targetPosition) => {
    // ✅ FIX: Trigger damage animation at exact target position
    // This should ALWAYS fire, even if attacker dies (animation completes before unit removal)
    // ✅ BUG FIX: Only trigger ONE damage animation at the TARGET position, never at attacker position
    // ✅ BUG FIX: Validate that this is actually the target position, not the attacker position
    // If targetZone/attackerZone are stored, verify we're using the target
    if (targetPosition?.targetZone && targetPosition?.attackerZone) {
      if (targetPosition.targetZone === targetPosition.attackerZone && 
          targetPosition.targetIndex === targetPosition.attackerIndex) {
        console.error(`❌ BUG: onDiveComplete received position where target === attacker! This is wrong!`, {
          attackId,
          targetPosition
        });
        return; // Don't trigger animation at wrong position
      }
    }
    
    // ✅ CRITICAL FIX: Validate targetPosition more strictly
    // Must have valid x and y coordinates (not 0, not NaN, not undefined)
    if (!targetPosition) {
      console.error(`❌ onDiveComplete: targetPosition is null/undefined for attack ${attackId}`);
      return;
    }
    
    const x = targetPosition.x;
    const y = targetPosition.y;
    
    // ✅ FIX: Reject positions that are 0, NaN, or undefined (likely invalid/fallback positions)
    if (x === undefined || x === null || isNaN(x) || x === 0 || 
        y === undefined || y === null || isNaN(y) || y === 0) {
      console.error(`❌ onDiveComplete: Invalid targetPosition coordinates for attack ${attackId}:`, {
        x,
        y,
        targetPosition
      });
      return;
    }
    
    // ✅ FIX: Also check if position seems reasonable (not at screen edge 0,0)
    // Valid positions should be within screen bounds (roughly 100-2000 for x, 100-1000 for y)
    // ✅ BUG FIX: Reject suspicious positions that are likely invalid (e.g., from empty slots or wrong calculations)
    // ✅ GENERAL ATTACK FIX: Allow y < 50 for general attacks (generals are positioned at top of screen)
    const isGeneralAttack = outcome.isGeneralAttack || outcome.targetZone === 'general' || targetPosition?.targetZone === 'general';
    const minY = isGeneralAttack ? 0 : 50; // Generals can be at y < 50 (top of screen)
    if (x < 50 || x > 3000 || y < minY || y > 2000) {
      console.error(`❌ onDiveComplete: Rejecting suspicious targetPosition coordinates for attack ${attackId} (likely invalid):`, {
        x,
        y,
        targetPosition,
        isGeneralAttack,
        minY
      });
      // ✅ BUG FIX: Don't allow suspicious positions - they cause animations outside of slots
      return;
    }
    
    // Store in parent's damageAnimationTriggers Map (passed via props)
    if (setDamageAnimationTrigger) {
      // ✅ BUG FIX: Check if we've already triggered a damage animation for this attack
      // This prevents duplicate animations when both units die
      if (damageAnimationTriggeredRef && damageAnimationTriggeredRef.current && damageAnimationTriggeredRef.current.has(attackId)) {
        console.error(`❌ BUG: onDiveComplete called multiple times for attack ${attackId}! Only ONE damage animation should be triggered per attack.`, {
          attackId,
          position: { x, y },
          targetZone: targetPosition?.targetZone,
          attackerZone: targetPosition?.attackerZone
        });
        return; // Don't trigger second animation
      }
      
      setDamageAnimationTrigger(prev => {
        const next = new Map(prev);
        // ✅ FIX: Only set if not already set (prevent duplicates)
        const existing = next.get(attackId);
        if (!existing) {
          // Mark this attackId as having triggered a damage animation
          if (damageAnimationTriggeredRef && damageAnimationTriggeredRef.current) {
            damageAnimationTriggeredRef.current.add(attackId);
          }
          next.set(attackId, {
            attackId,
            x: x,
            y: y,
            slotElement: targetPosition.slotElement,
            rect: targetPosition.rect,
            timestamp: Date.now(),
            targetZone: targetPosition.targetZone,
            targetIndex: targetPosition.targetIndex,
            attackerZone: targetPosition.attackerZone,
            attackerIndex: targetPosition.attackerIndex,
            isGeneralAnimation: isGeneralAttack // ✅ GENERAL ATTACK FIX: Mark general attacks for proper rendering
          });
        } else {
          console.error(`❌ BUG: Damage animation already exists for attack ${attackId}! This should not happen.`, {
            existing: { x: existing.x, y: existing.y },
            new: { x, y },
            attackId
          });
          // Don't overwrite - keep the first one (should be the target position)
        }
        return next;
      });
    } else {
      console.error(`❌ onDiveComplete: setDamageAnimationTrigger is not available for attack ${attackId}`);
    }
  }, [attackId, setDamageAnimationTrigger, damageAnimationTriggeredRef, outcome]);
  
  return (
    <>
      <AttackAnimation
        key={attackId}
        order={attacker}
        fromSlot={fromSlot}
        toSlot={toSlot}
        outcome={outcome}
        attackerSurvived={!outcome.attackerDestroyed}
        onComplete={onComplete}
        onDiveComplete={onDiveComplete}
        animationSpeedMultiplier={animationSpeedMultiplier}
      />
    </>
  );
};

// === Ability-aware helpers ===

// Token management functions moved to ./auth/tokenManager.js


// ✅ REFACTORED: validateGameStateInstanceIds moved to useGameStateSync hook


function BattleUI({ user, userId, token, onLogout, initialGameState, initialGameId, autobattlerWs, onGameStateChange, blockedOrderIds = [] }) {

  const { t, i18n: i18nInstance } = useTranslation();
  const { soundsVolume } = useMusic();
  
  // Viewport scaling for responsive design
  // Scales the game container to fit any screen size
  // Desktop: 1920x1080 base, Mobile: 844x390 base
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();
  
  
  // Spectator mode support
  const { 
    isSpectating, 
    spectatingGameState, 
    spectatingPlayerId, 
    pendingAttackInitiated, // ✅ SPECTATOR FIX: Get pending attack-initiated messages
    setPendingAttackInitiated, // ✅ SPECTATOR FIX: Clear after processing
    pendingEmote, // ✅ SPECTATOR FIX: Get pending emote messages
    setPendingEmote, // ✅ SPECTATOR FIX: Clear after processing
    stopSpectating,
    friends,
    spectators, // Users watching you
    handleSpectatorJoined, // ✅ FIX: Function to handle spectator-joined from game WebSocket
    handleSpectatorLeft // ✅ FIX: Function to handle spectator-left from game WebSocket
  } = useFriends();
  
  // Get friend's name for spectator banner
  const spectatingFriend = isSpectating ? friends.find(f => f.id === spectatingPlayerId) : null;
  const spectatingFriendName = spectatingFriend?.username || 'Friend';
  
  // Helper function to translate backend error messages
  const translateBackendError = (message) => {
    if (!message) return message;
    
    // Map common backend error messages to translation keys
    if (message.includes("Not your turn") || message.includes("not your turn")) {
      return t('battleUI.notYourTurn');
    }
    if (message.includes("Not enough intelligence")) {
      // Extract numbers if present (e.g., "Need 5, have 3")
      const needMatch = message.match(/Need (\d+)/);
      const haveMatch = message.match(/have (\d+)/);
      if (needMatch && haveMatch) {
        return `${t('battleUI.notEnoughIntelligence')}! ${t('battleUI.need')} ${needMatch[1]}, ${t('battleUI.have')} ${haveMatch[1]}`;
      }
      return t('battleUI.notEnoughIntelligence');
    }
    
    // Return original message if no translation found
    return message;
  };
  
  const location = useLocation();

  // Background selection:
  // - Tutorial missions 1–4 use background_tutorial_1.jpg
  // - Other games use a background derived from the opponent's base factions:
  //   League of Shadows → alps.jpg
  //   Asrah → ganges.jpg
  //   Ra's Children → pyramid.jpg
  //   Exolot Dominion → lavender.jpg
  //   Remnants of Humanity → citycenter.jpg / refinery.jpg / snow.jpg
  //   Combined factions (e.g. The Conspiracy) pick randomly from all involved base factions.
  const [backgroundImage, setBackgroundImage] = useState(() => {
    const tutorialMissionId = initialGameState?.missionId;
    const tutorialMissions = ['chapter_1_mission_1', 'chapter_1_mission_2', 'chapter_1_mission_3', 'chapter_1_mission_4'];
    if (tutorialMissions.includes(tutorialMissionId)) {
      return 'background_tutorial_1.jpg';
    }

    // If we don't have an initial game state yet (e.g. matchmaking/spectator paths),
    // use a neutral default; it will be updated once gameState/strategies are available.
    if (!initialGameState || !initialGameState.players || !initialGameState.state) {
      return 'snow.jpg';
    }

    try {
      const opponentId = initialGameState.players.find((id) => String(id) !== String(userId));
      const opponentState = opponentId ? initialGameState.state[opponentId] : null;
      const opponentStrategy = opponentState?.strategy;
      const baseFactions = getBaseFactionsFromStrategy(opponentStrategy);
      return getBackgroundForBaseFactions(baseFactions);
    } catch (error) {
      console.error('BattleUI background initialization failed:', error);
      // Keep neutral default; error is logged for debugging.
      return 'snow.jpg';
    }
  });

  // Mobile landscape detection
  const [isMobile, setIsMobile] = useState(() => {
    return window.matchMedia('(max-height: 768px)').matches || window.innerHeight <= 768;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-height: 768px)');
    const handleChange = (e) => {
      setIsMobile(e.matches || window.innerHeight <= 768);
    };
    mediaQuery.addEventListener('change', handleChange);
    // Also check on resize
    const handleResize = () => {
      setIsMobile(window.innerHeight <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const navigate = useNavigate();
  const { strategyId } = location.state ?? {};

  const { gameId: urlGameId } = useParams();
  // Use initialGameId prop if provided (for campaign), otherwise use URL param
  const gameId = initialGameId || urlGameId;

  // ✅ DEV AUTOBATTLER: Support autobattlerWs prop (for dev autobattler and regular autobattler)
  const passedWs = location.state?.ws || autobattlerWs;
  const [ws, setWs] = useState(passedWs ?? null);

  // ✅ FIX: Initialize gameState from props, navigation state, or null (avoids unnecessary reconnect).
  // Declared before any useEffect that depends on gameState to avoid "Cannot access before initialization".
  const [gameState, setGameState] = useState(initialGameState || (location.state?.gameState ?? null));

  // Set background once per game and never switch for the entire session.
  const backgroundSetForGameRef = useRef(false);
  useEffect(() => {
    if (backgroundSetForGameRef.current) return;

    const tutorialMissionId = gameState?.missionId;
    const tutorialMissions = ['chapter_1_mission_1', 'chapter_1_mission_2', 'chapter_1_mission_3', 'chapter_1_mission_4'];
    if (tutorialMissions.includes(tutorialMissionId)) {
      setBackgroundImage('background_tutorial_1.jpg');
      backgroundSetForGameRef.current = true;
      return;
    }

    if (!gameState || !gameState.players || !gameState.state || !userId) {
      return;
    }

    try {
      const opponentId = gameState.players.find((id) => String(id) !== String(userId));
      const opponentState = opponentId ? gameState.state[opponentId] : null;
      const opponentStrategy = opponentState?.strategy;
      const baseFactions = getBaseFactionsFromStrategy(opponentStrategy);
      const background = getBackgroundForBaseFactions(baseFactions);
      setBackgroundImage(background);
      backgroundSetForGameRef.current = true;
    } catch (error) {
      console.error('BattleUI background update failed:', error);
    }
  }, [gameState, userId]);

  const isBotGame = location.state?.isBotGame;

  // Capture initialGameState at mount time so the autobattler check in the game-fetch
  // effect doesn't re-trigger when the parent passes a new reference.
  const initialGameStateRef = useRef(initialGameState);
  
  // CAMPAIGN: Propagate game state to parent so "Out of reach" (queued by backend when bot deploys) can show
  useEffect(() => {
    if (gameState && onGameStateChange) onGameStateChange(gameState);
  }, [gameState, onGameStateChange]);
  
  // When spectating, use the spectating game state from context
  // Use useMemo to ensure we get a new reference when spectatingGameState changes
  const effectiveGameState = useMemo(() => {
    return isSpectating ? spectatingGameState : gameState;
  }, [isSpectating, spectatingGameState, gameState]);
  const effectiveUserId = isSpectating ? spectatingPlayerId : userId;
  /** Board POV for remapping deploy VFX (must not use spectator login id when spectating). */
  const deployAnimationViewerUserIdRef = useRef(effectiveUserId);
  deployAnimationViewerUserIdRef.current = effectiveUserId;
  
  // Autobattler mode support (must be after effectiveGameState is defined)
  const { isAutobattler, isSpectator: isAutobattlerSpectator } = useAutobattlerMode(effectiveGameState, userId);
  
  // ✅ SPEED CONTROL: Initialize speed control for autobattler spectators
  // Pass ws and gameId for backend pause/resume communication
  const speedControl = useSpectateSpeedControl(
    isAutobattlerSpectator, // Only enable for autobattler spectators
    100, // Initial speed: 100% (real-time)
    ws,  // WebSocket for pause/resume messages
    gameId // Game ID for pause/resume messages
  );
  
  // ✅ FIX: Store speedControl.queueUpdate and isAutobattlerSpectator in refs to avoid stale closures
  const speedControlQueueUpdateRef = useRef(speedControl.queueUpdate);
  const isAutobattlerSpectatorRef = useRef(isAutobattlerSpectator);
  useEffect(() => {
    if (speedControl.queueUpdate) {
      speedControlQueueUpdateRef.current = speedControl.queueUpdate;
    }
    isAutobattlerSpectatorRef.current = isAutobattlerSpectator;
  }, [speedControl.queueUpdate, isAutobattlerSpectator]);
  
  // ✅ FIX: Initialize gameStateRef and store opponent strategy when gameState comes from navigation (only once on mount)
  const initializedFromNavigationRef = useRef(false);
  useEffect(() => {
    if (gameState && location.state?.gameState && !initializedFromNavigationRef.current) {
      // GameState came from navigation state - initialize refs and store opponent strategy (only once)
      gameStateRefRef.current.current = gameState;
      storeOpponentStrategyId(gameState);
      // Validate state
      if (!validateStateRef.current(gameState)) {
        console.error('🚨 STATE VALIDATION FAILED in navigation state: InstanceIds missing!');
      }
      initializedFromNavigationRef.current = true;
    }
  }, []); // Only run once on mount
  
  // ✅ CAMPAIGN TUTORIAL: Dispatch when Opportunist (302) deployed to flank (for tutorial auto-advance)
  useEffect(() => {
    if (!gameState?.campaignMode || isSpectating || !userId) return;
    const flankUnits = gameState.state?.[userId]?.playedOrders?.flank || [];
    if (flankUnits.some(u => u && u.id === 302)) {
      const backendQueue = gameState.scriptState?.tutorialQueue || [];
      const nextTutorial = backendQueue.find(t => (t.title || t.id) === "Special Units");
      window.dispatchEvent(new CustomEvent('opportunist-deployed-to-flank', {
        detail: { gameState, nextTutorial }
      }));
    }
  }, [gameState, userId, isSpectating]);
  
  // Initialize attack animations hook
  // ✅ SPECTATOR FIX: Pass isSpectating flag so spectators don't send animation-complete messages
  // ✅ AUTOBATTLER FIX: Also check isAutobattlerSpectator - autobattler spectators should not send animation-complete
  // ✅ SPEED CONTROL: Pass animation speed multiplier for slower/faster animations
  const attackAnimations = useAttackAnimations(ws, gameId, isSpectating || isAutobattlerSpectator, speedControl.animationSpeedMultiplier);
  
  // ✅ CRITICAL FIX: Store attackAnimations in ref for passedWs handler
  // This ensures the handler always has access to the latest attackAnimations (avoids stale closure)
  const attackAnimationsRef = useRef(attackAnimations);
  attackAnimationsRef.current = attackAnimations;
  
  // ✅ FIX: Store damage animation triggers OUTSIDE MemoizedAttackAnimation
  // This ensures they persist even after the attack component unmounts
  const [damageAnimationTriggers, setDamageAnimationTriggers] = useState(new Map());
  const [explosionEffects, setExplosionEffects] = useState(new Map());
  
  // ✅ BUG FIX: Track which attackIds have already triggered damage animations
  // This prevents duplicate damage animations for the same attack
  const damageAnimationTriggeredRef = useRef(new Set());
  
  // Initialize deploy target animations hook
  const deployTargetAnimations = useDeployTargetAnimations();
  const deployTargetAnimationsRef = useRef(deployTargetAnimations);
  deployTargetAnimationsRef.current = deployTargetAnimations;

  // Initialize strategem target animations hook
  const strategemTargetAnimations = useStrategemTargetAnimations();
  
  // Track which zones have had strategem 219 (Plague of Darkness) animations complete
  // This delays showing immobilize indicators until the attack animation arrives
  const strategem219CompletedZonesRef = useRef(new Set());
  /** Map instanceId -> immobilized; null until first snapshot (skip sound on initial load). */
  const prevImmobilizedByInstanceRef = useRef(null);
  
  // ✅ TWO-PHASE STRATEGEM SYSTEM: Track mapping from strategemInstanceId to backend's strategemAnimationId
  // This allows the onComplete callback to send the correct animation ID back to the backend
  const pendingStrategemAnimationIdsRef = useRef(new Map());
  // NOTE: useOpponentStrategemAnimations is called after actionLog is defined (see below)

  // Faction translations for tooltips
  const [factionTranslations, setFactionTranslations] = useState({});

  // Fetch faction translations
  useEffect(() => {
    const fetchFactionTranslations = async () => {
      try {
        const currentLanguage = i18nInstance.language || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        
        const response = await fetch(`${API_URL}/factions?lang=${encodeURIComponent(normalizedLanguage)}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const factions = await response.json();
          const translationMap = {};
          factions.forEach(faction => {
            translationMap[faction.english_name] = faction.name;
          });
          setFactionTranslations(translationMap);
        }
      } catch (err) {
        console.error('Failed to fetch faction translations:', err);
        setFactionTranslations({});
      }
    };
    
    fetchFactionTranslations();
  }, [i18nInstance.language, token]);

  // Helper to get translated faction name
  const getTranslatedFactionName = (factionName) => {
    if (!factionName) return null;
    return factionTranslations[factionName] || factionName;
  };
  
  // ✅ FIX: Initialize yourTurn from navigation state if available
  const [yourTurn, setYourTurn] = useState(location.state?.yourTurn ?? false);
  const [error, setError] = useState("");

  // Discord Rich Presence: Update when battle starts
  const battleStartTimeRef = useRef(null);
  const lastPresenceUpdateRef = useRef(null);
  
  useEffect(() => {
    let interval = null;
    
    // Only update if we have effectiveGameState and we're not spectating
    // Use effectiveGameState for consistency with rest of component
    if (effectiveGameState && !isSpectating) {
      // Battle has started - update Discord presence with elapsed time
      // Only set start time once when battle first starts
      if (!battleStartTimeRef.current) {
        battleStartTimeRef.current = Math.floor(Date.now() / 1000);
      }
      
      // Update presence with the fixed start time
      const updatePresence = () => {
        const startTimestamp = battleStartTimeRef.current;
        
        // Check if Electron and Discord API are available
        // Use direct API call since we need to pass a custom start timestamp
        if (window && window.electron && window.electron.discord) {
          try {
            window.electron.discord.updatePresence('In Battle', null, startTimestamp);
            lastPresenceUpdateRef.current = Date.now();
          } catch (error) {
            console.error('❌ Error updating Discord presence:', error);
          }
        } else {
          console.warn('⚠️ Discord API not available in BattleUI', {
            window: typeof window !== 'undefined',
            electron: !!(window && window.electron),
            discord: !!(window && window.electron && window.electron.discord)
          });
        }
      };
      
      // Update immediately
      updatePresence();
      
      // Also update periodically (every 20 seconds) to keep it fresh
      // Discord rate limit is 15 seconds, so 20 seconds is safe
      interval = setInterval(() => {
        if (effectiveGameState && !isSpectating && battleStartTimeRef.current) {
          updatePresence();
        }
      }, 20000);
    } else if (!effectiveGameState && battleStartTimeRef.current) {
      // Battle ended - reset start time
      battleStartTimeRef.current = null;
      lastPresenceUpdateRef.current = null;
    }
    
    // Cleanup: Clear presence when component unmounts (battle ends)
    return () => {
      if (interval) {
        clearInterval(interval);
      }
      if (!isSpectating) {
        battleStartTimeRef.current = null;
        lastPresenceUpdateRef.current = null;
        clearDiscordPresence();
      }
    };
  }, [effectiveGameState, isSpectating]);
  // Store opponent strategy ID for potential subscription after loss
  const opponentStrategyIdRef = useRef(null);
  // Track if turn state has been initialized (to avoid showing image on initial load)
  const turnStateInitializedRef = useRef(false);
  // Track previous turn state to detect turn changes (initialize with current state)
  const previousYourTurnRef = useRef(location.state?.yourTurn ?? false);

  // Campaign/initial state: when we receive initialGameState (e.g. from REST start-mission), set turn state
  // so the first WebSocket update (e.g. deploy response) is not mistaken for "turn just switched to player"
  // which would show the "TIME TO ACT" overlay incorrectly after the first deploy in tutorial.
  const initialTurnStateFromGameStateRef = useRef(false);
  useEffect(() => {
    if (initialTurnStateFromGameStateRef.current || !initialGameState || !userId) return;
    const isMyTurn = initialGameState.turn === userId ||
      initialGameState.turn === userId.toString() ||
      String(initialGameState.turn) === String(userId);
    if (isMyTurn) {
      previousYourTurnRef.current = true;
      setYourTurn(true);
      turnStateInitializedRef.current = true;
      initialTurnStateFromGameStateRef.current = true;
    }
  }, [initialGameState, userId]);

  // Helper function to store opponent strategy ID from gameState
  const storeOpponentStrategyId = (gameState) => {
    if (gameState && gameState.players && gameState.state) {
      const opponentId = gameState.players.find(id => id !== userId);
      if (opponentId && opponentId !== 'bot') {
        const opponentState = gameState.state[opponentId];
        if (opponentState?.strategy) {
          const strategy = opponentState.strategy;
          const strategyId = strategy.id;
          if (strategyId && typeof strategyId === 'number' && strategyId !== 'bot-hardcoded') {
            opponentStrategyIdRef.current = strategyId;
          }
        }
      }
    }
  };
  
  // Helper function to update yourTurn and detect turn changes
  const updateYourTurn = (newYourTurn, source = 'unknown') => {
    const wasYourTurn = previousYourTurnRef.current;
    previousYourTurnRef.current = newYourTurn;
    setYourTurn(newYourTurn);
    
    // Check for turn change to player (only after turn state is initialized)
    if (turnStateInitializedRef.current && wasYourTurn !== null && !wasYourTurn && newYourTurn) {
      setShowTurnStartImage(true);
    }
  };
  
  // ✅ REFACTORED: Use custom hook for state synchronization
  const stateSync = useGameStateSync(userId, (newState) => {
    // Callback when state is updated - update React state
    setGameState(newState);
  });
  
  // Extract state sync utilities
  const {
    gameStateRef,
    isStateStabilizing,
    isStateUpdating,
    showLoadingOverlay,
    updateState,
    validateState,
    lockUI,
    unlockUI,
    cleanup: cleanupStateSync,
    getCurrentState,
    getStateVersion,
    lastStateVersionRef
  } = stateSync;
  
  // ✅ FIX: Store hook functions in refs so WebSocket handler always has latest versions
  const unlockUIRef = useRef(unlockUI);
  const updateStateRef = useRef(updateState);
  const validateStateRef = useRef(validateState);
  const checkStateVersionRef = useRef(stateSync.checkStateVersion);
  const gameStateRefRef = useRef(gameStateRef);
  const wsConnectingRef = useRef(false); // ✅ FIX: Track if we're already connecting to prevent loops
  const currentSocketRef = useRef(null); // ✅ FIX: Track current socket for reconnect verification
  const gameSocketReconnectSentRef = useRef(false); // ✅ FIX: Skip closing socket in effect cleanup when reconnect already sent (avoids close on Strict Mode / effect re-run)
  // ✅ FIX: Token ref so WebSocket effect does NOT depend on token - prevents "connection lost" when
  // War Effort or other routes trigger App re-renders / token updates (effect would re-run and close socket)
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // ✅ CRITICAL: Ref for current gameId so WebSocket onmessage always sees the latest game.
  const gameIdRef = useRef(gameId);
  // Keep ref in sync with URL on every render.
  gameIdRef.current = gameId;

  // Source of truth for "which game is the user viewing" when handling WS messages.
  // Reading from URL at handler execution time avoids stale ref when navigating A→B before next render.
  // Must use React Router pathname (works with HashRouter / Electron). window.location.pathname
  // is often "/" or "/index.html" while the real route lives in the hash — wrong window path broke
  // session guards and winner/after-battle checks after Escape → new PvE.
  const getCurrentGameIdFromUrl = useCallback(() => {
    return extractBattleGameIdFromPath(location.pathname || '');
  }, [location.pathname]);

  // WebSocket session key (e.g. matchmaking `4_1_...`) must match what the user is actually viewing.
  // Prefer URL at handler time, then ref, then prop — avoids soft-lock when gameIdRef lags behind /battle/:id (reconnect, Strict Mode, etc.).
  const resolveWsSessionGameIdRef = useRef(() => null);
  const isWsMessageForDifferentSessionRef = useRef(() => false);
  resolveWsSessionGameIdRef.current = () =>
    resolveExpectedSessionGameId(getCurrentGameIdFromUrl, gameIdRef.current, gameId);
  isWsMessageForDifferentSessionRef.current = (msgGameId) =>
    isWsMessageForDifferentSessionId(msgGameId, resolveWsSessionGameIdRef.current());

  // Campaign: sync currentAllowedActions to server when tutorial step changes (so backend can enforce continueOnly etc.)
  useEffect(() => {
    if (!gameId || !gameId.startsWith('campaign_')) return;
    const handler = (e) => {
      const { gameId: detailGameId, currentAllowedActions } = e.detail || {};
      if (detailGameId !== gameId) return;
      const activeSocket = ws || currentSocketRef.current;
      if (!activeSocket || activeSocket.readyState !== 1) return;
      activeSocket.send(JSON.stringify({
        type: 'campaign-sync-allowed-actions',
        gameId: detailGameId,
        currentAllowedActions
      }));
    };
    window.addEventListener('campaign-sync-allowed-actions', handler);
    return () => window.removeEventListener('campaign-sync-allowed-actions', handler);
  }, [gameId, ws]);

  // Update refs when functions change
  useEffect(() => {
    unlockUIRef.current = unlockUI;
    updateStateRef.current = updateState;
    validateStateRef.current = validateState;
    checkStateVersionRef.current = stateSync.checkStateVersion;
    gameStateRefRef.current = gameStateRef;
  }, [unlockUI, updateState, validateState, stateSync.checkStateVersion, gameStateRef]);
  
  const lastActionTimestamp = useRef(0);
  const ACTION_COOLDOWN_MS = 150; // Minimum time between actions
  const [playerGoesFirst, setPlayerGoesFirst] = useState(null); // null = unknown, true = player goes first, false = player goes second
  const [showFirstTurnMessage, setShowFirstTurnMessage] = useState(false); // Controls visibility of first turn message
  const [showTurnStartImage, setShowTurnStartImage] = useState(false); // Controls visibility of turn start image
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedOrderInstanceId, setSelectedOrderInstanceId] = useState(null);
  
  // Avatar state
  const [avatars, setAvatars] = useState([]);
  const [selectedGeneral, setSelectedGeneral] = useState(location.state?.selectedGeneral || '');
  const [selectedAdvisor, setSelectedAdvisor] = useState(location.state?.selectedAdvisor || '');
  
  // Opponent avatar state
  const [opponentGeneral, setOpponentGeneral] = useState('');
  const [opponentAdvisor, setOpponentAdvisor] = useState('');
  const [opponentUsername, setOpponentUsername] = useState(null);
  
  // Preparation bonus state
  const [preparationBonus, setPreparationBonus] = useState(null);
  const [preparationBonusUsed, setPreparationBonusUsed] = useState(false);
  const preparationBonusPendingRef = useRef(false); // Track if activation is pending
  
  // Opponent preparation bonus state
  const [opponentPreparationBonus, setOpponentPreparationBonus] = useState(null);
  const [opponentPreparationBonusUsed, setOpponentPreparationBonusUsed] = useState(false);
  
  // Diplomatic accord state
  const [diplomaticAccord, setDiplomaticAccord] = useState(null);
  const [opponentDiplomaticAccord, setOpponentDiplomaticAccord] = useState(null);
  
  // Player ranks state
  const [playerRank, setPlayerRank] = useState(null);
  const [opponentRank, setOpponentRank] = useState(null);
  
  // Battle end transition state
  const [battleEndTransition, setBattleEndTransition] = useState({ isTransitioning: false, type: null }); // type: 'win' | 'loss' | null
  const [showVictoryImage, setShowVictoryImage] = useState(false);
  const [showDefeatImage, setShowDefeatImage] = useState(false);
  const [victoryImageWhite, setVictoryImageWhite] = useState('rgba(255, 255, 255, 1)'); // Default to pure white, will be sampled from image
  const [defeatImageGray, setDefeatImageGray] = useState('rgba(0, 0, 0, 1)'); // Default to pure black, will be sampled from image
  const battleEndSoundRef = useRef(null);
  const isTransitioningRef = useRef(false); // Ref to track transition state synchronously
  
  // Function to handle battle end transition
  // triggerSource: optional string for diagnostics (e.g. 'game-update-winner', 'player-resigned', 'attack-complete')
  const handleBattleEnd = useCallback((didYouWin, gameId, socketToClose = null, isFriendlyMatch = false, triggerSource = 'unknown') => {
    // Access gameState from component scope
    const currentGameState = gameState || location.state?.gameState;
    // Prevent multiple calls
    if (isTransitioningRef.current) {
      return;
    }
    
    const transitionType = didYouWin ? 'win' : 'loss';
    
    // Set refs immediately to prevent race conditions
    isTransitioningRef.current = true;
    exitingRef.current = true;

    // ✅ FIX: Clear all animation states to prevent ghost images
    // This fixes the issue where attack animation images persist after game ends
    // Clear attack animations (pending attacks, active animations, hidden units)
    if (attackAnimationsRef.current?.reset) {
      attackAnimationsRef.current.reset();
    }
    
    // Clear damage animation triggers (explosion effects at attack targets)
    setDamageAnimationTriggers(new Map());
    
    // Clear damage animation triggered tracking ref
    if (damageAnimationTriggeredRef.current) {
      damageAnimationTriggeredRef.current.clear();
    }
    
    // Clear explosion effects
    setExplosionEffects(new Map());

    // Reset image visibility for a fresh transition
    setShowVictoryImage(false);
    setShowDefeatImage(false);
    
    // Start transition animation FIRST (fade to white/black)
    setBattleEndTransition({ isTransitioning: true, type: transitionType });
    
    // Helper to play the correct battle end sound
    const playBattleEndSound = () => {
      const soundPath = didYouWin ? '/sounds/victory.mp3' : '/sounds/loss.mp3';
      const audio = new Audio(soundPath);
      audio.volume = soundsVolume;
      audio.load(); // Ensure audio is ready
      battleEndSoundRef.current = audio;
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          console.error('Error playing battle end sound:', err);
        });
      }
    };
    
    // Close socket (use provided socket or ws from state)
    const socket = socketToClose || ws;
    try { 
      if (socket) socket.close(); 
    } catch {}
    
    // Prepare navigation state (include _triggerSource for redirect diagnostics)
    const navigationState = { result: transitionType, gameId, _triggerSource: triggerSource };
    if (isFriendlyMatch) {
      navigationState.isFriendlyMatch = true;
    }
    // Check if this is a tournament match from gameState
    if (currentGameState?.isTournamentMatch) {
      navigationState.isTournamentMatch = true;
      navigationState.gameState = currentGameState; // Pass gameState so AfterBattle can check it
    }
    // Check if this is an autobattler match
    if (currentGameState?.isAutobattler) {
      navigationState.isAutobattler = true;
      navigationState.gameState = currentGameState; // Pass gameState so AfterBattle can check it
    }
    // Check if this is a war effort battle (redirect back to war effort page)
    if (currentGameState?.warEffortMissionId || currentGameState?.isWarEffort) {
      navigationState.isWarEffort = true;
      navigationState.gameState = currentGameState;
    }
    // Check if this is a campaign battle (for tutorial reward choice)
    if (currentGameState?.campaignMode && currentGameState?.missionId) {
      navigationState.isCampaign = true;
      navigationState.missionId = currentGameState.missionId;
      navigationState.gameState = currentGameState;
    }
    
    if (didYouWin) {
      // WIN: fade to white first, then play sound and show centered victory image
      setTimeout(() => {
        setShowVictoryImage(true);
        playBattleEndSound();
      }, 2000);
      
      // Give the player a moment to see the victory screen before navigating
      setTimeout(() => {
        navigate('/after-battle', { replace: true, state: navigationState });
      }, 4000);
    } else {
      // LOSS: play sound immediately when game ends, then fade to gray/black, then show full screen defeat image
      playBattleEndSound();
      setTimeout(() => {
        setShowDefeatImage(true);
      }, 2000);
      
      // Give the player a moment to see the defeat screen before navigating
      setTimeout(() => {
        navigate('/after-battle', { replace: true, state: navigationState });
      }, 4000);
    }
  }, [navigate, ws, battleEndTransition.isTransitioning, gameState, location]);
  
  // Sample the white color from victory.jpg to match the fade background
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Allow canvas access if needed
    
    img.onload = () => {
      try {
        // Create a temporary canvas to sample the image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        // Sample corner pixels to get the background white color
        // Sample multiple corners and average them for better accuracy
        const samplePoints = [
          [0, 0],           // Top-left
          [img.width - 1, 0], // Top-right
          [0, img.height - 1], // Bottom-left
          [img.width - 1, img.height - 1], // Bottom-right
          [img.width / 2, 0], // Top-center
          [img.width / 2, img.height - 1], // Bottom-center
        ];
        
        let totalR = 0, totalG = 0, totalB = 0;
        for (const [x, y] of samplePoints) {
          const pixelData = ctx.getImageData(x, y, 1, 1).data;
          totalR += pixelData[0];
          totalG += pixelData[1];
          totalB += pixelData[2];
        }
        
        const avgR = Math.round(totalR / samplePoints.length);
        const avgG = Math.round(totalG / samplePoints.length);
        const avgB = Math.round(totalB / samplePoints.length);
        
        // Store both RGB and RGBA formats for CSS
        const sampledColor = `rgb(${avgR}, ${avgG}, ${avgB})`;
        const sampledColorRgba = `rgba(${avgR}, ${avgG}, ${avgB}, 1)`;
        setVictoryImageWhite(sampledColorRgba);
      } catch (error) {
        console.warn('⚠️ Could not sample victory image color, using default white:', error);
        // Keep default white if sampling fails
      }
    };
    
    img.onerror = () => {
      console.warn('⚠️ Could not load victory image for color sampling, using default white');
      // Keep default white if image fails to load
    };
    
    // Load the image
    img.src = '/images/victory.jpg';
  }, []); // Run once on mount

  // Sample the gray/black color from defeat.jpg to match the fade background
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Allow canvas access if needed
    
    img.onload = () => {
      try {
        // Create a temporary canvas to sample the image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        // Sample corner pixels to get the background gray/black color
        // Sample multiple corners and average them for better accuracy
        const samplePoints = [
          [0, 0],           // Top-left
          [img.width - 1, 0], // Top-right
          [0, img.height - 1], // Bottom-left
          [img.width - 1, img.height - 1], // Bottom-right
          [img.width / 2, 0], // Top-center
          [img.width / 2, img.height - 1], // Bottom-center
        ];
        
        let totalR = 0, totalG = 0, totalB = 0;
        for (const [x, y] of samplePoints) {
          const pixelData = ctx.getImageData(x, y, 1, 1).data;
          totalR += pixelData[0];
          totalG += pixelData[1];
          totalB += pixelData[2];
        }
        
        const avgR = Math.round(totalR / samplePoints.length);
        const avgG = Math.round(totalG / samplePoints.length);
        const avgB = Math.round(totalB / samplePoints.length);
        
        // Store both RGB and RGBA formats for CSS
        const sampledColor = `rgb(${avgR}, ${avgG}, ${avgB})`;
        const sampledColorRgba = `rgba(${avgR}, ${avgG}, ${avgB}, 1)`;
        setDefeatImageGray(sampledColorRgba);
      } catch (error) {
        console.warn('⚠️ Could not sample defeat image color, using default black:', error);
        // Keep default black if sampling fails
      }
    };
    
    img.onerror = () => {
      console.warn('⚠️ Could not load defeat image for color sampling, using default black');
      // Keep default black if image fails to load
    };
    
    // Load the image
    img.src = '/images/defeat.jpg';
  }, []); // Run once on mount

  // Load player avatars from game state when available
  useEffect(() => {
    // Use activeUserId for spectator mode (watched player) or userId for normal mode
    const effectiveUserIdForAvatars = isSpectating && spectatingPlayerId ? spectatingPlayerId : userId;
    const effectiveGameStateForAvatars = isSpectating && spectatingGameState ? spectatingGameState : gameState;
    
    if (effectiveGameStateForAvatars && effectiveUserIdForAvatars) {
      // In spectator mode, use the watched player's avatars
      const playerAvatars = effectiveGameStateForAvatars.playerAvatars?.[effectiveUserIdForAvatars] || {};
      if (playerAvatars.general || playerAvatars.advisor) {
        if (playerAvatars.general) setSelectedGeneral(playerAvatars.general);
        if (playerAvatars.advisor) setSelectedAdvisor(playerAvatars.advisor);
      }
      
      // Track preparation bonus usage from game state
      const player = effectiveGameStateForAvatars.state?.[effectiveUserIdForAvatars];
      if (player) {
        const newPreparationBonusUsed = player.preparationBonusUsed || false;
        if (newPreparationBonusUsed !== preparationBonusUsed) {
          setPreparationBonusUsed(newPreparationBonusUsed);
        }
        // Clear pending ref when game state confirms it's used
        if (newPreparationBonusUsed && preparationBonusPendingRef.current) {
          preparationBonusPendingRef.current = false;
        }
      }
      
      // Load opponent's preparation bonus from game state
      const opponentId = effectiveGameStateForAvatars.players?.find(id => String(id) !== String(effectiveUserIdForAvatars));
      if (opponentId) {
        // In spectator mode, opponent prep bonus is keyed by the watched player's ID
        const opponentPrepBonus = effectiveGameStateForAvatars.opponentPreparationBonuses?.[effectiveUserIdForAvatars];
        if (opponentPrepBonus !== opponentPreparationBonus) {
          setOpponentPreparationBonus(opponentPrepBonus || null);
        }
        
        // Track opponent's preparation bonus usage
        const opponent = effectiveGameStateForAvatars.state?.[opponentId];
        if (opponent) {
          const newOpponentPrepBonusUsed = opponent.preparationBonusUsed || false;
          setOpponentPreparationBonusUsed(newOpponentPrepBonusUsed);
        } else {
          // If opponent state doesn't exist, reset to false
          setOpponentPreparationBonusUsed(false);
        }
        
        // Load opponent's diplomatic accord from game state
        const opponentDiplomaticAccordValue = effectiveGameStateForAvatars.opponentDiplomaticAccords?.[effectiveUserIdForAvatars];
        if (opponentDiplomaticAccordValue !== opponentDiplomaticAccord) {
          setOpponentDiplomaticAccord(opponentDiplomaticAccordValue || null);
        }
      }
      
      // Load player's own preparation bonus from game state (gameState takes precedence over profile)
      // This ensures the player sees their own bonus even when they go second
      const playerPrepBonusValue = effectiveGameStateForAvatars.playerPreparationBonuses?.[effectiveUserIdForAvatars];
      if (playerPrepBonusValue !== undefined) {
        // Normalize 'team_leader_meeting' to 'team_leader' for frontend consistency
        const normalizedBonus = playerPrepBonusValue === 'team_leader_meeting' ? 'team_leader' : playerPrepBonusValue;
        if (normalizedBonus !== preparationBonus) {
          setPreparationBonus(normalizedBonus || null);
        }
      }
      
      // Load player's diplomatic accord from game state (only if not already set from profile)
      // gameState takes precedence if it exists, otherwise keep the profile value
      const playerDiplomaticAccordValue = effectiveGameStateForAvatars.playerDiplomaticAccords?.[effectiveUserIdForAvatars];
      if (playerDiplomaticAccordValue !== undefined && playerDiplomaticAccordValue !== diplomaticAccord) {
        setDiplomaticAccord(playerDiplomaticAccordValue || null);
      } else if (playerDiplomaticAccordValue === undefined && !diplomaticAccord) {
        // If gameState doesn't have it and we don't have it from profile, keep null
        // This prevents overwriting profile value with null from gameState
      }
    }
  }, [gameState, spectatingGameState, userId, spectatingPlayerId, isSpectating, preparationBonusUsed, opponentPreparationBonus, opponentDiplomaticAccord, preparationBonus]);

  // Fetch player ranks when game state is available
  useEffect(() => {
    const fetchPlayerRanks = async () => {
      if (!gameState || !userId) return;
      
      const effectiveUserId = isSpectating && spectatingPlayerId ? spectatingPlayerId : userId;
      const effectiveGameState = isSpectating && spectatingGameState ? spectatingGameState : gameState;
      
      const opponentId = effectiveGameState.players?.find(id => id !== effectiveUserId);
      if (!opponentId) return;
      
      // Don't fetch ranks if opponent is a bot
      const isBot = opponentId === 'bot';
      
      const userIdsToFetch = [effectiveUserId];
      if (!isBot) {
        userIdsToFetch.push(opponentId);
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/player-ranks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ userIds: userIdsToFetch })
        });
        
        if (response.ok) {
          const ranks = await response.json();
          
          // Compare IDs as strings to handle both string and number formats
          const playerRankData = ranks.find(r => String(r.id) === String(effectiveUserId));
          const opponentRankData = !isBot ? ranks.find(r => String(r.id) === String(opponentId)) : null;
          
          // Convert rank to number if it exists
          const playerRankNum = playerRankData?.rank ? Number(playerRankData.rank) : null;
          const opponentRankNum = opponentRankData?.rank ? Number(opponentRankData.rank) : null;
          
          setPlayerRank(playerRankNum);
          setOpponentRank(opponentRankNum);
        } else {
          console.error('🏆 Failed to fetch ranks:', response.status, response.statusText);
          const errorText = await response.text();
          console.error('🏆 Error response:', errorText);
        }
      } catch (error) {
        console.error('🏆 Error fetching player ranks:', error);
      }
    };
    
    fetchPlayerRanks();
  }, [gameState, spectatingGameState, userId, spectatingPlayerId, isSpectating]);

  // Fetch opponent username
  useEffect(() => {
    const fetchOpponentUsername = async () => {
      const effectiveUserId = isSpectating && spectatingPlayerId ? spectatingPlayerId : userId;
      const effectiveGameState = isSpectating && spectatingGameState ? spectatingGameState : gameState;
      
      if (!effectiveGameState || !effectiveUserId) {
        setOpponentUsername(null);
        return;
      }
      
      const opponentId = effectiveGameState.players?.find(id => String(id) !== String(effectiveUserId));
      if (!opponentId) {
        setOpponentUsername(null);
        return;
      }
      
      // Check if opponent is a bot
      const isBot = opponentId === 'bot' || String(opponentId).includes('bot');
      
      if (isBot) {
        setOpponentUsername('Bot');
        return;
      }
      
      // Fetch username from player-ranks endpoint (which also returns usernames)
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/player-ranks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ userIds: [opponentId] })
        });
        
        if (response.ok) {
          const ranks = await response.json();
          const opponentData = ranks.find(r => String(r.id) === String(opponentId));
          if (opponentData && opponentData.username) {
            setOpponentUsername(opponentData.username);
          } else {
            setOpponentUsername('Opponent');
          }
        } else {
          setOpponentUsername('Opponent');
        }
      } catch (error) {
        console.error('Error fetching opponent username:', error);
        setOpponentUsername('Opponent');
      }
    };

    fetchOpponentUsername();
  }, [gameState, userId, isSpectating, spectatingPlayerId, spectatingGameState]);

  // Avatar helper functions moved to ./avatars/avatarManager.js
  
  // Helper function to get rank styling based on rank tier
  const getRankStyle = (rank) => {
    if (!rank) return {};
    
    if (rank <= 10) {
      return {
        color: '#FFD700',
        textShadow: '0 0 10px #FFD700, 0 0 20px #FFD700, 0 0 30px #FFD700',
        fontWeight: 'bold'
      };
    } else if (rank <= 50) {
      return {
        color: '#C0C0C0',
        textShadow: '0 0 10px #C0C0C0, 0 0 20px #C0C0C0, 0 0 30px #C0C0C0',
        fontWeight: 'bold'
      };
    } else if (rank <= 100) {
      return {
        color: '#CD7F32',
        textShadow: '0 0 10px #CD7F32, 0 0 20px #CD7F32, 0 0 30px #CD7F32',
        fontWeight: 'bold'
      };
    } else if (rank <= 500) {
      return {
        color: '#FFFFFF',
        textShadow: '0 0 10px #FFFFFF, 0 0 20px #FFFFFF',
        fontWeight: 'bold'
      };
    } else {
      return {
        color: '#FFFFFF',
        fontWeight: 'bold'
      };
    }
  };
  
  // NEW: For attack selection
  const [selectedAttacker, setSelectedAttacker] = useState(null); // {zone, idx}
  const [attackMode, setAttackMode] = useState(false);
  const [lastAttackedTarget, setLastAttackedTarget] = useState(null); // Track last attacked target {zone, idx} to keep expand disabled until mouse leaves
  const attackInProgressRef = useRef(false); // Prevent double-click attacks (using ref for synchronous access)
  const pendingAttackInfoRef = useRef(null); // Store pending attack info for animation (only set if attack succeeds)
  // NEW: For drag-and-drop from carousel
  const [draggedOrderId, setDraggedOrderId] = useState(null);
  const [draggedOrderInstanceId, setDraggedOrderInstanceId] = useState(null);
  // Desktop drag-to-deploy: custom mouse drag (no HTML5 DnD), red line only, drop by half-screen
  const [dragDeployState, setDragDeployState] = useState(null); // { order, originX, originY, currentX, currentY }
  const mouseDragPendingRef = useRef(null); // { order, startX, startY } while tracking
  const clickHandledByMouseupRef = useRef(false); // so card onClick doesn't double-fire
  const dragStartedRef = useRef(false); // true once we've shown the line (moved past threshold)
  // Strategem drag-and-drop: store intended target when we play strategem via drag so we can send it when backend sets needsStrategemTarget
  const pendingStrategemDropTargetRef = useRef(null); // { targetType, targetZone, targetIndex, targetUnitId, targetInstanceId, generalPlayer? }
  const [strategemDragState, setStrategemDragState] = useState(null); // { order, originX, originY, currentX, currentY } during strategem drag
  const strategemDragStartedRef = useRef(false);
  const strategemMouseDragPendingRef = useRef(null); // { order, startX, startY } while tracking strategem drag
  const [weaponDragState, setWeaponDragState] = useState(null); // { order, originX, originY, currentX, currentY } during weapon drag
  const weaponDragStartedRef = useRef(false);
  const weaponMouseDragPendingRef = useRef(null); // { order, startX, startY } while tracking weapon drag
  const [pendingWeaponDropCoords, setPendingWeaponDropCoords] = useState(null); // { clientX, clientY, weaponId, weaponInstanceId } after drop to trigger synthetic click
  const [droneDragState, setDroneDragState] = useState(null); // { order, originX, originY, currentX, currentY } during drone drag
  const droneDragStartedRef = useRef(false);
  const droneMouseDragPendingRef = useRef(null); // { order, startX, startY } while tracking drone drag
  const [pendingDroneDropCoords, setPendingDroneDropCoords] = useState(null); // { clientX, clientY, orderId } after drop to call playDrone
  const [pendingDeployFullZoneDrop, setPendingDeployFullZoneDrop] = useState(null); // { clientX, clientY } when deploy drop on full zone → synthetic click to enter replace mode
  const [attackDragState, setAttackDragState] = useState(null); // { attacker: { zone, idx }, originX, originY, currentX, currentY } during attack drag from friendly unit
  const attackDragStartedRef = useRef(false);
  const attackMouseDragPendingRef = useRef(null); // { attacker: { zone, idx }, startX, startY } while tracking attack drag
  const [pendingAttackDropCoords, setPendingAttackDropCoords] = useState(null); // { clientX, clientY } after drop → set attacker + synthetic click on target
  const [droneTargetDragState, setDroneTargetDragState] = useState(null); // { drone: { id, instanceId, index }, originX, originY, currentX, currentY } when dragging deployed drone (with charges) to target
  const droneTargetDragStartedRef = useRef(false);
  const droneTargetMouseDragPendingRef = useRef(null); // { drone, startX, startY } while tracking drone target drag
  const [pendingDroneTargetDropCoords, setPendingDroneTargetDropCoords] = useState(null); // { clientX, clientY } after drop → set drone + drone target mode + synthetic click on target
  const [reconnecting, setReconnecting] = useState(false);
  const reconnectingRef = useRef(false); // Track reconnecting state to avoid stale closures
  const reconnectingTimeoutRef = useRef(null); // Track timeout for clearing stuck reconnecting state
  // Temporary message state for "feeling tired" message
  const [temporaryMessage, setTemporaryMessage] = useState(null);
  const [stateSyncMonitorEnabled, setStateSyncMonitorEnabled] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  // Cache for orders fetched from database for tooltips
  const [orderDBCache, setOrderDBCache] = useState({});
  // ✅ Action queuing to prevent race conditions
  const actionQueueRef = useRef([]);
  const isProcessingActionRef = useRef(false);
  
  // Helper function to get deployed unit info for special deploy units
  const getDeployedUnitInfo = useCallback((orderId) => {
    const deployUnitMap = {
      5: { id: 10, name: 'Bio-Engineered Howler', customStats: { attack: 1, health: 1 }, faction_name: 'Asrah' }, // Hive-Bound Howler - Deploys 1/1 Bio-Engineered Howler in the other zone
      22: { id: 22, name: 'Swarm Queen', faction_name: 'Asrah' }, // Deploys another Swarm Queen
      // 134: Removed - Beastcaller of the Rift (deploys random Animal, tooltip not useful)
      41: { id: 44, name: 'Infected', customStats: { attack: 1, health: 1 }, faction_name: 'Asrah' }, // Armed Fleshthrall - Deploys 1/1 Infected
      137: { id: 150, name: 'Shadow Strike', customStats: { attack: 2, health: 2, abilities: ['Blitz'] }, faction_name: 'League of Shadows' }, // 2/2 Shadow Strike with Blitz
      159: { id: 155, name: 'Standard Combat Orb', customStats: { attack: 2, health: 1 }, faction_name: 'League of Shadows' }, // Combat Orb Main Hub - Fills battlefield with 2/1 Combat Orbs
      210: { id: 213, name: 'Nobleborn Sentinel', customStats: { attack: 0, health: 4, abilities: ['Protect'] }, faction_name: "Ra's Children" }, // Neith - Deploys 0/4 Nobleborn Sentinel with Protect
      239: { id: null, name: 'Random Unit', isRandom: true, source: 'Completed Orders' }, // Random from Completed Orders
      248: { id: 260, name: 'Unfinished Mummy', customStats: { attack: 1, health: 1 }, faction_name: "Ra's Children" }, // Ba - Fills zone with 1/1 Unfinished Mummies
      307: { id: 329, name: 'Ironclad Tank', faction_name: 'Remnants of Humanity' }, // Deploys Ironclad Tank in each zone
      394: { id: 216, name: 'Doomed Defender', customStats: { attack: 2, health: 2, abilities: ['Protect'] }, faction_name: "Ra's Children" }, // Ptharr, the Hollow - Deploys 2/2 Doomed Defender with Protect
      382: { id: 148, name: 'Shadow Sniper', count: 3, faction_name: 'League of Shadows' }, // Adds 3 Shadow Snipers to deck
      // Special units (event-triggered effects)
      97: { id: 99, name: 'Stinger', customStats: { attack: 1, health: 1 }, count: 2, faction_name: 'Exolot Dominion' }, // Stinger Commander - Gets 2x 1/1 Stinger Orders when attacking
      164: { id: 180, name: 'Touch of darkness', faction_name: 'League of Shadows' }, // Kendrex Warlock - Gets Touch of Darkness at start of turn
      281: { id: 330, name: 'Steel Viper Tank', customStats: { attack: 5, health: 5, abilities: ['Protect'] }, faction_name: 'Remnants of Humanity' }, // Chief Engineer - Deploys 5/5 Steel Viper Tank with Protect when shield destroyed
      288: { id: 334, name: 'Close Quarters Blaster', faction_name: 'Remnants of Humanity' }, // Survival Architect - Gets Close Quarters Blaster when shield destroyed
      327: { id: 318, name: 'Venom Cloud', faction_name: 'League of Shadows' }, // Plague Alchemist - Gets Venom Cloud Order at start of turn
      287: { id: 317, name: 'Guided Missile', faction_name: 'Remnants of Humanity' } // Business Guy - Gets Guided Missile if next order is Remnants of Humanity
    };
    return deployUnitMap[orderId] || null;
  }, []);
  
  // Fetch orders from database for tooltips when hovering (same method as deployment)
  useEffect(() => {
    if (hoveredIndex !== null && gameState?.state?.[userId]?.hand) {
      const hand = gameState.state[userId].hand;
      const hoveredOrder = hand[hoveredIndex];
      if (hoveredOrder) {
        const deployedUnitInfo = getDeployedUnitInfo(hoveredOrder.id);
        if (deployedUnitInfo && deployedUnitInfo.id && !orderDBCache[deployedUnitInfo.id]) {
          const fetchOrder = async () => {
            try {
              // Use the same endpoint as the backend uses for fetching orders (same as deployment)
              const response = await fetch(`${API_URL}/order/${deployedUnitInfo.id}`, {
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });
              if (response.ok) {
                const orderData = await response.json();
                // Use the order data directly from DB (includes type, faction_name, description)
                setOrderDBCache(prev => ({ ...prev, [deployedUnitInfo.id]: orderData }));
              }
            } catch (error) {
              console.warn(`Failed to fetch order ${deployedUnitInfo.id} from database:`, error);
            }
          };
          fetchOrder();
        }
      }
    }
  }, [hoveredIndex, token, orderDBCache, gameState, userId, getDeployedUnitInfo]);
  // ✅ REFACTORED: lastStateVersionRef now comes from useGameStateSync hook
  const [hoveredPlayed, setHoveredPlayed] = useState({ zone: null, idx: null });
  const [opponentLookingAtInstanceId, setOpponentLookingAtInstanceId] = useState(null);
  // ✅ FIXED: Use useRef for timeout to prevent race conditions with state updates
  const hoverTimeoutRef = useRef(null);
  // Completed orders modal state
  const [showCompletedOrders, setShowCompletedOrders] = useState(false);
  const [hoveredCompletedOrder, setHoveredCompletedOrder] = useState(null);
  
  // Emote selection state
  const [showEmoteButtons, setShowEmoteButtons] = useState(false);
  const [playerEmotes, setPlayerEmotes] = useState([]);
  const [playerEmoteSelections, setPlayerEmoteSelections] = useState({});
  const [emotesLoading, setEmotesLoading] = useState(true); // ✅ FIX: Track loading state
  const [emotesError, setEmotesError] = useState(null); // ✅ FIX: Track error state
  const [currentEmoteBubble, setCurrentEmoteBubble] = useState(null); // { text: string, timestamp: number }
  const [opponentEmoteBubble, setOpponentEmoteBubble] = useState(null); // { text: string, timestamp: number }
  
  // Notify opponent when player hovers over an order (for "looking at" glow on their view)
  const sendOrderHover = useCallback((instanceId) => {
    const s = ws || currentSocketRef.current;
    if (s && gameId && s.readyState === 1) {
      s.send(JSON.stringify({ type: 'order-hover', gameId, instanceId: instanceId || null }));
    }
  }, [ws, gameId]);
  
  // Close emote buttons when clicking outside
  useEffect(() => {
    if (!showEmoteButtons) return;
    
    const handleClickOutside = (e) => {
      // Check if click is outside the emote buttons container
      const emoteContainer = document.querySelector('[data-emote-buttons]');
      if (emoteContainer && !emoteContainer.contains(e.target)) {
        // Also check if click is not on the general image
        const generalImage = document.querySelector('[data-zone="player_general"]');
        if (generalImage && !generalImage.contains(e.target)) {
          setShowEmoteButtons(false);
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmoteButtons]);
  // Strategy modal state
  const [showStrategy, setShowStrategy] = useState(false);
  const [hoveredStrategyOrder, setHoveredStrategyOrder] = useState(null);
  // Force re-render when dev targeting override toggles
  const [targetingDevTick, setTargetingDevTick] = useState(0);
  // Token management state
  const [refreshTokenValue, setRefreshTokenValue] = useState(localStorage.getItem('refreshToken'));
  // All targeting state managed by useTargetingState hook
  const {
    // Database targeting data
    targetingData,
    targetingDataLoaded,
    targetingDataError,
    
    // Deploy targeting state
    deployTargetMode,
    selectedDeployOrderId,
    selectedDeployInstanceId,
    selectedDeployZone,
    deployTargetInfo,
    setDeployTargetMode,
    setSelectedDeployOrderId,
    setSelectedDeployInstanceId,
    setSelectedDeployZone,
    setDeployTargetInfo,
    
    // Weapon targeting state
    weaponTargetMode,
    selectedWeaponId,
    selectedWeaponInstanceId,
    setWeaponTargetMode,
    setSelectedWeaponId,
    setSelectedWeaponInstanceId,
    
    // Weapon deploy targeting state
    weaponDeployTargetMode,
    weaponDeployTargetInfo,
    setWeaponDeployTargetMode,
    setWeaponDeployTargetInfo,
    
    // Drone targeting state
    droneTargetMode,
    selectedDroneId,
    selectedDroneInstanceId,
    setDroneTargetMode,
    setSelectedDroneId,
    setSelectedDroneInstanceId,
    
    // Immobilize targeting state
    immobilizeTargetMode,
    selectedImmobilizeWeaponId,
    setImmobilizeTargetMode,
    setSelectedImmobilizeWeaponId,
    
    // Confuse targeting state
    confuseTargetMode,
    selectedConfuseWeaponId,
    setConfuseTargetMode,
    setSelectedConfuseWeaponId,

    // Strategem targeting state
    strategemTargetMode,
    selectedStrategemId,
    strategemTargetInfo,
    setStrategemTargetMode,
    setSelectedStrategemId,
    setStrategemTargetInfo,
    
    // Special unit targeting state
    specialTargetMode,
    selectedSpecialUnitId,
    setSpecialTargetMode,
    setSelectedSpecialUnitId,
    
    // Replacement targeting state
    replaceTargetMode,
    selectedReplaceUnitId,
    selectedReplaceInstanceId,
    replaceTargetZone,
    clickedUnitForReplace,
    setReplaceTargetMode,
    setSelectedReplaceUnitId,
    setSelectedReplaceInstanceId,
    setReplaceTargetZone,
    setClickedUnitForReplace,
    
    // Drone replacement targeting state
    replaceDroneTargetMode,
    selectedReplaceDroneId,
    selectedReplaceDroneInstanceId,
    clickedDroneForReplace,
    setReplaceDroneTargetMode,
    setSelectedReplaceDroneId,
    setSelectedReplaceDroneInstanceId,
    setClickedDroneForReplace
  } = useTargetingState();


  // Developer mode state
  const {
    devUnitSearch,
    setDevUnitSearch,
    devSelectedUnit,
    setDevSelectedUnit,
    showGridOverlay,
    setShowGridOverlay,
    showDebugOverlay,
    setShowDebugOverlay,
    showTargetedDebug,
    setShowTargetedDebug,
    showElementSelector,
    setShowElementSelector,
    showEditableOverlay,
    setShowEditableOverlay,
    gridSize,
    setGridSize,
    savedDesign,
    setSavedDesign,
    isConsoleMinimized,
    setIsConsoleMinimized,
    targetElements,
    setTargetElements
  } = useDeveloperMode();

  // Turn timer hook
  const { turnTimeLeft, isWarning, shouldShowTimer } = useTurnTimer(yourTurn, gameState);

  // Counter Strike hook
  const { counterStrikeOrders, opponentHasCounterStrike, updateCounterStrikeOrders, playCounterStrike, keepCounterStrike, processingCounterStrikeId } = useCounterStrike(ws, gameId, userId);
  
  // Mulligan hook
  const { mulliganOrders, needsMulligan, mulliganTimeLeft, updateMulliganOrders, submitMulligan } = useMulligan(ws, gameId, userId);
  
  // Touch drag deployment hook for mobile
  const {
    isDragging: isTouchDragging,
    draggedOrder: touchDraggedOrder,
    dragPosition: touchDragPosition,
    targetZone: touchTargetZone,
    handleTouchStart: handleTouchDragStart,
    handleTouchMove: handleTouchDragMove,
    handleTouchEnd: handleTouchDragEnd,
    cancelDrag: cancelTouchDrag,
  } = useTouchDragDeploy({ isMobile, yourTurn });
  
  // Touch drag attack hook for mobile
  const {
    isDragging: isAttackDragging,
    attackerInfo: attackDragAttacker,
    dragPosition: attackDragPosition,
    startPosition: attackDragStartPosition,
    hoveredTarget: attackDragTarget,
    handleTouchStart: handleAttackTouchStart,
    handleTouchMove: handleAttackTouchMove,
    handleTouchEnd: handleAttackTouchEnd,
    cancelDrag: cancelAttackDrag,
  } = useTouchDragAttack({ isMobile, yourTurn, gameState, userId });
  
  // ✅ Auto-hide first turn message after 5 seconds
  useEffect(() => {
    if (showFirstTurnMessage) {
      const timer = setTimeout(() => {
        setShowFirstTurnMessage(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showFirstTurnMessage]);
  
  // ✅ Auto-hide turn start image after 1.5 seconds
  useEffect(() => {
    if (showTurnStartImage) {
      const timer = setTimeout(() => {
        setShowTurnStartImage(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [showTurnStartImage]);
  
  // Targeting Cancel hook
  const { createCancelDeployHandler, createCancelStrategemHandler, createCancelWeaponHandler, createCancelWeaponDeployHandler, createCancelDroneHandler } = useTargetingCancel(ws, gameId, setError);

  // Helper function to cancel all active targeting modes
  const cancelAllTargeting = useCallback(() => {
    // Cancel deploy targeting
    if (deployTargetMode && selectedDeployOrderId) {
      const cancelDeploy = createCancelDeployHandler(
        setDeployTargetMode,
        setSelectedDeployOrderId,
        setSelectedDeployInstanceId,
        setSelectedDeployZone,
        setDeployTargetInfo,
        selectedDeployOrderId,
        selectedDeployInstanceId,
        selectedDeployZone
      );
      cancelDeploy();
    }
    
    // Cancel strategem targeting
    if (strategemTargetMode && selectedStrategemId) {
      const cancelStrategem = createCancelStrategemHandler(
        setStrategemTargetMode,
        setSelectedStrategemId,
        setStrategemTargetInfo
      );
      cancelStrategem();
    }
    
    // Cancel weapon targeting
    if (weaponTargetMode && selectedWeaponId) {
      const cancelWeapon = createCancelWeaponHandler(
        setWeaponTargetMode,
        setSelectedWeaponId,
        setSelectedWeaponInstanceId
      );
      cancelWeapon();
    }
    
    // Cancel weapon deploy targeting
    if (weaponDeployTargetMode && weaponDeployTargetInfo) {
      const cancelWeaponDeploy = createCancelWeaponDeployHandler(
        setWeaponDeployTargetMode,
        setWeaponDeployTargetInfo
      );
      cancelWeaponDeploy();
    }
    
    // Cancel drone targeting
    if (droneTargetMode && selectedDroneId) {
      const cancelDrone = createCancelDroneHandler(
        setDroneTargetMode,
        setSelectedDroneId,
        setSelectedDroneInstanceId
      );
      cancelDrone();
    }
    
    // Cancel immobilize targeting
    if (immobilizeTargetMode && selectedImmobilizeWeaponId) {
      setImmobilizeTargetMode(false);
      setSelectedImmobilizeWeaponId(null);
    }
    
    // Cancel confuse targeting
    if (confuseTargetMode && selectedConfuseWeaponId) {
      setConfuseTargetMode(false);
      setSelectedConfuseWeaponId(null);
    }
  }, [
    deployTargetMode,
    selectedDeployOrderId,
    selectedDeployInstanceId,
    selectedDeployZone,
    strategemTargetMode,
    selectedStrategemId,
    weaponTargetMode,
    selectedWeaponId,
    weaponDeployTargetMode,
    weaponDeployTargetInfo,
    droneTargetMode,
    selectedDroneId,
    immobilizeTargetMode,
    selectedImmobilizeWeaponId,
    confuseTargetMode,
    selectedConfuseWeaponId,
    createCancelDeployHandler,
    createCancelStrategemHandler,
    createCancelWeaponHandler,
    createCancelWeaponDeployHandler,
    createCancelDroneHandler,
    setDeployTargetMode,
    setSelectedDeployOrderId,
    setSelectedDeployInstanceId,
    setSelectedDeployZone,
    setDeployTargetInfo,
    setStrategemTargetMode,
    setSelectedStrategemId,
    setStrategemTargetInfo,
    setWeaponTargetMode,
    setSelectedWeaponId,
    setWeaponDeployTargetMode,
    setWeaponDeployTargetInfo,
    setDroneTargetMode,
    setSelectedDroneId,
    setSelectedDroneInstanceId,
    setImmobilizeTargetMode,
    setSelectedImmobilizeWeaponId,
    setConfuseTargetMode,
    setSelectedConfuseWeaponId
  ]);

  // Cancel targeting when user selects a different unit in hand
  const prevSelectedOrderInstanceIdRef = useRef(selectedOrderInstanceId);
  useEffect(() => {
    // Only cancel if a new unit is being selected (not when clearing selection)
    if (selectedOrderInstanceId && 
        selectedOrderInstanceId !== prevSelectedOrderInstanceIdRef.current &&
        prevSelectedOrderInstanceIdRef.current !== null) {
      // User clicked a different unit in hand - cancel any active targeting
      cancelAllTargeting();
    }
    prevSelectedOrderInstanceIdRef.current = selectedOrderInstanceId;
  }, [selectedOrderInstanceId, cancelAllTargeting]);
  
  // Counter Strike state
  const [selectedCounterStrikeOrder, setSelectedCounterStrikeOrder] = useState(null);

  // Revealed enemy order state (Ghost Operative - Unit 295)
  const [revealedEnemyOrder, setRevealedEnemyOrder] = useState(null);

  // Action log: server-authoritative; client only displays
  const [actionLog, setActionLog] = useState([]);
  
  // Highlighted units from action log hover (optional, for future use)
  const [highlightedInstanceIds, setHighlightedInstanceIds] = useState([]);
  
  // Detect and animate opponent strategem plays (works for human and bot opponents)
  // Uses actionLog (server entries with type 'strategem-requested') from opponent
  useOpponentStrategemAnimations(gameState, userId, strategemTargetAnimations.registerAnimation, strategemTargetAnimations.ghostUnitsRef, actionLog);
  
  // Unit Registry: Persistent snapshot of all units by instanceId
  // This allows us to reference units even after they're removed from the battlefield
  const unitRegistryRef = useUnitRegistry();

const [strategyDetails, setStrategyDetails] = useState(null);
const [showResignDialog, setShowResignDialog] = useState(false);
const exitingRef = useRef(false);
  
  // Battle music - after mulligan for normal battles; for tutorial missions 1–4 start as soon as we have gameState (no mulligan)
  // Stable placeholder so useBattleMusic effect doesn't re-run every render (which would restart music)
  const tutorialMusicPlaceholder = useMemo(() => ({ orders: [] }), []);
  const gameStarted = gameState && !needsMulligan && gameState.state?.[userId]?.hasMulliganed;
  const isTutorialMission = gameState?.campaignMode && ['chapter_1_mission_1', 'chapter_1_mission_2', 'chapter_1_mission_3', 'chapter_1_mission_4'].includes(gameState?.missionId);
  const musicShouldPlay = gameStarted || (isTutorialMission && !!gameState);
  const musicStrategyDetails = musicShouldPlay ? (isTutorialMission ? tutorialMusicPlaceholder : strategyDetails) : null;
  useBattleMusic(musicStrategyDetails, 2000, { missionId: gameState?.missionId });
  
  // Check if developer mode is enabled (only for username/ID "player1")
  // Player1 should ALWAYS see it, regardless of battle position
  const isPlayer1 = userId === 'player1' || user?.username === 'player1' || user?.id === 'player1';
  const isDeveloperMode = Boolean(location.state?.developerMode && isPlayer1);
  

  // ✅ FIX: Fetch player's emotes with retry logic (moved outside useEffect for retry button access).
  // On 403 (invalid/expired token), refresh token and retry once—same pattern as fetchGameWithRetry.
  const fetchEmotes = useCallback(async (retryCount = 0, tokenOverride = null) => {
    const tokenToUse = tokenOverride !== null && tokenOverride !== undefined ? tokenOverride : token;
    if (!tokenToUse) {
      setEmotesLoading(false);
      setEmotesError('No authentication token');
      return;
    }

    const maxRetries = 3;
    const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Exponential backoff, max 5s

    try {
      setEmotesLoading(true);
      setEmotesError(null);

      const res = await fetch(`${API_URL}/profile/emotes`, {
        headers: {
          Authorization: `Bearer ${tokenToUse}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        const emotes = data.emotes || [];
        setPlayerEmotes(emotes);
        setPlayerEmoteSelections(data.selections || {});
        setEmotesLoading(false);
        setEmotesError(null);
      } else {
        // ✅ FIX: On 403 (invalid/expired token), refresh and retry once
        if (res.status === 403 && tokenOverride == null) {
          try {
            const refreshTokenValue = localStorage.getItem('refreshToken');
            if (refreshTokenValue) {
              const newToken = await refreshToken(refreshTokenValue);
              localStorage.setItem('token', newToken);
              return fetchEmotes(0, newToken);
            }
          } catch (refreshErr) {
            console.warn('Emote fetch: token refresh failed', refreshErr);
          }
        }
        // Retry on server errors (5xx) or rate limits (429)
        if ((res.status >= 500 || res.status === 429) && retryCount < maxRetries) {
          console.warn(`⚠️ Emote fetch failed (${res.status}), retrying in ${retryDelay}ms... (attempt ${retryCount + 1}/${maxRetries})`);
          setTimeout(() => fetchEmotes(retryCount + 1), retryDelay);
          return;
        }

        const errorMsg = `Failed to fetch emotes: ${res.status} ${res.statusText}`;
        console.error(errorMsg);
        setEmotesError(errorMsg);
        setEmotesLoading(false);
      }
    } catch (err) {
      // ✅ FIX: Retry on network errors
      if (retryCount < maxRetries) {
        console.warn(`⚠️ Emote fetch error (network), retrying in ${retryDelay}ms... (attempt ${retryCount + 1}/${maxRetries}):`, err.message);
        setTimeout(() => fetchEmotes(retryCount + 1), retryDelay);
        return;
      }

      const errorMsg = `Failed to fetch emotes: ${err.message}`;
      console.error(errorMsg, err);
      setEmotesError(errorMsg);
      setEmotesLoading(false);
    }
  }, [token]);

  // Periodic token refresh check
  // Fetch avatars and preparation bonus on component mount
  useEffect(() => {
    const fetchAvatars = async () => {
      try {
        const res = await fetch(`${API_URL}/get-avatar`);
        const data = await res.json();
        if (Array.isArray(data)) {
          setAvatars(data);
        } else {
          console.error('Unexpected avatar response format:', data);
          setAvatars([]);
        }
      } catch (err) {
        console.error('Failed to fetch avatars:', err);
        setAvatars([]);
      }
    };

    const fetchPreparationBonus = async () => {
      try {
        const res = await fetch(`${API_URL}/profile/features`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          setPreparationBonus(data.selections?.preparation_bonus || null);
          const accordValue = data.selections?.diplomatic_accords || null;
          setDiplomaticAccord(accordValue);
        }
      } catch (err) {
        console.error('Failed to fetch preparation bonus:', err);
      }
    };

    fetchAvatars();
    fetchPreparationBonus();
    fetchEmotes();
  }, [token, fetchEmotes]);

  // Clean up all bot avatar entries when starting any bot game
  useEffect(() => {
    if (isBotGame) {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('botAvatars_')) {
          localStorage.removeItem(key);
        }
      });
    }
  }, [isBotGame]);

  // Listen for dev targeting override changes to re-render highlights
  useEffect(() => {
    const handler = () => setTargetingDevTick(prev => prev + 1);
    window.addEventListener('force-highlight-change', handler);
    return () => window.removeEventListener('force-highlight-change', handler);
  }, []);

  // Listen for campaign auto end-turn (e.g. "Deploy both to front" tutorial)
  useEffect(() => {
    const handler = () => {
      endTurn();
    };
    window.addEventListener('campaign-auto-end-turn', handler);
    return () => window.removeEventListener('campaign-auto-end-turn', handler);
  }, []);

  // Log intelligence changes for player
  useEffect(() => {
    if (gameState?.state?.[userId]) {
      const player = gameState.state[userId];
    }
  }, [gameState?.state?.[userId]?.intelligence, gameState?.state?.[userId]?.maxIntelligence, userId]);

  // Log intelligence changes for opponent
  useEffect(() => {
    if (gameState?.state && gameState.players) {
      const opponentId = gameState.players.find(id => id !== userId);
      if (opponentId && gameState.state[opponentId]) {
        const opponent = gameState.state[opponentId];
      }
    }
  }, [gameState?.state, gameState?.players, userId]);


  useEffect(() => {
    if (!token || !refreshTokenValue) {
      return;
    }

    const checkTokenRefresh = async () => {
      if (needsTokenRefresh(token)) {
        try {
          const newToken = await refreshToken(refreshTokenValue);
          // Store new token in localStorage for next session
          localStorage.setItem('token', newToken);
        } catch (error) {
          // Don't show error to user yet, let it fail naturally on next request
        }
      }
    };

    // Check every 2 minutes
    const interval = setInterval(checkTokenRefresh, 2 * 60 * 1000);
    
    // Check immediately
    checkTokenRefresh();

    return () => clearInterval(interval);
  }, [token, refreshTokenValue]);
  
  // Target elements are now managed by useDeveloperMode hook

  // Load saved design changes on component mount
  useEffect(() => {
    const savedDesign = localStorage.getItem('battleUI_design');
    if (savedDesign) {
      try {
        const designChanges = JSON.parse(savedDesign);
        setSavedDesign(designChanges);
        
        // Apply saved changes to DOM elements after a short delay to ensure elements are rendered
        setTimeout(() => {
          Object.keys(designChanges).forEach(elementId => {
            const changes = designChanges[elementId];
            const elementIndex = parseInt(elementId.replace('editable-', ''));
            const targetElement = targetElements[elementIndex];
            
            if (targetElement) {
              // Find the actual DOM element
              let domElements = [];
              if (targetElement.className) {
                domElements = document.querySelectorAll(`.${targetElement.className.split(' ').join('.')}`);
              } else if (targetElement.selector) {
                domElements = document.querySelectorAll(targetElement.selector);
              }
              
              // Apply changes to all matching elements
              domElements.forEach((el, elIndex) => {
                if (changes.x !== undefined && changes.y !== undefined) {
                  el.style.position = 'absolute';
                  el.style.left = changes.x + 'px';
                  el.style.top = changes.y + 'px';
                }
                if (changes.width !== undefined) {
                  el.style.width = changes.width + 'px';
                }
                if (changes.height !== undefined) {
                  el.style.height = changes.height + 'px';
                }
              });
            }
          });
        }, 100);
      } catch (error) {
        console.error('Error loading saved design:', error);
      }
    }
  }, [targetElements]);

  // Developer mode functions are now imported from devmode folder

useEffect(() => {
  if (passedWs) {
    const socket = passedWs;
    setWs(socket);

    socket.onmessage = event => {
      if (exitingRef.current) return;
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch (parseErr) {
        console.error('[passedWs] Failed to parse WebSocket message:', parseErr);
        return;
      }

      // ✅ Same as main game socket: consume deploy VFX before any handler returns early (spectate, errors, etc.)
      tryConsumePendingDeployAnimationsForViewer(
        msg.gameState,
        deployAnimationViewerUserIdRef.current,
        deployTargetAnimationsRef.current,
        msg.gameId,
        isWsMessageForDifferentSessionRef.current
      );
      
      if (msg.type === "match-found") {
        navigate(`/battle/${msg.gameId}`, {
          state: {
            strategyId,
            ws: socket,
            token,
            userId,
          },
        });
      }
      
      // ✅ CRITICAL FIX: Handle attack-initiated for passedWs (bot attacks)
      // This was missing, causing bot attacks to never complete when using passedWs
      if (msg.type === "attack-initiated") {
        const passedAiGameId = msg.gameId;
        if (isWsMessageForDifferentSessionRef.current(passedAiGameId)) return;
        // ✅ SPEED CONTROL: For autobattler spectators, queue the attack update
        if (isAutobattlerSpectatorRef.current) {
          const applyUpdate = () => {
            // Update game state
            if (msg.gameState) {
              gameStateRefRef.current.current = msg.gameState;
              setGameState(msg.gameState);
              setYourTurn(msg.yourTurn || false);
            }
            
            // Register attack for animation (use ref to avoid stale closure)
            const currentAttackAnimations = attackAnimationsRef.current;
            if (msg.attackId && msg.attackOutcome && currentAttackAnimations) {
              try {
                currentAttackAnimations.registerAttack(msg.attackId, msg.attackOutcome, msg.isBotAttack || false);
              } catch (registerError) {
                console.error(`❌ [passedWs] Error calling registerAttack:`, registerError);
              }
            } else {
              console.error(`❌ [passedWs] Cannot register attack: missing data`, {
                hasAttackId: !!msg.attackId,
                hasAttackOutcome: !!msg.attackOutcome,
                hasAttackAnimations: !!currentAttackAnimations
              });
            }
          };
          
          speedControlQueueUpdateRef.current(msg, applyUpdate);
          return; // Don't process immediately for autobattler spectators
        }
        
        // Update game state (non-spectator path)
        if (msg.gameState) {
          gameStateRefRef.current.current = msg.gameState;
          setGameState(msg.gameState);
          setYourTurn(msg.yourTurn || false);
        }
        
        // Register attack for animation (use ref to avoid stale closure)
        const currentAttackAnimations = attackAnimationsRef.current;
        if (msg.attackId && msg.attackOutcome && currentAttackAnimations) {
          try {
            currentAttackAnimations.registerAttack(msg.attackId, msg.attackOutcome, msg.isBotAttack || false);
          } catch (registerError) {
            console.error(`❌ [passedWs] Error calling registerAttack:`, registerError);
          }
        } else {
          console.error(`❌ [passedWs] Cannot register attack: missing data`, {
            hasAttackId: !!msg.attackId,
            hasAttackOutcome: !!msg.attackOutcome,
            hasAttackAnimations: !!currentAttackAnimations
          });
        }
        return; // Don't fall through
      }
      
      // ✅ CRITICAL FIX: Handle attack-complete for passedWs
      if (msg.type === "attack-complete") {
        const passedAcGameId = msg.gameId;
        if (isWsMessageForDifferentSessionRef.current(passedAcGameId)) return;
        attackInProgressRef.current = false;
        
        if (msg.gameState) {
          gameStateRefRef.current.current = msg.gameState;
          setGameState(msg.gameState);
          setActionLog(msg.gameState.actionLog ?? []);
          setYourTurn(msg.yourTurn || false);
        }
        return; // Don't fall through
      }
      
      // ✅ State sync for passedWs — MUST mirror main socket message types that carry gameState.
      // targetDeploy (e.g. Encrazed Bombardier) sends turn-played, not game-update; we only handled game-update before,
      // so deploy VFX + state never applied for clients using the passed-in match socket.
      if (
        msg.type === 'game-update' ||
        msg.type === 'turn-played' ||
        msg.type === 'turn-ended' ||
        msg.type === 'turn-timeout' ||
        msg.type === 'player-disconnected' ||
        msg.type === 'player-abandoned' ||
        msg.type === 'counter-strike-played'
      ) {
        const passedSyncGameId = msg.gameId;
        if (isWsMessageForDifferentSessionRef.current(passedSyncGameId)) return;
        // ✅ FIX: Clear reconnecting state when gameState is received
        if (reconnectingRef.current && msg.gameState) {
          reconnectingRef.current = false;
          setReconnecting(false);
          wsConnectingRef.current = false;
          if (reconnectingTimeoutRef.current) {
            clearTimeout(reconnectingTimeoutRef.current);
            reconnectingTimeoutRef.current = null;
          }
        }

        const applyPassedWsState = () => {
          if (!msg.gameState) return;
          // Deploy VFX consumed at start of passedWs onmessage (tryConsumePendingDeployAnimationsForViewer)
          const prevState = previousGameStateRef.current;
          if (prevState && msg.gameState) {
            const players = msg.gameState.players || Object.keys(msg.gameState.state || {});
            for (const playerId of players) {
              const prevDrones = prevState.state?.[playerId]?.drones?.length ?? 0;
              const currDrones = msg.gameState.state?.[playerId]?.drones?.length ?? 0;
              if (prevDrones > currDrones) {
                playSoundEffect('/sounds/destroy.mp3', 0.6);
                break;
              }
            }
          }
          gameStateRefRef.current.current = msg.gameState;
          setGameState(msg.gameState);
          setActionLog(msg.gameState.actionLog ?? []);
          setYourTurn(msg.yourTurn || false);
        };

        if (isAutobattlerSpectatorRef.current && msg.gameState) {
          speedControlQueueUpdateRef.current(msg, applyPassedWsState);
          return;
        }

        applyPassedWsState();
        return;
      }
      
      // ✅ AUTOBATTLER FIX: Handle spectate-update messages for autobattler spectators
      if (msg.type === "spectate-update") {
        const isAutobattlerGame = msg.gameState?.isAutobattler || msg.gameId?.includes('_autobattler_');
        if (isAutobattlerGame && msg.gameState) {
          // ✅ FIX: Clear reconnecting state when gameState is received
          // This prevents getting stuck in "Reconnecting..." state
          if (reconnectingRef.current) {
            reconnectingRef.current = false;
            setReconnecting(false);
            wsConnectingRef.current = false;
            if (reconnectingTimeoutRef.current) {
              clearTimeout(reconnectingTimeoutRef.current);
              reconnectingTimeoutRef.current = null;
            }
          }
          
          // ✅ SPEED CONTROL: Queue update instead of applying immediately
          const applyUpdate = () => {
            gameStateRefRef.current.current = msg.gameState;
            setGameState(msg.gameState);
            setYourTurn(false); // Spectators never have their turn
            
            if (msg.gameState) {
              setActionLog(msg.gameState.actionLog ?? []);
            }
            previousGameStateRef.current = JSON.parse(JSON.stringify(msg.gameState));
          };
          
          // Queue the update (will apply immediately if speed control disabled)
          // Only use speed control for autobattler spectators
          if (isAutobattlerSpectatorRef.current && speedControlQueueUpdateRef.current) {
            speedControlQueueUpdateRef.current(msg, applyUpdate);
          } else {
            // Normal spectating or speed control not available - apply immediately
            applyUpdate();
          }
        }
        return; // Don't fall through
      }
      
      // ✅ DEV AUTOBATTLER: Handle dev-autobattler messages
      if (msg.type === "dev-autobattler-started" || msg.type === "dev-autobattler-turn-complete" || msg.type === "dev-autobattler-game-ended") {
        if (msg.gameState) {
          if (msg.gameState) {
            setActionLog(msg.gameState.actionLog ?? []);
          }
          previousGameStateRef.current = JSON.parse(JSON.stringify(msg.gameState));
          
          gameStateRefRef.current.current = msg.gameState;
          setGameState(msg.gameState);
          setYourTurn(false); // Player is spectator in dev autobattler
        }
        return; // Don't fall through
      }
      
      // If server ever includes winner in pushed state, start transition
      // ✅ FIX: Only accept winner when message is for the game currently in the URL (avoids stale ref / delayed message from previous game).
      const msgGameId = msg.gameId;
      const expectedSid = resolveWsSessionGameIdRef.current();
      if (msg.gameState?.winner && !isSpectating && msgGameId != null && expectedSid != null && String(msgGameId) === String(expectedSid)) {
        const didYouWin = msg.gameState.winner === userId;
        const isFriendlyMatch = msg.gameState.isFriendlyMatch || false;
        handleBattleEnd(didYouWin, gameIdRef.current, socket, isFriendlyMatch, 'passedWs-winner');
      }
    };

    socket.onclose = () => {
      setWs(null);
    };
    
    socket.onerror = (error) => {
      console.error(`🔌 [passedWs] WebSocket ERROR:`, error);
    };

    return () => {
      // Don't close autobattlerWs - it's managed by parent component
      if (!autobattlerWs) {
        socket.close();
      }
    };
  }
}, [passedWs, autobattlerWs, navigate, strategyId, token, userId, getCurrentGameIdFromUrl]);

useEffect(() => {
  if (exitingRef.current) return;
  if (!gameId || !token) return;
  
  // Skip game fetch for spectators - they get game state via spectate WebSocket
  if (isSpectating) {
    return;
  }
  
  // ✅ AUTOBATTLER: Skip game fetch if initialGameState was provided at mount (autobattler/dev autobattler).
  // Uses ref so this check doesn't cause re-fetches when parent passes a new initialGameState reference.
  const mountInitial = initialGameStateRef.current;
  if (mountInitial && (mountInitial.isAutobattler || mountInitial.devAutobattlerMode)) {
    return;
  }

  // ✅ FIX: Helper function to fetch game with token refresh retry
  const fetchGameWithRetry = async (currentToken, retryOn403 = true) => {
    const response = await fetch(`${API_URL}/game/${gameId}`, {
      headers: {
        Authorization: `Bearer ${currentToken}`,
      },
    });

    // If 403 and we haven't retried yet, try refreshing token
    if (!response.ok && response.status === 403 && retryOn403) {
      try {
        const refreshTokenValue = localStorage.getItem('refreshToken');
        if (refreshTokenValue) {
          // refreshToken is already imported at the top of the file
          const newToken = await refreshToken(refreshTokenValue);
          localStorage.setItem('token', newToken);
          // Retry with new token (only once)
          return fetchGameWithRetry(newToken, false);
        }
      } catch (refreshError) {
        console.error('❌ Token refresh failed:', refreshError);
        // Fall through to error handling
      }
    }

    if (!response.ok) {
      throw { status: response.status, response };
    }

    return response.json();
  };

  // ✅ FIX: First verify the gameId is actually active for this user
  // This prevents loading old games from stale URLs
  const requestedGameId = String(gameId);
  let fetchCancelled = false;

  fetchGameWithRetry(token)
    .then(data => {
      if (fetchCancelled) return;
      // ✅ CRITICAL: Ignore responses from a previous /game/:id fetch after navigating to a new battle
      // (otherwise an abandoned/completed game's state can overwrite the new session and trigger reward UI).
      if (shouldDiscardStaleGameFetch(gameIdRef.current, requestedGameId)) {
        return;
      }
      if (!data) return; // Error case handled below
      
      // ✅ Additional validation: Check if user is actually part of this game
      // In autobattler mode, user is a spectator (spectatorUserId), not a player
      // ✅ FIX: Handle both string and number types for player IDs (war effort battles use strings, regular battles may use numbers)
      const isPlayer = data.gameState?.players?.includes(userId) || 
                      data.gameState?.players?.includes(userId.toString()) || 
                      data.gameState?.players?.includes(Number(userId));
      const isAutobattlerSpectator = data.gameState?.isAutobattler && data.gameState?.spectatorUserId === userId;
      
      if (!data.gameState || !data.gameState.players || (!isPlayer && !isAutobattlerSpectator)) {
        console.warn(`⚠️ User ${userId} is not part of game ${gameId}. Redirecting.`, {
          isPlayer,
          isAutobattlerSpectator,
          players: data.gameState?.players,
          spectatorUserId: data.gameState?.spectatorUserId,
          isAutobattler: data.gameState?.isAutobattler
        });
        setError(t('battleUI.gameNotFound'));
        setTimeout(() => {
          navigate('/battleoverview', { replace: true });
        }, 2000);
        return;
      }
      
      setGameState(data.gameState);
      // ✅ FIX: Handle both string and number types for turn check
      const isMyTurn = data.gameState.turn === userId || 
                      data.gameState.turn === userId.toString() || 
                      String(data.gameState.turn) === String(userId);
      setYourTurn(isMyTurn);
      storeOpponentStrategyId(data.gameState);
      
      // ✅ FIX: Initialize previousGameStateRef with initial state so first action can be detected
      // Create a baseline state for comparison (deep copy to avoid reference issues)
      if (!previousGameStateRef.current) {
        previousGameStateRef.current = JSON.parse(JSON.stringify(data.gameState));
      }
      
      // ✅ Determine if player goes first (after both players have mulliganed)
      const playerState = data.gameState.state?.[userId];
      const opponentId = data.gameState.players?.find(p => p !== userId);
      const opponentState = opponentId ? data.gameState.state?.[opponentId] : null;
      
      // Check if both players have completed mulligan
      const playerMulliganed = playerState && !playerState.needsMulligan && playerState.hasMulliganed;
      const opponentMulliganed = opponentState && !opponentState.needsMulligan && opponentState.hasMulliganed;
      
      if (playerMulliganed && opponentMulliganed) {
        // Both players have mulliganed - determine who goes first (only set once)
        // ✅ FIX: Use players[0] to determine who goes first, not current turn
        setPlayerGoesFirst(prev => {
          if (prev === null) {
            const firstPlayerId = data.gameState.players?.[0];
            // ✅ FIX: Handle both string and number types for player ID comparison
            const goesFirst = firstPlayerId === userId || 
                             firstPlayerId === userId.toString() || 
                             String(firstPlayerId) === String(userId);
            // Show message (will auto-hide after 5 seconds via useEffect)
            setShowFirstTurnMessage(true);
            return goesFirst;
          }
          return prev; // Keep existing value
        });
      }
      
      // ✅ REFACTORED: Use hook's validateState method
      validateState(data.gameState);
    })
    .catch(err => {
      if (fetchCancelled) return;
      if (shouldDiscardStaleGameFetch(gameIdRef.current, requestedGameId)) {
        return;
      }
      console.error("❌ Failed to fetch game state:", err);
      const status = err.status || (err.response?.status);
      
      // Game not found or not accessible - likely an old gameId or auth issue
      if (status === 404 || status === 403) {
        console.warn(`⚠️ Game ${gameId} not found or not accessible (status: ${status}). Redirecting to battle overview.`);
        setError(t('battleUI.gameNotFound'));
      } else {
        setError(t('battleUI.connectionLost'));
      }
      
      // Redirect to battle overview after a short delay
      setTimeout(() => {
        if (shouldDiscardStaleGameFetch(gameIdRef.current, requestedGameId)) {
          return;
        }
        navigate('/battleoverview', { replace: true });
      }, 2000);
    });

  return () => {
    fetchCancelled = true;
  };
}, [gameId, token, userId, location.state?.developerMode, t, navigate, isSpectating]);

// === Handle temporary messages (e.g., "feeling tired") ===
useEffect(() => {
  if (!gameState || !gameState.temporaryMessages || !userId) return;
  
  const playerMessage = gameState.temporaryMessages[userId];
  if (playerMessage && playerMessage.message === 'feelingTired') {
    // Show the message
    setTemporaryMessage('feelingTired');
    
    // Auto-hide after 3 seconds
    const timer = setTimeout(() => {
      setTemporaryMessage(null);
    }, 3000);
    
    return () => clearTimeout(timer);
  }
}, [gameState, userId]);

// Play immobilize.mp3 when any battlefield unit transitions to immobilized (uses effectiveGameState for spectators)
useEffect(() => {
  if (!effectiveGameState?.state) return;

  const isImmobilizedOrder = (order) =>
    !!order &&
    ((order.isImmobilized === true) || (order.immobilized === true) || (order.status === 'immobilized'));

  const current = new Map();
  for (const playerId of Object.keys(effectiveGameState.state)) {
    const played = effectiveGameState.state[playerId]?.playedOrders;
    if (!played) continue;
    for (const zone of [played.front, played.flank]) {
      if (!Array.isArray(zone)) continue;
      for (const u of zone) {
        if (!u?.instanceId) continue;
        current.set(String(u.instanceId), isImmobilizedOrder(u) === true);
      }
    }
  }

  const prev = prevImmobilizedByInstanceRef.current;
  if (prev !== null) {
    let newlyImmobilized = false;
    for (const [id, nowImm] of current) {
      if (nowImm === true && prev.get(id) !== true) {
        newlyImmobilized = true;
        break;
      }
    }
    if (newlyImmobilized) {
      playSoundEffect('/sounds/immobilize.mp3', 0.6);
    }
  }
  prevImmobilizedByInstanceRef.current = current;
}, [effectiveGameState]);


useEffect(() => {
  if (strategyDetails?.orders) {
    // Check if orders already have .id property to avoid infinite loop
    const needsNormalization = strategyDetails.orders.some(order => !order.id && order.order_id);
    if (needsNormalization) {
      const normalizedOrders = strategyDetails.orders.map((order) => ({
        ...order,
        id: order.order_id, // ✅ Ensure .id is available
      }));
      // ✅ FIX: Use functional update to avoid infinite loop
      // Only update if the normalized orders are actually different
      setStrategyDetails((prev) => {
        // Check if normalization is still needed (avoid infinite loop)
        const stillNeedsNormalization = prev?.orders?.some(order => !order.id && order.order_id);
        if (!stillNeedsNormalization) {
          return prev; // Already normalized, don't update
        }
        return {
          ...prev,
          orders: normalizedOrders,
        };
      });
    }
  }
}, [strategyDetails]);



useEffect(() => {
  if (exitingRef.current) return;
  if (!strategyId || !token) return;

  // ✅ CRITICAL: Do NOT pass language parameter for game logic
  // Strategy orders must use English faction names for game logic to work correctly
  // Translations are only for display, not for matching/filtering in game logic
  fetch(`${API_URL}/strategy/${strategyId}`, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
    .then(res => res.json())
    .then(data => {
      setStrategyDetails(data);
    })
    .catch(err => {
      console.error("❌ Failed to fetch strategy:", err);
    });
}, [strategyId, token]);

// Update counter strike orders when game state changes
// ✅ FIX: updateCounterStrikeOrders and updateMulliganOrders are useCallback functions
// They are stable and won't cause infinite loops, so we can safely include them in deps
useEffect(() => {
  if (gameState) {
    updateCounterStrikeOrders(gameState);
    updateMulliganOrders(gameState);
  }
}, [gameState, userId, updateCounterStrikeOrders, updateMulliganOrders]);

// ✅ FIX: Clear selectedCounterStrikeOrder if it's no longer in the pending counterstrike array
// This handles the case where a counterstrike was played and removed from the array
useEffect(() => {
  if (selectedCounterStrikeOrder && counterStrikeOrders.length > 0) {
    // Check if the selected order is still in the pending counterstrike array
    const stillPending = counterStrikeOrders.find(
      order => order.instanceId === selectedCounterStrikeOrder.instanceId
    );
    if (!stillPending) {
      setSelectedCounterStrikeOrder(null);
    }
  } else if (selectedCounterStrikeOrder && counterStrikeOrders.length === 0) {
    // If there are no pending counterstrikes, clear the selected order
    setSelectedCounterStrikeOrder(null);
  }
}, [counterStrikeOrders, selectedCounterStrikeOrder]);

// Ghost Operative (295): stable signature so we only open the modal once per distinct reveal
// and do not re-apply state on every gameState tick (which also re-showed after local-only dismiss).
const lastGhostRevealSigRef = useRef(null);

// Track hand size to detect when new cards are drawn
const previousHandSizeRef = useRef(0);
useEffect(() => {
  if (gameState && gameState.state && gameState.state[userId]) {
    const playerState = gameState.state[userId];
    const currentHandSize = playerState.hand?.length || 0;
    const previousHandSize = previousHandSizeRef.current;
    
    // If hand size increased, play draw sound
    if (currentHandSize > previousHandSize && previousHandSize > 0) {
      const cardsDrawn = currentHandSize - previousHandSize;
      // Play draw sound for each card drawn
      for (let i = 0; i < cardsDrawn; i++) {
        setTimeout(() => {
          playSoundEffect('/sounds/draw.mp3');
        }, i * 100); // Stagger sounds slightly if multiple cards drawn
      }
    }
    
    // Update previous hand size
    previousHandSizeRef.current = currentHandSize;
  }
}, [gameState, userId]);

// Update revealed enemy order when game state changes (once per distinct reveal from server)
useEffect(() => {
  if (gameState && gameState.state && gameState.state[userId]) {
    const playerState = gameState.state[userId];
    const newRevealedOrder = playerState.revealedEnemyOrder || null;
    const sig =
      newRevealedOrder && newRevealedOrder.order
        ? `${newRevealedOrder.enemyId}:${newRevealedOrder.order.instanceId ?? ''}:${newRevealedOrder.order.id}`
        : null;
    if (sig === null) {
      lastGhostRevealSigRef.current = null;
      setRevealedEnemyOrder(null);
      return;
    }
    if (sig !== lastGhostRevealSigRef.current) {
      lastGhostRevealSigRef.current = sig;
      setRevealedEnemyOrder(newRevealedOrder);
    }
  } else {
    lastGhostRevealSigRef.current = null;
    setRevealedEnemyOrder(null);
  }
}, [gameState, userId]);

const acknowledgeGhostRevealedOrder = useCallback(() => {
  setRevealedEnemyOrder(null);
  const gid = gameIdRef.current;
  if (!gid) {
    console.error('Ghost Operative acknowledge: gameId is missing');
    return;
  }
  const socket =
    (currentSocketRef.current && currentSocketRef.current.readyState === WebSocket.OPEN && currentSocketRef.current) ||
    (ws && ws.readyState === WebSocket.OPEN ? ws : null);
  if (!socket) {
    console.error('Ghost Operative acknowledge: WebSocket not connected; modal closed locally only');
    return;
  }
  try {
    socket.send(JSON.stringify({ action: 'acknowledge-revealed-order', gameId: gid }));
  } catch (err) {
    console.error('Ghost Operative acknowledge: WebSocket send failed', err);
  }
}, [ws]);

useEffect(() => {
  if (error) {
    const timeout = setTimeout(() => {
      setError("");
    }, 3000); // 3 seconds

    return () => clearTimeout(timeout);
  }
}, [error]);

// Turn timer logic moved to ./timer/useTurnTimer.js


  const handlePrev = () => {
    if (!you || !you.hand.length) return;
    setCarouselIndex(prev => (prev === 0 ? you.hand.length - 1 : prev - 1));
  };
  const handleNext = () => {
    if (!you || !you.hand.length) return;
    setCarouselIndex(prev => (prev === you.hand.length - 1 ? 0 : prev + 1));
  };

useEffect(() => {
  let socket;

  // ✅ CAMPAIGN MODE: Skip WebSocket reconnect for campaign games if gameState is already provided
  // Campaign games use scripted actions and don't need real-time sync
  const isCampaignFromState = location.state?.isCampaign || gameState?.campaignMode;
  const hasGameStateFromNavigation = location.state?.gameState && gameState;
  
  if (isCampaignFromState && hasGameStateFromNavigation) {
    return; // Don't establish WebSocket for campaign games with gameState from navigation
  }

  // ✅ FIX: Always establish WebSocket connection if we have gameId and don't have a connection yet
  // WebSocket is needed for sending actions (deploy, attack, etc.) regardless of whether we have gameState
  // Use ref to prevent multiple connection attempts
  // SKIP for friend spectators - they get game state through friends WebSocket, not game WebSocket
  // ✅ CRITICAL FIX: Allow WebSocket creation for autobattler spectators - they need game WebSocket to receive updates
  // SKIP for campaign games with gameState from navigation (they'll connect when needed)
  const isAutobattlerGame = gameId?.includes('_autobattler_') || initialGameState?.isAutobattler || gameState?.isAutobattler;
  const shouldCreateWebSocket = !isSpectating || isAutobattlerGame; // Allow for autobattler even if isSpectating is true

  if (shouldCreateWebSocket && !exitingRef.current && gameId && !ws && !wsConnectingRef.current && !(isCampaignFromState && hasGameStateFromNavigation)) {
    wsConnectingRef.current = true; // Mark that we're connecting
    // If we don't have gameState, we need to reconnect to get it
    const needsReconnect = !gameState;
    if (needsReconnect) {
      reconnectingRef.current = true;
      setReconnecting(true);
      // ✅ FIX: Set timeout to clear reconnecting state if no response received
      // This prevents getting stuck in "Reconnecting..." state
      if (reconnectingTimeoutRef.current) {
        clearTimeout(reconnectingTimeoutRef.current);
      }
      reconnectingTimeoutRef.current = setTimeout(() => {
        console.warn('⚠️ [Reconnect] Timeout waiting for gameState - clearing reconnecting state');
        reconnectingRef.current = false;
        setReconnecting(false);
        wsConnectingRef.current = false;
        reconnectingTimeoutRef.current = null;
      }, 10000); // 10 second timeout
    }
    
    // Check if token needs refresh before connecting (use tokenRef so we don't depend on token in effect deps)
    const refreshTokenIfNeeded = async () => {
      const currentToken = tokenRef.current;
      if (needsTokenRefresh(currentToken) && refreshTokenValue) {
        try {
          const newToken = await refreshToken(refreshTokenValue);
          tokenRef.current = newToken;
          return newToken;
        } catch (error) {
          return currentToken;
        }
      }
      return currentToken;
    };

    // Start WebSocket connection (different URL/port than CRA/webpack HMR on the dev server page)
    socket = new window.WebSocket(WS_URL);
    // ✅ CRITICAL: Set both state and ref immediately when socket is created
    // This ensures endTurn can find the socket even if state update is delayed
    currentSocketRef.current = socket;
    setWs(socket);

  socket.onerror = () => {
    console.error(
      '[CODA] Game WebSocket error — connection failed or dropped. Is the game server listening? URL:',
      WS_URL
    );
  };
  
  socket.onopen = async () => {
    const tokenToUse = await refreshTokenIfNeeded();
    // Get current language from i18next (normalize to lowercase format)
    const currentLanguage = localStorage.getItem('i18nextLng') || 'en-us';
    const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
    // Use centralized auth message with API key
    socket.send(JSON.stringify(getWebSocketAuthMessage(tokenToUse, normalizedLanguage)));
  };
  socket.onmessage = event => {
    if (exitingRef.current) return;
    let msg;
    try {
      msg = JSON.parse(event.data);
    } catch (err) {
      console.error("Failed to parse WebSocket message:", err);
      return;
    }

    // ✅ Deploy VFX (e.g. Encrazed Bombardier fire arc): consume before early version rejection.
    // Otherwise turn-played can be dropped as "stale" while still carrying the only copy of
    // pendingDeployAnimations — opponent never registers the animation.
    tryConsumePendingDeployAnimationsForViewer(
      msg.gameState,
      deployAnimationViewerUserIdRef.current,
      deployTargetAnimationsRef.current,
      msg.gameId,
      isWsMessageForDifferentSessionRef.current
    );
    
    // ✅ IMPROVEMENT #1: Early state version check - reject stale state before any processing
    // This prevents unnecessary work if the state is stale
    // ✅ FIX: Skip version check for spectate-update - autobattler spectators should always receive updates
    // They're passive observers, so version conflicts don't matter (no actions to desync)
    if (msg.gameState && msg.type !== "state-desync-error" && msg.type !== "reconnect-success" && msg.type !== "spectate-update" && msg.type !== "weapon-attached") {
      // Skip version check for desync recovery, reconnect, and spectate updates (they need to force update)
      // Skip for weapon-attached: must always apply needsDeployTargeting (e.g. Sandra Lehnsherr 395) even if embedded stateVersion races with turn-played
      const versionAcceptable = checkStateVersionRef.current(msg.gameState);
      if (!versionAcceptable) {
        const currentVersion = lastStateVersionRef.current || (gameStateRefRef.current?.current?.stateVersion || 0);
        const incomingVersion = msg.gameState.stateVersion || 0;
        console.warn(`⚠️ Early rejection: Stale state update detected before processing. incoming=${incomingVersion}, current=${currentVersion}. Requesting fresh state...`);
        // Request fresh state instead of processing stale one
        if (socket && socket.readyState === 1) {
          socket.send(JSON.stringify({ type: 'request-state-refresh', gameId }));
        }
        return; // Don't process stale state
      }
    }
    
    if (msg.type === "auth-success") {
      // ✅ CRITICAL FIX: Include gameId for autobattler spectators
      // Use tokenRef.current so we use latest token without making effect depend on token
      gameSocketReconnectSentRef.current = true; // So effect cleanup won't close this socket on re-run (e.g. Strict Mode)
      socket.send(JSON.stringify({
        type: "reconnect",
        token: tokenRef.current,
        gameId // Include gameId for autobattler spectating
      }));
      // Campaign: ask wrapper to re-sync allowed actions so server has them after connect
      if (gameId && String(gameId).startsWith('campaign_')) {
        window.dispatchEvent(new CustomEvent('campaign-request-sync', { detail: { gameId } }));
      }
    }
    if (msg.type === "reconnect-success") {
      // ✅ FIX: Never apply completed game state from reconnect - prevents infinite redirect to after-battle
      if (msg.gameState?.winner != null && msg.gameState?.winner !== undefined) {
        if (isWsMessageForDifferentSessionRef.current(msg.gameId)) {
          return;
        }
        console.warn(`⚠️ Reconnect sent completed game (winner: ${msg.gameState.winner}). Redirecting to battle overview.`);
        reconnectingRef.current = false;
        setReconnecting(false);
        wsConnectingRef.current = false;
        if (reconnectingTimeoutRef.current) {
          clearTimeout(reconnectingTimeoutRef.current);
          reconnectingTimeoutRef.current = null;
        }
        navigate('/battleoverview', { replace: true });
        return;
      }
      // ✅ FIX: Validate that the reconnected gameId matches what the user is viewing (URL first, then ref).
      // If the server omits gameId, do not apply payload — avoids poisoning a new session after leaving a prior battle.
      const reconnectExpectedSid = resolveWsSessionGameIdRef.current();
      if (reconnectExpectedSid != null) {
        if (!msg.gameId || String(msg.gameId) !== String(reconnectExpectedSid)) {
          if (msg.gameId) {
            console.warn(`⚠️ Reconnected gameId (${msg.gameId}) doesn't match resolved session gameId (${reconnectExpectedSid}, ref=${gameIdRef.current}). Redirecting to correct game.`);
            navigate(`/battle/${msg.gameId}`, { replace: true });
          } else {
            console.warn('⚠️ reconnect-success missing gameId; refusing to apply state for this session.');
            reconnectingRef.current = false;
            setReconnecting(false);
            wsConnectingRef.current = false;
            if (reconnectingTimeoutRef.current) {
              clearTimeout(reconnectingTimeoutRef.current);
              reconnectingTimeoutRef.current = null;
            }
          }
          return;
        }
      }
      
      // ✅ REFACTORED: Use hook's unlockUI method (via ref to avoid stale closure)
      unlockUIRef.current();
      
      // ✅ REFACTORED: Use hook's validateState method (via ref to avoid stale closure)
      if (!validateStateRef.current(msg.gameState)) {
        console.error('🚨 STATE VALIDATION FAILED in reconnect: InstanceIds missing!');
      }
      
      // ✅ CAMPAIGN MODE: Preserve tutorial queue when reconnecting campaign games
      // Merge scriptState from existing gameState if it exists
      const existingScriptState = gameState?.scriptState;
      const reconnectedScriptState = msg.gameState?.scriptState;
      
      if (gameState?.campaignMode && existingScriptState && reconnectedScriptState) {
        // Preserve tutorial queue from existing state if reconnected state doesn't have it
        if (existingScriptState.tutorialQueue && (!reconnectedScriptState.tutorialQueue || reconnectedScriptState.tutorialQueue.length === 0)) {
          msg.gameState.scriptState.tutorialQueue = existingScriptState.tutorialQueue;
        }
        // Merge other scriptState properties
        msg.gameState.scriptState = {
          ...reconnectedScriptState,
          tutorialQueue: reconnectedScriptState.tutorialQueue || existingScriptState.tutorialQueue || []
        };
      }
      
      // ✅ ENHANCED DESYNC FIX: Update ref first, then state
      // ✅ FIX: Always update gameState from server (it's the source of truth)
      // Even if we had gameState from navigation, server state is authoritative
      gameStateRefRef.current.current = msg.gameState;
      setGameState(msg.gameState);
      setYourTurn(msg.yourTurn);
      storeOpponentStrategyId(msg.gameState);
      
      // ✅ Determine if player goes first (after both players have mulliganed)
      const playerState = msg.gameState.state?.[userId];
      const opponentId = msg.gameState.players?.find(p => p !== userId);
      const opponentState = opponentId ? msg.gameState.state?.[opponentId] : null;
      
      // Check if both players have completed mulligan
      const playerMulliganed = playerState && !playerState.needsMulligan && playerState.hasMulliganed;
      const opponentMulliganed = opponentState && !opponentState.needsMulligan && opponentState.hasMulliganed;
      
      if (playerMulliganed && opponentMulliganed) {
        // Both players have mulliganed - determine who goes first (only set once)
        // ✅ FIX: Use players[0] to determine who goes first, not current turn
        setPlayerGoesFirst(prev => {
          if (prev === null) {
            const firstPlayerId = msg.gameState.players?.[0];
            const goesFirst = firstPlayerId === userId;
            // Show message (will auto-hide after 5 seconds via useEffect)
            setShowFirstTurnMessage(true);
            return goesFirst;
          }
          return prev; // Keep existing value
        });
      }
      
      setSelectedOrderId(null);
      setSelectedAttacker(null);
      setAttackMode(false);
      attackInProgressRef.current = false; // Reset attack in progress flag
      reconnectingRef.current = false;
      setReconnecting(false);
      wsConnectingRef.current = false; // ✅ FIX: Reset connection flag after successful reconnect
      // ✅ FIX: Clear timeout since we received reconnect-success
      if (reconnectingTimeoutRef.current) {
        clearTimeout(reconnectingTimeoutRef.current);
        reconnectingTimeoutRef.current = null;
      }
      
      // ✅ CRITICAL FIX: Find the best available socket
      // The socket from closure might be stale, so check all possible sources
      let bestSocket = socket; // Start with closure socket
      
      // Check if ref has a better socket (more recent, or actually OPEN)
      if (currentSocketRef.current) {
        if (currentSocketRef.current.readyState === WebSocket.OPEN) {
          bestSocket = currentSocketRef.current;
        } else if (!bestSocket || bestSocket.readyState !== WebSocket.OPEN) {
          // Ref socket might be better even if not OPEN (at least it exists)
          if (currentSocketRef.current.readyState === WebSocket.CONNECTING) {
            bestSocket = currentSocketRef.current;
          }
        }
      }
      
      // Check if ws state has a better socket
      if (ws && ws.readyState === WebSocket.OPEN) {
        bestSocket = ws;
      }
      
      // Verify we have a socket
      if (!bestSocket) {
        console.error('❌ No socket available after reconnect-success');
        wsConnectingRef.current = false;
        setWs(null);
        currentSocketRef.current = null;
        return;
      }
      
      // ✅ CRITICAL: Always update both ref and state with the best socket
      // This ensures endTurn can find the socket even if state is stale
      currentSocketRef.current = bestSocket;
      setWs(bestSocket);
      
      // ✅ CRITICAL: Verify the socket is actually OPEN and ready
      if (bestSocket.readyState !== WebSocket.OPEN) {
        console.warn('⚠️ WARNING: Socket not OPEN after reconnect-success', {
          readyState: bestSocket.readyState,
          readyStateName: bestSocket.readyState === WebSocket.OPEN ? 'OPEN' : 
                         bestSocket.readyState === WebSocket.CONNECTING ? 'CONNECTING' :
                         bestSocket.readyState === WebSocket.CLOSING ? 'CLOSING' : 'CLOSED',
          expected: WebSocket.OPEN
        });
      }
      
      
      // Sync strategem targeting state with game state
      if (window.syncStrategemTargeting) {
        window.syncStrategemTargeting(msg.gameState, userId);
      }
      // ✅ FIX: Skip for spectators - they should be handled by friendsContext
      // ✅ FIX: Ignore winner from another game (stale/delayed message) to prevent redirect loop
      if (msg.gameState?.winner && !isSpectating && !isWsMessageForDifferentSessionRef.current(msg.gameId)) {
        const didYouWin = msg.gameState.winner === userId;
        const isFriendlyMatch = msg.gameState.isFriendlyMatch || false;
        handleBattleEnd(didYouWin, gameIdRef.current, socket, isFriendlyMatch, 'reconnect-success-winner');
        return;
      }
    }

    // ✅ AUTOBATTLER PAUSE/RESUME: Handle backend confirmation of pause/resume
    if (msg.type === "autobattler-paused" || msg.type === "autobattler-resumed") {
      // Notify speed control hook about backend confirmation
      if (speedControl.handleBackendPauseConfirmation) {
        speedControl.handleBackendPauseConfirmation(msg.success, msg.isPaused);
      }
      return; // Don't process further - this is just a confirmation message
    }
    
    // Handle weapon responses
    if (msg.type === "weapon-attached") {
      if (msg.success) {
        if (msg.needsDeployTargeting) {
          // Step 2: Weapon needs deploy targeting
          // ✅ CRITICAL: Clear weapon targeting mode before entering deploy targeting
          setWeaponTargetMode(false);
          setSelectedWeaponId(null);
          setSelectedWeaponInstanceId(null);
          
          setWeaponDeployTargetMode(true);
          setWeaponDeployTargetInfo({
            weaponInstanceId: msg.weapon.instanceId,
            weaponId: msg.weapon.id, // Include weapon ID for message lookup
            weaponName: msg.weapon.name, // Include weapon name for debugging
            targetUnitInstanceId: msg.targetUnit.instanceId
          });
          setError(t('battleUI.selectTargetWeapon'));
        } else {
          // Weapon attached successfully, no deploy targeting needed
          // ✅ Play equip sound when weapon is successfully equipped
          playSoundEffect('/sounds/equip.mp3', 0.5); // Play at 50% volume
          
          setWeaponTargetMode(false);
          setSelectedWeaponId(null);
          setSelectedWeaponInstanceId(null);
          setError("");
        }
      } else {
        setError(t('battleUI.connectionLost'));
      }
      // Don't return early - let it fall through to update gameState
    }

    if (msg.type === "weapon-deploy-completed") {
      // Backend sends two messages: (1) { success: true } then (2) { gameState } from sendGameStateToPlayers.
      // Treat as success when we have success: true OR gameState (second message has no success field).
      const isSuccess = msg.success === true || (msg.gameState != null);
      if (isSuccess) {
        // Weapon deploy effect completed successfully
        if (msg.success === true) {
          playSoundEffect('/sounds/equip.mp3', 0.5); // Play at 50% volume
        }
        setWeaponDeployTargetMode(false);
        setWeaponDeployTargetInfo(null);
        setWeaponTargetMode(false);
        setSelectedWeaponId(null);
        setSelectedWeaponInstanceId(null);
        setError("");
      } else {
        setError(t('battleUI.connectionLost'));
      }
      // Don't return early when we have gameState - let it fall through to update game state below
      if (!msg.gameState) return;
    }

    // ✅ Handle emote message from opponent
    if (msg.type === "emote") {
      if (msg.text) {
        setOpponentEmoteBubble({
          text: msg.text,
          timestamp: Date.now()
        });
        
        // Hide bubble after 3 seconds
        setTimeout(() => {
          setOpponentEmoteBubble(null);
        }, 3000);
      }
      return; // Don't fall through to other handlers
    }

    // Opponent is hovering over an order; show subtle glow on that order for this player
    if (msg.type === 'opponent-looking-at-order') {
      setOpponentLookingAtInstanceId(msg.instanceId ?? null);
      return;
    }

    // ✅ FIX: Handle spectator-joined message from game WebSocket
    if (msg.type === "spectator-joined") {
      if (handleSpectatorJoined && msg.spectatorId && msg.username) {
        handleSpectatorJoined({
          spectatorId: msg.spectatorId,
          username: msg.username
        });
      }
      return; // Don't fall through to other handlers
    }

    // ✅ FIX: Handle spectator-left message from game WebSocket
    if (msg.type === "spectator-left") {
      if (handleSpectatorLeft && msg.spectatorId) {
        handleSpectatorLeft({
          spectatorId: msg.spectatorId,
          username: msg.username
        });
      }
      return; // Don't fall through to other handlers
    }

    // ✅ Handle attack-initiated message (damage delayed until animation completes)
    if (msg.type === "attack-initiated") {
      // ✅ FIX: Ignore messages for a different game (stale/delayed).
      // Use only msg.gameId (WebSocket session / activeGames key). gameState.gameId is internal and mismatches session id.
      const aiMsgGameId = msg.gameId;
      if (isWsMessageForDifferentSessionRef.current(aiMsgGameId)) {
        return;
      }
      // ✅ SPEED CONTROL: For autobattler spectators, queue the attack update
      if (isAutobattlerSpectator) {
        const applyUpdate = () => {
          // ✅ LOG ATTACK INSTANTLY: Log attack action immediately when message arrives
          if (msg.attackOutcome) {
            const outcome = msg.attackOutcome;
            
            // Check if this is a general attack
            if (outcome.isGeneralAttack) {
              // General attack - log as general-interaction
              const attackerUnit = outcome.attacker;
              if (attackerUnit && attackerUnit.name) {
                const newAction = {
                  type: 'general-interaction',
                  playerId: outcome.targetPlayerId,
                  timestamp: Date.now(),
                  source: {
                    id: attackerUnit.id,
                    name: attackerUnit.name,
                    instanceId: attackerUnit.instanceId,
                    type: 'unit'
                  },
                  effectType: 'attack',
                  amount: outcome.damageToTarget || 0,
                  attackerDamageTaken: outcome.damageToAttacker ?? 0,
                  message: `${attackerUnit.name} dealt ${outcome.damageToTarget || 0} damage`,
                  general: {
                    playerId: outcome.targetPlayerId,
                    healthBefore: outcome.targetHealthBefore || 0,
                    damageTaken: outcome.damageToTarget || 0
                  }
                };
              }
            } else {
              // Unit vs unit attack - server logs via actionLog; no client-side add
            }
          }
          
          // Update game state (units still alive, damage not applied yet)
          if (msg.gameState) {
            gameStateRefRef.current.current = msg.gameState;
            setGameState(msg.gameState);
            updateYourTurn(msg.yourTurn || false, 'attack-initiated');
          }
          // Play destroy sound when a drone is destroyed by attack (destroy effect)
          if (msg.attackOutcome?.targetDestroyed && msg.attackOutcome?.targetZone === 'opponent_drone') {
            playSoundEffect('/sounds/destroy.mp3', 0.6);
          }
          // Register attack for animation
          if (msg.attackId && msg.attackOutcome) {
            attackAnimations.registerAttack(msg.attackId, msg.attackOutcome, msg.isBotAttack || false);
          } else {
            console.error(`❌ Cannot register attack: missing attackId or attackOutcome`, {
              hasAttackId: !!msg.attackId,
              hasAttackOutcome: !!msg.attackOutcome,
              msgType: msg.type
            });
          }
        };

        speedControlQueueUpdateRef.current(msg, applyUpdate);
        return; // Don't process immediately for autobattler spectators
      }
      
      // ✅ LOG ATTACK INSTANTLY: Log attack action immediately when message arrives
      if (msg.attackOutcome) {
        const outcome = msg.attackOutcome;
        
        // Check if this is a general attack
        if (outcome.isGeneralAttack) {
          // General attack - log as general-interaction
          const attackerUnit = outcome.attacker;
          if (attackerUnit && attackerUnit.name) {
            const newAction = {
              type: 'general-interaction',
              playerId: outcome.targetPlayerId, // The player whose general was attacked
              timestamp: Date.now(),
              source: {
                id: attackerUnit.id,
                name: attackerUnit.name,
                instanceId: attackerUnit.instanceId,
                type: 'unit'
              },
              effectType: 'attack',
              amount: outcome.damageToTarget || 0,
              attackerDamageTaken: outcome.damageToAttacker ?? 0,
              message: `${attackerUnit.name} dealt ${outcome.damageToTarget || 0} damage`,
              general: {
                playerId: outcome.targetPlayerId,
                healthBefore: outcome.targetHealthBefore || 0,
                damageTaken: outcome.damageToTarget || 0
              }
            };
          }
        } else {
          // Unit vs unit attack - log as unit-attacked
          const attackerUnit = outcome.attacker;
          const targetUnit = outcome.target;
          const damageToTarget = outcome.damageToTarget || 0;
          if (attackerUnit && targetUnit && attackerUnit.name && targetUnit.name) {
            const newAction = {
              type: 'unit-attacked',
              playerId: outcome.attackerPlayerId,
              timestamp: Date.now(),
              amount: damageToTarget,
              attacker: {
                id: attackerUnit.id,
                name: attackerUnit.name,
                instanceId: attackerUnit.instanceId,
                died: outcome.attackerDestroyed || false,
                healthBefore: outcome.attackerHealthBefore || attackerUnit.health || 0,
                damageTaken: outcome.damageToAttacker || 0
              },
              target: {
                id: targetUnit.id,
                name: targetUnit.name,
                instanceId: targetUnit.instanceId,
                died: outcome.targetDestroyed || false,
                healthBefore: outcome.targetHealthBefore || targetUnit.health || 0,
                damageTaken: damageToTarget
              },
              message: `${attackerUnit.name} dealt ${damageToTarget} damage to ${targetUnit.name}`
            };
          }
        }
      }
      
      // Update game state (units still alive, damage not applied yet)
      if (msg.gameState) {
        gameStateRefRef.current.current = msg.gameState;
        setGameState(msg.gameState);
        updateYourTurn(msg.yourTurn || false, 'attack-initiated');
      }
      // Play destroy sound when a drone is destroyed by attack (destroy effect)
      if (msg.attackOutcome?.targetDestroyed && msg.attackOutcome?.targetZone === 'opponent_drone') {
        playSoundEffect('/sounds/destroy.mp3', 0.6);
      }
      // Register attack for animation
      // ✅ BOT ANIMATION FIX: Pass isBotAttack flag to auto-complete bot attacks
      // ✅ CRITICAL: Bot attacks should animate even when it's not the player's turn
      if (msg.attackId && msg.attackOutcome) {
        attackAnimations.registerAttack(msg.attackId, msg.attackOutcome, msg.isBotAttack || false);
      } else {
        console.error(`❌ Cannot register attack: missing attackId or attackOutcome`, {
          hasAttackId: !!msg.attackId,
          hasAttackOutcome: !!msg.attackOutcome
        });
      }
      return; // Don't fall through to game-update handler
    }

    // ✅ Handle attack-complete message (damage applied, final state)
    if (msg.type === "attack-complete") {
      // ✅ FIX: Ignore messages for a different game (stale/delayed) - do not apply state or redirect.
      // Use only msg.gameId (session key); gameState.gameId is internal.
      const acMsgGameId = msg.gameId;
      if (isWsMessageForDifferentSessionRef.current(acMsgGameId)) {
        return;
      }
      // ✅ FIX: Clear attack in progress flag when backend confirms attack is complete
      attackInProgressRef.current = false;
      
      // ✅ SPEED CONTROL: For autobattler spectators, queue the state update part
      // (Side effects like dead unit tracking and action logging run immediately for both paths)
      if (isAutobattlerSpectatorRef.current) {
        // Queue the critical state update
        const applyUpdate = () => {
            if (msg.gameState) {
            gameStateRefRef.current.current = msg.gameState;
            setGameState(msg.gameState);
            setActionLog(msg.gameState.actionLog ?? []);
            updateYourTurn(msg.yourTurn || false, 'attack-complete');
            storeOpponentStrategyId(msg.gameState);
          }
        };
        
        speedControlQueueUpdateRef.current(msg, applyUpdate);
        // Continue with rest of handler for side effects (dead units, actions, etc.)
      }
      
      // ✅ CRITICAL FIX: Update gameState from attack-complete message
      // This ensures the frontend has the latest state with protection effects applied
      // (e.g., Isabela at 1 health should be in the gameState)
      // ✅ SPEED CONTROL: Only update immediately if NOT an autobattler spectator
      if (!isAutobattlerSpectator && msg.gameState) {
        updateStateRef.current(msg.gameState);
      }
      
      // ✅ CRITICAL FIX: Update gameState from attack-complete message
      // This ensures the frontend has the latest state with protection effects applied
      // (e.g., Isabela at 1 health should be in the gameState)
      if (msg.gameState) {
        updateStateRef.current(msg.gameState);
      }
      
      // ✅ CRITICAL FIX: Update dead targets/attackers tracking with corrected outcome from backend
      // The backend sends the corrected outcome with actual destruction status after protection effects
      // This is the AUTHORITATIVE source - always trust the backend's final determination
      if (msg.attackOutcome) {
        const outcome = msg.attackOutcome;
        
        // Update dead targets tracking based on corrected outcome from backend
        if (outcome.targetInstanceId) {
          if (outcome.targetDestroyed) {
            // Target was actually destroyed - add to dead targets
            attackAnimations.deadTargetsRef.current.add(outcome.targetInstanceId);
            // Clean up after delay to prevent interference with strategem animations
            if (attackAnimations.cleanupDeadTarget) {
              attackAnimations.cleanupDeadTarget(outcome.targetInstanceId);
            }
          } else {
            // Target survived (e.g., Isabela with protection) - remove from dead targets
            const wasInDeadTargets = attackAnimations.deadTargetsRef.current.has(outcome.targetInstanceId);
            attackAnimations.deadTargetsRef.current.delete(outcome.targetInstanceId);
          }
        }
        
        // Update dead attackers tracking based on corrected outcome from backend
        if (outcome.attackerInstanceId) {
          if (outcome.attackerDestroyed) {
            // Attacker was actually destroyed
            attackAnimations.deadAttackersRef.current.add(outcome.attackerInstanceId);
          } else {
            // Attacker survived - remove from dead attackers
            attackAnimations.deadAttackersRef.current.delete(outcome.attackerInstanceId);
          }
          
          // ✅ CRITICAL FIX: Show attacker if they survived (e.g., Isabela with protection)
          // Remove from hiddenUnits so the unit displays correctly
          // ✅ ELECTRON FIX: Always remove from hiddenUnits when attack-complete arrives
          // This ensures units are shown even if they were hidden during animation
          if (outcome.attackerInstanceId) {
            attackAnimations.setHiddenUnits(prev => {
              const next = new Set(prev);
              if (next.has(outcome.attackerInstanceId)) {
                next.delete(outcome.attackerInstanceId);
              }
              return next;
            });
          }
          
          // ✅ ELECTRON FIX: Also show target if it survived (in case it was hidden)
          if (outcome.targetInstanceId && !outcome.targetDestroyed) {
            attackAnimations.setHiddenUnits(prev => {
              const next = new Set(prev);
              if (next.has(outcome.targetInstanceId)) {
                next.delete(outcome.targetInstanceId);
              }
              return next;
            });
          }
        }
      }
      
      // ✅ FIX: Clean up dead attacker tracking after delay (allows slot death detection to check first)
      if (msg.attackOutcome?.attackerDestroyed && msg.attackOutcome?.attackerInstanceId) {
        attackAnimations.cleanupDeadAttacker(msg.attackOutcome.attackerInstanceId);
      }
      
      // ✅ CAMPAIGN TUTORIAL: Dispatch custom event for attack-complete (for tutorial progression)
      if (msg.attackOutcome) {
        window.dispatchEvent(new CustomEvent('attack-complete', {
          detail: {
            attackOutcome: msg.attackOutcome,
            gameState: msg.gameState
          }
        }));
      }
      
      // ✅ Unit-vs-unit: do not log a separate "unit1 destroyed unit2" entry here.
      // The damage entry ("dealt X damage" / "suffered X damage from") already shows the outcome.
      // For other killers (strategem, drone, etc.) the "destroyed" entry is still added from
      // actionHistoryManager when it detects completedOrders with destructionType !== 'combat'.
      
      // Update game state with final state (damage applied)
      // ✅ SPEED CONTROL: Only update state directly if NOT an autobattler spectator
      // (autobattler spectators already queued the update above)
      if (!isAutobattlerSpectator && msg.gameState) {
        gameStateRefRef.current.current = msg.gameState;
        setGameState(msg.gameState);
        setActionLog(msg.gameState.actionLog ?? []);
        updateYourTurn(msg.yourTurn || false, 'attack-complete');
        storeOpponentStrategyId(msg.gameState);
      }
      // Check for winner
      // ✅ FIX: Skip for spectators - they should be handled by friendsContext
      // ✅ FIX: Ignore winner from another game (stale/delayed message) to prevent redirect loop.
      // Use only msg.gameId (session key); gameState.gameId is internal.
      const attackCompleteMsgGameId = msg.gameId;
      if (msg.gameState?.winner && !isSpectating && !isWsMessageForDifferentSessionRef.current(attackCompleteMsgGameId)) {
        const didYouWin = msg.gameState.winner === userId;
        const isFriendlyMatch = msg.gameState.isFriendlyMatch || false;
        handleBattleEnd(didYouWin, gameIdRef.current, null, isFriendlyMatch, 'attack-complete-winner');
        return;
      }
      return; // Don't fall through to game-update handler
    }

    // ✅ Handle strategem-initiated message (two-phase strategem system - effect deferred until animation completes)
    if (msg.type === "strategem-initiated") {
      // ✅ TWO-PHASE STRATEGEM: Store mapping from strategemInstanceId to backend's strategemAnimationId
      // This allows the onComplete callback to send the correct animation ID back
      if (msg.strategemInstanceId && msg.strategemAnimationId) {
        pendingStrategemAnimationIdsRef.current.set(msg.strategemInstanceId, {
          strategemAnimationId: msg.strategemAnimationId,
          strategemName: msg.strategemName,
          strategemId: msg.strategemId,
          timestamp: Date.now()
        });
      }

      // ✅ OPPONENT STRATEGEM HANDLING: If this is opponent's strategem (not our turn and not our animation),
      // register the animation from pendingAnimations so we can see the animation
      // For YOUR strategem, the animation was already registered locally in targetStrategem.js
      const isOpponentStrategem = !msg.yourTurn && msg.pendingAnimations && msg.pendingAnimations.length > 0;
      if (isOpponentStrategem && strategemTargetAnimations && strategemTargetAnimations.registerAnimation) {
        // Get opponent general position for "from" position
        const generalElement = document.querySelector('[data-zone="opponent_general"]');
        let fromPosition = { x: window.innerWidth - 200, y: 100 }; // Fallback
        if (generalElement) {
          const rect = generalElement.getBoundingClientRect();
          fromPosition = {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2
          };
        }
        
        msg.pendingAnimations.forEach((anim, idx) => {
          const animationKey = `opponent-${msg.strategemAnimationId}-${idx}-${Date.now()}`;
          strategemTargetAnimations.registerAnimation({
            fromHand: { 
              instanceId: msg.strategemInstanceId, 
              position: fromPosition 
            },
            toSlot: anim.toSlot,
            targetType: anim.targetType,
            strategemId: msg.strategemId,
            strategemInstanceId: msg.strategemInstanceId,
            animationKey,
            isOpponentStrategem: true
          });
        });
      }
      
      // Update game state (effect NOT yet applied - this is the pre-effect state)
      if (msg.gameState) {
        gameStateRefRef.current.current = msg.gameState;
        setGameState(msg.gameState);
        updateYourTurn(msg.yourTurn || false, 'strategem-initiated');
      }

      // For YOUR strategem: Animation is already registered by targetStrategem.js before WebSocket message was sent
      // For OPPONENT strategem: Animation was just registered above
      // When animation completes, onComplete callback will send strategem-animation-complete
      
      return; // Don't fall through to game-update handler
    }

    // ✅ Handle strategem-complete message (two-phase strategem system - effect has been applied)
    if (msg.type === "strategem-complete") {
      // ✅ FIX: Ignore messages for a different game (stale/delayed) - do not apply state or redirect
      const scMsgGameId = msg.gameId;
      if (isWsMessageForDifferentSessionRef.current(scMsgGameId)) {
        return;
      }

      // Clean up any pending animation ID mapping (in case timeout applied it)
      // Find and remove by strategemAnimationId
      if (msg.strategemAnimationId) {
        for (const [instanceId, info] of pendingStrategemAnimationIdsRef.current.entries()) {
          if (info.strategemAnimationId === msg.strategemAnimationId) {
            pendingStrategemAnimationIdsRef.current.delete(instanceId);
            break;
          }
        }
      }
      
      // Update game state with final state (effect applied)
      if (msg.gameState) {
        // ✅ FIX: Update previousGameStateRef BEFORE detecting actions
        // This ensures we can detect targets by comparing pre-effect and post-effect states
        const prevStateForStrategemDetection = previousGameStateRef.current;
        previousGameStateRef.current = JSON.parse(JSON.stringify(msg.gameState));
        
        gameStateRefRef.current.current = msg.gameState;
        setGameState(msg.gameState);
        setActionLog(msg.gameState.actionLog ?? []);
        updateYourTurn(msg.yourTurn || false, 'strategem-complete');
        storeOpponentStrategyId(msg.gameState);
        
        // Strategem targets: server sends full actionLog; no client-side target detection for log
        if (prevStateForStrategemDetection && msg.strategemAnimationId && false) {
          const strategemActionIndex = -1;
          if (strategemActionIndex !== -1) {
            const strategemAction = null;
            const strategemInstanceId = strategemAction.order?.instanceId;
            const strategemId = strategemAction.order?.id;
            
            if (strategemInstanceId && strategemId) {
              // Manually detect targets by comparing states
              const targets = [];
              const allPlayerIds = msg.gameState.players || Object.keys(msg.gameState.state);
              
              // ✅ FIX: Try to find the actual target from strategem animation metadata first
              // This is the most reliable way to identify which unit was targeted
              let actualTargetInstanceId = null;
              if (msg.gameState.pendingStrategemAnimations && Array.isArray(msg.gameState.pendingStrategemAnimations)) {
                const strategemAnimation = msg.gameState.pendingStrategemAnimations.find(
                  anim => anim.strategemInstanceId === strategemInstanceId || 
                         anim.strategemAnimationId === msg.strategemAnimationId
                );
                if (strategemAnimation && strategemAnimation.targetInstanceId) {
                  actualTargetInstanceId = strategemAnimation.targetInstanceId;
                }
              }
              
              // 1. Check for units killed by this strategem
              allPlayerIds.forEach(targetPlayerId => {
                const prevTargetPlayer = prevStateForStrategemDetection.state?.[targetPlayerId];
                const currTargetPlayer = msg.gameState.state?.[targetPlayerId];
                if (!prevTargetPlayer || !currTargetPlayer) return;
                
                const prevCompleted = prevTargetPlayer.completedOrders || [];
                const currCompleted = currTargetPlayer.completedOrders || [];
                
                currCompleted.forEach(destroyedUnit => {
                  if (!destroyedUnit || !destroyedUnit.instanceId) return;
                  
                  // Check if this unit was destroyed this turn by this strategem
                  const wasDestroyedThisTurn = !prevCompleted.some(
                    o => o && o.instanceId === destroyedUnit.instanceId
                  );
                  
                  if (wasDestroyedThisTurn && 
                      (destroyedUnit.destructionType === 'strategem' || 
                       (destroyedUnit.destroyedBy && destroyedUnit.destroyedBy.startsWith('Strategem ')))) {
                    // Extract strategem ID from destroyedBy (format: "Strategem 317 (Guided Missile)")
                    const strategemMatch = destroyedUnit.destroyedBy?.match(/Strategem (\d+)/);
                    if (strategemMatch && parseInt(strategemMatch[1]) === strategemId) {
                      // ✅ FIX: For single-target strategems, verify this is the actual target
                      // If we have actualTargetInstanceId from animation metadata, only add if it matches
                      if (actualTargetInstanceId && destroyedUnit.instanceId !== actualTargetInstanceId) {
                        return; // Skip - not the actual target
                      }
                      
                      // ✅ FIX: Find the unit in previous state to get healthBefore and calculate damageTaken
                      let healthBefore = 0;
                      let damageTaken = 0;
                      
                      // Try to find the unit in previous state to get accurate healthBefore
                      let foundPrevUnit = false;
                      ['front', 'flank'].forEach(zone => {
                        if (foundPrevUnit) return; // Already found
                        
                        const prevUnit = prevTargetPlayer.playedOrders?.[zone]?.find(
                          u => u && u.instanceId === destroyedUnit.instanceId
                        );
                        if (prevUnit) {
                          foundPrevUnit = true;
                          healthBefore = prevUnit.health || prevUnit.maxHealth || 0;
                          const finalHealth = destroyedUnit.finalHealth !== undefined ? destroyedUnit.finalHealth : 0;
                          damageTaken = healthBefore - finalHealth;
                          
                          // If we couldn't calculate damageTaken from finalHealth, use healthBefore as fallback
                          // (unit was killed, so it took at least that much damage)
                          if (damageTaken <= 0 && healthBefore > 0) {
                            damageTaken = healthBefore;
                          }
                        }
                      });
                      
                      // Fallback: if we couldn't find the unit in previous state, use destroyedUnit data
                      if (!foundPrevUnit) {
                        healthBefore = destroyedUnit.health || destroyedUnit.maxHealth || 0;
                        // If unit was killed, it took at least its full health as damage
                        damageTaken = healthBefore;
                      }
                      
                      targets.push({
                        id: destroyedUnit.id,
                        name: destroyedUnit.name,
                        instanceId: destroyedUnit.instanceId,
                        died: true,
                        healthBefore: healthBefore,
                        damageTaken: damageTaken
                      });
                    }
                  }
                });
              });
              
              // 1.5. Kinetron Blast (111): unit decommissioned - disappeared from battlefield and added to hand
              if (strategemId === 111 && targets.length === 0) {
                allPlayerIds.forEach(targetPlayerId => {
                  const prevTargetPlayer = prevStateForStrategemDetection.state?.[targetPlayerId];
                  const currTargetPlayer = msg.gameState.state?.[targetPlayerId];
                  if (!prevTargetPlayer || !currTargetPlayer) return;
                  ['front', 'flank'].forEach(zone => {
                    if (!prevTargetPlayer.playedOrders?.[zone]) return;
                    prevTargetPlayer.playedOrders[zone].forEach((prevUnit, index) => {
                      if (!prevUnit || !prevUnit.instanceId) return;
                      const currUnitOnBattlefield = currTargetPlayer.playedOrders?.[zone]?.[index];
                      const unitDisappeared = !currUnitOnBattlefield || currUnitOnBattlefield.instanceId !== prevUnit.instanceId;
                      if (!unitDisappeared) return;
                      const wasInHandBefore = prevTargetPlayer.hand?.some(handUnit => handUnit && handUnit.id === prevUnit.id);
                      const wasAddedToHand = currTargetPlayer.hand?.some(handUnit => handUnit && handUnit.id === prevUnit.id);
                      const wasDecommissioned = !wasInHandBefore && wasAddedToHand;
                      if (wasDecommissioned) {
                        const alreadyAdded = targets.some(t => t.instanceId === prevUnit.instanceId);
                        if (!alreadyAdded) {
                          targets.push({
                            id: prevUnit.id,
                            name: prevUnit.name,
                            instanceId: prevUnit.instanceId,
                            decommissioned: true
                          });
                        }
                      }
                    });
                  });
                });
              }
              
              // 2. Check for units that took damage from this strategem (but didn't die)
              // ✅ FIX: For single-target strategems like Guided Missile, only find ONE target
              // Check if this strategem targets a single unit (most strategems do)
              const isSingleTargetStrategem = strategemId === 317 || // Guided Missile
                                             strategemId === 55 ||  // Vein Sever
                                             strategemId === 56 ||  // Viral Echo
                                             strategemId === 57 ||  // etc. (add more as needed)
                                             true; // Default to single-target for safety
              
              // Only proceed if we haven't found a target yet (for single-target strategems)
              if (!isSingleTargetStrategem || targets.length === 0) {
                allPlayerIds.forEach(targetPlayerId => {
                  const prevTargetPlayer = prevStateForStrategemDetection.state?.[targetPlayerId];
                  const currTargetPlayer = msg.gameState.state?.[targetPlayerId];
                  if (!prevTargetPlayer || !currTargetPlayer) return;
                  
                  ['front', 'flank'].forEach(zone => {
                    if (!currTargetPlayer.playedOrders?.[zone]) return;
                    
                    currTargetPlayer.playedOrders[zone].forEach((currUnit, idx) => {
                      // ✅ FIX: For single-target strategems, stop if we already found a target
                      if (isSingleTargetStrategem && targets.length > 0) return;
                      
                      if (!currUnit || !currUnit.instanceId) return;
                      
                      // Find the same unit in previous state
                      const prevUnit = prevTargetPlayer.playedOrders?.[zone]?.[idx];
                      if (!prevUnit || prevUnit.instanceId !== currUnit.instanceId) return;
                      
                      // Check if unit took damage this turn
                      const prevHealth = prevUnit.health || prevUnit.maxHealth || 0;
                      const currHealth = currUnit.health || currUnit.maxHealth || 0;
                      const prevDamageTaken = prevUnit.damageTaken || 0;
                      const currDamageTaken = currUnit.damageTaken || 0;
                      
                      const healthDecreased = currHealth < prevHealth;
                      const damageTakenIncreased = currDamageTaken > prevDamageTaken;
                      
                      // ✅ FIX: For single-target strategems, only add if this is the actual target
                      // Check if we have actualTargetInstanceId from animation metadata
                      if (isSingleTargetStrategem && actualTargetInstanceId) {
                        // Only add if this is the actual target
                        if (currUnit.instanceId !== actualTargetInstanceId) {
                          return; // Skip - not the actual target
                        }
                      }
                      
                      // If unit took damage and is still alive, it might be a target
                      if ((healthDecreased || damageTakenIncreased) && currUnit.health > 0) {
                        // Check if we already added this target (might have been added as killed)
                        const alreadyAdded = targets.some(t => t.instanceId === currUnit.instanceId);
                        if (!alreadyAdded) {
                          // For single-target strategems, only add if we haven't found a target yet
                          // or if this is the actual target from animation metadata
                          const shouldAdd = !isSingleTargetStrategem || 
                                           targets.length === 0 || 
                                           (actualTargetInstanceId && currUnit.instanceId === actualTargetInstanceId);
                          
                          if (shouldAdd) {
                            // ✅ FIX: Include health information for damaged units
                            const healthBefore = prevHealth;
                            const damageTaken = healthDecreased ? (prevHealth - currHealth) : (damageTakenIncreased ? (currDamageTaken - prevDamageTaken) : 0);
                            
                            targets.push({
                              id: currUnit.id,
                              name: currUnit.name,
                              instanceId: currUnit.instanceId,
                              damaged: true,
                              healthBefore: healthBefore,
                              damageTaken: damageTaken > 0 ? damageTaken : undefined
                            });
                          }
                        }
                      }
                    });
                  });
                });
              }
              
              // ✅ FIX: Check for units spawned/deployed by this strategem
              // This handles strategems like Forbidden Clone Protocol (324) that deploy new units
              allPlayerIds.forEach(targetPlayerId => {
                const prevTargetPlayer = prevStateForStrategemDetection.state?.[targetPlayerId];
                const currTargetPlayer = msg.gameState.state?.[targetPlayerId];
                if (!prevTargetPlayer || !currTargetPlayer) return;
                
                ['front', 'flank'].forEach(zone => {
                  const prevZone = prevTargetPlayer.playedOrders?.[zone] || [];
                  const currZone = currTargetPlayer.playedOrders?.[zone] || [];
                  
                  // Check for new units that appeared in this zone
                  currZone.forEach((currUnit, idx) => {
                    if (!currUnit || !currUnit.instanceId) return;
                    
                    // Check if this unit is new (wasn't in previous state at this position)
                    const prevUnit = prevZone[idx];
                    const isNewUnit = !prevUnit || prevUnit.instanceId !== currUnit.instanceId;
                    
                    if (isNewUnit) {
                      let isSpawnedByStrategem = false;
                      
                      // Method 1: Check if unit has deployedBy metadata linking to this strategem
                      if (currUnit.deployedBy && 
                          currUnit.deployedBy.type === 'strategem' &&
                          (currUnit.deployedBy.instanceId === strategemInstanceId || 
                           currUnit.deployedBy.id === strategemId)) {
                        isSpawnedByStrategem = true;
                      }
                      
                      // Method 2: Fallback - check for strategem-specific spawned units
                      // Forbidden Clone Protocol (324) spawns Animated Fleshsacks (ID 328)
                      if (!isSpawnedByStrategem && strategemId === 324 && currUnit.id === 328) {
                        // Check if this unit appeared in the same turn as the strategem
                        // and wasn't in the previous state
                        const unitDeployedThisTurn = currUnit.deployedTurn === msg.gameState.turnNumber ||
                                                     currUnit.playedTurn === msg.gameState.turnNumber;
                        if (unitDeployedThisTurn) {
                          isSpawnedByStrategem = true;
                        }
                      }
                      
                      if (isSpawnedByStrategem) {
                        // This unit was spawned by this strategem
                        const alreadyAdded = targets.some(t => t.instanceId === currUnit.instanceId);
                        if (!alreadyAdded) {
                          targets.push({
                            id: currUnit.id,
                            name: currUnit.name,
                            instanceId: currUnit.instanceId,
                            spawned: true,
                            deployed: true
                          });
                        }
                      }
                    }
                  });
                });
              });
              
              // ✅ FIX: Check for general damage/healing from this strategem
              // This handles strategems like Kamikaze (314) that deal damage to generals
              allPlayerIds.forEach(targetPlayerId => {
                const prevTargetPlayer = prevStateForStrategemDetection.state?.[targetPlayerId];
                const currTargetPlayer = msg.gameState.state?.[targetPlayerId];
                if (!prevTargetPlayer || !currTargetPlayer) return;
                
                // Check if general health changed
                const prevGeneralHealth = prevTargetPlayer.health || 0;
                const currGeneralHealth = currTargetPlayer.health || 0;
                const generalHealthDecreased = currGeneralHealth < prevGeneralHealth;
                const generalHealthIncreased = currGeneralHealth > prevGeneralHealth;
                
                if (generalHealthDecreased || generalHealthIncreased) {
                  // Check if this strategem caused the general interaction
                  const generalInteractions = currTargetPlayer.generalInteractions || [];
                  const strategemInteraction = generalInteractions.find(
                    interaction => interaction.sourceType === 'strategem' &&
                                  (interaction.sourceInstanceId === strategemInstanceId || 
                                   interaction.sourceId === strategemId) &&
                                  interaction.interactionTurn === msg.gameState.turnNumber
                  );
                  
                  if (strategemInteraction) {
                    // This strategem affected the general
                    const generalTarget = {
                      id: null,
                      name: 'General',
                      instanceId: null,
                      type: 'general',
                      playerId: targetPlayerId,
                      died: false,
                      healthBefore: prevGeneralHealth,
                      damageTaken: generalHealthDecreased ? (prevGeneralHealth - currGeneralHealth) : undefined,
                      healed: generalHealthIncreased,
                      healAmount: generalHealthIncreased ? (currGeneralHealth - prevGeneralHealth) : undefined
                    };
                    
                    // Check if we already added this general (shouldn't happen, but safety check)
                    const alreadyAdded = targets.some(t => t.type === 'general' && t.playerId === targetPlayerId);
                    if (!alreadyAdded) {
                      targets.push(generalTarget);
                    }
                  }
                }
              });
              
              // 6. Phase Traverse (186): detect unit repositioned from one zone to the other (front <-> flank)
              if (strategemId === 186 && targets.length === 0) {
                const playerIdWhoPlayed = strategemAction.playerId;
                const prevPlayer = prevStateForStrategemDetection.state?.[playerIdWhoPlayed];
                const currPlayer = msg.gameState.state?.[playerIdWhoPlayed];
                if (prevPlayer?.playedOrders && currPlayer?.playedOrders) {
                  ['front', 'flank'].forEach((fromZone) => {
                    const toZone = fromZone === 'front' ? 'flank' : 'front';
                    if (!prevPlayer.playedOrders[fromZone]) return;
                    prevPlayer.playedOrders[fromZone].forEach((prevUnit) => {
                      if (!prevUnit || !prevUnit.instanceId) return;
                      const stillInFrom = currPlayer.playedOrders[fromZone]?.some(u => u?.instanceId === prevUnit.instanceId);
                      if (stillInFrom) return;
                      const nowInTo = currPlayer.playedOrders[toZone]?.some(u => u?.instanceId === prevUnit.instanceId);
                      if (!nowInTo) return;
                      targets.push({
                        id: prevUnit.id,
                        name: prevUnit.name,
                        instanceId: prevUnit.instanceId,
                        repositioned: true,
                        fromZone,
                        toZone
                      });
                    });
                  });
                }
              }
              
              // Update the strategem action with targets
              if (targets.length > 0) {
                // ✅ FIX: Generate appropriate message based on target types
                // Separate targets by type: killed, damaged, spawned, general, repositioned (Phase Traverse)
                const killedTargets = targets.filter(tgt => tgt.died);
                const damagedTargets = targets.filter(tgt => tgt.damaged && !tgt.died);
                const spawnedTargets = targets.filter(tgt => tgt.spawned || tgt.deployed);
                const decommissionedTargets = targets.filter(tgt => tgt.decommissioned);
                const generalTargets = targets.filter(tgt => tgt.type === 'general');
                const repositionedTargets = targets.filter(tgt => tgt.repositioned);
                
                let message = '';
                if (repositionedTargets.length === 1) {
                  const target = repositionedTargets[0];
                  message = target.fromZone === 'front'
                    ? t('battleUI.actionHistory.movedFromFrontToFlank', { unitName: target.name, defaultValue: `Moved ${target.name} from front to flank.` })
                    : t('battleUI.actionHistory.movedFromFlankToFront', { unitName: target.name, defaultValue: `Moved ${target.name} from flank to front.` });
                } else if (targets.length === 1) {
                  const target = targets[0];
                  const strategemName = strategemAction.order.name;
                  if (target.died) {
                    message = t('battleUI.actionHistory.strategemHasDestroyed', { strategemName, targetName: target.name, defaultValue: `${strategemName} has destroyed ${target.name}` });
                  } else if (target.spawned || target.deployed) {
                    message = t('battleUI.actionHistory.strategemHasSpawned', { strategemName, targetName: target.name, defaultValue: `${strategemName} has spawned ${target.name}` });
                  } else if (target.decommissioned) {
                    message = t('battleUI.actionHistory.strategemHasDecommissioned', { strategemName, targetName: target.name, defaultValue: `${strategemName} has decommissioned ${target.name}` });
                  } else if (target.type === 'general') {
                    message = t('battleUI.actionHistory.strategemRequestedOn', { strategemName, targetName: target.name, defaultValue: `${strategemName} was requested on ${target.name}` });
                  } else {
                    message = t('battleUI.actionHistory.strategemRequestedOn', { strategemName, targetName: target.name, defaultValue: `${strategemName} was requested on ${target.name}` });
                  }
                } else {
                  // Multiple targets - describe what happened (localized)
                  const parts = [];
                  if (killedTargets.length > 0) {
                    parts.push(t('battleUI.actionHistory.strategemDestroyedUnits', { count: killedTargets.length, defaultValue: `destroyed ${killedTargets.length} unit(s)` }));
                  }
                  if (damagedTargets.length > 0) {
                    parts.push(t('battleUI.actionHistory.strategemDamagedUnits', { count: damagedTargets.length, defaultValue: `damaged ${damagedTargets.length} unit(s)` }));
                  }
                  if (decommissionedTargets.length > 0) {
                    parts.push(t('battleUI.actionHistory.strategemDecommissionedUnits', { count: decommissionedTargets.length, defaultValue: `decommissioned ${decommissionedTargets.length} unit(s)` }));
                  }
                  if (spawnedTargets.length > 0) {
                    parts.push(t('battleUI.actionHistory.strategemSpawnedUnits', { count: spawnedTargets.length, defaultValue: `spawned ${spawnedTargets.length} unit(s)` }));
                  }
                  if (generalTargets.length > 0) {
                    parts.push(t('battleUI.actionHistory.strategemAffectedGeneral', { defaultValue: 'affected general' }));
                  }
                  
                  if (parts.length > 0) {
                    message = `${strategemAction.order.name} has ${parts.join(', ')}`;
                  } else {
                    message = t('battleUI.actionHistory.strategemRequestedOnTargets', { strategemName: strategemAction.order.name, count: targets.length, defaultValue: `${strategemAction.order.name} was requested on ${targets.length} target(s)` });
                  }
                }
                
              }
            }
          }
        }
      }
      
      // Check for winner
      // ✅ FIX: Ignore winner from another game (stale/delayed message) to prevent redirect loop
      if (msg.gameState?.winner && !isSpectating && !isWsMessageForDifferentSessionRef.current(msg.gameId)) {
        const didYouWin = msg.gameState.winner === userId;
        const isFriendlyMatch = msg.gameState.isFriendlyMatch || false;
        handleBattleEnd(didYouWin, gameIdRef.current, null, isFriendlyMatch, 'strategem-complete-winner');
        return;
      }
      
      return; // Don't fall through to game-update handler
    }

    // ✅ Handle state desync errors - automatically recover by updating game state
    if (msg.type === "state-desync-error") {
      if (isWsMessageForDifferentSessionRef.current(msg.gameId)) {
        return;
      }
      console.warn('🔄 State desync detected, recovering with backend state:', msg.message);
      
      // ✅ REFACTORED: Use hook's unlockUI method (via ref to avoid stale closure)
      unlockUIRef.current();
      
      if (msg.gameState) {
        // Force complete state update - backend state is authoritative
        // ✅ FIX: Only set previousGameStateRef if not already initialized (preserve baseline for action detection)
        if (!previousGameStateRef.current) {
          previousGameStateRef.current = JSON.parse(JSON.stringify(msg.gameState));
        }
        // ✅ REFACTORED: Use hook's updateState (via ref to avoid stale closure)
        updateStateRef.current(msg.gameState, { skipVersionCheck: true, skipStabilization: true });
        setYourTurn(msg.yourTurn || false);
        storeOpponentStrategyId(msg.gameState);
        // Clear action queue on desync - backend state is authoritative
        actionQueueRef.current = [];
        isProcessingActionRef.current = false;
        lastStateVersionRef.current = msg.gameState.stateVersion || null;
        // Clear selections that might be invalid
        setSelectedOrderId(null);
        setSelectedOrderInstanceId(null);
        setError(t('battleUI.gameStateOutOfSync'));
        setTimeout(() => setError(''), 3000);
        // Force UI update by updating unit registry
        if (msg.gameState) {
          updateUnitRegistry(msg.gameState, unitRegistryRef);
        }
      }
      return;
    }

    // ✅ AUTOBATTLER FIX: Handle spectate-update messages for autobattler spectators
    // Check if this is an autobattler game by looking at the gameState or message properties
    if (msg.type === "spectate-update") {
      const isAutobattlerGame = msg.gameState?.isAutobattler || msg.gameId?.includes('_autobattler_');
      const hasGameState = !!msg.gameState;
      const willProcess = isAutobattlerGame && hasGameState;

      if (willProcess) {
        // ✅ FIX: Clear reconnecting state when gameState is received
        // This prevents getting stuck in "Reconnecting..." state
        if (reconnectingRef.current) {
          reconnectingRef.current = false;
          setReconnecting(false);
          wsConnectingRef.current = false;
          if (reconnectingTimeoutRef.current) {
            clearTimeout(reconnectingTimeoutRef.current);
            reconnectingTimeoutRef.current = null;
          }
        }
        
        // ✅ SPEED CONTROL: Queue update instead of applying immediately
        const applyUpdate = () => {
          gameStateRefRef.current.current = msg.gameState;
          setGameState(msg.gameState);
          setYourTurn(false); // Spectators never have their turn
          
          if (msg.gameState) {
            setActionLog(msg.gameState.actionLog ?? []);
          }
          previousGameStateRef.current = JSON.parse(JSON.stringify(msg.gameState));
        };

        // Queue the update (will apply immediately if speed control disabled)
        // Only use speed control for autobattler spectators
        if (isAutobattlerSpectatorRef.current && speedControlQueueUpdateRef.current) {
          speedControlQueueUpdateRef.current(msg, applyUpdate);
        } else {
          // Normal spectating or speed control not available - apply immediately
          applyUpdate();
        }
      }
      return;
    }

    if (
      msg.type === "game-update" ||
      msg.type === "turn-played" ||
      msg.type === "turn-ended" ||
      msg.type === "turn-timeout" ||
      msg.type === "player-disconnected" ||
      msg.type === "player-abandoned" ||
      msg.type === "player-resigned" ||
      msg.type === "counter-strike-played" ||
      msg.type === "weapon-attached" ||
      msg.type === "weapon-deploy-completed"
    ) {
      // ✅ FIX: Ignore messages for a different game (stale/delayed) - prevents redirect to reward screen mid-game
      // Use only msg.gameId (WebSocket session / activeGames key). gameState.gameId is internal and mismatches session id.
      const rawMsgGameId = msg.gameId;
      if (isWsMessageForDifferentSessionRef.current(rawMsgGameId)) {
        return;
      }

      // ✅ REFACTORED: Use hook's unlockUI method (via ref to avoid stale closure)
      unlockUIRef.current();

      // ✅ FIX: Clear reconnecting state when gameState is received in game-update
      // This prevents getting stuck in "Reconnecting..." state for autobattler spectators
      // who might receive game-update instead of reconnect-success
      if (reconnectingRef.current && msg.gameState) {
        reconnectingRef.current = false;
        setReconnecting(false);
        wsConnectingRef.current = false;
        if (reconnectingTimeoutRef.current) {
          clearTimeout(reconnectingTimeoutRef.current);
          reconnectingTimeoutRef.current = null;
        }
      }

      // ✅ Handle deploy targeting animations from backend (ref: same as passedWs / pre-version consume)
      if (msg.gameState) {
        consumePendingDeployAnimationsForViewer(
          msg.gameState,
          deployAnimationViewerUserIdRef.current,
          deployTargetAnimationsRef.current
        );
      }

      // ✅ FIX: Handle player-resigned message - start transition if winner is set
      // ✅ FIX: Skip for spectators - they should be handled by friendsContext
      // ✅ FIX: Ignore resignation from another game (stale/delayed message) to prevent redirect loop
      if (msg.type === "player-resigned" && msg.gameState?.winner && !isSpectating && !isWsMessageForDifferentSessionRef.current(msg.gameId)) {
        const didYouWin = msg.gameState.winner === userId;
        const resultVal = didYouWin ? 'win' : 'loss';
        
        // Store opponent strategy ID from resignation message gameState
        if (msg.gameState) {
          storeOpponentStrategyId(msg.gameState);
        }
        
        // Start transition animation
        setShowVictoryImage(false);
        setShowDefeatImage(false);
        setBattleEndTransition({ isTransitioning: true, type: resultVal });
        
        // Helper to play the correct battle end sound
        const playBattleEndSound = () => {
          const soundPath = didYouWin ? '/sounds/victory.mp3' : '/sounds/loss.mp3';
          const audio = new Audio(soundPath);
          audio.volume = soundsVolume;
          battleEndSoundRef.current = audio;
          audio.play().catch(err => {
            console.error('Error playing battle end sound:', err);
          });
        };
        
        exitingRef.current = true;
        try { 
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.close();
          }
        } catch {}
        
        // Prepare navigation state with opponent strategy ID if lost
        const navigationState = { result: resultVal, gameId: gameIdRef.current };
        const isFriendlyMatch = msg.gameState?.isFriendlyMatch || false;
        if (isFriendlyMatch) {
          navigationState.isFriendlyMatch = true;
        }
        if (resultVal === 'loss') {
          const storedStrategyId = opponentStrategyIdRef.current;
          if (storedStrategyId) {
            navigationState.opponentStrategyId = storedStrategyId;
          } else if (msg.gameState && msg.gameState.players) {
            const opponentId = msg.gameState.players.find(id => id !== userId);
            if (opponentId && opponentId !== 'bot' && msg.gameState.state?.[opponentId]) {
              const opponentState = msg.gameState.state[opponentId];
              if (opponentState?.strategy) {
                const strategyId = opponentState.strategy.id;
                if (strategyId && typeof strategyId === 'number' && strategyId !== 'bot-hardcoded') {
                  navigationState.opponentStrategyId = strategyId;
                }
              }
            }
          }
        }
        if (msg.gameState?.warEffortMissionId || msg.gameState?.isWarEffort) {
          navigationState.isWarEffort = true;
          navigationState.gameState = msg.gameState;
        }
        if (msg.gameState?.campaignMode && msg.gameState?.missionId) {
          navigationState.isCampaign = true;
          navigationState.missionId = msg.gameState.missionId;
          navigationState.gameState = msg.gameState;
        }
        
        if (didYouWin) {
          // WIN: fade to white first, then play sound and show centered victory image
          setTimeout(() => {
            setShowVictoryImage(true);
            playBattleEndSound();
          }, 2000);
          
          setTimeout(() => {
            navigate('/after-battle', { replace: true, state: navigationState });
          }, 4000);
        } else {
          // LOSS: play sound immediately when game ends, then fade to gray/black, then show full screen defeat image
          playBattleEndSound();
          setTimeout(() => {
            setShowDefeatImage(true);
          }, 2000);
          
          setTimeout(() => {
            navigate('/after-battle', { replace: true, state: navigationState });
          }, 4000);
        }
        
        return;
      }
      
      if (msg.gameState) {
        // OLD ATTACK DETECTION CODE REMOVED - Now using attack-initiated message flow
        // This old code was detecting attacks by comparing gameState, but the new flow
        // uses attack-initiated message which is sent BEFORE damage is applied
        const prevState = previousGameStateRef.current;
        if (false && prevState && !activeAttack) { // DISABLED - using new attack-initiated flow
          // Check for attacks (both player and bot units that just attacked)
          const allPlayerIds = msg.gameState.players || Object.keys(msg.gameState.state);
          const currentTurn = msg.gameState.turnNumber || msg.gameState.turn;
          
          allPlayerIds.forEach(playerId => {
            const prevPlayer = prevState.state?.[playerId];
            const currPlayer = msg.gameState.state?.[playerId];
            
            if (!prevPlayer || !currPlayer || !prevPlayer.playedOrders || !currPlayer.playedOrders) return;
            
            ['front', 'flank'].forEach(zone => {
              if (!prevPlayer.playedOrders[zone] || !currPlayer.playedOrders[zone]) return;
              
              prevPlayer.playedOrders[zone].forEach((prevUnit, idx) => {
                if (!prevUnit) return;
                
                const currUnit = currPlayer.playedOrders[zone]?.[idx];
                if (!currUnit || currUnit.instanceId !== prevUnit.instanceId) return;
                
                // Check if unit just attacked (lastAttackTurn changed)
                const prevLastAttack = prevUnit.lastAttackTurn;
                const currLastAttack = currUnit.lastAttackTurn;
                
                if (prevLastAttack !== currLastAttack && currLastAttack === currentTurn) {
                  // Unit just attacked - trigger animation
                  // For player attacks, use stored pending attack info if available
                  const isPlayerAttack = playerId === userId;
                  let attackInfo = null;
                  
                  if (isPlayerAttack && pendingAttackInfoRef.current) {
                    // Use stored pending attack info
                    attackInfo = pendingAttackInfoRef.current;
                    // Clear pending attack info after using it
                    pendingAttackInfoRef.current = null;
                  } else {
                    // Bot attack or no stored info - find actual target by checking opponent units that took damage
                    const opponentId = allPlayerIds.find(id => id !== playerId);
                    let foundTarget = null;
                    
                    if (opponentId) {
                      const opponent = msg.gameState.state[opponentId];
                      const prevOpponent = prevState.state?.[opponentId];
                      
                      if (opponent && prevOpponent) {
                        // Prioritize unit attacks over general attacks (more visually interesting)
                        // Check opponent units for damage or death first
                        ['front', 'flank'].forEach(oppZone => {
                          if (foundTarget) return; // Already found target
                          
                          if (prevOpponent.playedOrders?.[oppZone] && opponent.playedOrders?.[oppZone]) {
                            prevOpponent.playedOrders[oppZone].forEach((prevTarget, targetIdx) => {
                              if (foundTarget || !prevTarget) return;
                              
                              const currTarget = opponent.playedOrders[oppZone]?.find(t => t && t.instanceId === prevTarget.instanceId);
                              
                              // Check if target died or took damage
                              const died = !currTarget && prevTarget;
                              const tookDamage = currTarget && currTarget.instanceId === prevTarget.instanceId && 
                                                 currTarget.health < prevTarget.health;
                              
                              if (died || tookDamage) {
                                foundTarget = {
                                  type: 'unit',
                                  targetZone: oppZone,
                                  targetIndex: targetIdx,
                                  targetPlayerId: opponentId,
                                };
                              }
                            });
                          }
                        });
                        
                        // If no unit was attacked, check if general was attacked (health decreased)
                        if (!foundTarget && opponent.health < prevOpponent.health) {
                          foundTarget = {
                            type: 'general',
                            targetZone: 'general',
                            targetIndex: null,
                            targetPlayerId: opponentId,
                          };
                        }
                      }
                    }
                    
                    // Use found target or fallback to general attack
                    if (foundTarget) {
                      attackInfo = {
                        attackerZone: zone,
                        attackerIndex: idx,
                        attackerPlayerId: playerId,
                        targetZone: foundTarget.targetZone === 'general' ? 'general' : `opponent_${foundTarget.targetZone}`,
                        targetIndex: foundTarget.targetIndex,
                        targetPlayerId: foundTarget.targetPlayerId,
                      };
                    } else {
                      // Fallback: assume general attack if no target found
                      attackInfo = {
                        attackerZone: zone,
                        attackerIndex: idx,
                        attackerPlayerId: playerId,
                        targetZone: 'general',
                        targetIndex: null,
                        targetPlayerId: playerId === userId ? opponentId : userId,
                      };
                    }
                  }
                  
                  setActiveAttack({
                    attacker: currUnit,
                    ...attackInfo,
                    startTime: Date.now(),
                  });
                  
                  // Clear after animation completes (~600ms total - must complete before damage animations)
                  setTimeout(() => {
                    setActiveAttack(null);
                  }, 600);
                }
              });
            });
          });
        }
        
        // Update unit registry with PREVIOUS state BEFORE detecting actions
        // This ensures we have a snapshot of all units that existed before state changes
        // We update with previous state so we can reference units even after they're removed
        const prevStateForActions = previousGameStateRef.current;
        if (prevStateForActions) {
          updateUnitRegistry(prevStateForActions, unitRegistryRef);
        }
        
        // ALSO update registry with CURRENT state BEFORE detecting actions
        // This ensures we have the latest data (like lastAttackTurn) when checking current state
        // The registry will have both old and new data, with new data taking precedence
        if (msg.gameState) {
          updateUnitRegistry(msg.gameState, unitRegistryRef);
        }
        
        // Detect actions by comparing previous and current game state
        // ✅ FIX: Always run detection on every state update to catch kills instantly
        if (prevStateForActions && msg.gameState) {
          // Play destroy sound when a drone was removed (e.g. charges spent); skip when update is from attack-complete (drone destroy sound already played on attack-initiated)
          if (msg.type !== 'attack-complete') {
            const players = msg.gameState.players || Object.keys(msg.gameState.state || {});
            for (const playerId of players) {
              const prevDrones = prevStateForActions.state?.[playerId]?.drones?.length ?? 0;
              const currDrones = msg.gameState.state?.[playerId]?.drones?.length ?? 0;
              if (prevDrones > currDrones) {
                playSoundEffect('/sounds/destroy.mp3', 0.6);
                break;
              }
            }
          }
          // ✅ DEBUG: Log state comparison for bot turns
          if (msg.type === 'turn-played') {
            const botId = msg.gameState.players?.find(p => p === 'bot' || String(p).includes('bot'));
            // Debug logging disabled for bot turn tracking
          }
          
          if (msg.gameState) {
            setActionLog(msg.gameState.actionLog ?? []);
          }
        }
        // ✅ FIX: Update previousGameStateRef with deep copy to avoid reference issues
        // This ensures the next comparison will detect new actions correctly
        // ✅ CRITICAL: Only update AFTER action detection, so we compare the correct states
        // ✅ CRITICAL: For 'turn-ended', this sets the baseline for bot turn detection
        // ✅ CRITICAL: For 'turn-played', this sets the baseline for next turn detection
        if (msg.gameState) {
          previousGameStateRef.current = JSON.parse(JSON.stringify(msg.gameState));
          
          // Debug logging disabled
        }
        
        // ✅ REFACTORED: Use hook's updateState method (via ref to avoid stale closure)
        const stateUpdated = updateStateRef.current(msg.gameState);
        
        if (!stateUpdated) {
          // State was rejected (stale version) - request fresh state
          // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
          const activeSocket = ws || currentSocketRef.current;
          if (activeSocket && activeSocket.readyState === 1) {
            activeSocket.send(JSON.stringify({ type: 'request-state-refresh', gameId }));
          }
          return; // Don't process rejected state
        }
        
        // State was updated successfully - continue with game logic
        storeOpponentStrategyId(msg.gameState);
        const wasYourTurn = previousYourTurnRef.current;
        // ✅ FIX: Handle both string and number types for turn check
        const isYourTurn = msg.gameState.turn === userId || 
                          msg.gameState.turn === userId.toString() || 
                          String(msg.gameState.turn) === String(userId);
        
        // Mark turn state as initialized after first game state update
        if (!turnStateInitializedRef.current) {
          turnStateInitializedRef.current = true;
        }

        // Update turn state using helper function
        updateYourTurn(isYourTurn, 'game-update');
        
        // ✅ Determine if player goes first (after both players have mulliganed)
        const playerState = msg.gameState.state?.[userId];
        const opponentId = msg.gameState.players?.find(p => p !== userId);
        const opponentState = opponentId ? msg.gameState.state?.[opponentId] : null;
        
        // Check if both players have completed mulligan
        const playerMulliganed = playerState && !playerState.needsMulligan && playerState.hasMulliganed;
        const opponentMulliganed = opponentState && !opponentState.needsMulligan && opponentState.hasMulliganed;
        
        if (playerMulliganed && opponentMulliganed) {
          // Both players have mulliganed - determine who goes first (only set once)
          // ✅ FIX: Use players[0] to determine who goes first, not current turn
          // (current turn might have changed if bot already took their turn)
          setPlayerGoesFirst(prev => {
            if (prev === null) {
              const firstPlayerId = msg.gameState.players?.[0];
              const goesFirst = firstPlayerId === userId;
              // Show message (will auto-hide after 5 seconds via useEffect)
              setShowFirstTurnMessage(true);
              return goesFirst;
            }
            return prev; // Keep existing value
          });
        }
        
        // ✅ Process queued actions after state update
        if (actionQueueRef.current.length > 0 && !isProcessingActionRef.current) {
          const nextAction = actionQueueRef.current.shift();
          isProcessingActionRef.current = true;
          // Process next action after a short delay to ensure state is updated
          setTimeout(() => {
            if (nextAction.type === 'playOrder') {
              playOrder(nextAction.orderId, nextAction.zone, nextAction.targetInfo, nextAction.instanceId);
            }
            isProcessingActionRef.current = false;
          }, 100);
        }
        
        // Reset attack ref when receiving game state update (allows multiple actions per turn)
        // This was previously only reset when turn changed, blocking multiple attacks per turn
        attackInProgressRef.current = false;
        
        // Keep turn change logic for other cleanup
        if (wasYourTurn && !isYourTurn) {
          // Turn changed to opponent
        }
        
        // Show turn start image when player's turn begins (only after turn state is initialized)
        if (turnStateInitializedRef.current && wasYourTurn !== null && !wasYourTurn && isYourTurn) {
          setShowTurnStartImage(true);
        }
        
        // Clear selected attacker if it no longer exists (unit was killed)
        if (selectedAttacker && msg.gameState.state?.[userId]?.playedOrders?.[selectedAttacker.zone]) {
          const attackerStillExists = msg.gameState.state[userId].playedOrders[selectedAttacker.zone][selectedAttacker.idx];
          if (!attackerStillExists) {
            setSelectedAttacker(null);
            setAttackMode(false);
          }
        }
      }
      
      // Only clear selection state for certain message types
      if (msg.type === "turn-ended" || msg.type === "turn-timeout" || 
          msg.type === "player-disconnected" || msg.type === "player-abandoned" || 
          msg.type === "player-resigned") {
        setSelectedOrderId(null);
        setSelectedOrderInstanceId(null);
        setSelectedAttacker(null);
        setAttackMode(false);
        attackInProgressRef.current = false; // Reset attack in progress flag when turn ends
      }
      
      // Sync deploy targeting state with game state
      if (window.syncDeployTargeting) {
        window.syncDeployTargeting(msg.gameState, userId);
      }
      
      // Sync strategem targeting state with game state
      if (window.syncStrategemTargeting) {
        window.syncStrategemTargeting(msg.gameState, userId);
      }
      
      // ✅ REFACTORED: Use hook's validateState method (via ref to avoid stale closure)
      validateStateRef.current(msg.gameState);
      
      
      // DEBUG: Check Unit 1 attack ratings in WebSocket update
      if (msg.gameState?.state?.[userId]?.playedOrders) {
        ['front', 'flank'].forEach(zone => {
          if (msg.gameState.state[userId].playedOrders[zone]) {
            msg.gameState.state[userId].playedOrders[zone].forEach((unit, index) => {
              if (unit && unit.id === 1) {
              }
            });
          }
        });
      }
      
      // Check for weapons that need immobilize targeting (resolve player key: id type mismatches skip whole block otherwise)
      const playerForTargeting =
        msg.gameState?.state?.[userId] ??
        msg.gameState?.state?.[String(userId)] ??
        (typeof userId === 'string' && !Number.isNaN(Number(userId)) ? msg.gameState?.state?.[Number(userId)] : null);
      if (playerForTargeting) {
        const player = playerForTargeting;
        let needsImmobilizeTarget = false;
        let needsConfuseTarget = false;
        let immobilizeWeaponId = null;
        let confuseWeaponId = null;
        
        ['front', 'flank'].forEach(zone => {
          if (player.playedOrders[zone]) {
            player.playedOrders[zone].forEach(unit => {
              if (unit && unit.attachedWeapons) {
                unit.attachedWeapons.forEach(weapon => {
                  if (weapon.needsImmobilizeTarget) {
                    needsImmobilizeTarget = true;
                    immobilizeWeaponId = weapon.id;
                  }
                  if (weapon.needsConfuseTarget) {
                    needsConfuseTarget = true;
                    confuseWeaponId = weapon.id;
                  }
                });
              }
            });
          }
        });
        
        if (needsImmobilizeTarget) {
          setImmobilizeTargetMode(true);
          setSelectedImmobilizeWeaponId(immobilizeWeaponId);
          setError(t('battleUI.selectTargetImmobilize'));
        } else if (needsConfuseTarget) {
          setConfuseTargetMode(true);
          setSelectedConfuseWeaponId(confuseWeaponId);
          setError(t('battleUI.selectTargetConfuse'));
        } else if (player.needsStrategemTarget) {
          setStrategemTargetMode(true);
          setSelectedStrategemId(player.needsStrategemTarget.strategemId);
          setStrategemTargetInfo(player.needsStrategemTarget);
          // Don't show "Select a target" when we have a pending drag-drop target (we'll auto-send it)
          if (pendingStrategemDropTargetRef.current?.strategemInstanceId !== player.needsStrategemTarget.strategemInstanceId) {
            setError(`${t('battleUI.selectTargetStrategem')} ${player.needsStrategemTarget.strategemId}`);
          }
        }

        // Sandra Lehnsherr (395): server keeps weapon deploy pending in player state; enrich now includes it for turn-played sync
        const isYourTurnFor395 =
          msg.gameState.turn === userId ||
          msg.gameState.turn === userId?.toString?.() ||
          String(msg.gameState.turn) === String(userId);
        if (
          isYourTurnFor395 &&
          player.unit395WaitingForDeployTargeting &&
          player.unit395CurrentWeapon &&
          player.unit395CurrentUnit
        ) {
          const w = player.unit395CurrentWeapon;
          const u = player.unit395CurrentUnit;
          setWeaponTargetMode(false);
          setSelectedWeaponId(null);
          setSelectedWeaponInstanceId(null);
          setWeaponDeployTargetMode(true);
          setWeaponDeployTargetInfo({
            weaponInstanceId: w.instanceId,
            weaponId: w.id,
            weaponName: w.name,
            targetUnitInstanceId: u.instanceId
          });
          setError(t('battleUI.selectTargetWeapon'));
        }
        
        // ✅ CRITICAL: Only check for targeting mode if there's no error
        // This prevents targeting mode activation when intelligence check fails
        if (!error) {
          // Check for units that need deploy targeting
          let needsDeployTarget = false;
          let deployOrderId = null;
          let deployInstanceId = null;
          let deployZone = null;
          
          // ✅ CRITICAL FIX: Always check backend's selectedDeployInstanceId FIRST (authoritative source)
          // Even if deployTargetMode is false, the backend may have set selectedDeployInstanceId
          // This prevents race conditions where backend sets instanceId but gameState update hasn't arrived
          if (player.selectedDeployInstanceId && player.selectedDeployOrderId && player.selectedDeployZone) {
            // Validate that the instanceId from backend actually exists in the zone
            const zoneUnits = player.playedOrders[player.selectedDeployZone] || [];
            const unitExists = zoneUnits.some(u => u && u.instanceId === player.selectedDeployInstanceId);
            
            if (unitExists) {
              // Backend has authoritative instanceId and unit exists - use it
              needsDeployTarget = true;
              deployOrderId = player.selectedDeployOrderId;
              deployInstanceId = player.selectedDeployInstanceId;
              deployZone = player.selectedDeployZone;
            } else {
              // Backend instanceId doesn't exist in zone - this is a sync issue
              console.warn('⚠️ DEPLOY TARGETING: Backend instanceId not found in zone, falling back to scan', {
                backendInstanceId: player.selectedDeployInstanceId,
                backendOrderId: player.selectedDeployOrderId,
                backendZone: player.selectedDeployZone,
                zoneUnits: zoneUnits.map(u => u ? { id: u.id, name: u.name, instanceId: u.instanceId } : null)
              });
            }
          }
          
          // If backend didn't provide instanceId or it doesn't exist, check if deployTargetMode is active
          if (!needsDeployTarget && player.deployTargetMode && player.selectedDeployOrderId) {
            needsDeployTarget = true;
            deployOrderId = player.selectedDeployOrderId;
            deployZone = player.selectedDeployZone;
            // ✅ CRITICAL: Always use authoritative instanceId from server (backend is source of truth)
            // The backend sets selectedDeployInstanceId when the unit is deployed
            // If it's not set, that means the unit deployment failed or there's a backend bug
            if (!player.selectedDeployInstanceId) {
              console.error(`❌ ROOT CAUSE: Backend deployTargetMode is active but selectedDeployInstanceId is not set!`);
              console.error(`   This indicates the unit deployment didn't properly set selectedDeployInstanceId.`);
              console.error(`   deployOrderId: ${deployOrderId}, deployZone: ${deployZone}`);
              console.error(`   Zone ${deployZone} units:`, player.playedOrders[deployZone]?.map(u => u ? { id: u.id, name: u.name, instanceId: u.instanceId } : null));
              // This is a backend bug - the unit was deployed but selectedDeployInstanceId wasn't set
              throw new Error(`Backend error: deployTargetMode active but selectedDeployInstanceId not set. Check DeployOrders.js line 202.`);
            }
            deployInstanceId = player.selectedDeployInstanceId;
          }
          
          // Fallback: Look for units that need targeting (only if backend didn't provide instanceId)
          if (!needsDeployTarget) {
            ['front', 'flank'].forEach(zone => {
              if (player.playedOrders[zone]) {
                player.playedOrders[zone].forEach(unit => {
                  if (unit && (unit.needsDeployTarget || (targetingData && unitNeedsTargetingOnDeploy(unit.id, targetingData)))) {
                    needsDeployTarget = true;
                    deployOrderId = unit.id;
                    deployInstanceId = unit.instanceId;
                    deployZone = zone;
                  }
                });
              }
            });
          }
          
          if (needsDeployTarget) {
            // ✅ CRITICAL: Only activate targeting mode if there's no error
            // This prevents yellow borders when intelligence check fails
            if (!error) {
              // Find the unit object to get its name for the targeting message
              let unitObject = null;
              if (deployInstanceId && deployZone) {
                const zoneUnits = player.playedOrders[deployZone] || [];
                unitObject = zoneUnits.find(u => u && u.instanceId === deployInstanceId);
              }
              
              setDeployTargetMode(true);
              setSelectedDeployOrderId(deployOrderId);
              setSelectedDeployInstanceId(deployInstanceId);
              setSelectedDeployZone(deployZone);
              setDeployTargetInfo(unitObject ? { order: unitObject } : null);
              setError(t('battleUI.selectTargetDeploy'));
            }
          }
        }
      }
      
      // ✅ FIX: Only accept winner when message matches resolved session (URL first, then ref) — avoids stale ref vs /battle/:id.
      const updateMsgGameId = msg.gameId;
      const updateExpectedSid = resolveWsSessionGameIdRef.current();
      if (msg.gameState?.winner && !isSpectating) {
        if (updateMsgGameId != null && updateExpectedSid != null && String(updateMsgGameId) === String(updateExpectedSid)) {
          const didYouWin = msg.gameState.winner === userId;
          const isFriendlyMatch = msg.gameState.isFriendlyMatch || false;
          handleBattleEnd(didYouWin, gameIdRef.current, socket, isFriendlyMatch, `${msg.type}-winner`);
          return;
        }
      }
    }
    if (msg.type === "error") {
      // ✅ FIX: Handle reconnect errors - if no active game, game ended, or stale state, redirect to battle overview
      const redirectToOverview = msg.message && (
        msg.message.includes('No active game found') ||
        msg.message.includes('Game state not found') ||
        msg.message.includes('Game already ended') ||
        msg.message.includes('Stale game state detected')
      );
      if (redirectToOverview) {
        console.warn(`⚠️ Reconnect failed: ${msg.message}. Redirecting to battle overview.`);
        setError(t('battleUI.gameNotFound'));
        reconnectingRef.current = false;
        setReconnecting(false);
        wsConnectingRef.current = false;
        // ✅ FIX: Clear timeout on error
        if (reconnectingTimeoutRef.current) {
          clearTimeout(reconnectingTimeoutRef.current);
          reconnectingTimeoutRef.current = null;
        }
        setTimeout(() => {
          navigate('/battleoverview', { replace: true });
        }, 2000);
        return;
      }
      // ✅ REFACTORED: Use hook's unlockUI method (via ref to avoid stale closure)
      unlockUIRef.current();
      
      setError(translateBackendError(msg.message));
      
      // ✅ FIX: Handle preparation bonus errors
      if (msg.message && msg.message.includes('Preparation bonus')) {
        if (msg.message.includes('has already been used')) {
          // Backend confirms it's already used - keep it as true (optimistic update was correct)
          setPreparationBonusUsed(true);
          preparationBonusPendingRef.current = false; // Clear pending since backend confirmed
        } else {
          // Other preparation bonus errors (e.g., "not yet implemented", network issues)
          // Revert optimistic update to allow retry
          setPreparationBonusUsed(false);
          preparationBonusPendingRef.current = false; // Clear pending to allow retry
        }
      }
      
      // If attack failed, clear pending attack info and reset attack state
      // This prevents the unit from becoming inactive after an illegal attack
      if (pendingAttackInfoRef.current) {
        pendingAttackInfoRef.current = null;
        attackInProgressRef.current = false;
        // Don't clear selectedAttacker or attackMode - let user try again or cancel manually
      }
      
      // If we're in targeting mode and get an error, keep targeting mode active
      // Don't reset targeting mode on error - let user try again or cancel
    }
  };
  socket.onclose = () => {
    // ✅ FIX: Don't clear socket reference on close if we're in the middle of reconnecting
    // The socket might close temporarily during reconnect, but we need to keep the ref
    // Only clear if we're definitely not reconnecting
    if (!wsConnectingRef.current) {
      setWs(null);
      // Only clear ref if it's the same socket that closed
      if (currentSocketRef.current === socket) {
        currentSocketRef.current = null;
      }
    } else {
      // If we're connecting, keep the ref but mark ws as null
      // This allows the reconnect flow to complete
      setWs(null);
      // Don't clear currentSocketRef.current - keep it for reconnect
    }
    wsConnectingRef.current = false; // Reset connection flag when socket closes
  };
  return () => {
    if (socket) {
      // Always close on cleanup (Escape → leave battle, or /battle/A → /battle/B remount).
      // Leaving the socket open leaked a second connection after new PvE and let abandoned-game
      // traffic keep hitting a stale handler. Strict Mode remount opens a fresh socket on the next run.
      wsConnectingRef.current = false;
      currentSocketRef.current = null;
      gameSocketReconnectSentRef.current = false;
      if (reconnectingTimeoutRef.current) {
        clearTimeout(reconnectingTimeoutRef.current);
        reconnectingTimeoutRef.current = null;
      }
      try {
        if (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN) {
          socket.close();
        }
      } catch (err) {
        // Ignore errors when closing socket
      }
    }
  };
}

}, [userId, gameId, navigate, getCurrentGameIdFromUrl]); // ✅ FIX: Removed token so War Effort / App re-renders don't tear down socket. Token is read via tokenRef.current. Removed ws and gameState to prevent connection loops.




const playOrder = async (orderId, zone, targetInfo = null, instanceId = null) => {
  // ✅ DESYNC FIX: Check if state is currently updating
  if (isStateUpdating) {
    setError(t('battleUI.waitForAction'));
    return;
  }
  
  // ✅ ENHANCED DESYNC FIX: Check if state is stabilizing (React is rendering new state)
  if (isStateStabilizing) {
    setError(t('battleUI.waitForAction'));
    return;
  }
  
  // ✅ DESYNC FIX: Enforce cooldown between actions (prevent spam clicking)
  const now = Date.now();
  const timeSinceLastAction = now - lastActionTimestamp.current;
  if (timeSinceLastAction < ACTION_COOLDOWN_MS) {
    return;
  }
  lastActionTimestamp.current = now;
  
  if (!yourTurn || !gameId || !ws || ws.readyState !== 1) {
      setError(t('battleUI.connectionLost'));
      return;
    }

  // ✅ REFACTORED: Use hook's getCurrentState method
  const currentState = getCurrentState() || gameState;
  
  // ✅ CRITICAL: Validate card is actually in hand before playing (prevent state desync errors)
  if (!currentState?.state?.[userId]) {
    setError(t('battleUI.gameStateNotAvailable'));
    console.error('🚨 STATE DESYNC: Game state not available for player', { userId, hasGameState: !!currentState });
    return;
  }

  const player = currentState.state[userId];
  
  // ✅ CRITICAL: Check if order is in deck FIRST (before checking hand)
  // This catches the case where frontend shows card in hand but backend has it in deck
  const orderInDeck = instanceId 
    ? player.deck?.find(o => o && o.instanceId === instanceId)
    : player.deck?.find(o => o && o.id === orderId);
  
  if (orderInDeck) {
    console.error('🚨 STATE DESYNC: Order is in deck, not in hand. Card not drawn yet.', {
      orderId,
      instanceId,
      orderName: orderInDeck.name,
      deckPosition: player.deck?.indexOf(orderInDeck),
      handSize: player.hand?.length,
      deckSize: player.deck?.length,
      stateVersion: currentState.stateVersion,
      turnNumber: currentState.turnNumber
    });
    setError(t('battleUI.cardNotAvailable'));
    // Force state refresh
    // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
    const activeSocket = ws || currentSocketRef.current;
    if (activeSocket && activeSocket.readyState === 1) {
      activeSocket.send(JSON.stringify({ type: 'request-state-refresh', gameId }));
    }
    return;
  }
  
  // ✅ CRITICAL: Check if order is in completedOrders BEFORE checking hand
  const orderInCompleted = player.completedOrders?.find(o => 
    (instanceId && o.instanceId === instanceId) || 
    (!instanceId && o.id === orderId)
  );
  
  if (orderInCompleted) {
    console.error('🚨 STATE DESYNC: Order is in completedOrders (already played).', { 
      orderId, 
      instanceId,
      orderName: orderInCompleted.name 
    });
    setError(t('battleUI.cardAlreadyPlayed'));
    // Force state refresh by requesting it
    // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
    const activeSocket = ws || currentSocketRef.current;
    if (activeSocket && activeSocket.readyState === 1) {
      activeSocket.send(JSON.stringify({ type: 'request-state-refresh', gameId }));
    }
    return;
  }
  
  // ✅ IMPROVED: Use exact instanceId matching first, then fall back to orderId
  const order = instanceId 
    ? player.hand.find(o => o.instanceId === instanceId)
    : player.hand.find(o => o.id === orderId);
  
  // ✅ CRITICAL: Check if order is actually in hand (not in deck, discard, etc.)
  if (!order) {
    // Check where the order actually is - this indicates state desync
    const orderInDiscard = player.discard?.find(o => o.id === orderId || o.instanceId === instanceId);
    
    if (orderInDiscard) {
      console.error('🚨 STATE DESYNC: Order is in discard pile.', { orderId, instanceId });
      setError(t('battleUI.cardAlreadyDiscarded'));
      return;
    } else {
      console.error('🚨 STATE DESYNC: Order not found anywhere in player state.', { orderId, instanceId });
      setError(t('battleUI.cardNotFound'));
      // Force state refresh
      // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
      const activeSocket = ws || currentSocketRef.current;
      if (activeSocket && activeSocket.readyState === 1) {
        activeSocket.send(JSON.stringify({ type: 'request-state-refresh', gameId }));
      }
      return;
    }
  }

  // ✅ CRITICAL: Check intelligence BEFORE sending action to prevent targeting mode activation
  // Use Ability Array System to get correct cost
  const orderCost = order.hasInstanceModifications && order.instanceCost !== undefined ? order.instanceCost : order.cost;
  if (player.intelligence < orderCost) {
    setError(`${t('battleUI.notEnoughIntelligence')} ${order.name}! ${t('battleUI.need')} ${orderCost}, ${t('battleUI.have')} ${player.intelligence}`);
    return;
  }

  // Check if token needs refresh before sending
  if (needsTokenRefresh(token) && refreshTokenValue) {
    try {
      const newToken = await refreshToken(refreshTokenValue);
      // Note: We can't update the token prop here, but the server will handle auth
    } catch (error) {
      setError(t('battleUI.connectionLost'));
      return;
    }
  } else if (needsTokenRefresh(token) && !refreshTokenValue) {
    setError(t('battleUI.connectionLost'));
    return;
  }

  try {
    // Clear any error message
    setError("");

    // ✅ CRITICAL: Check for targeting BEFORE locking UI
    // If targeting is needed, enter targeting mode without locking
    // Handle deploy orders with targeting
    if (needsTargetingOnDeploy(orderId) && targetInfo === null) {
      setDeployMode(true);
      setSelectedDeployOrderId(orderId);
      setSelectedDeployInstanceId(instanceId);
      setSelectedOrderId(orderId);
      setSelectedOrderInstanceId(instanceId);
      return;
    }
    
    // Handle special unit deploy targeting
    if (specialUnitNeedsTargeting(orderId) && targetInfo === null) {
      setDeployMode(true);
      setSelectedDeployOrderId(orderId);
      setSelectedDeployInstanceId(instanceId);
      setSelectedOrderId(orderId);
      setSelectedOrderInstanceId(instanceId);
      setIsSpecialDeploy(true);
      return;
    }

    // ✅ REFACTORED: Use hook's lockUI method with timeout callbacks
    lockUI(
      () => setError(t('battleUI.actionTimeout')), // onTimeout callback
      () => { // onTimeoutRequestRefresh callback
        // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
        const activeSocket = ws || currentSocketRef.current;
        if (activeSocket && activeSocket.readyState === 1) {
          activeSocket.send(JSON.stringify({ type: 'request-state-refresh', gameId }));
        }
      }
    );

    const action = { type: "playOrder", orderId, zone };
    if (instanceId) {
      action.instanceId = instanceId;
    }
    
    // Add targeting information if provided
    if (targetInfo) {
      action.targetUnitId = targetInfo.targetUnitId;
      action.targetZone = targetInfo.targetZone;
      action.targetIndex = targetInfo.targetIndex;
    }

    // Send the action to backend
    // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
    const activeSocket = ws || currentSocketRef.current;
    if (!activeSocket || activeSocket.readyState !== 1) {
      console.error('❌ playOrder blocked: WebSocket not ready', { hasWs: !!ws, hasRef: !!currentSocketRef.current, wsReady: ws?.readyState, refReady: currentSocketRef.current?.readyState });
      setError(t('battleUI.connectionLost'));
      unlockUI();
      return;
    }
    activeSocket.send(
      JSON.stringify({
        type: "play-turn",
        gameId,
        action,
        stateVersion: lastStateVersionRef.current, // Send current state version for optimistic locking
      })
    );
    
    
    // UI will be unlocked when we receive state update from backend
    
  } catch (error) {
    console.error('❌ Error in playOrder:', error);
    setError(`${t('battleUI.failedToPlayCard')}: ${error.message}`);
    // ✅ REFACTORED: Use hook's unlockUI method
    unlockUI();
  }
};

const playDrone = (orderId) => {
  // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
  const activeSocket = ws || currentSocketRef.current;
  if (!yourTurn || !gameId || !activeSocket || activeSocket.readyState !== 1) {
    console.error('❌ playDrone blocked:', { yourTurn, gameId, hasWs: !!ws, hasRef: !!currentSocketRef.current, wsReady: ws?.readyState, refReady: currentSocketRef.current?.readyState });
    setError(t('battleUI.connectionLost'));
    return;
  }

  // ✅ CRITICAL: Check intelligence BEFORE sending action to prevent targeting mode activation
  if (gameState?.state?.[userId]) {
    const player = gameState.state[userId];
    const order = player.hand.find(o => o.id === orderId);
    
    if (!order) {
      setError(t('battleUI.cardNotFound'));
      return;
    }
    
    // ✅ FIX: Extract instanceId from order (drones should have instanceIds like any other order)
    if (!order.instanceId) {
      console.error(`🚨 CRITICAL: Drone ${order.name} (ID: ${order.id}) missing instanceId in hand`);
      setError('Card missing instanceId. This indicates a backend data integrity issue.');
      return;
    }
    
    // Use Ability Array System to get correct cost
    const orderCost = order.hasInstanceModifications && order.instanceCost !== undefined ? order.instanceCost : order.cost;
    if (player.intelligence < orderCost) {
      setError(`${t('battleUI.notEnoughIntelligence')} ${order.name}! ${t('battleUI.need')} ${orderCost}, ${t('battleUI.have')} ${player.intelligence}`);
      return;
    }

    // ✅ FIX: Use activeSocket instead of ws to handle browser refresh scenarios
    activeSocket.send(
      JSON.stringify({
        type: "play-turn",
        gameId,
        action: { type: "playOrder", orderId, zone: "drone", instanceId: order.instanceId },
      })
    );
  }
};

const playStrategem = (orderId, instanceId = null) => {
  // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
  const activeSocket = ws || currentSocketRef.current;
  if (!yourTurn || !gameId || !activeSocket || activeSocket.readyState !== 1) {
    console.error('❌ playStrategem blocked:', { yourTurn, gameId, hasWs: !!ws, hasRef: !!currentSocketRef.current, wsReady: ws?.readyState, refReady: currentSocketRef.current?.readyState });
    setError(t('battleUI.connectionLost'));
    return;
  }

  // ✅ CRITICAL: Check intelligence BEFORE sending action to prevent targeting mode activation
  if (gameState?.state?.[userId]) {
    const player = gameState.state[userId];
    // ✅ FIX: Find by instanceId if provided, otherwise fall back to orderId
    const order = instanceId 
      ? player.hand.find(o => o.instanceId === instanceId)
      : player.hand.find(o => o.id === orderId);
    
    if (!order) {
      setError(t('battleUI.cardNotFound'));
      return;
    }
    
    // ✅ FIX: Extract instanceId from order (strategems should have instanceIds like any other order)
    if (!order.instanceId) {
      console.error(`🚨 CRITICAL: Strategem ${order.name} (ID: ${order.id}) missing instanceId in hand`);
      setError('Card missing instanceId. This indicates a backend data integrity issue.');
      return;
    }
    
    // Use Ability Array System to get correct cost
    const orderCost = order.hasInstanceModifications && order.instanceCost !== undefined ? order.instanceCost : order.cost;
    if (player.intelligence < orderCost) {
      setError(`${t('battleUI.notEnoughIntelligence')} ${order.name}! ${t('battleUI.need')} ${orderCost}, ${t('battleUI.have')} ${player.intelligence}`);
      return;
    }

    // ✅ Strategem 114 (Tactical Loadout), 115 (Stinger Unit Alpha), 221, and 225: Trigger explosion effect when requested
    if (orderId === 114 || orderId === 115 || orderId === 221 || orderId === 225) {
      // Get card position from DOM
      const cardElement = document.querySelector(`[data-instance-id="${order.instanceId}"]`);
      let explosionPosition;
      
      if (cardElement) {
        const rect = cardElement.getBoundingClientRect();
        explosionPosition = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        };
      } else {
        // Fallback to approximate hand position
        explosionPosition = {
          x: window.innerWidth / 2,
          y: window.innerHeight - 100
        };
      }
      
      // Determine color: black for 221 and 225, purple for 115, orange for 114
      const explosionColor = (orderId === 221 || orderId === 225) ? 'black' : orderId === 115 ? 'purple' : 'orange';
      
      // Trigger explosion effect
      const explosionKey = `explosion-${orderId}-${order.instanceId}-${Date.now()}`;
      setExplosionEffects(prev => {
        const next = new Map(prev);
        next.set(explosionKey, {
          key: explosionKey,
          x: explosionPosition.x,
          y: explosionPosition.y,
          strategemInstanceId: order.instanceId,
          color: explosionColor
        });
        return next;
      });
      
      // Clean up after animation completes
      setTimeout(() => {
        setExplosionEffects(prev => {
          const next = new Map(prev);
          next.delete(explosionKey);
          return next;
        });
      }, 700); // Slightly longer than animation duration
    }

    // ✅ FIX: Use activeSocket instead of ws to handle browser refresh scenarios
    activeSocket.send(
      JSON.stringify({
        type: "play-turn",
        gameId,
        action: { type: "playOrder", orderId, zone: "strategem", instanceId: order.instanceId },
      })
    );
  }
};

const activateDrone = (droneId) => {
  // Campaign tutorial: block drone activation when step forbids it (e.g. "Your Drones" – deploy only)
  if (typeof document !== 'undefined' && document.body?.getAttribute('data-tutorial-block-drone-activation') === 'true') {
    setError(t('battleUI.deployYourDroneFirst') || 'Deploy your drone first, then pass the turn.');
    return;
  }

  // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
  const activeSocket = ws || currentSocketRef.current;
  if (!yourTurn || !gameId || !activeSocket || activeSocket.readyState !== 1) {
    console.error('❌ activateDrone blocked:', { yourTurn, gameId, hasWs: !!ws, hasRef: !!currentSocketRef.current, wsReady: ws?.readyState, refReady: currentSocketRef.current?.readyState });
    setError(t('battleUI.connectionLost'));
    return;
  }

  // ✅ BUG FIX: Find drone by instanceId (unique per deployed instance)
  const drone = you?.drones?.find(d => d.instanceId === droneId);
  if (!drone) {
    console.error(`❌ Drone with instanceId ${droneId} not found in player drones:`, you?.drones);
    return;
  }

  // ✅ BUG FIX: Check if drone has already been used this turn
  if (drone.usedThisTurn) {
    setError(t('battleUI.droneUsedThisTurn'));
    return;
  }

  // ✅ BUG FIX: Check if drone has charges remaining
  if (drone.charges !== undefined && drone.charges <= 0) {
    setError(t('battleUI.droneNoCharges'));
    return;
  }

  // ✅ Unit 502 (Disruptor): Check if any Disruptor is deployed (deactivates all drones)
  const allPlayerIds = gameState?.players || (gameState?.state ? Object.keys(gameState.state) : []);
  let disruptorDeployed = false;
  for (const pid of allPlayerIds) {
    const p = gameState?.state?.[pid];
    if (p && p.playedOrders) {
      for (const zone of ['front', 'flank']) {
        if (p.playedOrders[zone]) {
          for (const unit of p.playedOrders[zone]) {
            if (unit && unit.id === 502 && unit.health > 0) {
              disruptorDeployed = true;
              break;
            }
          }
          if (disruptorDeployed) break;
        }
      }
      if (disruptorDeployed) break;
    }
  }
  
  if (disruptorDeployed) {
    setError('Drones are inactive while Disruptor is deployed');
    return;
  }

  // Check if drone needs targeting using extracted function
  const needsTargeting = droneNeedsTargeting(drone.id, targetingData);
  
  // Check if drone can target opponent units using extracted function
  const canTargetOpponents = droneCanTargetOpponents(drone.id, targetingData);
  
  if (needsTargeting) {
    setSelectedDroneId(drone.id);              // Order ID for reference
    setSelectedDroneInstanceId(drone.instanceId); // ✅ Unique instance ID
    setDroneTargetMode(true);
    setError(t('battleUI.selectTargetDrone'));
    return;
  }

  // Drones that don't need targeting: 20, 21, 275, 277, 278
  // ✅ FIX: Use activeSocket instead of ws to handle browser refresh scenarios
  activeSocket.send(
    JSON.stringify({
      type: "play-turn",
      gameId,
      action: { 
        type: "activateDrone", 
        droneId: drone.id,            // Order ID
        droneInstanceId: drone.instanceId, // ✅ REQUIRED: Unique instance ID
        targetUnitId: null,
        targetZone: null,
        targetIndex: null
      },
    })
  );
};


// attachWeapon function moved to targeting folder

// targetDrone function moved to targeting folder

// targetImmobilize function moved to targeting folder

// targetConfuse function moved to targeting folder

// targetStrategem function moved to targeting folder

// Handle deploy targeting
const handleTargetDeploy = (targetZone, targetIndex, targetUnitId) => {
  targetDeploy(
    targetZone,
    targetIndex,
    targetUnitId,
    yourTurn,
    gameId,
    ws,
    setError,
    selectedDeployOrderId,
    selectedDeployInstanceId,
    selectedDeployZone,
    setDeployTargetMode,
    setSelectedDeployOrderId,
    setSelectedDeployInstanceId,
    setSelectedDeployZone,
    setDeployTargetInfo,
    gameState, // ✅ FIX: Pass gameState for validation
    userId // ✅ FIX: Pass playerId for validation
  );
};

const endTurn = () => {
  playSoundEffect('/sounds/click.mp3');
  
  // Cancel any active targeting when ending turn
  cancelAllTargeting();
  
  // ✅ FIX: Get active socket - prioritize ref over state (ref is more reliable after refresh)
  // Also check if ws state exists and is different from ref (might be more recent)
  let activeSocket = currentSocketRef.current;
  if (!activeSocket || activeSocket.readyState !== WebSocket.OPEN) {
    // Try ws state if ref is not available or not open
    if (ws && (ws.readyState === WebSocket.OPEN || !activeSocket)) {
      activeSocket = ws;
      // Update ref if ws is better
      if (ws.readyState === WebSocket.OPEN) {
        currentSocketRef.current = ws;
      }
    }
  }
  
  // ✅ FIX: If no socket at all, try to trigger reconnection
  if (!activeSocket) {
    console.error('❌ endTurn blocked: No WebSocket connection', { 
      yourTurn, 
      gameId, 
      hasWs: !!ws, 
      hasRef: !!currentSocketRef.current,
      wsConnecting: wsConnectingRef.current
    });
    
    // If we're not already connecting, try to reconnect
    if (!wsConnectingRef.current && gameId) {
      wsConnectingRef.current = false; // Reset flag to allow reconnection
      setWs(null); // Clear ws to trigger useEffect reconnection
    }
    
    setError(t('battleUI.connectionLost'));
    return;
  }
  
  // ✅ FIX: If socket is connecting, wait a bit and retry (handles refresh scenarios)
  if (activeSocket.readyState === WebSocket.CONNECTING) {
    setTimeout(() => {
      // Re-check both ref and state
      let retrySocket = currentSocketRef.current;
      if (!retrySocket || retrySocket.readyState !== WebSocket.OPEN) {
        retrySocket = ws;
      }
      if (retrySocket && retrySocket.readyState === WebSocket.OPEN) {
        retrySocket.send(JSON.stringify({ type: "end-turn", gameId }));
      } else {
        console.error('❌ endTurn retry failed: socket still not ready', {
          hasRef: !!currentSocketRef.current,
          hasWs: !!ws,
          refReady: currentSocketRef.current?.readyState,
          wsReady: ws?.readyState
        });
        setError(t('battleUI.connectionLost'));
      }
    }, 1000); // Increased timeout to 1 second
    return;
  }
  
  if (!yourTurn || !gameId || activeSocket.readyState !== WebSocket.OPEN) {
    console.error('❌ endTurn blocked:', { 
      yourTurn, 
      gameId, 
      hasWs: !!ws, 
      hasRef: !!currentSocketRef.current, 
      wsReady: ws?.readyState, 
      refReady: currentSocketRef.current?.readyState,
      activeSocketReady: activeSocket?.readyState
    });
    setError(t('battleUI.connectionLost'));
    return;
  }

  try {
    const message = JSON.stringify({ type: "end-turn", gameId });
    activeSocket.send(message);
  } catch (error) {
    console.error('❌ Error sending end-turn message:', error);
    console.error('❌ Socket state:', {
      readyState: activeSocket?.readyState,
      url: activeSocket?.url,
      hasRef: !!currentSocketRef.current,
      hasWs: !!ws
    });
    setError(t('battleUI.connectionLost'));
  }
};

const handleResign = () => {
  playSoundEffect('/sounds/click.mp3');
  setShowResignDialog(true);
};

const confirmResign = () => {
  playSoundEffect('/sounds/click.mp3');
  // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
  const activeSocket = ws || currentSocketRef.current;
  if (!gameId || !activeSocket || activeSocket.readyState !== 1) {
    console.error('❌ confirmResign blocked:', { gameId, hasWs: !!ws, hasRef: !!currentSocketRef.current, wsReady: ws?.readyState, refReady: currentSocketRef.current?.readyState });
    setError(t('battleUI.connectionLost'));
    return;
  }

  activeSocket.send(JSON.stringify({ type: "resign", gameId }));
  setShowResignDialog(false);
};

const cancelResign = () => {
  playSoundEffect('/sounds/click.mp3');
  setShowResignDialog(false);
};




  const attackOrder = (attackerZone, attackerIdx, targetZone, targetIdx) => {
    // Client-side protect validation removed - server handles all protect logic validation
    if (!yourTurn || !gameId) {
      console.error('❌ ATTACK ORDER BLOCKED: Not your turn or no gameId', { yourTurn, gameId });
      return;
    }
    if (!ws || ws.readyState !== 1) {
      console.error('❌ ATTACK ORDER BLOCKED: WebSocket not ready', { hasWs: !!ws, readyState: ws?.readyState });
      setError(t('battleUI.connectionLost'));
      return;
    }
    
    // Prevent double-click attacks (using ref for synchronous check)
    if (attackInProgressRef.current) {
      // ✅ SAFETY: If attack has been in progress for more than 10 seconds, reset it
      // This handles cases where attack-complete message was lost or animation never completed
      const attackTimeout = 10000; // 10 seconds
      const lastAttackTime = pendingAttackInfoRef.current?.timestamp || 0;
      const timeSinceAttack = Date.now() - lastAttackTime;
      
      if (timeSinceAttack > attackTimeout && lastAttackTime > 0) {
        attackInProgressRef.current = false;
        pendingAttackInfoRef.current = null;
        // Continue with the attack
      } else {
        return;
      }
    }

    // Get the actual unit instances
    const attacker = you?.playedOrders?.[attackerZone]?.[attackerIdx];
    if (!attacker) {
      console.error('❌ ATTACK ORDER BLOCKED: No attacker found', { attackerZone, attackerIdx, playedOrders: you?.playedOrders });
      setError(t('battleUI.connectionLost'));
      return;
    }
    
    
    // ✅ VALIDATION: Check if attacker has 0 attack rating - cannot attack
    const attackRating = attacker.attack_rating ?? 0;
    if (attackRating === 0) {
      setError('This unit has 0 attack rating and cannot attack');
      attackInProgressRef.current = false;
      return;
    }
    
    // Determine target based on targetZone
    let target = null;
    let actualTargetZone = targetZone;

    if (targetZone === 'general') {
      // Attacking opponent general
      target = null;
      actualTargetZone = 'general';
    } else if (targetZone === 'opponent_drone' || targetZone === 'drones') {
      // Attacking opponent drone
      const opponentId = gameState.players?.find(id => String(id) !== String(userId));
      const opponentState = opponentId ? gameState.state?.[opponentId] : null;
      const drone = opponentState?.drones?.[targetIdx];
      
      if (!drone) {
        console.error('No drone found', { targetZone, targetIdx });
        setError(t('battleUI.connectionLost'));
        return;
      }
      
      target = drone;
      actualTargetZone = 'drone'; // Special zone identifier for drones
    } else {
      // Determine if the target zone belongs to opponent or player
      // Zone names starting with 'opponent_' are opponent zones
      const isOpponentZone = targetZone.startsWith('opponent_');
      const normalizedZone = targetZone.replace('opponent_', '');
      
      if (isOpponentZone) {
        // Attacking opponent unit
        target = opponent?.playedOrders?.[normalizedZone]?.[targetIdx];
        actualTargetZone = normalizedZone;
      } else {
        // Attacking friendly unit (rare, but possible with confuse)
        target = you?.playedOrders?.[normalizedZone]?.[targetIdx];
        actualTargetZone = normalizedZone;
      }
      
      if (!target) {
        console.error('No target found', { targetZone, normalizedZone, targetIdx, isOpponentZone });
        setError(t('battleUI.connectionLost'));
        return;
      }
    }

    // ✅ OOR VALIDATION: Check if target has Out of Reach status
    // CRITICAL: OOR units cannot be attacked by ANYONE, regardless of Blitz
    // Blitz only allows attacking the same turn you're deployed, NOT bypassing OOR
    if (target && target.isOOR) {
      setError(`Cannot attack ${target.name} - target has Out of Reach status. OOR units cannot be attacked.`);
      return; // Block the attack
    }

    // Send attack action with instanceIds
    // NOTE: Animation will be triggered only after backend confirms successful attack
    // This prevents animations for illegal attacks (e.g., attacking protected units)
    const attackMessage = {
      type: "play-turn",
      gameId,
      action: {
        type: "attackOrder",
        attackerInstanceId: attacker.instanceId,
        targetInstanceId: target?.instanceId,
        targetZone: actualTargetZone,
      },
    };

    // ✅ FIX: Check both ws state and socket ref for browser refresh scenarios
    const activeSocket = ws || currentSocketRef.current;
    if (!activeSocket || activeSocket.readyState !== 1) {
      console.error('❌ attackOrder blocked: WebSocket not ready', { hasWs: !!ws, hasRef: !!currentSocketRef.current, wsReady: ws?.readyState, refReady: currentSocketRef.current?.readyState });
      setError(t('battleUI.connectionLost'));
      attackInProgressRef.current = false;
      return;
    }
    try {
      activeSocket.send(JSON.stringify(attackMessage));
    } catch (error) {
      console.error('❌ ATTACK ORDER SEND FAILED:', error);
      setError('Failed to send attack order');
      attackInProgressRef.current = false;
      return;
    }
    
    // Mark attack as in progress (using ref for synchronous update)
    attackInProgressRef.current = true;
    setSelectedAttacker(null);
    setAttackMode(false);
    
    // Store the target that was just attacked - expand will stay disabled for this target until mouse leaves
    if (targetIdx !== null) {
      // Unit attack - store target info
      const targetZoneForTracking = targetZone.startsWith('opponent_') ? targetZone : `opponent_${targetZone}`;
      setLastAttackedTarget({ zone: targetZoneForTracking, idx: targetIdx });
    } else if (targetZone === 'general') {
      // General attack - clear last attacked target (general doesn't have expand anyway)
      setLastAttackedTarget(null);
    }
    
    // Store pending attack info for animation (will be used if attack succeeds)
    // This allows us to trigger animation only after backend confirms success
    pendingAttackInfoRef.current = {
      attacker,
      attackerZone,
      attackerIndex: attackerIdx,
      attackerPlayerId: userId,
      targetZone: actualTargetZone,
      targetIndex: targetIdx,
      targetPlayerId: targetZone === 'general' ? opponentId : (targetZone.startsWith('opponent_') ? opponentId : userId),
      timestamp: Date.now(), // Store timestamp for timeout check
    };
  };

  const attackGeneral = (attackerZone, attackerIdx) => {
    attackOrder(attackerZone, attackerIdx, 'general', null);
  };

  // Unit replacement handlers (for full zone replacement)
  const handleReplaceUnit = (targetUnit) => {
    if (!yourTurn || !ws || ws.readyState !== 1 || !gameId || !selectedReplaceInstanceId) {
      setError(t('battleUI.connectionLost'));
      return;
    }

    // Send replace action to backend
    ws.send(JSON.stringify({
      type: 'play-turn',
      gameId,
      action: {
        type: 'replaceUnit',
        selectedUnitId: selectedReplaceUnitId,
        selectedInstanceId: selectedReplaceInstanceId,
        targetUnitId: targetUnit.unit ? targetUnit.unit.id : targetUnit.id,
        targetInstanceId: targetUnit.instanceId,
        targetZone: targetUnit.zone,
        targetIndex: targetUnit.index
      }
    }));

    // Clear replacement state
    setReplaceTargetMode(false);
    setSelectedReplaceUnitId(null);
    setSelectedReplaceInstanceId(null);
    setReplaceTargetZone(null);
    setClickedUnitForReplace(null);
    
    // Clear hand selection
    setSelectedOrderId(null);
    setSelectedOrderInstanceId(null);
    
    // Clear all visual states to ensure proper sizing
    setHoveredPlayed({ zone: null, idx: null });
    setSelectedAttacker(null);
    setAttackMode(false);
    
    setError('');
  };

  const handleCancelReplace = () => {
    // Clear replacement state
    setReplaceTargetMode(false);
    setSelectedReplaceUnitId(null);
    setSelectedReplaceInstanceId(null);
    setReplaceTargetZone(null);
    setClickedUnitForReplace(null);
    
    // Clear hand selection
    setSelectedOrderId(null);
    setSelectedOrderInstanceId(null);
    
    // Clear all visual states
    setHoveredPlayed({ zone: null, idx: null });
    setSelectedAttacker(null);
    setAttackMode(false);
    
    setError('');
  };

  // Portal displacement (War Effort): use one charge to move unit to the other zone
  const handlePortalDisplacement = (unit, targetZone) => {
    if (!yourTurn || !ws || ws.readyState !== 1 || !gameId) {
      setError(t('battleUI.connectionLost'));
      return;
    }
    if (!unit?.instanceId || (targetZone !== 'front' && targetZone !== 'flank')) return;
    ws.send(JSON.stringify({
      type: 'play-turn',
      gameId,
      action: { type: 'portalDisplacement', instanceId: unit.instanceId, targetZone }
    }));
    setError('');
  };

  // Drone replacement handlers (for full drone zone replacement)
  const handleReplaceDrone = (targetDrone) => {
    if (!yourTurn || !ws || ws.readyState !== 1 || !gameId || !selectedReplaceDroneInstanceId) {
      setError(t('battleUI.connectionLost'));
      return;
    }

    // Send replace action to backend
    ws.send(JSON.stringify({
      type: 'play-turn',
      gameId,
      action: {
        type: 'replaceDrone',
        selectedDroneId: selectedReplaceDroneId,
        selectedInstanceId: selectedReplaceDroneInstanceId,
        targetDroneId: targetDrone.id,
        targetInstanceId: targetDrone.instanceId,
        targetIndex: targetDrone.index
      }
    }));

    // Clear replacement state
    setReplaceDroneTargetMode(false);
    setSelectedReplaceDroneId(null);
    setSelectedReplaceDroneInstanceId(null);
    setClickedDroneForReplace(null);
    
    // Clear hand selection
    setSelectedOrderId(null);
    setSelectedOrderInstanceId(null);
    
    setError('');
  };

  const handleCancelReplaceDrone = () => {
    // Clear replacement state
    setReplaceDroneTargetMode(false);
    setSelectedReplaceDroneId(null);
    setSelectedReplaceDroneInstanceId(null);
    setClickedDroneForReplace(null);
    
    // Clear hand selection
    setSelectedOrderId(null);
    setSelectedOrderInstanceId(null);
    
    setError('');
  };

  // Counter Strike mode handler - don't use carousel mechanism
  const handleCounterStrikeModeActivated = (order) => {
    
    // Check if this is a strategem - strategems should be played immediately
    if (order.type === 'Strategem') {
      // For strategems, play immediately with a dummy zone (backend will handle it correctly)
      // The backend will either play it immediately or enter targeting mode
      playCounterStrike(order.id, 'front', order.instanceId);
      // Clear any existing selected counter strike order to avoid confusion
      setSelectedCounterStrikeOrder(null);
      return;
    }
    
    // For units and other orders, store for zone selection
    setSelectedCounterStrikeOrder(order);
  };


  // Strategem Counter Strike functions
  const executeStrategemCounterStrike = async (strategemIndex) => {
    if (!gameId) {
      setError(t('battleUI.connectionLost'));
      return;
    }

    try {
      const response = await fetch(`${API_URL}/execute-strategem-counter-strike`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          gameId,
          strategemIndex
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to execute strategem Counter Strike');
      }

      const result = await response.json();
      
      // The strategem will now enter targeting mode
      // The game state will be updated via WebSocket
    } catch (error) {
      console.error('Error executing strategem Counter Strike:', error);
      setError(translateBackendError(error.message));
    }
  };

  const keepStrategemCounterStrike = async (strategemIndex) => {
    if (!gameId) {
      setError(t('battleUI.connectionLost'));
      return;
    }

    try {
      const response = await fetch(`${API_URL}/keep-strategem-counter-strike`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          gameId,
          strategemIndex
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to keep strategem Counter Strike');
      }

      const result = await response.json();
      
      // The strategem will be added to hand
      // The game state will be updated via WebSocket
    } catch (error) {
      console.error('Error keeping strategem Counter Strike:', error);
      setError(translateBackendError(error.message));
    }
  };

  // Use effective state/userId for spectator mode support
  // ✅ CRITICAL: Memoize activeGameState so it only changes when gameState/effectiveGameState actually changes
  // This ensures 'you' and 'opponent' memos update properly when state changes
  const activeGameState = useMemo(() => {
    return isSpectating ? effectiveGameState : gameState;
  }, [isSpectating, effectiveGameState, gameState]);
  // ✅ FIX: For autobattler mode, use playerBotId instead of userId
  // In autobattler mode, the actual players are bot IDs, not the user ID
  // Priority: autobattler mode > friend spectating > normal player
  const activeUserId = useMemo(() => {
    if (activeGameState?.isAutobattler && activeGameState?.playerBotId) {
      return activeGameState.playerBotId;
    }
    if (isSpectating) {
      return effectiveUserId;
    }
    return userId;
  }, [activeGameState?.isAutobattler, activeGameState?.playerBotId, isSpectating, effectiveUserId, userId]);
  
  // Debug spectator mode - log when state changes
  useEffect(() => {
    if (isSpectating && spectatingGameState) {
    }
  }, [isSpectating, spectatingGameState, effectiveGameState, activeGameState, activeUserId, spectatingPlayerId]);
  
  // ✅ FIX: Clean up invalid pending attacks that can't be rendered
  // This prevents ghost animations from staying on screen when attacker is not found
  useEffect(() => {
    if (!attackAnimations?.pendingAttacks?.length || !activeGameState?.state) {
      return;
    }
    
    // Check each pending attack and clean up any that can't be rendered
    attackAnimations.pendingAttacks.forEach(([attackId, pendingAttack]) => {
      const outcome = pendingAttack?.outcome;
      if (!outcome) {
        attackAnimations.completeAnimation(attackId);
        return;
      }
      
      const attackerPlayer = activeGameState.state[outcome.attackerPlayerId];
      if (!attackerPlayer) {
        attackAnimations.completeAnimation(attackId);
        return;
      }
      
      const targetPlayer = activeGameState.state[outcome.targetPlayerId];
      if (!targetPlayer) {
        attackAnimations.completeAnimation(attackId);
        return;
      }
      
      // Find attacker by instanceId or index
      let attacker = null;
      if (attackerPlayer.playedOrders?.[outcome.attackerZone]) {
        if (outcome.attackerInstanceId) {
          attacker = attackerPlayer.playedOrders[outcome.attackerZone].find(
            u => u && u.instanceId === outcome.attackerInstanceId
          );
        }
        if (!attacker && outcome.attackerIndex >= 0) {
          attacker = attackerPlayer.playedOrders[outcome.attackerZone][outcome.attackerIndex];
        }
      }
      
      if (!attacker) {
        attackAnimations.completeAnimation(attackId);
        return;
      }
    });
  }, [attackAnimations?.pendingAttacks, activeGameState?.state, attackAnimations]);
  
  // ✅ SPECTATOR FIX: Handle attack-initiated messages for spectators
  useEffect(() => {
    // Only log when there's actually a pending attack (not on every render)
    if (process.env.NODE_ENV === 'development' && pendingAttackInitiated) {
    }
    
    if (isSpectating && pendingAttackInitiated && attackAnimations) {
      
      // Play destroy sound when a drone is destroyed by attack
      if (pendingAttackInitiated.attackOutcome?.targetDestroyed && pendingAttackInitiated.attackOutcome?.targetZone === 'opponent_drone') {
        playSoundEffect('/sounds/destroy.mp3', 0.6);
      }
      // Register attack for animation (same as regular players)
      if (pendingAttackInitiated.attackId && pendingAttackInitiated.attackOutcome) {
        try {
          attackAnimations.registerAttack(
            pendingAttackInitiated.attackId,
            pendingAttackInitiated.attackOutcome,
            pendingAttackInitiated.isBotAttack || false
          );
        } catch (error) {
          console.error('❌ Error registering spectator attack animation:', error);
        }
      } else {
        console.warn('⚠️ Cannot register spectator attack: missing attackId or attackOutcome', {
          hasAttackId: !!pendingAttackInitiated.attackId,
          hasAttackOutcome: !!pendingAttackInitiated.attackOutcome
        });
      }
      
      // Clear the pending attack-initiated message after processing
      setPendingAttackInitiated(null);
    }
  }, [isSpectating, pendingAttackInitiated, attackAnimations, setPendingAttackInitiated]);
  
  // ✅ SPECTATOR FIX: Handle emote messages for spectators
  useEffect(() => {
    if (isSpectating && pendingEmote && pendingEmote.text) {
      
      // ✅ FIX: Normalize types for comparison (handle number/string mismatch)
      const normalizedFromPlayerId = String(pendingEmote.fromPlayerId);
      const normalizedActiveUserId = String(activeUserId);
      
      // Determine if emote is from the player being watched or their opponent
      const isFromWatchedPlayer = normalizedFromPlayerId === normalizedActiveUserId;
      
      
      if (isFromWatchedPlayer) {
        // Emote from the player being watched - show as "your" emote
        setCurrentEmoteBubble({
          text: pendingEmote.text,
          timestamp: pendingEmote.timestamp
        });
        setTimeout(() => {
          setCurrentEmoteBubble(null);
        }, 3000);
      } else {
        // Emote from opponent - show as opponent emote
        setOpponentEmoteBubble({
          text: pendingEmote.text,
          timestamp: pendingEmote.timestamp
        });
        setTimeout(() => {
          setOpponentEmoteBubble(null);
        }, 3000);
      }
      
      // Clear the pending emote after processing
      setPendingEmote(null);
    }
  }, [isSpectating, pendingEmote, activeUserId, spectatingPlayerId, setPendingEmote]);
  
  // Memoize 'you' and 'opponent' to ensure they update when activeGameState changes
  // This is critical for spectator mode to see real-time health updates
  const you = useMemo(() => {
    return activeGameState?.state?.[activeUserId];
  }, [activeGameState, activeUserId]);
  
  const opponentId = useMemo(() => {
    return activeGameState?.players?.find(id => id !== activeUserId);
  }, [activeGameState, activeUserId]);
  
  const opponent = useMemo(() => {
    return opponentId ? activeGameState?.state?.[opponentId] : null;
  }, [activeGameState, opponentId]);

  const isDisorientationHandLocked = useMemo(() => {
    const fortTypes = Array.isArray(activeGameState?.warEffortFortificationTypes)
      ? activeGameState.warEffortFortificationTypes
      : (activeGameState?.warEffortFortificationType ? [activeGameState.warEffortFortificationType] : []);
    if (!fortTypes.includes('disorientation')) return false;

    const defenderId = activeGameState?.warEffortDefenderPlayerId;
    if (!defenderId) return false;
    const players = activeGameState?.players;
    if (!Array.isArray(players) || players.length < 2) return false;

    const attackerId = players.find((id) => String(id) !== String(defenderId));
    if (!attackerId || String(attackerId) !== String(activeUserId)) return false;

    const turnsTaken = activeGameState?.state?.[activeUserId]?.turnsTaken;
    if (typeof turnsTaken !== 'number') return false;
    return turnsTaken <= 2;
  }, [activeGameState, activeUserId]);

  // Mobile-friendly card click handler (extracted for reuse)
  const handleCardClick = useCallback((order, e) => {
    // Allow during counter strike deploy targeting (e.g. selecting Bulwark target from hand context)
    const isCounterStrikeDeployTargeting = deployTargetMode && selectedDeployOrderId && !yourTurn;
    if (!yourTurn && !isCounterStrikeDeployTargeting) return;
    if (isDisorientationHandLocked) {
      setError(t('battleUI.disorientationLock') || 'Disorientation active: your hand is deactivated for your first 2 turns');
      return;
    }

    // Handle deploy targeting first
    if (deployTargetMode && selectedDeployOrderId) {
      const isEnemyUnit = order.zone && order.zone.startsWith('opponent_');
      const targetUnitId = isEnemyUnit ? 'opponent' : 'friendly';
      const targetZone = isEnemyUnit ? order.zone : order.zone;
      handleTargetDeploy(targetZone, order.idx, targetUnitId);
      return;
    }
    
    if (order.type === 'Weapon') {
      // For weapons, enter targeting mode
      if (selectedWeaponInstanceId === order.instanceId && weaponTargetMode) {
        // Deselect if already in targeting mode
        setWeaponTargetMode(false);
        setSelectedWeaponId(null);
        setSelectedWeaponInstanceId(null);
      } else {
        const orderCost = order.cost !== undefined ? order.cost : (order.hasInstanceModifications && order.instanceCost !== undefined ? order.instanceCost : order.originalCost || 0);
        if (gameState?.state?.[userId] && gameState.state[userId].intelligence >= orderCost) {
          setWeaponTargetMode(true);
          setSelectedWeaponId(order.id);
          setSelectedWeaponInstanceId(order.instanceId);
          // Show message on mobile
          if (isMobile) {
            setError(t('battleUI.selectTargetWeapon') || 'Select a unit to attach this weapon to');
            setTimeout(() => setError(''), 3000);
          }
        } else {
          setError(`${t('battleUI.notEnoughIntelligence')} ${order.name}! ${t('battleUI.need')} ${orderCost}, ${t('battleUI.have')} ${gameState?.state?.[userId]?.intelligence || 0}`);
        }
      }
    } else if (order.type === 'Strategem') {
      // For strategems, check if already in targeting mode (backend has set needsStrategemTarget)
      if (selectedStrategemId === order.id && strategemTargetMode) {
        // Deselect if already in targeting mode
        setStrategemTargetMode(false);
        setSelectedStrategemId(null);
        setStrategemTargetInfo(null);
      } else {
        // ✅ FIX: Always call playStrategem() first - the backend will set needsStrategemTarget
        // if targeting is needed, and the game state sync will enter targeting mode
        const orderCost = order.hasInstanceModifications && order.instanceCost !== undefined ? order.instanceCost : order.cost;
        if (gameState?.state?.[userId] && gameState.state[userId].intelligence < orderCost) {
          setError(`${t('battleUI.notEnoughIntelligence')} ${order.name}! ${t('battleUI.need')} ${orderCost}, ${t('battleUI.have')} ${gameState.state[userId].intelligence}`);
        } else {
          playStrategem(order.id, order.instanceId);
        }
      }
    } else if (order.type === 'Drone') {
      // Drones cannot be selected by clicking
      return;
    } else {
      // ✅ FIX: Cancel weapon targeting when selecting a unit from hand
      if (weaponTargetMode && selectedWeaponId) {
        setWeaponTargetMode(false);
        setSelectedWeaponId(null);
        setSelectedWeaponInstanceId(null);
      }
      
      // ✅ FIX: Cancel strategem targeting when selecting a unit from hand
      if (strategemTargetMode && selectedStrategemId) {
        setStrategemTargetMode(false);
        setSelectedStrategemId(null);
        setStrategemTargetInfo(null);
      }
      
      // ✅ FIX: Cancel drone targeting when selecting a unit from hand
      if (droneTargetMode && selectedDroneId) {
        setDroneTargetMode(false);
        setSelectedDroneId(null);
        setSelectedDroneInstanceId(null);
      }
      
      // For other orders, toggle selection
      const newInstanceId = order.instanceId === selectedOrderInstanceId ? null : order.instanceId;
      const newOrderId = order.id === selectedOrderId ? null : order.id;
      if (order) {
        const orderCost = order.hasInstanceModifications && order.instanceCost !== undefined ? order.instanceCost : order.cost;
        if (newInstanceId && gameState?.state?.[userId] && gameState.state[userId].intelligence < orderCost) {
          setError(`${t('battleUI.notEnoughIntelligence')} ${order.name}! ${t('battleUI.need')} ${orderCost}, ${t('battleUI.have')} ${gameState.state[userId].intelligence}`);
        } else {
          setSelectedOrderInstanceId(newInstanceId);
          setSelectedOrderId(newOrderId);
        }
      }
    }
  }, [yourTurn, deployTargetMode, selectedDeployOrderId, selectedWeaponInstanceId, weaponTargetMode, selectedWeaponId, selectedStrategemId, strategemTargetMode, strategemTargetInfo, droneTargetMode, selectedDroneId, selectedDroneInstanceId, selectedOrderInstanceId, selectedOrderId, gameState, userId, t, isMobile, targetingData, handleTargetDeploy, setWeaponTargetMode, setSelectedWeaponId, setSelectedWeaponInstanceId, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, setDroneTargetMode, setSelectedDroneId, setSelectedDroneInstanceId, setSelectedOrderInstanceId, setSelectedOrderId, playStrategem, getStrategemTargetingRules, strategemNeedsTargeting, setError, isDisorientationHandLocked]);

  // Mobile touch handler (prevents double-firing)
  const handleCardTouch = useCallback((order, e) => {
    // Use the same logic as click handler
    handleCardClick(order, e);
  }, [handleCardClick]);

  // Resolve drop target for strategem drag: elementUnderPoint -> general | zone | unit | battlefield
  // Use elementsFromPoint and check every element in the stack (top to bottom); the topmost is often
  // a container (#root, etc.) with no data attrs, so we must try each until we find a battlefield element.
  const resolveStrategemDropTarget = useCallback((clientX, clientY) => {
    if (typeof document === 'undefined') return null;
    const elements = typeof document.elementsFromPoint === 'function'
      ? document.elementsFromPoint(clientX, clientY)
      : [document.elementFromPoint(clientX, clientY)].filter(Boolean);
    if (!elements.length) {
      return { type: 'battlefield' };
    }
    let fallbackZone = null; // Prefer unit over zone: only return zone if no element resolved to unit
    for (let i = 0; i < elements.length; i++) {
      const top = elements[i];
      if (top.closest?.('.deploy-drag-overlay')) continue;
      let el = top;
      let steps = 0;
      while (el && el !== document.body) {
        steps++;
        const zoneAttr = el.getAttribute('data-zone');
        if (zoneAttr === 'player_general') {
          return { type: 'general', generalPlayer: 'friendly' };
        }
        if (zoneAttr === 'opponent_general') {
          return { type: 'general', generalPlayer: 'opponent' };
        }
        const slotZone = el.getAttribute('data-slot-zone');
        const slotIndex = el.getAttribute('data-slot-index');
        const unitKey = el.getAttribute('data-unit-key');
        if (slotZone != null && slotIndex != null && unitKey) {
          const idx = parseInt(slotIndex, 10);
          if (!Number.isNaN(idx)) {
            const opponentId = Object.keys(gameState?.state || {}).find(id => id !== userId);
            const isYour = slotZone === 'your_front' || slotZone === 'your_flank';
            const playerId = isYour ? userId : opponentId;
            const zoneKey = slotZone === 'your_front' || slotZone === 'opponent_front' ? 'front' : 'flank';
            const zoneUnits = gameState?.state?.[playerId]?.playedOrders?.[zoneKey] || [];
            const unit = zoneUnits[idx];
            if (unit && unit.instanceId) {
              return { type: 'unit', zone: slotZone, index: idx, unitId: unit.id, instanceId: unit.instanceId };
            }
          }
        }
        const zoneKeyAttr = el.getAttribute('data-zone-key');
        if (zoneKeyAttr) {
          if (!fallbackZone) fallbackZone = { type: 'zone', zone: zoneKeyAttr };
        }
        el = el.parentElement;
      }
    }
    if (fallbackZone) {
      return fallbackZone;
    }
    return { type: 'battlefield' };
  }, [gameState, userId]);

  // Desktop: custom mouse-based drag (no HTML5 DnD) so drag-to-deploy works reliably
  const startCarouselMouseDrag = useCallback((order, clientX, clientY) => {
    document.body.classList.add('deploy-drag-tracking');
    if (window.getSelection) window.getSelection().removeAllRanges();
    const cleanupTimeout = setTimeout(() => {
      document.body.classList.remove('deploy-drag-tracking');
    }, 8000);
    mouseDragPendingRef.current = { order, startX: clientX, startY: clientY };
    clickHandledByMouseupRef.current = false;
    dragStartedRef.current = false;
    const DRAG_THRESHOLD_PX = 5;
    const onMove = (e) => {
      const pending = mouseDragPendingRef.current;
      if (!pending) return;
      const dx = e.clientX - pending.startX;
      const dy = e.clientY - pending.startY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (!dragStartedRef.current && dist > DRAG_THRESHOLD_PX) {
        dragStartedRef.current = true;
        setDragDeployState({
          order: pending.order,
          originX: pending.startX,
          originY: pending.startY,
          currentX: e.clientX,
          currentY: e.clientY,
        });
      } else if (dragStartedRef.current) {
        setDragDeployState(prev => prev ? { ...prev, currentX: e.clientX, currentY: e.clientY } : null);
      }
    };
    const onContextMenu = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
    };
    const onUp = (e) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('contextmenu', onContextMenu);
      clearTimeout(cleanupTimeout);
      document.body.classList.remove('deploy-drag-tracking');
      if (window.getSelection) window.getSelection().removeAllRanges();
      const pending = mouseDragPendingRef.current;
      mouseDragPendingRef.current = null;
      if (e.button === 2) {
        e.preventDefault();
        e.stopPropagation();
        setDragDeployState(null);
        setDraggedOrderId(null);
        setDraggedOrderInstanceId(null);
        dragStartedRef.current = false;
        const preventNextContextMenu = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          document.removeEventListener('contextmenu', preventNextContextMenu, true);
        };
        document.addEventListener('contextmenu', preventNextContextMenu, true);
        return;
      }
      if (dragStartedRef.current && pending) {
        const order = pending.order;
        const isDeployable = order?.type !== 'Drone' && order?.type !== 'Strategem' && order?.type !== 'Weapon';
        if (order && isDeployable) {
          const zone = e.clientX < window.innerWidth / 2 ? 'front' : 'flank';
          const zoneCount = you?.playedOrders?.[zone]?.filter(u => u !== null).length ?? 0;
          if (zoneCount >= 5) {
            // Same as click: select unit from hand then synthetic click on slot → replace mode (Replace/Cancel)
            setSelectedOrderInstanceId(order.instanceId);
            setSelectedOrderId(order.id);
            setPendingDeployFullZoneDrop({ clientX: e.clientX, clientY: e.clientY });
          } else {
            playOrder(order.id, zone, null, order.instanceId);
            setSelectedOrderId(null);
            setSelectedOrderInstanceId(null);
          }
        }
        setDragDeployState(null);
        setDraggedOrderId(null);
        setDraggedOrderInstanceId(null);
        dragStartedRef.current = false;
      } else if (pending) {
        clickHandledByMouseupRef.current = true;
        handleCardClick(pending.order, e);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('contextmenu', onContextMenu);
  }, [you?.playedOrders, playOrder, setError, t, setDragDeployState, setSelectedOrderId, setSelectedOrderInstanceId, setDraggedOrderId, setDraggedOrderInstanceId, handleCardClick]);

  // Desktop: strategem drag-and-drop — drop on exact target (general, zone, unit, or anywhere for battlefield)
  const startStrategemMouseDrag = useCallback((order, clientX, clientY) => {
    document.body.classList.add('deploy-drag-tracking');
    if (window.getSelection) window.getSelection().removeAllRanges();
    const cleanupTimeout = setTimeout(() => {
      document.body.classList.remove('deploy-drag-tracking');
    }, 8000);
    strategemMouseDragPendingRef.current = { order, startX: clientX, startY: clientY };
    clickHandledByMouseupRef.current = false;
    strategemDragStartedRef.current = false;
    const DRAG_THRESHOLD_PX = 5;
    const onMove = (e) => {
      const pending = strategemMouseDragPendingRef.current;
      if (!pending) return;
      const dx = e.clientX - pending.startX;
      const dy = e.clientY - pending.startY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (!strategemDragStartedRef.current && dist > DRAG_THRESHOLD_PX) {
        strategemDragStartedRef.current = true;
        setStrategemDragState({
          order: pending.order,
          originX: pending.startX,
          originY: pending.startY,
          currentX: e.clientX,
          currentY: e.clientY,
        });
      } else if (strategemDragStartedRef.current) {
        setStrategemDragState(prev => prev ? { ...prev, currentX: e.clientX, currentY: e.clientY } : null);
      }
    };
    const onContextMenu = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
    };
    const onUp = (e) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('contextmenu', onContextMenu);
      clearTimeout(cleanupTimeout);
      document.body.classList.remove('deploy-drag-tracking');
      if (window.getSelection) window.getSelection().removeAllRanges();
      const pending = strategemMouseDragPendingRef.current;
      strategemMouseDragPendingRef.current = null;
      if (e.button === 2) {
        e.preventDefault();
        e.stopPropagation();
        setStrategemDragState(null);
        strategemDragStartedRef.current = false;
        const preventNextContextMenu = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          document.removeEventListener('contextmenu', preventNextContextMenu, true);
        };
        document.addEventListener('contextmenu', preventNextContextMenu, true);
        return;
      }
      if (strategemDragStartedRef.current && pending) {
        const order = pending.order;
        const rules = getStrategemTargetingRules(order.id, targetingData);
        const needsTarget = strategemNeedsTargeting(order.id, targetingData);
        // Hide overlay first so next-tick elementFromPoint hits the battlefield, not the overlay
        setStrategemDragState(null);
        strategemDragStartedRef.current = false;
        if (!needsTarget || (rules && rules.targeting === 'none')) {
          playStrategem(order.id, order.instanceId);
          return;
        }
        if (!rules) {
          setError(t('battleUI.selectTargetStrategem') || 'Select a target');
          return;
        }
        // Reuse the same code path as click: store drop coordinates and dispatch a synthetic click
        // after backend sets needsStrategemTarget, so the unit/zone/general onClick runs and calls
        // handleTargetingClick / handleZoneClick / general onClick -> targetStrategem.
        pendingStrategemDropTargetRef.current = {
          type: 'click',
          clientX: e.clientX,
          clientY: e.clientY,
          strategemInstanceId: order.instanceId
        };
        playStrategem(order.id, order.instanceId);
      } else if (pending) {
        clickHandledByMouseupRef.current = true;
        handleCardClick(pending.order, e);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('contextmenu', onContextMenu);
  }, [getStrategemTargetingRules, strategemNeedsTargeting, playStrategem, setError, t, handleCardClick, targetingData]);

  // Desktop: weapon drag-and-drop — drop on a unit to attach; reuse click path via synthetic click
  const startWeaponMouseDrag = useCallback((order, clientX, clientY) => {
    document.body.classList.add('deploy-drag-tracking');
    if (window.getSelection) window.getSelection().removeAllRanges();
    const cleanupTimeout = setTimeout(() => {
      document.body.classList.remove('deploy-drag-tracking');
    }, 8000);
    weaponMouseDragPendingRef.current = { order, startX: clientX, startY: clientY };
    clickHandledByMouseupRef.current = false;
    weaponDragStartedRef.current = false;
    const DRAG_THRESHOLD_PX = 5;
    const onMove = (e) => {
      const pending = weaponMouseDragPendingRef.current;
      if (!pending) return;
      const dx = e.clientX - pending.startX;
      const dy = e.clientY - pending.startY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (!weaponDragStartedRef.current && dist > DRAG_THRESHOLD_PX) {
        weaponDragStartedRef.current = true;
        setWeaponDragState({
          order: pending.order,
          originX: pending.startX,
          originY: pending.startY,
          currentX: e.clientX,
          currentY: e.clientY,
        });
      } else if (weaponDragStartedRef.current) {
        setWeaponDragState(prev => prev ? { ...prev, currentX: e.clientX, currentY: e.clientY } : null);
      }
    };
    const onContextMenu = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
    };
    const onUp = (e) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('contextmenu', onContextMenu);
      clearTimeout(cleanupTimeout);
      document.body.classList.remove('deploy-drag-tracking');
      if (window.getSelection) window.getSelection().removeAllRanges();
      const pending = weaponMouseDragPendingRef.current;
      weaponMouseDragPendingRef.current = null;
      if (e.button === 2) {
        e.preventDefault();
        e.stopPropagation();
        setWeaponDragState(null);
        weaponDragStartedRef.current = false;
        const preventNextContextMenu = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          document.removeEventListener('contextmenu', preventNextContextMenu, true);
        };
        document.addEventListener('contextmenu', preventNextContextMenu, true);
        return;
      }
      if (weaponDragStartedRef.current && pending && pending.order?.type === 'Weapon') {
        setWeaponDragState(null);
        weaponDragStartedRef.current = false;
        const order = pending.order;
        const orderCost = order.cost !== undefined ? order.cost : (order.hasInstanceModifications && order.instanceCost !== undefined ? order.instanceCost : order.originalCost || 0);
        if (gameState?.state?.[userId] && gameState.state[userId].intelligence >= orderCost) {
          // Same pattern as strategem: set targeting mode then store coords so effect can dispatch synthetic click.
          // (Strategem gets mode from backend; weapon has no backend so we set it here.)
          setWeaponTargetMode(true);
          setSelectedWeaponId(order.id);
          setSelectedWeaponInstanceId(order.instanceId);
          setPendingWeaponDropCoords({
            clientX: e.clientX,
            clientY: e.clientY
          });
        } else {
          setError(`${t('battleUI.notEnoughIntelligence')} ${order.name}! ${t('battleUI.need')} ${orderCost}, ${t('battleUI.have')} ${gameState?.state?.[userId]?.intelligence || 0}`);
        }
      } else if (pending) {
        clickHandledByMouseupRef.current = true;
        handleCardClick(pending.order, e);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('contextmenu', onContextMenu);
  }, [gameState, userId, setError, t, handleCardClick]);

  // Desktop: drone drag-and-drop — drop on player drone zone (empty slot) to deploy; same path as Deploy button (playDrone).
  const startDroneMouseDrag = useCallback((order, clientX, clientY) => {
    document.body.classList.add('deploy-drag-tracking');
    if (window.getSelection) window.getSelection().removeAllRanges();
    const cleanupTimeout = setTimeout(() => {
      document.body.classList.remove('deploy-drag-tracking');
    }, 8000);
    droneMouseDragPendingRef.current = { order, startX: clientX, startY: clientY };
    clickHandledByMouseupRef.current = false;
    droneDragStartedRef.current = false;
    const DRAG_THRESHOLD_PX = 5;
    const onMove = (e) => {
      const pending = droneMouseDragPendingRef.current;
      if (!pending) return;
      const dx = e.clientX - pending.startX;
      const dy = e.clientY - pending.startY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (!droneDragStartedRef.current && dist > DRAG_THRESHOLD_PX) {
        droneDragStartedRef.current = true;
        setDroneDragState({
          order: pending.order,
          originX: pending.startX,
          originY: pending.startY,
          currentX: e.clientX,
          currentY: e.clientY,
        });
      } else if (droneDragStartedRef.current) {
        setDroneDragState(prev => prev ? { ...prev, currentX: e.clientX, currentY: e.clientY } : null);
      }
    };
    const onContextMenu = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
    };
    const onUp = (e) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('contextmenu', onContextMenu);
      clearTimeout(cleanupTimeout);
      document.body.classList.remove('deploy-drag-tracking');
      if (window.getSelection) window.getSelection().removeAllRanges();
      const pending = droneMouseDragPendingRef.current;
      droneMouseDragPendingRef.current = null;
      if (e.button === 2) {
        e.preventDefault();
        e.stopPropagation();
        setDroneDragState(null);
        droneDragStartedRef.current = false;
        const preventNextContextMenu = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          document.removeEventListener('contextmenu', preventNextContextMenu, true);
        };
        document.addEventListener('contextmenu', preventNextContextMenu, true);
        return;
      }
      if (droneDragStartedRef.current && pending && pending.order?.type === 'Drone') {
        setDroneDragState(null);
        droneDragStartedRef.current = false;
        const order = pending.order;
        const orderCost = order.cost !== undefined ? order.cost : (order.hasInstanceModifications && order.instanceCost !== undefined ? order.instanceCost : order.originalCost || 0);
        if (gameState?.state?.[userId] && gameState.state[userId].intelligence >= orderCost) {
          setPendingDroneDropCoords({
            clientX: e.clientX,
            clientY: e.clientY,
            orderId: order.id,
            orderInstanceId: order.instanceId,
          });
        } else {
          setError(`${t('battleUI.notEnoughIntelligence')} ${order.name}! ${t('battleUI.need')} ${orderCost}, ${t('battleUI.have')} ${gameState?.state?.[userId]?.intelligence || 0}`);
        }
      } else if (pending) {
        clickHandledByMouseupRef.current = true;
        handleCardClick(pending.order, e);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('contextmenu', onContextMenu);
  }, [gameState, userId, setError, t, handleCardClick]);

  // Desktop: attack drag-and-drop — drag friendly unit (front/flank), drop on opponent unit or general; reuse click path via synthetic click
  const startAttackMouseDrag = useCallback((zone, idx, clientX, clientY) => {
    document.body.classList.add('deploy-drag-tracking');
    if (window.getSelection) window.getSelection().removeAllRanges();
    const cleanupTimeout = setTimeout(() => {
      document.body.classList.remove('deploy-drag-tracking');
    }, 8000);
    const attacker = { zone, idx };
    attackMouseDragPendingRef.current = { attacker, startX: clientX, startY: clientY };
    attackDragStartedRef.current = false;
    const DRAG_THRESHOLD_PX = 5;
    const onMove = (e) => {
      const pending = attackMouseDragPendingRef.current;
      if (!pending) return;
      const dx = e.clientX - pending.startX;
      const dy = e.clientY - pending.startY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (!attackDragStartedRef.current && dist > DRAG_THRESHOLD_PX) {
        attackDragStartedRef.current = true;
        setAttackDragState({
          attacker: pending.attacker,
          originX: pending.startX,
          originY: pending.startY,
          currentX: e.clientX,
          currentY: e.clientY,
        });
      } else if (attackDragStartedRef.current) {
        setAttackDragState(prev => prev ? { ...prev, currentX: e.clientX, currentY: e.clientY } : null);
      }
    };
    const onContextMenu = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
    };
    const onUp = (e) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('contextmenu', onContextMenu);
      clearTimeout(cleanupTimeout);
      document.body.classList.remove('deploy-drag-tracking');
      if (window.getSelection) window.getSelection().removeAllRanges();
      const pending = attackMouseDragPendingRef.current;
      attackMouseDragPendingRef.current = null;
      if (e.button === 2) {
        e.preventDefault();
        e.stopPropagation();
        setAttackDragState(null);
        attackDragStartedRef.current = false;
        const preventNextContextMenu = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          document.removeEventListener('contextmenu', preventNextContextMenu, true);
        };
        document.addEventListener('contextmenu', preventNextContextMenu, true);
        return;
      }
      if (attackDragStartedRef.current && pending?.attacker) {
        setAttackDragState(null);
        attackDragStartedRef.current = false;
        setSelectedAttacker({ zone: pending.attacker.zone, idx: pending.attacker.idx });
        setAttackMode(true);
        setPendingAttackDropCoords({
          clientX: e.clientX,
          clientY: e.clientY,
        });
      }
      // If we didn't drag, no pendingAttackDropCoords; the same mouseup will trigger click on the unit slot (select attacker)
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('contextmenu', onContextMenu);
  }, []);

  // Desktop: drone-with-charges targeting drag — drag deployed drone (that needs targeting), drop on yellow-bordered target to execute effect
  const startDroneTargetMouseDrag = useCallback((drone, clientX, clientY) => {
    document.body.classList.add('deploy-drag-tracking');
    if (window.getSelection) window.getSelection().removeAllRanges();
    const cleanupTimeout = setTimeout(() => {
      document.body.classList.remove('deploy-drag-tracking');
    }, 8000);
    droneTargetMouseDragPendingRef.current = { drone, startX: clientX, startY: clientY };
    droneTargetDragStartedRef.current = false;
    const DRAG_THRESHOLD_PX = 5;
    const onMove = (e) => {
      const pending = droneTargetMouseDragPendingRef.current;
      if (!pending) return;
      const dx = e.clientX - pending.startX;
      const dy = e.clientY - pending.startY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (!droneTargetDragStartedRef.current && dist > DRAG_THRESHOLD_PX) {
        droneTargetDragStartedRef.current = true;
        setDroneTargetDragState({
          drone: pending.drone,
          originX: pending.startX,
          originY: pending.startY,
          currentX: e.clientX,
          currentY: e.clientY,
        });
      } else if (droneTargetDragStartedRef.current) {
        setDroneTargetDragState(prev => prev ? { ...prev, currentX: e.clientX, currentY: e.clientY } : null);
      }
    };
    const onContextMenu = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
    };
    const onUp = (e) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('contextmenu', onContextMenu);
      clearTimeout(cleanupTimeout);
      document.body.classList.remove('deploy-drag-tracking');
      if (window.getSelection) window.getSelection().removeAllRanges();
      const pending = droneTargetMouseDragPendingRef.current;
      droneTargetMouseDragPendingRef.current = null;
      if (e.button === 2) {
        e.preventDefault();
        e.stopPropagation();
        setDroneTargetDragState(null);
        droneTargetDragStartedRef.current = false;
        const preventNextContextMenu = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          document.removeEventListener('contextmenu', preventNextContextMenu, true);
        };
        document.addEventListener('contextmenu', preventNextContextMenu, true);
        return;
      }
      if (droneTargetDragStartedRef.current && pending?.drone) {
        setDroneTargetDragState(null);
        droneTargetDragStartedRef.current = false;
        setSelectedDroneId(pending.drone.id);
        setSelectedDroneInstanceId(pending.drone.instanceId);
        setDroneTargetMode(true);
        setPendingDroneTargetDropCoords({
          clientX: e.clientX,
          clientY: e.clientY,
        });
      }
      // If we didn't drag, no pendingDroneTargetDropCoords; the same mouseup will trigger click on the drone (activateDrone → may enter target mode)
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('contextmenu', onContextMenu);
  }, []);

  // Effective targeting state for highlights: show yellow targeting boxes during drag as well as click
  const isDeployDragActive = !isContainerMobile && dragDeployState?.order && dragDeployState.order.type !== 'Strategem' && dragDeployState.order.type !== 'Drone' && dragDeployState.order.type !== 'Weapon';
  const isDroneDragActive = !isContainerMobile && droneDragState?.order?.type === 'Drone';
  const isDroneTargetDragActive = !isContainerMobile && droneTargetDragState?.drone;
  const isWeaponDragActive = !isContainerMobile && weaponDragState?.order?.type === 'Weapon';
  const isAttackDragActive = !isContainerMobile && attackDragState?.attacker;
  const effectiveDroneTargetMode = (droneTargetMode && selectedDroneId) || isDroneTargetDragActive;
  const effectiveSelectedDroneId = (droneTargetMode && selectedDroneId) ? selectedDroneId : (isDroneTargetDragActive && droneTargetDragState?.drone ? droneTargetDragState.drone.id : null);
  const effectiveWeaponTargetMode = (weaponTargetMode && selectedWeaponId) || isWeaponDragActive;
  const effectiveAttackMode = (attackMode && selectedAttacker) || isAttackDragActive;
  const effectiveSelectedAttacker = (attackMode && selectedAttacker) ? selectedAttacker : (isAttackDragActive && attackDragState?.attacker ? { zone: attackDragState.attacker.zone, idx: attackDragState.attacker.idx } : null);
  const isStrategemDragActive = !isContainerMobile && strategemDragState?.order && strategemNeedsTargeting(strategemDragState.order.id, targetingData);
  const effectiveStrategemTargetMode = (strategemTargetMode && selectedStrategemId) || isStrategemDragActive;
  const effectiveSelectedStrategemId = (strategemTargetMode && selectedStrategemId) ? selectedStrategemId : (isStrategemDragActive ? strategemDragState?.order?.id : null);
  const effectiveStrategemTargetInfo = (strategemTargetMode && strategemTargetInfo) ? strategemTargetInfo : (isStrategemDragActive && strategemDragState?.order ? (() => {
    const rules = getStrategemTargetingRules(strategemDragState.order.id, targetingData);
    return rules ? { type: rules.targeting === 'zone' ? 'zone' : 'unit', targetingOptions: [rules], strategemInstanceId: strategemDragState.order.instanceId, strategemId: strategemDragState.order.id } : null;
  })() : null);

  // More spectator debug
  if (isSpectating && activeGameState) {
  }
  
  // Auto-open completed orders modal when Essia Luhr needs selection (skip in spectator mode)
  useEffect(() => {
    if (isSpectating) return;
    const playerState = gameState?.state?.[userId];
    if (playerState?.essiaLuhrNeedsSelection && !playerState?.essiaLuhrSelected) {
      setShowCompletedOrders(true);
    }
  }, [gameState?.state?.[userId]?.essiaLuhrNeedsSelection, gameState?.state?.[userId]?.essiaLuhrSelected, userId, isSpectating]);

  // When backend sets needsStrategemTarget after a drag-drop play, dispatch a synthetic click at the drop
  // coordinates so the same handler as "click strategem then click target" runs (handleTargetingClick etc).
  useEffect(() => {
    const needs = gameState?.state?.[userId]?.needsStrategemTarget;
    const pending = pendingStrategemDropTargetRef.current;
    if (needs) {
    }
    if (!needs || !pending) return;
    if (pending.strategemInstanceId !== needs.strategemInstanceId) {
      pendingStrategemDropTargetRef.current = null;
      return;
    }
    pendingStrategemDropTargetRef.current = null;
    setError('');
    if (pending.type === 'click' && typeof pending.clientX === 'number' && typeof pending.clientY === 'number') {
      const el = document.elementFromPoint(pending.clientX, pending.clientY);
      if (el) {
        el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window, clientX: pending.clientX, clientY: pending.clientY }));
      }
      return;
    }
    // Legacy payload path (kept for any non-click pending shape)
    if (pending.targetType) {
      const animConfig = {
        registerAnimation: strategemTargetAnimations.registerAnimation,
        strategemInstanceId: needs.strategemInstanceId,
        strategemId: needs.strategemId,
        gameState,
        userId
      };
      targetStrategem(
        pending.targetType,
        pending.targetZone,
        pending.targetIndex,
        pending.targetUnitId,
        pending.targetInstanceId,
        ws || currentSocketRef.current,
        gameId,
        setError,
        setStrategemTargetMode,
        setSelectedStrategemId,
        setStrategemTargetInfo,
        animConfig
      );
    }
  }, [gameState?.state?.[userId]?.needsStrategemTarget, gameState, userId, ws, gameId, setError, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, strategemTargetAnimations.registerAnimation, targetStrategem]);

  // Weapon drop: same pattern as strategem so drag uses the exact same code path as click.
  // Difference: strategem has backend needsStrategemTarget → state sync sets mode → effect runs later and clicks.
  // Weapon has no backend; we set weapon mode in onUp and store coords; effect runs next commit and dispatches click.
  // requestAnimationFrame so overlay is gone from DOM before elementFromPoint.
  useEffect(() => {
    if (!pendingWeaponDropCoords) return;
    const { clientX, clientY } = pendingWeaponDropCoords;
    setPendingWeaponDropCoords(null);
    const id = requestAnimationFrame(() => {
      const el = document.elementFromPoint(clientX, clientY);
      if (el) {
        el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window, clientX, clientY }));
      }
    });
    return () => cancelAnimationFrame(id);
  }, [pendingWeaponDropCoords]);

  // Attack drop: we set selectedAttacker and attackMode in onUp; dispatch synthetic click at drop coords so target's onClick runs (attackOrder).
  // Resolve clickable target: opponent general has a sibling overlay (targeting border) with pointer-events:none on top of the img;
  // elementFromPoint can return that overlay, so the img's onClick would never run. Use elementsFromPoint and pick the element
  // with data-zone="opponent_general" so we always hit the general's click handler when dropping on it.
  // Clear pendingAttackDropCoords only inside rAF so React Strict Mode cleanup (which cancels rAF) doesn't prevent the click:
  // without this, effect runs -> clear state -> schedule rAF -> cleanup cancels rAF -> effect re-runs with null -> click never fires.
  useEffect(() => {
    if (!pendingAttackDropCoords) return;
    const { clientX, clientY } = pendingAttackDropCoords;
    const id = requestAnimationFrame(() => {
      setPendingAttackDropCoords(null);
      const elements = typeof document.elementsFromPoint === 'function'
        ? document.elementsFromPoint(clientX, clientY)
        : [document.elementFromPoint(clientX, clientY)].filter(Boolean);
      let el = elements[0] || null;
      for (let i = 0; i < elements.length; i++) {
        const candidate = elements[i];
        if (candidate?.getAttribute?.('data-zone') === 'opponent_general') {
          el = candidate;
          break;
        }
      }
      if (el) {
        el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window, clientX, clientY }));
      }
    });
    return () => cancelAnimationFrame(id);
  }, [pendingAttackDropCoords]);

  // Drone target drop: we set selectedDroneId, selectedDroneInstanceId, droneTargetMode in onUp; dispatch synthetic click at drop coords so target's onClick runs (targetDrone).
  useEffect(() => {
    if (!pendingDroneTargetDropCoords) return;
    const { clientX, clientY } = pendingDroneTargetDropCoords;
    setPendingDroneTargetDropCoords(null);
    const id = requestAnimationFrame(() => {
      const el = document.elementFromPoint(clientX, clientY);
      if (el) {
        el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window, clientX, clientY }));
      }
    });
    return () => cancelAnimationFrame(id);
  }, [pendingDroneTargetDropCoords]);

  // Deploy drop on full zone: we already set selectedOrderInstanceId/selectedOrderId in onUp; dispatch synthetic
  // click at drop coords so the unit slot's onClick runs and enters replace mode (Replace/Cancel) like the click path.
  useEffect(() => {
    if (!pendingDeployFullZoneDrop) return;
    const { clientX, clientY } = pendingDeployFullZoneDrop;
    setPendingDeployFullZoneDrop(null);
    const id = requestAnimationFrame(() => {
      const el = document.elementFromPoint(clientX, clientY);
      if (el) {
        el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window, clientX, clientY }));
      }
    });
    return () => cancelAnimationFrame(id);
  }, [pendingDeployFullZoneDrop]);

  // Drone drop: resolve drop on player drone zone. If zone has room → playDrone. If full → drop on a deployed
  // drone slot enters replace mode directly (Replace/Cancel on that drone); no hand selection step.
  useEffect(() => {
    if (!pendingDroneDropCoords) return;
    const { clientX, clientY, orderId, orderInstanceId } = pendingDroneDropCoords;
    setPendingDroneDropCoords(null);
    const id = requestAnimationFrame(() => {
      const elements = typeof document.elementsFromPoint === 'function'
        ? document.elementsFromPoint(clientX, clientY)
        : [document.elementFromPoint(clientX, clientY)].filter(Boolean);
      let onPlayerDroneZone = false;
      for (let i = 0; i < elements.length; i++) {
        let el = elements[i];
        while (el) {
          if (el.getAttribute && el.getAttribute('data-player-drone-zone') === 'true') {
            onPlayerDroneZone = true;
            break;
          }
          el = el.parentElement;
        }
        if (onPlayerDroneZone) break;
      }
      if (!onPlayerDroneZone) {
        setError(t('battleUI.selectTargetDrone') || 'Drop on your drone zone to deploy');
        setTimeout(() => setError(''), 3000);
        return;
      }
      const dronesFiltered = you?.drones?.filter(d => !d.markedForRemoval) || [];
      const dronesCount = dronesFiltered.length;
      if (dronesCount >= 4) {
        // Full zone: resolve which deployed drone slot they dropped on and show Replace/Cancel on it directly.
        let slotEl = null;
        for (let i = 0; i < elements.length; i++) {
          let el = elements[i];
          while (el) {
            if (el.getAttribute && el.getAttribute('data-drone-zone') === 'your_drone') {
              const idxAttr = el.getAttribute('data-drone-index');
              if (idxAttr != null) {
                slotEl = el;
                break;
              }
            }
            el = el.parentElement;
          }
          if (slotEl) break;
        }
        if (slotEl) {
          const slotIndex = parseInt(slotEl.getAttribute('data-drone-index'), 10);
          const drone = dronesFiltered[slotIndex];
          if (drone != null) {
            setReplaceDroneTargetMode(true);
            setSelectedReplaceDroneId(orderId);
            setSelectedReplaceDroneInstanceId(orderInstanceId);
            setClickedDroneForReplace({ ...drone, index: slotIndex });
            return;
          }
        }
        setError(t('battleUI.droneZoneFull') || 'Drop on a drone to replace it');
        setTimeout(() => setError(''), 3000);
        return;
      }
      playDrone(orderId);
    });
    return () => cancelAnimationFrame(id);
  }, [pendingDroneDropCoords, you?.drones, playDrone, setError, t, setReplaceDroneTargetMode, setSelectedReplaceDroneId, setSelectedReplaceDroneInstanceId, setClickedDroneForReplace]);

  // Debug Strategic Forecast state
  if (you?.needsStrategicForecastSelection) {
  }
  
  // Developer mode intelligence handling removed - use dev commands instead
  
  // Only log error if we've attempted to load gameState but still don't have it
  if (!activeGameState && gameId && token && !reconnecting) {
  }

  // opponentId and opponent are now memoized above to ensure real-time updates in spectator mode
  
  
  // ✅ FIXED: Only validate when game state is fully loaded and structured
  if (gameState && you && opponent) {
    if (!opponent?.playedOrders?.front || !Array.isArray(opponent.playedOrders.front)) {
      throw new Error('Invalid opponent front zone: expected array');
    }
    if (!opponent?.playedOrders?.flank || !Array.isArray(opponent.playedOrders.flank)) {
      throw new Error('Invalid opponent flank zone: expected array');
    }
    if (!you?.playedOrders?.front || !Array.isArray(you.playedOrders.front)) {
      throw new Error('Invalid player front zone: expected array');
    }
    if (!you?.playedOrders?.flank || !Array.isArray(you.playedOrders.flank)) {
      throw new Error('Invalid player flank zone: expected array');
    }
  }
  
  // ✅ REMOVED: hasAnimationsPlaying - no longer needed with two-phase attack system

  // Helper to keep destroyed units visible if animations are playing
  const getVisibleUnits = (zoneArray, playerId) => {
    return zoneArray.map((order, idx) => {
      // If unit is null but has animations playing, keep it visible temporarily
      if (!order && idx < zoneArray.length) {
        // Try to find the unit by checking previous state or context
        // For now, we'll handle this in the rendering logic below
        return null;
      }
      return order;
    });
  };

  // Track previous units to maintain visibility during animations
  // ✅ REMOVED: delayedRemovals - no longer needed with two-phase attack system
  
  // Track repositioning units - keep unit visible at old position during animation
  
  // ✅ REMOVED: Delayed removal logic for old damage animation system
  // Two-phase attack system handles attack timing - units are destroyed after animations complete
  // Slot death animations are handled separately by useSlotDeathDetection

  // Memoize unit arrays to ensure React detects health changes in spectator mode
  // Use stateVersion or deep comparison to force updates when units change
  const opponentFront = useMemo(() => {
    // Force new array reference by spreading - ensures React detects changes
    return opponent?.playedOrders?.front ? [...opponent.playedOrders.front] : [];
  }, [opponent, activeGameState?.stateVersion]);
  
  const opponentFlank = useMemo(() => {
    return opponent?.playedOrders?.flank ? [...opponent.playedOrders.flank] : [];
  }, [opponent, activeGameState?.stateVersion]);
  
  const yourFront = useMemo(() => {
    return you?.playedOrders?.front ? [...you.playedOrders.front] : [];
  }, [you, activeGameState?.stateVersion]);
  
  const yourFlank = useMemo(() => {
    return you?.playedOrders?.flank ? [...you.playedOrders.flank] : [];
  }, [you, activeGameState?.stateVersion]);
  
  // ========== DEBUGGING: SLOT VISIBILITY AND NUMBERING ==========
  // TODO: Remove all debugging code marked with "DEBUGGING:" comments
  // Helper function to get global slot number (1-20) for debugging
  // Opponent Front: 1-5, Opponent Flank: 6-10, Your Front: 11-15, Your Flank: 16-20
  // COMMENTED OUT - no longer needed
  /*
  const getGlobalSlotNumber = (zone, idx) => {
    if (zone === 'opponent_front') return idx + 1;        // 1-5
    if (zone === 'opponent_flank') return idx + 6;        // 6-10
    if (zone === 'front') return idx + 11;               // 11-15
    if (zone === 'flank') return idx + 16;                // 16-20
    return idx + 1; // fallback
  };
  */
  // ========== END DEBUGGING ==========
  
  // Detect unit deaths by tracking slot states
  // ✅ FIX: Pass deadAttackersRef and deadTargetsRef directly for synchronous checking (no callback timing issues)
  // Use activeGameState/activeUserId for spectator support
  // Also pass strategemTargetedUnitsRef to suppress death animations for strategem targets (handled by onDamageAtTarget)
  useSlotDeathDetection(opponentFront, opponentFlank, yourFront, yourFlank, activeGameState, activeUserId, attackAnimations.deadAttackersRef, attackAnimations.deadTargetsRef, strategemTargetAnimations.targetedUnitInstanceIdsRef);
  
  // Reconcile ghost units with authoritative state: clear ghosts for slots that are empty in state
  // and not targeted by an active strategem animation (fixes ghost units persisting after destruction)
  useEffect(() => {
    const emptySlotKeys = [];
    const pushEmpty = (zoneName, arr) => {
      if (!Array.isArray(arr)) return;
      for (let i = 0; i < arr.length; i += 1) {
        if (arr[i] == null) emptySlotKeys.push(`${zoneName}_${i}`);
      }
    };
    pushEmpty('opponent_front', opponentFront);
    pushEmpty('opponent_flank', opponentFlank);
    pushEmpty('your_front', yourFront);
    pushEmpty('your_flank', yourFlank);
    if (emptySlotKeys.length > 0 && strategemTargetAnimations.reconcileGhostUnitsForEmptySlots) {
      strategemTargetAnimations.reconcileGhostUnitsForEmptySlots(emptySlotKeys);
    }
  }, [opponentFront, opponentFlank, yourFront, yourFlank, activeGameState?.stateVersion, strategemTargetAnimations.reconcileGhostUnitsForEmptySlots]);
  
  // Game start entrance animations - use activeGameState for spectator support
  const { animationsStarted } = useGameStartAnimations(activeGameState);
  
  // Unit deployment animations - use activeGameState for spectator support
  const { getDeploymentAnimationStyles, isNewlyDeployed, isNewlyDeployedLegendaryUnique, isNewlyDeployedLegendary } = useUnitDeploymentAnimations(activeGameState, activeUserId);
  
  // Drone deployment animations - use activeGameState for spectator support
  useDroneDeploymentAnimations(activeGameState, activeUserId);
  
  // Shield grant animations - use activeGameState for spectator support
  useShieldGrantAnimations(activeGameState, activeUserId);
  
  // Strategem sound effects - use activeGameState for spectator support
  useStrategemSoundEffects(activeGameState, activeUserId);
  useDeployEffectSounds(activeGameState, activeUserId);
  
  // Strategem 113 shockwave effect - use activeGameState for spectator support
  const strategemShockwave = useStrategemShockwave(activeGameState, activeUserId);

  // Inferno Drop (strategem 319) napalm effect over all 4 zones - detected from completedOrders (no-target strategem)
  const infernoNapalm = useInfernoDropNapalm(activeGameState, activeUserId);
  
  // Unit repositioning animations - use activeGameState for spectator support
  const { isUnitRepositioning, repositioningUnits: repositioningUnitsFromHook } = useUnitRepositionAnimations(activeGameState, activeUserId);
  
  // Track active repositioning overlays (copies of units for animation)
  const [activeRepositioningOverlays, setActiveRepositioningOverlays] = useState(new Map());
  
  // Decommission animations - detect units decommissioned by strategem 111
  const { decommissionedUnits: decommissionedUnitsFromHook } = useDecommissionAnimations(activeGameState, activeUserId);
  
  // Track active decommission overlays (copies of units for animation)
  const [activeDecommissionOverlays, setActiveDecommissionOverlays] = useState(new Map());
  
  // Attack animations - REMOVED (useAttackAnimations hook deleted)
  
  // Create overlays for repositioning units
  useEffect(() => {
    if (!repositioningUnitsFromHook || repositioningUnitsFromHook.size === 0) {
      setActiveRepositioningOverlays(new Map());
      return;
    }
    
    // For each repositioning unit, create an overlay
    const newOverlays = new Map();
    repositioningUnitsFromHook.forEach((reposition, key) => {
      const playerId = reposition.playerId;
      const instanceId = reposition.instanceId;
      
      // Find the unit in gameState
      const player = gameState?.state?.[playerId];
      if (!player?.playedOrders) return;
      
      // Get unit from new position
      const unitAtNewPos = player.playedOrders[reposition.toZone]?.[reposition.toIndex];
      if (unitAtNewPos && unitAtNewPos.instanceId === instanceId) {
        newOverlays.set(key, {
          reposition: reposition,
          unit: { ...unitAtNewPos },
          startTime: Date.now()
        });
      }
    });
    
    setActiveRepositioningOverlays(newOverlays);
    
    // Clean up old overlays after animation (handled by hook timeout, but cleanup here too)
    const cleanupTimeout = setTimeout(() => {
      setActiveRepositioningOverlays(prev => {
        const next = new Map(prev);
        let changed = false;
        next.forEach((data, key) => {
          if (!repositioningUnitsFromHook.has(key)) {
            next.delete(key);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }, 2100);
    
    return () => clearTimeout(cleanupTimeout);
  }, [repositioningUnitsFromHook, gameState]);
  
  // Create overlays for decommissioned units
  useEffect(() => {
    if (!decommissionedUnitsFromHook || decommissionedUnitsFromHook.size === 0) {
      setActiveDecommissionOverlays(new Map());
      return;
    }
    
    // For each decommissioned unit, create an overlay
    const newOverlays = new Map();
    decommissionedUnitsFromHook.forEach((decommission, key) => {
      // Get unit from decommission data (includes full unitData)
      let unit = decommission.unitData;
      
      // Try to get from ghost units if unitData not available
      if (!unit) {
        const slotKey = `${decommission.zone}_${decommission.index}`;
        const ghostData = strategemTargetAnimations.ghostUnitsRef?.current?.get(slotKey);
        if (ghostData && ghostData.unit) {
          unit = ghostData.unit;
        } else {
          // Fallback: create unit object from decommission data
          unit = {
            id: decommission.id,
            name: decommission.name,
            instanceId: decommission.instanceId
          };
        }
      }
      
      if (unit) {
        newOverlays.set(key, {
          decommission: decommission,
          unit: { ...unit },
          startTime: Date.now()
        });
      }
    });
    
    setActiveDecommissionOverlays(newOverlays);
    
    // Clean up old overlays after animation (handled by hook timeout, but cleanup here too)
    const cleanupTimeout = setTimeout(() => {
      setActiveDecommissionOverlays(prev => {
        const next = new Map(prev);
        let changed = false;
        next.forEach((data, key) => {
          if (!decommissionedUnitsFromHook.has(key)) {
            next.delete(key);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }, 2100);
    
    return () => clearTimeout(cleanupTimeout);
  }, [decommissionedUnitsFromHook, strategemTargetAnimations.ghostUnitsRef]);
  
  // Attack overlays - REMOVED (useAttackAnimations hook deleted)
  
  // OLD Attack animations state - REMOVED, now using useAttackAnimations hook
  // const [activeAttack, setActiveAttack] = useState(null); // DISABLED - using new attack-initiated flow
  const unitRefs = useRef(new Map()); // Store refs to unit containers for position calculation
  const previousGameStateRef = useRef(null); // Track previous game state for bot attack detection

  // Helper function to get visible unit
  // ✅ SIMPLIFIED: Removed delayed removal logic - two-phase attack system handles timing
  // Slot death animations are handled separately by useSlotDeathDetection
  const getVisibleUnit = (zoneName, idx, actualOrder) => {
    if (actualOrder) {
      // Unit still exists in game state
      
      // Extract playerId from zoneName for repositioning check
      let playerId = null;
      if (gameState && zoneName) {
        const allPlayerIds = gameState.players || Object.keys(gameState.state || {});
        if (zoneName.startsWith('your_')) {
          playerId = allPlayerIds[0] || null;
        } else if (zoneName.startsWith('opponent_')) {
          playerId = allPlayerIds.length > 1 ? allPlayerIds[1] : (allPlayerIds[0] || null);
        }
      }
      
      // Check if this unit is being repositioned (hide it during animation - overlay shows the copy)
      if (playerId && actualOrder.instanceId && isUnitRepositioning && isUnitRepositioning(playerId, actualOrder.instanceId)) {
        // Unit is being animated in overlay - hide it at both old and new positions
        return null; // Will be shown after animation completes
      }
      
      return actualOrder;
    }
    
    // Unit was removed from game state - check for ghost unit
    // Ghost units keep targets visible while strategem animation plays
    const ghostUnit = strategemTargetAnimations.getGhostUnit(zoneName, idx);
    if (ghostUnit) {
      return ghostUnit;
    }
    
    // No ghost - unit is truly gone (no logging - too noisy)
    // No ghost - unit is truly gone
    return null;
  };

  // Helper: UI immobilize flag
  // For strategem 219 (Plague of Darkness), only show immobilize indicator after animation completes
  const isImmobilizedUI = (u, zoneName = null) => {
    if (!u) return false;
    const isImmobilized = (u.isImmobilized === true) || (u.immobilized === true) || (u.status === 'immobilized');
    if (!isImmobilized) return false;
    
    // Check if this unit is in a zone where strategem 219 animation hasn't completed yet
    // We need to check if the unit's zone matches a pending strategem 219 animation
    if (zoneName) {
      // Normalize zone name (remove player prefix for comparison)
      const normalizedZone = zoneName.replace('your_', '').replace('opponent_', '');
      const zoneKey = normalizedZone; // e.g., 'front' or 'flank'
      
      // Check if this zone has a pending strategem 219 animation
      // If the zone is NOT in the completed set, delay showing the indicator
      if (!strategem219CompletedZonesRef.current.has(zoneKey)) {
        // Check if there's an active strategem 219 animation for this zone
        const hasActive219Animation = strategemTargetAnimations.activeAnimations.some(anim => {
          if (anim.strategemId !== 219) return false;
          // Check if animation targets this zone
          const animZone = anim.toSlot?.zone?.replace('your_', '').replace('opponent_', '');
          return animZone === zoneKey;
        });
        
        // If there's an active animation, delay showing the indicator
        if (hasActive219Animation) {
          return false;
        }
      }
    }
    
    return true;
  };

  // Damage flash hook (after you and opponent are defined)
  const { playerDamageFlash, opponentDamageFlash } = useDamageFlash(you, opponent);
  
  // Strategem general damage animations - triggers explosion when strategem damages general
  // Pass getAnimatedStrategemInstanceIds to suppress duplicates when targeting animation already handles it
  useStrategemGeneralDamage(you, opponent, setDamageAnimationTriggers, strategemTargetAnimations.getAnimatedStrategemInstanceIds);

  // Fetch avatars from server
  useEffect(() => {
    const fetchAvatars = async () => {
      try {
        const res = await fetch(`${API_URL}/get-avatar`);
        const data = await res.json();
        if (Array.isArray(data)) {
          setAvatars(data);
        } else {
          console.error('Unexpected avatar response format:', data);
          setAvatars([]);
        }
      } catch (err) {
        console.error('Failed to fetch avatars:', err);
        setAvatars([]);
      }
    };

    fetchAvatars();
  }, []);

  // Extract opponent avatar selections from game state
  useEffect(() => {
    // Use effectiveUserId for spectator mode (watched player) or userId for normal mode
    // For autobattler, use the playerBotId as the effective user (the bot the spectator is watching)
    const effectiveUserIdForAvatars = isAutobattlerSpectator && gameState?.playerBotId 
      ? gameState.playerBotId 
      : (isSpectating && spectatingPlayerId ? spectatingPlayerId : userId);
    // For autobattler, always use gameState (not spectatingGameState) since autobattler doesn't use friends context
    const effectiveGameStateForAvatars = isAutobattlerSpectator 
      ? gameState 
      : (isSpectating && spectatingGameState ? spectatingGameState : gameState);
    
    if (effectiveGameStateForAvatars) {
      // Get opponent's actual player ID
      const opponentId = effectiveGameStateForAvatars.players?.find(id => String(id) !== String(effectiveUserIdForAvatars));
      
      if (opponentId) {
        // In spectator mode OR autobattler mode, get opponent avatars from the opponent's playerAvatars
        // In normal mode, get from opponentAvatars keyed by userId
        let opponentAvatars = {};
        if (isSpectating || isAutobattlerSpectator) {
          // In spectator mode or autobattler mode, opponent avatars are in playerAvatars keyed by opponent's ID
          opponentAvatars = effectiveGameStateForAvatars.playerAvatars?.[opponentId] || {};
        } else {
          // In normal mode, opponent avatars are keyed by userId
          opponentAvatars = effectiveGameStateForAvatars.opponentAvatars?.[effectiveUserIdForAvatars] || {};
        }
        
        const opponentState = effectiveGameStateForAvatars.state?.[opponentId];
        let opponentGeneral = opponentAvatars.general || opponentState?.selectedGeneral || effectiveGameStateForAvatars.opponentGeneral || '';
        let opponentAdvisor = opponentAvatars.advisor || opponentState?.selectedAdvisor || effectiveGameStateForAvatars.opponentAdvisor || '';
        
        // For bot games, avatars should already be in game state from server
        // Fallback to localStorage only if game state doesn't have them (for old games)
        if (isBotGame && (!opponentGeneral || !opponentAdvisor)) {
          const gameId = effectiveGameStateForAvatars.gameId || effectiveGameStateForAvatars.id;
          const storedBotAvatars = localStorage.getItem(`botAvatars_${gameId}`);
          
          if (storedBotAvatars) {
            // Use stored avatars from localStorage (fallback for old games)
            const parsedAvatars = JSON.parse(storedBotAvatars);
            opponentGeneral = parsedAvatars.general || opponentGeneral;
            opponentAdvisor = parsedAvatars.advisor || opponentAdvisor;
          }
        }
        
        setOpponentGeneral(opponentGeneral);
        setOpponentAdvisor(opponentAdvisor);
      }
    }
  }, [gameState, spectatingGameState, opponent, isBotGame, userId, spectatingPlayerId, isSpectating, isAutobattlerSpectator]);

  // ✅ REFACTORED: Cleanup state sync timers on unmount
  useEffect(() => {
    return () => {
      cleanupStateSync();
    };
  }, [cleanupStateSync]);

  // ✅ FIXED: Cleanup hover timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    };
  }, []);

  // Damage flash logic moved to ./effects/useDamageFlash.js

// Navigate back after game ends (skip for spectators)
// ✅ FIX: Use handleBattleEnd to ensure consistent transition behavior
useEffect(() => {
  if (isSpectating) return; // Spectators don't navigate on game end
  if (!gameState?.winner) return;
  if (battleEndTransition.isTransitioning || isTransitioningRef.current) return; // Already transitioning

  const urlSid = getCurrentGameIdFromUrl();
  if (urlSid != null && String(urlSid) !== String(gameId)) {
    return;
  }

  // gameState.gameId is the engine internal id (e.g. game_*); URL / gameIdRef use the WebSocket session key (e.g. 4_1_*).
  // Comparing them always failed for PvP and blocked after-battle navigation. Wrong-game filtering is done on incoming WS messages via msg.gameId.

  strategemTargetAnimations.clearGhostUnits();
  const didYouWin = gameState.winner === userId;
  
  // Prepare navigation state with opponent strategy ID if lost
  const navigationState = { result: didYouWin ? 'win' : 'loss', gameId, _triggerSource: 'useEffect-gameState.winner' };
  if (!didYouWin) {
    const storedStrategyId = opponentStrategyIdRef.current;
    if (storedStrategyId) {
      navigationState.opponentStrategyId = storedStrategyId;
    } else if (gameState && gameState.players) {
      const opponentId = gameState.players.find(id => id !== userId);
      if (opponentId && opponentId !== 'bot' && gameState.state?.[opponentId]) {
        const opponentState = gameState.state[opponentId];
        if (opponentState?.strategy) {
          const strategyId = opponentState.strategy.id;
          if (strategyId && typeof strategyId === 'number' && strategyId !== 'bot-hardcoded') {
            navigationState.opponentStrategyId = strategyId;
          }
        }
      }
    }
  }
  if (gameState?.warEffortMissionId || gameState?.isWarEffort) {
    navigationState.isWarEffort = true;
    navigationState.gameState = gameState;
  }
  if (gameState?.campaignMode && gameState?.missionId) {
    navigationState.isCampaign = true;
    navigationState.missionId = gameState.missionId;
    navigationState.gameState = gameState;
  }
  
  const transitionType = didYouWin ? 'win' : 'loss';
  
  // Set refs immediately to prevent race conditions
  isTransitioningRef.current = true;
  exitingRef.current = true;
  
  // Reset any previous image state
  setShowVictoryImage(false);
  setShowDefeatImage(false);
  
  // Start transition animation FIRST
  setBattleEndTransition({ isTransitioning: true, type: transitionType });
  
  // Helper to play the correct battle end sound
  const playBattleEndSound = () => {
    const soundPath = didYouWin ? '/sounds/victory.mp3' : '/sounds/loss.mp3';
    const audio = new Audio(soundPath);
    audio.volume = 0.7;
    audio.load(); // Ensure audio is ready
    battleEndSoundRef.current = audio;
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch(err => {
        console.error('Error playing battle end sound:', err);
      });
    }
  };
  
  try { ws?.close(); } catch {}
  
  let soundTimeout = null;
  let navigateTimeout = null;
  
  if (didYouWin) {
    // WIN: fade to white first, then play sound and show centered victory image
    soundTimeout = setTimeout(() => {
      setShowVictoryImage(true);
      playBattleEndSound();
    }, 2000);
    
    navigateTimeout = setTimeout(() => {
      navigate('/after-battle', { replace: true, state: navigationState });
    }, 4000);
  } else {
    // LOSS: play sound immediately when game ends, then fade to gray/black, then show full screen defeat image
    playBattleEndSound();
    soundTimeout = setTimeout(() => {
      setShowDefeatImage(true);
    }, 2000);
    
    navigateTimeout = setTimeout(() => {
      navigate('/after-battle', { replace: true, state: navigationState });
    }, 4000);
  }
  
  return () => {
    if (soundTimeout) clearTimeout(soundTimeout);
    if (navigateTimeout) clearTimeout(navigateTimeout);
  };
}, [gameState, gameId, getCurrentGameIdFromUrl, navigate, ws, userId, battleEndTransition.isTransitioning, strategemTargetAnimations]);

// ✅ AUTOBATTLER SPECTATOR: Navigate back to dispatch-asset when spectated game ends
useEffect(() => {
  // Only handle autobattler spectators
  if (!isAutobattlerSpectator) return;
  
  // Check if game has ended (winner set)
  const hasWinner = activeGameState?.winner;
  if (!hasWinner) return;
  
  // Prevent multiple navigations
  if (isTransitioningRef.current || exitingRef.current) return;
  
  
  // ✅ SPEED CONTROL: Clear queue when game ends
  speedControl.clearQueue();
  
  // ✅ FIX: Clear all animation states to prevent ghost images
  // This fixes the issue where attack animation images persist after autobattler game ends
  
  // Clear attack animations (pending attacks, active animations, hidden units)
  if (attackAnimationsRef.current?.reset) {
    attackAnimationsRef.current.reset();
  }
  
  // Clear damage animation triggers (explosion effects at attack targets)
  setDamageAnimationTriggers(new Map());
  
  // Clear damage animation triggered tracking ref
  if (damageAnimationTriggeredRef.current) {
    damageAnimationTriggeredRef.current.clear();
  }
  
  // Clear explosion effects
  setExplosionEffects(new Map());
  
  // Mark as transitioning
  isTransitioningRef.current = true;
  exitingRef.current = true;
  
  // Determine if the player's bot won
  const playerBotId = activeGameState?.playerBotId;
  const didPlayerWin = playerBotId && activeGameState.winner === playerBotId;
  
  // Short delay to let the final state render, then navigate back
  const navTimeout = setTimeout(() => {
    navigate('/dispatch-asset', { 
      replace: true, 
      state: { 
        spectateEnded: true,
        result: didPlayerWin ? 'win' : 'loss',
        gameId: gameId
      } 
    });
  }, 2000); // 2 second delay so user can see the final result
  
  return () => {
    clearTimeout(navTimeout);
  };
}, [isAutobattlerSpectator, activeGameState?.winner, activeGameState?.playerBotId, navigate, gameId, speedControl]);

// ✅ SPEED CONTROL: Cleanup on unmount or when no longer spectating
useEffect(() => {
  if (!isAutobattlerSpectator) {
    speedControl.clearQueue();
  }
  
  return () => {
    speedControl.clearQueue();
  };
}, [isAutobattlerSpectator, speedControl]);

// ✅ FIX: Show reconnecting message for autobattler spectators too
// But only if we're actually reconnecting and don't have gameState yet
const isAutobattlerGame = gameId?.includes('_autobattler_') || initialGameState?.isAutobattler || gameState?.isAutobattler;
const shouldShowReconnecting = reconnecting && (!isSpectating || (isAutobattlerGame && !gameState));

if (shouldShowReconnecting) {
  return <div style={{ padding: 24 }}>{t('battleUI.reconnecting')}</div>;
}

  // For spectators, wait for game state from spectate-started message
  if (isSpectating && (!activeGameState || !you || !opponent)) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: '24px', marginBottom: '10px' }}>👁️</div>
        <div>Connecting to spectator view...</div>
        <div style={{ fontSize: '12px', color: '#888', marginTop: '10px' }}>
          Waiting for game state from {spectatingFriendName}'s game
        </div>
      </div>
    );
  }

  if (!activeGameState || !you || !opponent) {
    return <div style={{ padding: 24 }}>{t('battleUI.loadingBattle')}</div>;
  }

  // Show error if targeting data failed to load
  if (targetingDataError) {
    return <div style={{ padding: 24, color: 'red' }}>Error loading targeting data: {targetingDataError}</div>;
  }

  // Show loading if targeting data is not yet loaded
  if (!targetingDataLoaded || !targetingData) {
    return <div style={{ padding: 24 }}>Loading targeting data...</div>;
  }

// Use activeGameState for spectator support
// ✅ FIX: Don't exit immediately if we're transitioning - let the transition animation play
const shouldExit = Boolean(activeGameState?.winner) && !battleEndTransition.isTransitioning && !isTransitioningRef.current;
const resultForExit = shouldExit ? (activeGameState.winner === activeUserId ? 'win' : 'loss') : null;

// Show glowing targeting cursor when any targeting mode requires the user to pick a target (e.g. deploy effect targeting)
const isTargetingCursorActive = (deployTargetMode && selectedDeployOrderId) || (strategemTargetMode && selectedStrategemId) || (weaponTargetMode && selectedWeaponId) || (weaponDeployTargetMode && weaponDeployTargetInfo) || (droneTargetMode && selectedDroneId) || (immobilizeTargetMode && selectedImmobilizeWeaponId) || (confuseTargetMode && selectedConfuseWeaponId) || (specialTargetMode && selectedSpecialUnitId) || (replaceTargetMode && selectedReplaceUnitId) || (replaceDroneTargetMode && selectedReplaceDroneId);

return (
  <>
    {/* Viewport Wrapper - creates the scaling container for responsive design */}
    <div className="viewport-wrapper">
      <TargetingCursor active={isTargetingCursorActive} />
      {/* Full-screen background - outside viewport-container to avoid letterboxing */}
      {/* Uses object-fit: cover to fill entire screen regardless of aspect ratio */}
      <div 
        className="fullscreen-background"
        style={{
          backgroundImage: `url(/images/${backgroundImage})`,
        }}
      />
      <div 
        className="viewport-container"
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
    {/* Turn Start Image - rendered via portal so it's always centered on the viewport (not affected by hand/units layout) */}
    {showTurnStartImage && createPortal(
      (() => {
        const sparkCount = 12;
        const sparkRadius = 90;
        const sparks = Array.from({ length: sparkCount }, (_, i) => {
          const angle = (i / sparkCount) * 2 * Math.PI;
          return {
            dx: Math.round(sparkRadius * Math.cos(angle)),
            dy: Math.round(sparkRadius * Math.sin(angle)),
            delay: i * 0.02,
          };
        });
        return (
          <div
            className="turn-start-overlay-wrapper"
            style={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
              pointerEvents: 'none',
            }}
            aria-hidden="true"
          >
            <div className="turn-start-overlay-inner">
            <div className="turn-start-sparks" aria-hidden="true">
              {sparks.map((s, i) => (
                <div
                  key={i}
                  className="turn-start-spark"
                  style={{
                    '--spark-dx': `${s.dx}px`,
                    '--spark-dy': `${s.dy}px`,
                    animationDelay: `${s.delay}s`,
                  }}
                />
              ))}
            </div>
            <div className="turn-start-card">
              <img
                src="/images/turnstart2.png"
                alt="Turn Start"
                onLoad={() => {}}
                onError={(e) => console.error('❌ Turn start image failed to load:', e)}
                style={{
                  width: '900px',
                  height: 'auto',
                  maxWidth: '1728px',
                  maxHeight: '972px',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
              <span
                className="turn-start-text"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: '#fff',
                  fontSize: 'clamp(28px, 4vw, 56px)',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  whiteSpace: 'nowrap',
                }}
              >
{t('battleUI.timeToAct')}
            </span>
            </div>
            </div>
          </div>
        );
      })(),
      document.body
    )}
    
    {/* Repositioning Overlays - Rendered via portal to document.body to avoid overflow clipping */}
    {activeRepositioningOverlays && activeRepositioningOverlays.size > 0 && createPortal(
      <>
        {Array.from(activeRepositioningOverlays.values()).map((overlayData, idx) => (
          <RepositioningOverlay
            key={`reposition_overlay_${overlayData.reposition.instanceId}_${idx}`}
            reposition={overlayData.reposition}
            unit={overlayData.unit}
            gameState={activeGameState}
            userId={activeUserId}
          />
        ))}
      </>,
      document.body
    )}
    
    <div 
      className={`battle-ui-background ${isSpectating ? 'spectator-mode' : ''}`}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: `${containerBaseWidth}px`,
        height: `${containerBaseHeight}px`,
        /* Background image moved to fullscreen-background outside viewport-container */
        /* This div now just contains the game content overlays */
      }}
    >
    <div style={{ padding: 24 }}>
      {/* ✅ FIX: Disable Navigate component during transition - transition handles navigation */}
      {shouldExit && !battleEndTransition.isTransitioning && (
        <Navigate to={`/after-battle${resultForExit ? `?result=${resultForExit}` : ''}`} replace state={resultForExit ? { result: resultForExit, gameId, isWarEffort: !!(activeGameState?.warEffortMissionId || activeGameState?.isWarEffort), isCampaign: !!(activeGameState?.campaignMode && activeGameState?.missionId), missionId: activeGameState?.missionId, gameState: activeGameState } : { gameId }} />
      )}

      {/* Spectator Stop Watching Button */}
      {isSpectating && (
        <button
          className="spectator-control-button"
          onClick={stopSpectating}
          style={{
            position: "fixed",
            top: "10px",
            left: "10px",
            background: "rgba(239, 68, 68, 0.9)",
            border: "1px solid rgba(255,255,255,0.4)",
            color: "white",
            padding: "8px 16px",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "500",
            pointerEvents: "auto",
            zIndex: 10001,
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
          }}
        >
          Stop Watching
        </button>
      )}

      {/* User Info */}
      {user && !isSpectating && (
        <div 
          className={isMobile ? "user-info-mobile-hidden" : ""} 
          style={{ 
            position: "absolute", 
            top: isContainerMobile ? 5 : 10, 
            left: isContainerMobile ? 5 : 10, 
            zIndex: 1000, 
            display: "flex", 
            alignItems: "center",
            transform: isContainerMobile ? "scale(0.8)" : "none",
            transformOrigin: "top left"
          }}
        >
          <FriendsIcon />
          <SettingsMenu onLogout={onLogout} />
          <span style={{ color: "white", marginRight: "10px" }}>{typeof user === 'string' ? user : (user?.username ?? '')}</span>
        </div>
        
      )}

      {/* Action log: server-authoritative, upper left next to user info */}
      {gameState && (
        <BattleActionLog
          actionLog={actionLog}
          gameState={gameState}
          userId={userId}
          opponentId={gameState.players?.find(id => id !== userId)}
        />
      )}
      

      {/* State Synchronization Monitor - Development Tool */}
      {(process.env.NODE_ENV === 'development' && localStorage.getItem('enableStateSyncMonitor') === 'true') && (
        <>
          <StateSyncToggle onToggle={setStateSyncMonitorEnabled} />
          <StateSyncMonitor 
            gameState={gameState}
            userId={userId}
            ws={ws}
            isVisible={stateSyncMonitorEnabled}
          />
        </>
      )}


      {/* Error Messages - Lower Right Corner */}
      {/* Desktop: lower-right, Mobile: scaled and repositioned */}
      {(error || (immobilizeTargetMode && selectedImmobilizeWeaponId) || (confuseTargetMode && selectedConfuseWeaponId) || (specialTargetMode && selectedSpecialUnitId)) && (
        <div className="battle-notification" style={{
          position: "absolute",
          bottom: isContainerMobile ? 5 : 20,
          right: isContainerMobile ? 50 : 140,
          zIndex: 1000,
          transform: isContainerMobile ? "scale(0.6)" : "none",
          transformOrigin: "bottom right",
          backgroundColor: "rgba(0,0,0,0.8)",
          color: error ? "#ff4444" : "#4a90e2",
          padding: "8px 12px",
          borderRadius: "6px",
          fontSize: "14px",
          fontWeight: "bold",
          maxWidth: "300px",
          textAlign: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
          border: error ? "2px solid #ff4444" : "2px solid #4a90e2"
        }}>
          {error && <div>{error}</div>}
          {immobilizeTargetMode && selectedImmobilizeWeaponId && !error && (
            <div>
              {t('battleUI.immobilizeTargetingMode')}
              <button 
                onClick={() => {
                  setImmobilizeTargetMode(false);
                  setSelectedImmobilizeWeaponId(null);
                }}
                style={{
                  marginLeft: '10px',
                  padding: '5px 10px',
                  backgroundColor: '#ff4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }}
              >
                {t('battleUI.cancelTargeting')}
              </button>
            </div>
          )}
          {confuseTargetMode && selectedConfuseWeaponId && !error && (
            <div>
              {t('battleUI.confuseTargetingMode')}
              <button 
                onClick={() => {
                  setConfuseTargetMode(false);
                  setSelectedConfuseWeaponId(null);
                }}
                style={{
                  marginLeft: '10px',
                  padding: '5px 10px',
                  backgroundColor: '#ff4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }}
              >
                {t('battleUI.cancelTargeting')}
              </button>
            </div>
          )}
          {specialTargetMode && selectedSpecialUnitId && !error && (
            <div>
              {t('battleUI.specialTargetingMode')}
              <button 
                onClick={() => {
                  setSpecialTargetMode(false);
                  setSelectedSpecialUnitId(null);
                }}
                style={{
                  marginLeft: '10px',
                  padding: '5px 10px',
                  backgroundColor: '#ff4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }}
              >
                {t('battleUI.cancelTargeting')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Desktop: Player strip (general + advisor docked above resign/end turn). Mobile: floating buttons. */}
      {!isContainerMobile ? (
        <div className="player-bottom-strip" style={{
          position: "absolute",
          bottom: 20,
          left: 20,
          right: 20,
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 12,
          pointerEvents: "none"
        }}>
          <div className="player-buttons-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", pointerEvents: "auto" }}>
            <button
              onClick={handleResign}
              className="resign-button"
              style={{ margin: 0, position: "relative" }}
            >
              {t('battleUI.resign')}
            </button>
            <button
              onClick={endTurn}
              disabled={(() => {
          // ✅ AUTOBATTLER: Disable if in autobattler mode
          if (isAutobattler || isAutobattlerSpectator) {
            return true;
          }
          // ✅ SEQUENTIAL ATTACKS: Disable button if:
          // 1. Not player's turn
          // 2. It's the bot's turn
          // 3. There are pending attack animations
          const isBotTurn = gameState?.turn && (
            gameState.turn === 'bot' || 
            gameState.turn === 'campaign_bot' ||
            (typeof gameState.turn === 'string' && gameState.turn.startsWith('bot'))
          );
          // attackAnimations.pendingAttacks is an array of [attackId, attackData] tuples
          const hasPendingAnimations = attackAnimations && attackAnimations.pendingAttacks && attackAnimations.pendingAttacks.length > 0;
          const hasAttackInProgress = attackInProgressRef.current;
          
          const shouldDisable = !yourTurn || isBotTurn || hasPendingAnimations || hasAttackInProgress;
          if (shouldDisable && (isBotTurn || hasPendingAnimations || hasAttackInProgress)) {
          }
          return shouldDisable;
        })()}
        className="end-turn-button"
        style={{
          position: "relative",
          margin: 0,
          opacity: (() => {
            const isBotTurn = gameState?.turn && (
              gameState.turn === 'bot' || gameState.turn === 'campaign_bot' ||
              (typeof gameState.turn === 'string' && gameState.turn.startsWith('bot'))
            );
            const hasPendingAnimations = attackAnimations?.pendingAttacks?.length > 0;
            const hasAttackInProgress = attackInProgressRef.current;
            const shouldBeEnabled = yourTurn && !isBotTurn && !hasPendingAnimations && !hasAttackInProgress;
            return shouldBeEnabled ? 1 : 0.5;
          })(),
          pointerEvents: (() => {
            const isBotTurn = gameState?.turn && (
              gameState.turn === 'bot' || gameState.turn === 'campaign_bot' ||
              (typeof gameState.turn === 'string' && gameState.turn.startsWith('bot'))
            );
            const hasPendingAnimations = attackAnimations?.pendingAttacks?.length > 0;
            const hasAttackInProgress = attackInProgressRef.current;
            const shouldBeEnabled = yourTurn && !isBotTurn && !hasPendingAnimations && !hasAttackInProgress;
            return shouldBeEnabled ? "auto" : "none";
          })(),
          border: (() => {
            const hasEnoughIntel = you.intelligence >= Math.min(...(you.hand?.map(order => order.cost) || [999]));
            const hasAttackableUnits = yourFront.some(unit => unit && unit.canAttackThisTurn === true) || yourFlank.some(unit => unit && unit.canAttackThisTurn === true);
            const shouldPass = !hasEnoughIntel && !hasAttackableUnits;
            return shouldPass ? "2px solid #ffd700" : "2px solid #555555";
          })(),
          boxShadow: (() => {
            const hasEnoughIntel = you.intelligence >= Math.min(...(you.hand?.map(order => order.cost) || [999]));
            const hasAttackableUnits = yourFront.some(unit => unit && unit.canAttackThisTurn === true) || yourFlank.some(unit => unit && unit.canAttackThisTurn === true);
            const shouldPass = !hasEnoughIntel && !hasAttackableUnits;
            return shouldPass ? "0 0 10px #ffd700, 0 0 20px #ffd700" : "0 2px 8px rgba(0,0,0,0.3)";
          })()
        }}
      >
        {t('battleUI.endTurn')}
      </button>
          </div>
        </div>
      ) : (
        <>
      <button
        onClick={handleResign}
        className="resign-button"
        style={{
          position: "absolute",
          bottom: 5,
          left: 5,
          zIndex: 1000,
          padding: "6px 12px",
          fontSize: "10px",
          minWidth: "60px",
          minHeight: "28px"
        }}
      >
        {t('battleUI.resign')}
      </button>
      <button
        onClick={endTurn}
        disabled={(() => {
          if (isAutobattler || isAutobattlerSpectator) return true;
          const isBotTurn = gameState?.turn && (gameState.turn === 'bot' || gameState.turn === 'campaign_bot' || (typeof gameState.turn === 'string' && gameState.turn.startsWith('bot')));
          const hasPendingAnimations = attackAnimations?.pendingAttacks?.length > 0;
          const hasAttackInProgress = attackInProgressRef.current;
          return !yourTurn || isBotTurn || hasPendingAnimations || hasAttackInProgress;
        })()}
        className="end-turn-button"
        style={{
          position: "absolute",
          bottom: 5,
          right: 5,
          zIndex: 1000,
          margin: 0,
          opacity: yourTurn && !(gameState?.turn && (gameState.turn === 'bot' || gameState.turn === 'campaign_bot' || (typeof gameState.turn === 'string' && gameState.turn.startsWith('bot')))) && !(attackAnimations?.pendingAttacks?.length > 0) && !attackInProgressRef.current ? 1 : 0.5,
          pointerEvents: yourTurn && !(gameState?.turn && (gameState.turn === 'bot' || gameState.turn === 'campaign_bot' || (typeof gameState.turn === 'string' && gameState.turn.startsWith('bot')))) && !(attackAnimations?.pendingAttacks?.length > 0) && !attackInProgressRef.current ? "auto" : "none",
          padding: "6px 12px",
          fontSize: "10px",
          minWidth: "60px",
          minHeight: "28px"
        }}
      >
        {t('battleUI.endTurn')}
      </button>
        </>
      )}

      {/* Mobile Player General - Outside battlefield-and-carousel for proper positioning */}
      {isContainerMobile && (
        <MobilePlayerAvatar
          you={you}
          playerDamageFlash={playerDamageFlash}
          playerRank={playerRank}
          selectedGeneral={selectedGeneral}
          avatars={avatars}
          getAvatarImage={getAvatarImage}
          getDamageFlashStyles={getDamageFlashStyles}
          shouldMirrorAvatar={shouldMirrorAvatar}
          getRankStyle={getRankStyle}
          calculateFactionFromStrategy={calculateFactionFromStrategy}
          getTranslatedFactionName={getTranslatedFactionName}
          yourTurn={yourTurn}
        />
      )}

      {/* Mobile Player Stats (O:, S:, C:) - Outside battlefield-and-carousel */}
      {isContainerMobile && (
        <MobilePlayerStats
          you={you}
          setShowStrategy={setShowStrategy}
          setShowCompletedOrders={setShowCompletedOrders}
        />
      )}

      {/* Mobile Player Advisor - Outside battlefield-and-carousel, mirrored to right side */}
      {isContainerMobile && (
        <MobilePlayerAdvisor
          you={you}
          playerDamageFlash={playerDamageFlash}
          selectedAdvisor={selectedAdvisor}
          avatars={avatars}
          getAvatarImage={getAvatarImage}
          shouldMirrorAvatar={shouldMirrorAvatar}
          activeGameState={activeGameState}
          activeUserId={activeUserId}
        />
      )}

      {/* Mobile Opponent Preparation Bonus and Diplomatic Accord - Left of opponent drone zone - Hidden in tutorial missions */}
      {isContainerMobile && !isTutorialMission && (
        <MobileOpponentBonuses
          gameState={gameState}
          userId={userId}
          opponentPreparationBonus={opponentPreparationBonus}
          opponentDiplomaticAccord={opponentDiplomaticAccord}
          t={t}
        />
      )}

      {/* Mobile Opponent Drones - Outside battlefield-and-carousel, top right */}
      {isContainerMobile && (
        <MobileOpponentDrones opponent={opponent} />
      )}

      {/* Mobile Opponent Stats - Outside battlefield-and-carousel, top right below drones */}
      {isContainerMobile && (
        <MobileOpponentStats opponent={opponent} />
      )}

      {/* Mobile Opponent Avatars - Outside battlefield-and-carousel, top middle */}
      {isContainerMobile && (
        <MobileOpponentAvatars
          opponent={opponent}
          opponentGeneral={opponentGeneral}
          opponentAdvisor={opponentAdvisor}
          opponentUsername={opponentUsername}
          opponentDamageFlash={opponentDamageFlash}
          avatars={avatars}
          yourTurn={yourTurn}
          attackMode={attackMode}
          selectedAttacker={selectedAttacker}
          attackGeneral={attackGeneral}
          getOpponentAvatarImage={getOpponentAvatarImage}
          getDamageFlashStyles={getDamageFlashStyles}
          shouldMirrorOpponentAvatar={shouldMirrorOpponentAvatar}
          calculateFactionFromStrategy={calculateFactionFromStrategy}
          getTranslatedFactionName={getTranslatedFactionName}
          activeGameState={activeGameState}
          activeUserId={activeUserId}
        />
      )}

      {/* Mobile Player Preparation Bonus and Diplomatic Accord - On top of player drone zone - Hidden in tutorial missions */}
      {isContainerMobile && !isTutorialMission && (
        <MobilePlayerBonuses
          preparationBonus={preparationBonus}
          preparationBonusUsed={preparationBonusUsed}
          preparationBonusPendingRef={preparationBonusPendingRef}
          playerGoesFirst={playerGoesFirst}
          diplomaticAccord={diplomaticAccord}
          yourTurn={yourTurn}
          ws={ws}
          gameId={gameId}
          lastStateVersionRef={lastStateVersionRef}
          setPreparationBonusUsed={setPreparationBonusUsed}
          setError={setError}
          t={t}
        />
      )}

      {/* Mobile Player Drones - Outside battlefield-and-carousel, left of end turn button */}
      {isContainerMobile && (
        <MobilePlayerDrones
          you={you}
          gameState={gameState}
          yourTurn={yourTurn}
          deployTargetMode={deployTargetMode}
          selectedDeployOrderId={selectedDeployOrderId}
          handleTargetDeploy={handleTargetDeploy}
          replaceDroneTargetMode={replaceDroneTargetMode}
          selectedReplaceDroneInstanceId={selectedReplaceDroneInstanceId}
          setClickedDroneForReplace={setClickedDroneForReplace}
          clickedDroneForReplace={clickedDroneForReplace}
          selectedOrderInstanceId={selectedOrderInstanceId}
          setReplaceDroneTargetMode={setReplaceDroneTargetMode}
          setSelectedReplaceDroneId={setSelectedReplaceDroneId}
          selectedOrderId={selectedOrderId}
          setSelectedReplaceDroneInstanceId={setSelectedReplaceDroneInstanceId}
          activateDrone={activateDrone}
          handleReplaceDrone={handleReplaceDrone}
          handleCancelReplaceDrone={handleCancelReplaceDrone}
        />
      )}

      {/* Resign Confirmation Dialog */}
      {/* Uses oversized background to cover full screen including areas beyond viewport-container */}
      {showResignDialog && (
        <div style={{
          position: "absolute",
          top: "-100%",
          left: "-100%",
          width: "300%",
          height: "300%",
          backgroundColor: "rgba(0,0,0,0.7)",
          zIndex: 2000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
        onClick={cancelResign}
        >
          <div style={{
            backgroundColor: "#333",
            padding: isContainerMobile ? "16px" : "24px",
            borderRadius: "12px",
            border: "2px solid #ff4444",
            textAlign: "center",
            maxWidth: isContainerMobile ? "300px" : "400px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
            transform: isContainerMobile ? "scale(0.9)" : "none"
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ color: "#ff4444", marginBottom: isContainerMobile ? "12px" : "16px", fontSize: isContainerMobile ? "16px" : "inherit" }}>{t('battleUI.resignConfirm')}</h3>
            <p style={{ color: "white", marginBottom: isContainerMobile ? "16px" : "24px", fontSize: isContainerMobile ? "14px" : "inherit" }}>
              {t('battleUI.resignMessage')}
            </p>
            <div style={{ display: "flex", gap: isContainerMobile ? "8px" : "12px", justifyContent: "center" }}>
              <button
                onClick={confirmResign}
                style={{
                  backgroundColor: "#ff4444",
                  color: "white",
                  border: "none",
                  padding: isContainerMobile ? "8px 16px" : "10px 20px",
                  borderRadius: "6px",
                  fontSize: isContainerMobile ? "12px" : "14px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  transition: "all 0.2s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#ff6666";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#ff4444";
                }}
              >
                {t('battleUI.yesResign')}
              </button>
              <button
                onClick={cancelResign}
                style={{
                  backgroundColor: "#666",
                  color: "white",
                  border: "none",
                  padding: isContainerMobile ? "8px 16px" : "10px 20px",
                  borderRadius: "6px",
                  fontSize: isContainerMobile ? "12px" : "14px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  transition: "all 0.2s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#888";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#666";
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

     
      {/* ✅ HIDDEN: Winner message removed - transition animation shows the result instead */}
      {/* {activeGameState?.winner && (
        <div style={{ marginTop: 24, fontWeight: "bold", color: "#0c0" }}>
          {t('battleUI.winner')} {activeGameState.winner === activeUserId ? t('battleUI.you') : t('battleUI.opponent')}
        </div>
      )} */}


      {/* Temporary Message Display (e.g., "feeling tired") */}
      {temporaryMessage === 'feelingTired' && (
        <div style={{
          position: "fixed",
          top: "80px",
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: "rgba(255, 107, 53, 0.95)",
          color: "white",
          padding: "12px 24px",
          borderRadius: "8px",
          zIndex: 1500,
          textAlign: "center",
          boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
          fontSize: "18px",
          fontWeight: "bold",
          animation: "fadeInOut 3s ease-in-out"
        }}>
          {t('battleUI.feelingTired')}
        </div>
      )}

      {/* Strategem Counter Strike Orders */}
      {you?.pendingStrategemCounterStrike?.length > 0 && (
        <div style={{ 
          position: "fixed", 
          top: "50%", 
          left: "50%", 
          transform: "translate(-50%, -50%)", 
          backgroundColor: "#333", 
          border: "3px solid #ff6b35", 
          borderRadius: "12px", 
          padding: "20px", 
          zIndex: 2000,
          textAlign: "center",
          boxShadow: "0 4px 16px rgba(0,0,0,0.5)"
        }}>
          <h3 style={{ color: "#ff6b35", marginBottom: "16px" }}>{t('battleUI.strategemCounterStrike')}</h3>
          <p style={{ color: "white", marginBottom: "20px" }}>
            {t('battleUI.strategemCounterStrikeMessage')}
          </p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
            {you.pendingStrategemCounterStrike.map((strategem, idx) => (
              <div key={strategem.id} style={{ marginBottom: "12px" }}>
                <div style={{ color: "white", marginBottom: "8px", fontWeight: "bold" }}>
                  {strategem.name}
                </div>
                <div style={{ color: "#ccc", marginBottom: "12px", fontSize: "12px" }}>
                  {strategem.description || strategem.effect || t('battleUI.strategemEffect')}
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => executeStrategemCounterStrike(idx)}
                    style={{
                      backgroundColor: "#ff6b35",
                      color: "white",
                      border: "none",
                      padding: "10px 20px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "bold",
                      transition: "background-color 0.2s"
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = "#e55a2b"}
                    onMouseLeave={(e) => e.target.style.backgroundColor = "#ff6b35"}
                  >
                    {t('battleUI.execute')}
                  </button>
                  <button
                    onClick={() => keepStrategemCounterStrike(idx)}
                    style={{
                      backgroundColor: "#4CAF50",
                      color: "white",
                      border: "none",
                      padding: "10px 20px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "bold",
                      transition: "background-color 0.2s"
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = "#45a049"}
                    onMouseLeave={(e) => e.target.style.backgroundColor = "#4CAF50"}
                  >
                    {t('battleUI.keep')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Battlefield Layout */}



      <div className="battlefield-and-carousel" style={{ 
        ...(isContainerMobile ? {
          position: 'absolute', // Match black background positioning
          top: '65px',
          bottom: '135px',
          left: '-24px', // Match black background left (extend beyond parent)
          width: 'calc(100% + 48px)', // Match black background width (extend beyond parent)
          marginTop: 0,
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: 'auto', // Will be determined by top/bottom positioning
          zIndex: 1 // Above black background
          // MOBILE FIX: Removed scale(0.48) - was restricting zones from filling width
        } : {
          marginTop: 100,
          marginBottom: 32,
          width: "100%"
        }),
        padding: "0",
        boxSizing: "border-box"
        // MOBILE FIX: No transform/scale on mobile - zones fill width naturally
      }}
      >
	  <div

  onClick={(e) => {
    
    // Check for blocking overlays
    const allBlockOverlays = [
      'front-block-overlay',
      'flank-block-overlay',
      'end-turn-block-overlay',
      'forsaken-sentinel-block-overlay',
      'counter-strike-player-front-block-overlay',
      'counter-strike-opponent-front-block-overlay',
      'counter-strike-keep-button-block-overlay',
      'isabela-block-overlay',
      'tranquilizer-block-overlay'
    ];
    const blockingOverlays = allBlockOverlays.filter(id => {
      const overlay = document.getElementById(id);
      return overlay && overlay.style.display !== 'none' && window.getComputedStyle(overlay).display !== 'none';
    });
    if (blockingOverlays.length > 0) {
      console.error('❌ BLOCKING OVERLAYS DETECTED ON GENERAL CLICK:', blockingOverlays);
    } else {
    }
    
    if (yourTurn && attackMode && selectedAttacker) {
      attackOrder(selectedAttacker.zone, selectedAttacker.idx, selectedAttacker.zone, null);
    } else {
    }
  }}
>

</div>
{/* Opponent General and Advisor - Upper Middle - DESKTOP ONLY */}
{/* Mobile version is rendered outside battlefield-and-carousel */}
{!isContainerMobile && (
<div
  className="opponent-avatars-container"
  style={{
    position: "absolute",
    top: "10px",
    left: "50%",
    transform: "translateX(-50%)",
    transformOrigin: "center top",
    display: "flex",
    gap: "10px",
    alignItems: "center",
    cursor: (yourTurn && attackMode && selectedAttacker) || 
            (deployTargetMode && selectedDeployOrderId) ||
            (effectiveStrategemTargetMode && effectiveSelectedStrategemId && getStrategemTargetingRules(effectiveSelectedStrategemId, targetingData)?.general === true)
              ? "pointer" : "default",
    zIndex: 1000
  }}
  onClick={() => {
    if (yourTurn && attackMode && selectedAttacker) {
      attackGeneral(selectedAttacker.zone, selectedAttacker.idx);
    } else if (deployTargetMode && selectedDeployOrderId) {
      // Allow deploy targeting to select enemy general
      handleTargetDeploy('general', null, 'opponent');
    } else if (strategemTargetMode && selectedStrategemId) {
      // Handle strategem targeting the opponent general
      const targetingRules = getStrategemTargetingRules(selectedStrategemId, targetingData);
      if (targetingRules?.general === true) {
        targetStrategem(
          'general',
          null,
          null,
          'opponent',
          null,
          ws,
          gameId,
          setError,
          setStrategemTargetMode,
          setSelectedStrategemId,
          setStrategemTargetInfo,
          // Animation config
          {
            registerAnimation: strategemTargetAnimations.registerAnimation,
            strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
            strategemId: selectedStrategemId,
            gameState,
            userId
          }
        );
      }
    }
  }}
>
  {/* Opponent Emote Bubble - Show when opponent sends emote */}
  {opponentEmoteBubble && (
    <div style={{
      position: "absolute",
      top: "100px", // ✅ FIX: Position below the general (general is 90px tall)
      left: "50%", // Center horizontally relative to general
      transform: "translateX(-50%)", // Center the bubble
      zIndex: 10002,
      pointerEvents: "none",
      maxWidth: "320px",
      minWidth: "120px"
    }}>
      {/* Speech bubble tail pointing upward toward general */}
      <div style={{
        position: "absolute",
        top: "-8px", // Position above the bubble
        left: "50%",
        transform: "translateX(-50%)",
        width: 0,
        height: 0,
        borderLeft: "10px solid transparent",
        borderRight: "10px solid transparent",
        borderBottom: "10px solid rgba(0, 0, 0, 0.85)", // Match bubble background
        filter: "drop-shadow(0 0 5px rgba(64, 224, 208, 0.6))", // Glow effect
        zIndex: 10003
      }} />
      {/* Speech bubble tail border (turquoise outline) */}
      <div style={{
        position: "absolute",
        top: "-10px", // Slightly above the main tail
        left: "50%",
        transform: "translateX(-50%)",
        width: 0,
        height: 0,
        borderLeft: "11px solid transparent",
        borderRight: "11px solid transparent",
        borderBottom: "11px solid #40E0D0", // Turquoise border
        zIndex: 10002
      }} />
      <div style={{
        backgroundColor: "rgba(0, 0, 0, 0.85)", // Black with opacity
        border: "2px solid #40E0D0", // Turquoise border
        borderRadius: "12px", // Rounded borders
        padding: "12px 16px",
        boxShadow: "0 0 10px rgba(64, 224, 208, 0.6), 0 0 20px rgba(64, 224, 208, 0.4)", // Glowing turquoise effect
        color: "white", // White text
        fontSize: "16px",
        fontWeight: "bold",
        textAlign: "center",
        lineHeight: "1.4",
        wordWrap: "break-word",
        whiteSpace: "pre-wrap",
        display: "inline-block",
        width: "auto",
        minHeight: "40px"
      }}>
        {(() => {
          // Wrap text after 2 words
          const words = opponentEmoteBubble.text.split(' ');
          const wrappedLines = [];
          for (let i = 0; i < words.length; i += 2) {
            wrappedLines.push(words.slice(i, i + 2).join(' '));
          }
          return wrappedLines.join('\n');
        })()}
      </div>
    </div>
  )}

  {/* Opponent General Image with Health Orb and Bar */}
  <div style={{ position: "relative" }} className="opponent-health-orb-container" data-target="opponent-general-area">
    {/* CSS override to move orb */}
    <style>{`
      .opponent-health-orb-container .compact-orb {
        left: -70px !important;
      }
    `}</style>
    {/* Compact Health Orb - positioned to left of avatar */}
    <GeneralStatus
      health={opponent.health}
      intelligenceLeft={opponent.intelligence}
      intelligenceTotal={opponent.maxIntelligence ?? 8}
      barriersRemoved={opponent.barriersRemoved ?? []}
      damageFlash={opponentDamageFlash}
      compact={true}
      showIntelligence={false}
      factionName={calculateFactionFromStrategy(opponent.strategy)}
      translatedFactionName={getTranslatedFactionName(calculateFactionFromStrategy(opponent.strategy))}
      isActiveTurn={!yourTurn}
    />
    
    {/* Opponent Name Display - Below Health Orb */}
    {opponentUsername && (
      <div style={{
        position: "absolute",
        top: "calc(50% + 25px)", // Position below the health orb (orb center is at 50%, orb is 40px tall, so 50% + 20px is bottom, +5px for gap)
        left: "-50px", // Center on the orb (orb is at -70px after shift, orb center is at -70px + 20px = -50px)
        transform: "translateX(-50%)", // Center the name on the orb
        width: "fit-content", // Allow width to expand to fit content
        minWidth: "40px", // At least as wide as the orb
        textAlign: "center",
        color: "white",
        fontSize: "11px",
        fontWeight: "bold",
        textShadow: "1px 1px 2px rgba(0, 0, 0, 0.8)",
        backgroundColor: "rgba(0, 0, 0, 0.6)", // Transparent black background
        padding: "2px 4px",
        borderRadius: "4px",
        whiteSpace: "nowrap", // Prevent text wrapping - keep on one line
        zIndex: 10001,
        pointerEvents: "none"
      }}>
        {opponentUsername}
      </div>
    )}
    
    <div 
      style={{ position: "relative", width: "90px", height: "90px", display: "inline-block", overflow: "visible" }}
      data-target="opponent-general"
      className="opponent-general-avatar"
    >
      {(() => {
        // Check if general can be targeted in attack mode (or during attack drag). Use effective attacker so drag shows yellow box.
        let canAttackGeneral = false;
        if (effectiveAttackMode && effectiveSelectedAttacker) {
          const selfId = String(userId);
          const playerState = gameState.state?.[selfId];
          const attackerUnit = playerState?.playedOrders?.[effectiveSelectedAttacker.zone]?.[effectiveSelectedAttacker.idx];
          const attackerIgnoresProtect = attackerUnit?.abilities?.includes('IgnoreProtect') || 
                                        attackerUnit?.attackEffect === 'ignore_protect_any_zone';
          const attackerZone = effectiveSelectedAttacker.zone;
          const opponentId = gameState.players?.find(id => String(id) !== selfId);
          const opponentState = opponentId ? gameState.state?.[opponentId] : null;
          const protectUnitsInZone = opponentState?.playedOrders?.[attackerZone]?.filter(unit => 
            unit && unit.abilities?.includes('Protect')
          ) || [];
          canAttackGeneral = protectUnitsInZone.length === 0 || attackerIgnoresProtect;
        }
        // Tutorial (e.g. Mentora, Kill threats): when attack targets are restricted to specific units only, general is not a valid target
        let allowGeneralInAttackMode = canAttackGeneral;
        if (effectiveAttackMode && effectiveSelectedAttacker && typeof document !== 'undefined') {
          const t4Attr = document.body.getAttribute('data-tutorial4-attack-restrict');
          if (t4Attr) {
            try {
              const t4 = JSON.parse(t4Attr);
              if (t4.targets && Array.isArray(t4.targets) && t4.targets.length > 0) {
                allowGeneralInAttackMode = false;
              }
            } catch (_) {}
          }
          const overlayAttackAttr = document.body.getAttribute('data-tutorial-overlay-attack-restrict');
          if (overlayAttackAttr) {
            try {
              const o = JSON.parse(overlayAttackAttr);
              if (o.targets && Array.isArray(o.targets) && o.targets.length > 0) {
                allowGeneralInAttackMode = false;
              }
            } catch (_) {}
          }
        }
        const isGeneralTarget = (
          (effectiveStrategemTargetMode && effectiveSelectedStrategemId && getStrategemTargetingRules(effectiveSelectedStrategemId, targetingData)?.general === true) ||
          (deployTargetMode && selectedDeployOrderId && getDeployTargetingRules(selectedDeployOrderId, targetingData)?.general === true) ||
          (effectiveAttackMode && effectiveSelectedAttacker && allowGeneralInAttackMode) // In attack mode (or attack drag), opponent general can be targeted only if not blocked by Protect (and not restricted by tutorial to units only)
        );
        return (
          <div
            className={getTargetingBorderClassName(isGeneralTarget)}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              borderRadius: '8px',
              pointerEvents: 'none',
              zIndex: 1
            }}
          >
            <img 
              src={getOpponentAvatarImage(opponentGeneral)} 
              alt="Opponent General"
              data-zone="opponent_general"
              data-target="opponent-general"
              onClick={() => {
                // Handle strategem targeting the opponent general
                if (strategemTargetMode && selectedStrategemId) {
                  const targetingRules = getStrategemTargetingRules(selectedStrategemId, targetingData);
                  
                  // Check if strategem can target generals and if it can target opponent generals
                  if (targetingRules?.general === true) {
                    const playerRestriction = targetingRules?.player || 'opponent'; // Default to opponent only
                    // Allow targeting if player is 'opponent' or 'both'
                    if (playerRestriction === 'opponent' || playerRestriction === 'both') {
                      targetStrategem(
                        'general',
                        null,
                        null,
                        'opponent',
                        null,
                        ws,
                        gameId,
                        setError,
                        setStrategemTargetMode,
                        setSelectedStrategemId,
                        setStrategemTargetInfo,
                        // Animation config
                        {
                          registerAnimation: strategemTargetAnimations.registerAnimation,
                          strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
                          strategemId: selectedStrategemId,
                          gameState,
                          userId
                        }
                      );
                      return;
                    }
                  }
                }
                
                // Handle deploy unit targeting the opponent general
                if (deployTargetMode && selectedDeployOrderId) {
                  // Get targeting rules for the deployed unit
                  const targetingRules = getDeployTargetingRules(selectedDeployOrderId, targetingData);
                  
                  // Check if the unit can target generals
                  if (targetingRules && targetingRules.general === true) {
                    // Target the general
                    const opponentId = Object.keys(gameState.state).find(id => id !== userId);
                    targetDeploy(
                      'general',
                      null,
                      'opponent',
                      yourTurn,
                      gameId,
                      ws,
                      setError,
                      selectedDeployOrderId,
                      selectedDeployInstanceId,
                      selectedDeployZone,
                      setDeployTargetMode,
                      setSelectedDeployOrderId,
                      setSelectedDeployInstanceId,
                      setSelectedDeployZone,
                      setDeployTargetInfo,
                      gameState, // ✅ FIX: Pass gameState for validation
                      userId // ✅ FIX: Pass playerId for validation
                    );
                  } else {
                  }
                }
                
                // Handle attack mode targeting the opponent general
                if (attackMode && selectedAttacker) {
                  attackOrder(selectedAttacker.zone, selectedAttacker.idx, 'general', null);
                  return;
                }
              }}
              style={{
                width: "100%", 
                height: "100%", 
                objectFit: "contain",
                display: "block",
                ...getDamageFlashStyles(opponentDamageFlash),
                transform: shouldMirrorOpponentAvatar(opponentGeneral, 'upper_middle', 'general', avatars) ? 'scaleX(-1)' : 'none',
                cursor: isGeneralTarget ? 'pointer' : 'default',
                border: (() => {
                  if (isGeneralTarget) {
                    const targetingStyle = getTargetingBorderStyles(true);
                    return targetingStyle ? targetingStyle.border : '3px solid #ffff00';
                  }
                  return 'none';
                })(),
                borderRadius: '8px',
                position: 'relative',
                zIndex: 2
              }} 
            />
          </div>
        );
      })()}
      
      {/* Same turn indicators as player general (arrows when opponent's turn, pause when player's turn), scaled to match 90px vs 180px */}
      <div className="opponent-general-turn-indicator" aria-hidden="true">
        {yourTurn ? (
          <div className="player-waiting-pause">
            <span className="player-waiting-pause-bar" />
            <span className="player-waiting-pause-bar" />
          </div>
        ) : (
          <div className="player-your-turn-arrows">
            <span className="player-your-turn-arrow" style={{ animationDelay: '0s' }} />
            <span className="player-your-turn-arrow" style={{ animationDelay: '0.2s' }} />
            <span className="player-your-turn-arrow" style={{ animationDelay: '0.4s' }} />
          </div>
        )}
      </div>
      
      {/* Opponent Rank Display - Lower Right Corner of Avatar - Only show if not a bot */}
      {opponentRank !== null && opponentRank !== undefined && (() => {
        const effectiveGameStateForAvatars = isSpectating && spectatingGameState ? spectatingGameState : gameState;
        const effectiveUserIdForAvatars = isSpectating && spectatingPlayerId ? spectatingPlayerId : userId;
        const opponentId = effectiveGameStateForAvatars?.players?.find(id => id !== effectiveUserIdForAvatars);
        const isBot = opponentId === 'bot';
        
        if (isBot) return null;
        
        return (
          <div
            style={{
              ...getRankStyle(opponentRank),
              position: "absolute",
              bottom: "2px",
              right: "8px",
              zIndex: 10002,
              fontSize: "14.4px",
              fontWeight: "bold",
              backgroundColor: "rgba(0, 0, 0, 0.85)",
              padding: "3px 6px",
              borderRadius: "5px",
              pointerEvents: "none",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.8)"
            }}
          >
            #{opponentRank}
          </div>
        );
      })()}
    </div>
  </div>
  
  {/* Opponent Advisor Image with Intelligence Orb and Bar */}
  <div style={{ position: "relative" }} className="opponent-intel-orb-container">
    {/* CSS override to move intel orb - mirror of health orb distance */}
    <style>{`
      .opponent-intel-orb-container .compact-intel-orb {
        right: -88px !important;
      }
    `}</style>
    {/* Compact Intelligence Orb and Bar */}
    <GeneralStatus
      health={opponent.health}
      intelligenceLeft={opponent.intelligence}
      intelligenceTotal={opponent.maxIntelligence ?? 8}
      barriersRemoved={opponent.barriersRemoved ?? []}
      damageFlash={opponentDamageFlash}
      compactIntelligence={true}
    />
    
    {/* Title Display - Below Intel Orb */}
    <div style={{
      position: "absolute",
      top: "calc(50% + 25px)",
      right: "-68px",
      transform: "translateX(50%)",
      width: "fit-content",
      minWidth: "40px",
      textAlign: "center",
      color: getTitleColorHex(activeGameState?.opponentTitleColors?.[activeUserId]),
      fontSize: "11px",
      fontWeight: "bold",
      textShadow: "1px 1px 2px rgba(0, 0, 0, 0.8)",
      backgroundColor: "rgba(0, 0, 0, 0.6)",
      padding: "2px 4px",
      borderRadius: "4px",
      whiteSpace: "nowrap",
      zIndex: 10001,
      pointerEvents: "none"
    }}>
      {activeGameState?.opponentTitles?.[activeUserId] || 'The undecided'}
    </div>
    
    <img 
      src={getOpponentAvatarImage(opponentAdvisor)} 
      alt="Opponent Advisor" 
      style={{
        width: "90px", 
        height: "90px", 
        objectFit: "contain",
        transform: shouldMirrorOpponentAvatar(opponentAdvisor, 'upper_middle', 'advisor', avatars) ? 'scaleX(-1)' : 'none'
      }}
    />
  </div>
      </div>
)}

{/* Opponent Drones - Upper Right Corner - DESKTOP ONLY */}
{/* Mobile version is rendered outside battlefield-and-carousel */}
{!isContainerMobile && (
<div
  className="opponent-drone-zone"
  style={{
    position: "absolute",
    top: "10px",
    right: "20px",
    zIndex: 1002,
    display: "flex",
    gap: "4px",
    flexDirection: "row"
  }}
>
    {opponent.drones?.filter(drone => !drone.markedForRemoval).map((drone, index) => {
      // Check if this drone should be highlighted as a target
      const isDroneTarget = isTarget({
        strategemTargetMode: effectiveStrategemTargetMode,
        selectedStrategemId: effectiveSelectedStrategemId,
        strategemTargetInfo: effectiveStrategemTargetInfo,
        immobilizeTargetMode,
        confuseTargetMode,
        weaponTargetMode: effectiveWeaponTargetMode,
        weaponDeployTargetMode,
        deployTargetMode,
        selectedDeployOrderId,
        selectedDeployInstanceId,
        selectedDeployZone,
        zone: "opponent_drones",
        targetingData,
        gameState,
        playerId: userId,
        unit: { ...drone, type: 'drone' },
        attackMode: effectiveAttackMode,
        selectedAttacker: effectiveSelectedAttacker,
        droneTargetMode: effectiveDroneTargetMode,
        selectedDroneId: effectiveSelectedDroneId
      });
      
      return (
        <div
          key={`opponent-drone-${index}`}
          className={getTargetingBorderClassName(isDroneTarget)}
          data-drone-index={index}
          data-drone-zone="opponent_drone"
          data-instance-id={drone.instanceId || ''}
          style={{
            width: '80px',
            height: '60px',
            backgroundColor: '#333',
            border: isDroneTarget ? undefined : '2px solid #a02c2c', // Border handled by className when targeting
            borderRadius: '4px',
            cursor: isDroneTarget ? 'pointer' : 'pointer',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'visible',
            ...(isDroneTarget ? getTargetingBorderStyles(true) : {})
          }}
          data-tooltip={`${drone.name} - ${drone.description || 'Drone'}${drone.charges !== undefined ? ` (${drone.charges} charges)` : ' (permanent effect)'}`}
          data-tooltip-position="bottom"
        onClick={() => {
          // Handle attack mode targeting for drones
          if (attackMode && selectedAttacker) {
            // Attack drone - drones are attacked differently than units
            // We need to call attackOrder with special handling for drones
            // For now, we'll use a special zone identifier for drones
            attackOrder(selectedAttacker.zone, selectedAttacker.idx, 'opponent_drone', index);
            return;
          }
          
          // Handle strategem targeting for drones
          if (strategemTargetMode && selectedStrategemId) {
            targetStrategem(
              "drone", 
              "opponent_drone", 
              index, 
              "opponent", 
              drone.instanceId, 
              ws, 
              gameId, 
              setError, 
              setStrategemTargetMode, 
              setSelectedStrategemId, 
              setStrategemTargetInfo,
              // Animation config
              {
                registerAnimation: strategemTargetAnimations.registerAnimation,
                strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
                strategemId: selectedStrategemId,
                gameState,
                userId
              }
            );
            return;
          }
          
          // Allow deploy targeting to select opponent drones
          if (deployTargetMode && selectedDeployOrderId) {
            handleTargetDeploy('opponent_drone', index, 'opponent');
          }
        }}
      >
        {/* Drone image */}
        <img
          src={`/images/${drone.id}.jpg`}
          alt={drone.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 1
          }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        {/* Drone charge counter overlay */}
        {drone.charges !== undefined && drone.charges > 0 && (
          <div style={{
            color: 'white',
            fontSize: '16px',
            fontWeight: 'bold',
            textShadow: '1px 1px 1px rgba(0,0,0,0.8)',
            position: 'absolute',
            top: '2px',
            right: '2px',
            backgroundColor: 'rgba(0,0,0,0.7)',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2
          }}>
            {drone.charges}
          </div>
        )}
        </div>
      );
    })}
    {/* Empty drone slots */}
    {Array.from({ length: 4 - (opponent.drones?.filter(drone => !drone.markedForRemoval).length || 0) }).map((_, index) => (
      <div
        key={`empty-opponent-drone-${index}`}
        style={{
          width: '80px',
          height: '60px',
          backgroundColor: '#333',
          border: '2px solid #555',
          borderRadius: '4px',
          opacity: 0.3
        }}
      />
    ))}
</div>
)}

{/* Opponent Preparation Bonus and Diplomatic Accord - Next to Drone Zone */}
{/* Desktop only - Mobile version rendered outside battlefield-and-carousel - Hidden in tutorial missions */}
{!isContainerMobile && !isTutorialMission && (() => {
  // Get opponent info directly from gameState to ensure we have latest values
  const currentOpponentId = gameState?.players?.find(id => id !== userId);
  const currentOpponent = currentOpponentId ? gameState?.state?.[currentOpponentId] : null;
  const opponentGoesSecond = currentOpponentId && gameState?.players?.[1] === currentOpponentId;
  const shouldShowPrepBonus = opponentGoesSecond && 
                     opponentPreparationBonus && 
                     (opponentPreparationBonus === 'plan_ahead' || opponentPreparationBonus === 'black_market' || opponentPreparationBonus === 'team_leader');
  
  // Calculate positions: drone zone is at right: 20px, width ~332px (4 drones * 80px + 3 gaps * 4px)
  // Position icons to the left of drone zone
  const droneZoneRight = 20;
  const droneZoneWidth = 332; // 4 * 80 + 3 * 4
  const iconWidth = 84;
  const gap = 10;
  
  // Position: If both exist, show prep bonus first, then diplomatic accord
  // If only one exists, show it at the first position
  const prepBonusRight = droneZoneRight + droneZoneWidth + gap;
  const diplomaticAccordRight = shouldShowPrepBonus 
    ? prepBonusRight + iconWidth + gap 
    : prepBonusRight;
  const iconTop = 10;
  const iconSize = 84;

  return (
    <>
      {/* Opponent Preparation Bonus Icon */}
      {shouldShowPrepBonus && currentOpponent && (
        <div 
          key={`opponent-prep-bonus-${currentOpponentId}-${String(currentOpponent.preparationBonusUsed === true)}`}
          style={{ 
            position: "absolute", 
            right: `${prepBonusRight}px`,
            top: "10px",
            width: "84px", 
            height: "84px",
            zIndex: 1001
          }}
          data-tooltip={
            currentOpponent.preparationBonusUsed === true ? (t('battleUI.preparationBonusUsed') || 'Preparation bonus already used') :
            opponentPreparationBonus === 'plan_ahead' ? t('profile.preparationBonus.planAhead') :
            opponentPreparationBonus === 'black_market' ? t('profile.preparationBonus.blackMarketDossier') :
            t('profile.preparationBonus.teamLeaderMeeting')
          }
          data-tooltip-position="bottom"
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              border: "2px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "8px",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              margin: 0,
              ...(currentOpponent.preparationBonusUsed === true ? {
                filter: 'grayscale(100%)',
                background: 'rgba(30, 30, 30, 0.7)',
                opacity: 0.7
              } : {
                background: 'rgba(50, 50, 50, 0.5)',
                filter: 'none',
                opacity: 1
              })
            }}
          >
            <img 
              src={`/images/${opponentPreparationBonus === 'plan_ahead' ? 'plan_ahead.jpg' : 
                            opponentPreparationBonus === 'black_market' ? 'black_market_dossier.jpg' : 
                            'team_leader_briefing.jpg'}`}
              alt={opponentPreparationBonus}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                pointerEvents: "none"
              }}
            />
          </div>
        </div>
      )}
      
      {/* Opponent Diplomatic Accord Icon */}
      {opponentDiplomaticAccord && (
        <div 
          style={{ 
            position: "absolute", 
            right: `${diplomaticAccordRight}px`,
            top: `${iconTop}px`,
            width: `${iconSize}px`, 
            height: `${iconSize}px`,
            zIndex: 1001
          }}
          data-tooltip={
            opponentDiplomaticAccord === 'strategic_coordination' ? t('profile.diplomaticAccords.strategicCoordination') :
            opponentDiplomaticAccord === 'public_relations' ? t('profile.diplomaticAccords.publicRelations') :
            opponentDiplomaticAccord === 'emergency_protocol' ? t('profile.diplomaticAccords.emergencyProtocol') :
            t('profile.diplomaticAccords.commandDirective')
          }
          data-tooltip-position="bottom"
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              border: "2px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "8px",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              margin: 0,
              background: 'rgba(50, 50, 50, 0.5)',
              filter: 'none',
              opacity: 1
            }}
          >
            <img 
              src={`/images/${opponentDiplomaticAccord === 'strategic_coordination' ? 'strategic_coordination.jpg' : 
                            opponentDiplomaticAccord === 'public_relations' ? 'public_relations.jpg' : 
                            opponentDiplomaticAccord === 'emergency_protocol' ? 'emergency_protocol.jpg' : 
                            'command_directive.jpg'}`}
              alt={opponentDiplomaticAccord}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                pointerEvents: "none"
              }}
            />
          </div>
        </div>
      )}
    </>
  );
})()}

{/* Spectator Indicator - Below Opponent Drone Zone, Left of O: S: C: */}
{/* Desktop: position right side, Mobile: scaled and hidden to save space */}
{spectators && spectators.length > 0 && !isContainerMobile && (
  <div className="spectator-indicator" style={{
    position: "absolute",
    right: "220px", // Position to the left of O: S: C: counters (which start at right: 20px)
    top: "70px", // Below drone zone (which is at top: 10px, height ~60px)
    zIndex: 1001,
    fontSize: '12px',
    color: '#a0a0a0',
    fontStyle: 'italic',
    whiteSpace: 'nowrap',
    maxWidth: '180px',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }}>
    {spectators.map((spectator, index) => (
      <div key={spectator.odId || index} style={{ marginBottom: index < spectators.length - 1 ? '2px' : '0' }}>
        {spectator.username} {t('battleUI.isSpectating')}
      </div>
    ))}
  </div>
)}

{/* Opponent Stats - Below Images in Upper Right - DESKTOP ONLY */}
{/* Mobile version is rendered outside battlefield-and-carousel */}
{!isContainerMobile && (
<div className="opponent-stats-counters" style={{ 
  position: "absolute", 
  right: "20px", 
  top: "80px", 
  zIndex: 1000,
  display: 'flex', 
  gap: "20px",
  fontSize: "14px",
  fontWeight: 'bold',
  color: 'white'
}}>
  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
    <span style={{ color: '#e24a4a', cursor: 'help' }} data-tooltip={t('battleUI.ordersReady')}>O:</span>
    <span>{opponent.hand.length}</span>
  </div>
  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
    <span style={{ color: '#e24a4a', cursor: 'help' }} data-tooltip={t('battleUI.strategySize')}>S:</span>
    <span>{opponent.deck?.length ?? opponent.strategy?.orders?.reduce((sum, order) => sum + (order.count || 1), 0) ?? 0}</span>
  </div>
  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
    <span style={{ color: '#e24a4a', cursor: 'help' }} data-tooltip={t('battleUI.completedOrders')}>C:</span>
    <span>{opponent.completedOrders?.length || 0}</span>
  </div>
</div>
)}



        {/* Battlefield: two rows of zones + zone cross overlay (always visible) */}
        <div className="battlefield-with-cross" style={{ position: 'relative', width: '100%', flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Zone cross: white lines separating the four zones; above zones, no pointer events */}
        <div className="battlefield-zone-cross-overlay" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 5 }}>
          <div className="zone-cross-line zone-cross-vertical" style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 2, marginLeft: -1, backgroundColor: 'rgba(255,255,255,0.5)' }} />
          <div className="zone-cross-line zone-cross-horizontal" style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 2, marginTop: -1, backgroundColor: 'rgba(255,255,255,0.5)' }} />
        </div>
        {/* Opponent Battlefield Zones */}
        <div 
          className="battlefield-container viewport-scaled" 
          style={{ 
            display: "flex", 
            flexDirection: "row", 
            justifyContent: "space-between", 
            // MOBILE FIX: Parent battlefield-and-carousel already extends -24px, so children just use 100%
            width: isContainerMobile ? "100%" : "calc(100% + 48px)",
            marginLeft: isContainerMobile ? "0" : "-24px",
            marginRight: isContainerMobile ? "0" : "-24px",
            padding: "0", 
            marginBottom: 0, 
            gap: "0px", 
            maxWidth: "none",
            boxSizing: "border-box",
            ...(isContainerMobile ? {
              height: 'calc(50% - 2px)',
              minHeight: 0
            } : {})
          }}
        >
          {/* Opponent Frontline */}
          <div
            data-zone-key="opponent_front"
            className={`battle-zone viewport-scaled opponent-front ${(() => {
              const zoneStyles = getZoneTargetingStyles(effectiveStrategemTargetMode, effectiveStrategemTargetInfo, 'zone', 'opponent_front');
              return zoneStyles && zoneStyles.border ? getTargetingBorderClassName(true) : '';
            })()}`}
            style={{ 
              position: 'relative',
              zIndex: 1, // Keep constant to prevent position shifts
              ...getSlideInStyles(animationsStarted, 'left', 100),
              outline: "3px solid #ff0000",
              borderRight: '1px solid rgba(255,255,255,0.5)',
              ...(isContainerMobile ? { 
                width: 'auto', 
                flex: '1 1 0', 
                minWidth: 0,
                height: '100%', // Fill the battlefield-container height
                minHeight: 0
              } : {}),
              ...getZoneTargetingStyles(effectiveStrategemTargetMode, effectiveStrategemTargetInfo, 'zone', 'opponent_front')
            }}
            onClick={() => {
              if (!yourTurn) return;
              const animationConfig = {
                registerAnimation: strategemTargetAnimations.registerAnimation,
                strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
                strategemId: selectedStrategemId,
                gameState,
                userId
              };
              handleZoneClick("opponent_front", strategemTargetMode, selectedStrategemId, strategemTargetInfo, targetStrategem, ws, gameId, setError, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, targetingData, animationConfig);
            }}
          >

            <div style={{ display: "flex", gap: 0, justifyContent: "center", alignItems: "center", width: "100%", height: "100%", minHeight: 0 }}>
{/* ========== DEBUGGING: Always render all 5 slots ========== */}
{/* TODO: Change back to opponentFront.slice(0, 5).map((order, idx) => { */}
{Array.from({length: 5}).map((_, idx) => {
  // Always render all 5 slots - get order from array if it exists
  const order = opponentFront[idx];
  // Use getVisibleUnit to handle delayed removals
  const visibleOrder = getVisibleUnit('opponent_front', idx, order);
  // COMMENTED OUT - slot number calculation no longer needed
  // const slotNumber = getGlobalSlotNumber('opponent_front', idx); // DEBUGGING: Slot number calculation
  
  if (!visibleOrder) {
    // Empty slot - render death animations if any
    // ========== DEBUGGING: Empty slot box with number and zone label ==========
    // COMMENTED OUT - no longer needed
    /*
    const isHighlighted = slotNumber === 4 || slotNumber === 18; // DEBUGGING: Highlight slots 4 and 18
    return (
      <React.Fragment key={`opponent_front_empty_${idx}`}>
        <SlotDeathAnimations zoneName="opponent_front" index={idx} leftOffset={0} />
        <div
          style={{
            position: 'absolute',
            position: 'relative',
            flex: '0 0 auto',
            top: '0px',
                width: '200px',
                height: '100px',
            backgroundColor: isHighlighted ? 'rgba(255, 255, 0, 0.4)' : 'rgba(100, 100, 100, 0.3)',
            border: isHighlighted ? '4px solid #ffff00' : '1px dashed rgba(102, 102, 102, 0.35)',
            borderRadius: '8px',
            opacity: 0.8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '-1px',
            padding: '0px',
            boxSizing: 'border-box',
            boxShadow: isHighlighted ? '0 0 20px rgba(255, 255, 0, 0.8)' : 'none'
          }}
        >
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px'
          }}>
            <span style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#fff',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
            }}>
              {slotNumber}
            </span>
            <span style={{
              fontSize: '10px',
              fontWeight: 'bold',
              color: '#fff',
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
              textTransform: 'uppercase'
            }}>
              Opponent Front
            </span>
          </div>
        </div>
      </React.Fragment>
    );
    */
    // ========== END DEBUGGING ==========
    return (
      <React.Fragment key={`opponent_front_empty_${idx}`}>
        <SlotDeathAnimations zoneName="opponent_front" index={idx} leftOffset={0} />
        <div
          className="empty-slot-container"
          data-slot-zone="opponent_front"
          data-slot-index={idx}
          style={{
            position: 'relative',
            // MOBILE FIX: Let CSS control flex, margins on mobile
            ...(isContainerMobile ? {} : { flex: '0 0 auto', marginLeft: idx > 0 ? '-15px' : '0' }),
          }}
        >
          <div
            style={{
              position: 'relative',
            }}
          >
            <div
              style={{
                ...ordersStyles.container,
                backgroundColor: 'transparent',
                width: '200px',
                height: '100px',
                margin: '-1px',
                padding: '0px',
                fontSize: '0.9em',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                border: '0px solid #ccc',
                borderRadius: '8px',
                boxSizing: 'border-box',
                overflow: 'visible',
                transform: 'none', // Removed to allow proper vertical centering
                pointerEvents: 'none',
              }}
            >
              {/* Border matching the image size (80% width, 140% height) */}
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '80%', // Match image width
                  height: '140%', // Match image height
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: '8px',
                  pointerEvents: 'none',
                }}
              />
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
  
  // OLD ATTACK ANIMATION CODE REMOVED - Now using isUnitAttacking from useAttackAnimations hook
  // The old activeAttack state is no longer used - all attack animations use the new attack-initiated flow
  const isAttacking = false; // OLD CODE DISABLED
  const isBeingAttacked = false; // OLD CODE DISABLED
  const attackerStillExists = true; // OLD CODE DISABLED - units are now hidden by getVisibleUnit using isUnitAttacking
  
  // Don't apply deployment animation if unit is attacking OR being attacked (attack animation takes precedence)
  const deploymentStyles = (isAttacking || isBeingAttacked) ? {} : getDeploymentAnimationStyles(opponentId, 'front', idx);
  // ========== DEBUGGING: Highlight slots 4 and 18 ==========
  // COMMENTED OUT - no longer needed
  // const isHighlighted = slotNumber === 4 || slotNumber === 18; // TODO: Remove highlighting
  // ========== END DEBUGGING ==========
  
  // Key by slot index only so the same DOM node stays mounted when slot content changes (unit dies/deploys).
  // Otherwise React would unmount the div and onMouseLeave never fires, leaving the card stuck expanded.
  return (
    <div
      key={`opponent_front_${idx}`}
      data-slot-zone="opponent_front"
      data-slot-index={idx}
      data-unit-key={visibleOrder?.instanceId ? `${opponentId}_${visibleOrder.instanceId}` : null}
      style={{
        position: 'relative',
        overflow: 'visible',
        // MOBILE FIX: Let CSS control flex, margins, transform on mobile
        ...(isContainerMobile ? {} : {
          flex: '0 0 auto',
          marginLeft: idx > 0 ? '-15px' : '0',
          transform: hoveredPlayed.zone === "opponent_front" && hoveredPlayed.idx === idx
            ? ((attackMode || (lastAttackedTarget && lastAttackedTarget.zone === "opponent_front" && lastAttackedTarget.idx === idx)) ? "scale(0.95)" : "scale(1.05)")
            : "scale(1)",
          transformOrigin: "center",
        }),
        marginTop: 0,
        marginRight: isContainerMobile ? 0 : 0,
        marginBottom: 0,
        cursor: (() => {
          const isCounterStrikeStrategemTargeting = strategemTargetMode && !yourTurn;
          const isCounterStrikeDeployTargeting = deployTargetMode && selectedDeployOrderId && !yourTurn;
          
          if (!yourTurn && !isCounterStrikeStrategemTargeting && !isCounterStrikeDeployTargeting) return "default";
          if (strategemTargetMode || immobilizeTargetMode || confuseTargetMode || weaponTargetMode || (deployTargetMode && selectedDeployOrderId === 4 && selectedDeployZone === "front")) {
            return "pointer";
          }
          if (attackMode && selectedAttacker && (selectedAttacker.zone === "front" || selectedAttacker.id === 75)) {
            if (visibleOrder?.isOOR) {
              return "not-allowed";
            }
            return "pointer";
          }
          return "default";
        })(),
        zIndex: hoveredPlayed.zone === "opponent_front" && hoveredPlayed.idx === idx ? 100 : 1,
        transition: isContainerMobile ? 'none' : (deploymentStyles.animation ? "opacity 0.2s ease" : "transform 0.2s ease"),
        ...(isContainerMobile ? {} : deploymentStyles),
        opacity: 1,
      }}
  onMouseEnter={() => {
    playSoundEffect('/sounds/hoverover.mp3', 0.3);
    setHoveredPlayed({ zone: "opponent_front", idx });
    sendOrderHover(visibleOrder?.instanceId);
  }}
  onMouseLeave={() => {
    setHoveredPlayed({ zone: null, idx: null });
    sendOrderHover(null);
    // Clear last attacked target when mouse leaves it, re-enabling expand
    if (lastAttackedTarget && lastAttackedTarget.zone === "opponent_front" && lastAttackedTarget.idx === idx) {
      setLastAttackedTarget(null);
    }
  }}
  onClick={(e) => {
    
    // Check for blocking overlays
    const allBlockOverlays = [
      'front-block-overlay',
      'flank-block-overlay',
      'end-turn-block-overlay',
      'forsaken-sentinel-block-overlay',
      'counter-strike-player-front-block-overlay',
      'counter-strike-opponent-front-block-overlay',
      'counter-strike-keep-button-block-overlay',
      'isabela-block-overlay',
      'tranquilizer-block-overlay'
    ];
    const blockingOverlays = allBlockOverlays.filter(id => {
      const overlay = document.getElementById(id);
      return overlay && overlay.style.display !== 'none' && window.getComputedStyle(overlay).display !== 'none';
    });
    if (blockingOverlays.length > 0) {
      console.error('❌ BLOCKING OVERLAYS DETECTED ON TARGET CLICK:', blockingOverlays);
      blockingOverlays.forEach(id => {
        const overlay = document.getElementById(id);
        if (overlay) {
          const rect = overlay.getBoundingClientRect();
          const clickX = e.clientX;
          const clickY = e.clientY;
          const isOverOverlay = clickX >= rect.left && clickX <= rect.right && clickY >= rect.top && clickY <= rect.bottom;
          console.error(`❌ Overlay ${id}:`, {
            display: overlay.style.display,
            computedDisplay: window.getComputedStyle(overlay).display,
            pointerEvents: overlay.style.pointerEvents,
            computedPointerEvents: window.getComputedStyle(overlay).pointerEvents,
            zIndex: overlay.style.zIndex,
            computedZIndex: window.getComputedStyle(overlay).zIndex,
            rect: { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom },
            clickPos: { x: clickX, y: clickY },
            isOverOverlay
          });
        }
      });
    } else {
    }
    
    e.stopPropagation(); // Prevent event from bubbling to zone handler
    // Handle drone targeting separately as it's not in the extracted function
    if (yourTurn && droneTargetMode && selectedDroneId) {
      targetDrone("opponent_front", idx, ws, setError, setDroneTargetMode, setSelectedDroneId, setSelectedDroneInstanceId, selectedDroneId, selectedDroneInstanceId, gameId, gameState, userId);
      return;
    }
    
    // Use extracted targeting click handler
    // Create animation config for strategem targeting
    const strategemAnimationConfig = {
      registerAnimation: strategemTargetAnimations.registerAnimation,
      strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
      strategemId: selectedStrategemId,
      gameState,
      userId
    };
    handleTargetingClick(
      "opponent_front",
      idx,
      yourTurn,
      strategemTargetMode,
      selectedStrategemId,
      strategemTargetInfo,
      immobilizeTargetMode,
      selectedImmobilizeWeaponId,
      confuseTargetMode,
      selectedConfuseWeaponId,
      weaponTargetMode,
      selectedWeaponId,
      selectedWeaponInstanceId,
      weaponDeployTargetMode,
      weaponDeployTargetInfo,
      deployTargetMode,
      selectedDeployOrderId,
      attackMode,
      selectedAttacker,
      targetStrategem,
      targetImmobilize,
      targetConfuse,
      handleTargetDeploy,
      weaponDeployTarget,
      attachWeapon,
      attackOrder,
      ws,
      gameId,
      setError,
      setStrategemTargetMode,
      setSelectedStrategemId,
      setStrategemTargetInfo,
      setImmobilizeTargetMode,
      setSelectedImmobilizeWeaponId,
      setConfuseTargetMode,
      setSelectedConfuseWeaponId,
      setWeaponTargetMode,
      setSelectedWeaponId,
      setWeaponDeployTargetMode,
      setWeaponDeployTargetInfo,
      // New validation parameters
      targetingData,
      gameState,
      userId,
      selectedDeployZone,
      // Replacement mode parameters
      replaceTargetMode,
      selectedReplaceUnitId,
      replaceTargetZone,
      setClickedUnitForReplace,
      // Animation config
      strategemAnimationConfig
    );
  }}
>
  {isNewlyDeployedLegendaryUnique(opponentId, 'front', idx) && (
    <LegendaryUniqueDeployRay />
  )}
  {(() => {
    const targetModeVal = getTargetingMode(strategemTargetMode, immobilizeTargetMode, confuseTargetMode, weaponTargetMode, deployTargetMode, selectedDeployOrderId, selectedDeployZone);
    // Always run isTarget validation first to prevent instanceId collisions
    const isTargetValid = isTarget({
      strategemTargetMode: effectiveStrategemTargetMode,
      selectedStrategemId: effectiveSelectedStrategemId,
      strategemTargetInfo: effectiveStrategemTargetInfo,
      immobilizeTargetMode,
      confuseTargetMode,
      weaponTargetMode: effectiveWeaponTargetMode,
      weaponDeployTargetMode,
      deployTargetMode,
      selectedDeployOrderId,
      selectedDeployInstanceId,
      selectedDeployZone,
        zone: "opponent_front",
      targetingData,
      gameState,
        playerId: userId,
        unit: visibleOrder,
        attackMode: effectiveAttackMode,
        selectedAttacker: effectiveSelectedAttacker,
        droneTargetMode: effectiveDroneTargetMode,
        selectedDroneId: effectiveSelectedDroneId
      });
      
      // Tutorial overlay can force yellow border when strategemTargetAllowOnly includes this unit (e.g. Strike the Berserker → 377)
      const tutorialOverlayHighlight = strategemTargetMode && selectedStrategemId && isTutorialOverlayStrategemTarget(visibleOrder, selectedStrategemId);
      const isTargetVal = isTargetValid || tutorialOverlayHighlight;
      return (
        <>
          <div
            className={`deployed-unit-slot ${!isNewlyDeployed(opponentId, 'front', idx) && !visibleOrder?._isRepositioning && !isImmobilizedUI(visibleOrder, "opponent_front") ? 'deployed-unit-slot-idle' : ''}`.trim()}
            data-slot-zone="opponent_front"
            data-slot-index={idx}
            style={{ position: 'relative' }}
          >
            {isNewlyDeployedLegendary(opponentId, 'front', idx) && (
              <LegendaryDeployGlow />
            )}
            <OrderDesign 
              order={visibleOrder} 
              count={0} 
              minimal={attackMode || (lastAttackedTarget && lastAttackedTarget.zone === "opponent_front" && lastAttackedTarget.idx === idx) || hoveredPlayed.zone !== "opponent_front" || hoveredPlayed.idx !== idx}
              scale={viewportScale} 
              isOOR={visibleOrder.isOOR}
              isSelected={selectedOrderInstanceId === visibleOrder.instanceId}
              isHighlighted={highlightedInstanceIds.some(id => String(id).trim() === String(visibleOrder.instanceId).trim())}
              isTarget={isTargetVal}
              forceTargetAppearance={tutorialOverlayHighlight}
              targetMode={targetModeVal}
              zoneName="opponent_front"
              slotIndex={idx}
              gameState={gameState}
              isHiddenDuringAnimation={attackAnimations.isUnitHidden(visibleOrder?.instanceId)}
              isViewedByOpponent={!!(opponentLookingAtInstanceId && visibleOrder?.instanceId && String(opponentLookingAtInstanceId) === String(visibleOrder.instanceId))}
            />
          </div>
          {/* ========== DEBUGGING: Slot number and zone label overlay ========== */}
          {/* COMMENTED OUT - no longer needed */}
          {/*
          <div style={{
            position: 'absolute',
            top: '5px',
            left: '5px',
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#fff',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: '2px 6px',
            borderRadius: '4px',
            zIndex: 1000,
            pointerEvents: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px'
          }}>
            <span>{slotNumber}</span>
            <span style={{ fontSize: '9px', textTransform: 'uppercase' }}>Opponent Front</span>
          </div>
          */}
          {/* ========== END DEBUGGING ========== */}
        {isImmobilizedUI(visibleOrder, "opponent_front") && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '84px',
              height: '84px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 6,
              animation: 'immobilizedPulse 2s ease-in-out infinite',
              pointerEvents: 'none',
            }}
            title="Immobilized"
          >
            <img
              src="/images/immobilize.png"
              alt="Immobilized"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
          </div>
        )}
      </>
    );
  })()}
</div>
  );
})}

            </div>
          </div>
          {/* Opponent Flank */}

          <div
            data-zone-key="opponent_flank"
            className={`battle-zone viewport-scaled opponent-flank ${(() => {
              const zoneStyles = getZoneTargetingStyles(effectiveStrategemTargetMode, effectiveStrategemTargetInfo, 'zone', 'opponent_flank');
              return zoneStyles && zoneStyles.border ? getTargetingBorderClassName(true) : '';
            })()}`}
            style={{ 
              position: 'relative',
              zIndex: 1, // Keep constant to prevent position shifts
              ...getSlideInStyles(animationsStarted, 'right', 200),
              outline: "3px solid #ff0000",
              borderLeft: '1px solid rgba(255,255,255,0.5)',
              ...(isContainerMobile ? { 
                width: 'auto', 
                flex: '1 1 0', 
                minWidth: 0,
                height: '100%', // Fill the battlefield-container height
                minHeight: 0
              } : {}),
              ...getZoneTargetingStyles(effectiveStrategemTargetMode, effectiveStrategemTargetInfo, 'zone', 'opponent_flank')
            }}
            onClick={() => {
              if (!yourTurn) return;
              const animationConfig = {
                registerAnimation: strategemTargetAnimations.registerAnimation,
                strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
                strategemId: selectedStrategemId,
                gameState,
                userId
              };
              handleZoneClick("opponent_flank", strategemTargetMode, selectedStrategemId, strategemTargetInfo, targetStrategem, ws, gameId, setError, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, targetingData, animationConfig);
            }}
          >

            <div style={{ display: "flex", gap: 0, justifyContent: "center", alignItems: "center", width: "100%", height: "100%", minHeight: 0 }}>
             {/* ========== DEBUGGING: Always render all 5 slots ========== */}
             {/* TODO: Change back to opponentFlank.slice(0, 5).map((order, idx) => { */}
             {Array.from({length: 5}).map((_, idx) => {
               // Always render all 5 slots - get order from array if it exists
               const order = opponentFlank[idx];
               const visibleOrder = getVisibleUnit('opponent_flank', idx, order);
               // COMMENTED OUT - slot number calculation no longer needed
               // const slotNumber = getGlobalSlotNumber('opponent_flank', idx); // DEBUGGING: Slot number calculation
               
               if (!visibleOrder) {
                 // ========== DEBUGGING: Empty slot box with number and zone label ==========
                 // COMMENTED OUT - no longer needed
                 /*
                 const isHighlighted = slotNumber === 4 || slotNumber === 18; // DEBUGGING: Highlight slots 4 and 18
                 return (
                   <React.Fragment key={`opponent_flank_empty_${idx}`}>
                     <SlotDeathAnimations zoneName="opponent_flank" index={idx} leftOffset={0} />
                     <div
                       style={{
                         position: 'absolute',
                         position: 'relative',
            flex: '0 0 auto',
                         top: '0px',
                width: '200px',
                height: '100px',
                         backgroundColor: isHighlighted ? 'rgba(255, 255, 0, 0.4)' : 'rgba(100, 100, 100, 0.3)',
                         border: isHighlighted ? '4px solid #ffff00' : '1px dashed rgba(102, 102, 102, 0.35)',
                         borderRadius: '8px',
                         opacity: 0.8,
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center',
                         margin: '-1px',
                         padding: '0px',
                         boxSizing: 'border-box',
                         boxShadow: isHighlighted ? '0 0 20px rgba(255, 255, 0, 0.8)' : 'none'
                       }}
                     >
                       <div style={{
                         display: 'flex',
                         flexDirection: 'column',
                         alignItems: 'center',
                         justifyContent: 'center',
                         gap: '4px'
                       }}>
                         <span style={{
                           fontSize: '24px',
                           fontWeight: 'bold',
                           color: '#fff',
                           textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                         }}>
                           {slotNumber}
                         </span>
                         <span style={{
                           fontSize: '10px',
                           fontWeight: 'bold',
                           color: '#fff',
                           textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                           textTransform: 'uppercase'
                         }}>
                           Opponent Flank
                         </span>
                       </div>
                     </div>
                   </React.Fragment>
                 );
                 */
                 // ========== END DEBUGGING ==========
                 return (
                   <React.Fragment key={`opponent_flank_empty_${idx}`}>
                     <SlotDeathAnimations zoneName="opponent_flank" index={idx} leftOffset={0} />
                     <div
                       className="empty-slot-container"
                       data-slot-zone="opponent_flank"
                       data-slot-index={idx}
                       style={{
                         position: 'relative',
                         // MOBILE FIX: Let CSS control flex, margins on mobile
                         ...(isContainerMobile ? {} : { flex: '0 0 auto', marginLeft: idx > 0 ? '-15px' : '0' }),
                       }}
                     >
                       <div
                         style={{
                           position: 'relative',
                         }}
                       >
                         <div
                           style={{
                             ...ordersStyles.container,
                             backgroundColor: 'transparent',
                width: '200px',
                height: '100px',
                             margin: '-1px',
                             padding: '0px',
                             fontSize: '0.9em',
                             display: 'flex',
                             flexDirection: 'column',
                             justifyContent: 'center',
                             alignItems: 'center',
                             border: '0px solid #ccc',
                             borderRadius: '8px',
                             boxSizing: 'border-box',
                             overflow: 'visible',
                             transform: 'none', // Removed to allow proper vertical centering
                             pointerEvents: 'none',
                           }}
                         >
                           {/* Border matching the image size (80% width, 140% height) */}
                           <div
                             style={{
                               position: 'absolute',
                               top: '50%',
                               left: '50%',
                               transform: 'translate(-50%, -50%)',
                               width: '80%', // Match image width
                               height: '140%', // Match image height
                               border: '1px solid rgba(255, 255, 255, 0.12)',
                               borderRadius: '8px',
                               pointerEvents: 'none',
                             }}
                           />
                         </div>
                       </div>
                     </div>
                   </React.Fragment>
                 );
               }
               
               // Check if this unit is currently animating an attack
               // OLD CODE DISABLED - using new attack-initiated flow
               const isAttackingFlank = false;
               const isBeingAttackedFlank = false;
               const attackerStillExistsFlank = true;
               const deploymentStylesFlank = getDeploymentAnimationStyles(opponentId, 'flank', idx);
               // ========== DEBUGGING: Highlight slots 4 and 18 ==========
               // COMMENTED OUT - no longer needed
               // const isHighlighted = slotNumber === 4 || slotNumber === 18; // TODO: Remove highlighting
               // ========== END DEBUGGING ==========
               
               // Key by slot index only so the same DOM node stays mounted when slot content changes (avoids stuck expand).
               return (
<div
  key={`opponent_flank_${idx}`}
  data-slot-zone="opponent_flank"
  data-slot-index={idx}
  data-unit-key={visibleOrder?.instanceId ? `${opponentId}_${visibleOrder.instanceId}` : null}
  style={{
    position: 'relative',
    // MOBILE FIX: Let CSS control flex, margins, transform on mobile
    ...(isContainerMobile ? {} : {
      flex: '0 0 auto',
      marginLeft: idx > 0 ? '-15px' : '0',
      transform: hoveredPlayed.zone === "opponent_flank" && hoveredPlayed.idx === idx
        ? ((attackMode || (lastAttackedTarget && lastAttackedTarget.zone === "opponent_flank" && lastAttackedTarget.idx === idx)) ? "scale(0.95)" : "scale(1.05)")
        : "scale(1)",
      transformOrigin: "center",
    }),
    marginTop: 0,
    marginRight: isContainerMobile ? 0 : 0,
    marginBottom: 0,
    cursor: (() => {
      const isCounterStrikeStrategemTargeting = strategemTargetMode && !yourTurn;
      const isCounterStrikeDeployTargeting = deployTargetMode && selectedDeployOrderId && !yourTurn;
      
      if (!yourTurn && !isCounterStrikeStrategemTargeting && !isCounterStrikeDeployTargeting) return "default";
      if (strategemTargetMode || immobilizeTargetMode || confuseTargetMode || weaponTargetMode || (deployTargetMode && selectedDeployOrderId === 4 && selectedDeployZone === "flank")) {
        return "pointer";
      }
      if (attackMode && selectedAttacker && (selectedAttacker.zone === "flank" || selectedAttacker.id === 75)) {
        if (visibleOrder?.isOOR) {
          return "not-allowed";
        }
        return "pointer";
      }
      return "default";
    })(),
    zIndex: hoveredPlayed.zone === "opponent_flank" && hoveredPlayed.idx === idx ? 100 : 1,
    transition: isContainerMobile ? 'none' : (deploymentStylesFlank.animation ? "opacity 0.2s ease" : "transform 0.2s ease"),
    ...(isContainerMobile ? {} : deploymentStylesFlank),
    opacity: 1,
  }}
  onMouseEnter={() => {
    playSoundEffect('/sounds/hoverover.mp3', 0.3);
    setHoveredPlayed({ zone: "opponent_flank", idx });
    sendOrderHover(visibleOrder?.instanceId);
  }}
  onMouseLeave={() => {
    setHoveredPlayed({ zone: null, idx: null });
    sendOrderHover(null);
    // Clear last attacked target when mouse leaves it, re-enabling expand
    if (lastAttackedTarget && lastAttackedTarget.zone === "opponent_flank" && lastAttackedTarget.idx === idx) {
      setLastAttackedTarget(null);
    }
  }}
  onClick={(e) => {
    e.stopPropagation(); // Prevent event from bubbling to zone handler
    // Handle drone targeting separately as it's not in the extracted function
    if (yourTurn && droneTargetMode && selectedDroneId) {
      targetDrone("opponent_flank", idx, ws, setError, setDroneTargetMode, setSelectedDroneId, setSelectedDroneInstanceId, selectedDroneId, selectedDroneInstanceId, gameId, gameState, userId);
      return;
    }
    
    // Use extracted targeting click handler
    // Create animation config for strategem targeting
    const strategemAnimationConfig = {
      registerAnimation: strategemTargetAnimations.registerAnimation,
      strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
      strategemId: selectedStrategemId,
      gameState,
      userId
    };
    handleTargetingClick(
      "opponent_flank",
      idx,
      yourTurn,
      strategemTargetMode,
      selectedStrategemId,
      strategemTargetInfo,
      immobilizeTargetMode,
      selectedImmobilizeWeaponId,
      confuseTargetMode,
      selectedConfuseWeaponId,
      weaponTargetMode,
      selectedWeaponId,
      selectedWeaponInstanceId,
      weaponDeployTargetMode,
      weaponDeployTargetInfo,
      deployTargetMode,
      selectedDeployOrderId,
      attackMode,
      selectedAttacker,
      targetStrategem,
      targetImmobilize,
      targetConfuse,
      handleTargetDeploy,
      weaponDeployTarget,
      attachWeapon,
      attackOrder,
      ws,
      gameId,
      setError,
      setStrategemTargetMode,
      setSelectedStrategemId,
      setStrategemTargetInfo,
      setImmobilizeTargetMode,
      setSelectedImmobilizeWeaponId,
      setConfuseTargetMode,
      setSelectedConfuseWeaponId,
      setWeaponTargetMode,
      setSelectedWeaponId,
      setWeaponDeployTargetMode,
      setWeaponDeployTargetInfo,
      // New validation parameters
      targetingData,
      gameState,
      userId,
      selectedDeployZone,
      // Replacement mode parameters
      replaceTargetMode,
      selectedReplaceUnitId,
      replaceTargetZone,
      setClickedUnitForReplace,
      // Animation config
      strategemAnimationConfig
    );
  }}
>
  {isNewlyDeployedLegendaryUnique(opponentId, 'flank', idx) && (
    <LegendaryUniqueDeployRay />
  )}
  <>
    <div
      className={`deployed-unit-slot ${!isNewlyDeployed(opponentId, 'flank', idx) && !visibleOrder?._isRepositioning && !isImmobilizedUI(visibleOrder, "opponent_flank") ? 'deployed-unit-slot-idle' : ''}`.trim()}
      data-slot-zone="opponent_flank"
      data-slot-index={idx}
      style={{ position: 'relative' }}
    >
      {isNewlyDeployedLegendary(opponentId, 'flank', idx) && (
        <LegendaryDeployGlow />
      )}
      <OrderDesign
        order={visibleOrder}
        count={0} 
        minimal={attackMode || (lastAttackedTarget && lastAttackedTarget.zone === "opponent_flank" && lastAttackedTarget.idx === idx) || hoveredPlayed.zone !== "opponent_flank" || hoveredPlayed.idx !== idx}
        scale={viewportScale}
        isOOR={visibleOrder.isOOR}
        isSelected={selectedOrderInstanceId === visibleOrder.instanceId}
        isHighlighted={highlightedInstanceIds.some(id => String(id).trim() === String(visibleOrder.instanceId).trim())}
        zoneName="opponent_flank"
        slotIndex={idx}
        gameState={gameState}
        isHiddenDuringAnimation={attackAnimations.isUnitHidden(visibleOrder?.instanceId)}
        isUnitTargetInPendingAttack={attackAnimations.isUnitTargetInPendingAttack}
        isViewedByOpponent={!!(opponentLookingAtInstanceId && visibleOrder?.instanceId && String(opponentLookingAtInstanceId) === String(visibleOrder.instanceId))}
      isTarget={isTarget({
        strategemTargetMode: effectiveStrategemTargetMode,
        selectedStrategemId: effectiveSelectedStrategemId,
        strategemTargetInfo: effectiveStrategemTargetInfo,
        immobilizeTargetMode,
        confuseTargetMode,
        weaponTargetMode: effectiveWeaponTargetMode,
        weaponDeployTargetMode,
        deployTargetMode,
        selectedDeployOrderId,
        selectedDeployInstanceId,
        selectedDeployZone,
        zone: "opponent_flank",
        targetingData,
        gameState,
        playerId: userId,
        attackMode: effectiveAttackMode,
        selectedAttacker: effectiveSelectedAttacker,
        droneTargetMode: effectiveDroneTargetMode,
        selectedDroneId: effectiveSelectedDroneId,
        unit: visibleOrder
      }) || (strategemTargetMode && selectedStrategemId && isTutorialOverlayStrategemTarget(visibleOrder, selectedStrategemId))}
      forceTargetAppearance={strategemTargetMode && selectedStrategemId && isTutorialOverlayStrategemTarget(visibleOrder, selectedStrategemId)}
      targetMode={getTargetingMode(strategemTargetMode, immobilizeTargetMode, confuseTargetMode, weaponTargetMode, deployTargetMode, selectedDeployOrderId, selectedDeployZone)}
    />
    </div>
    {/* ========== DEBUGGING: Slot number and zone label overlay ========== */}
    {/* COMMENTED OUT - no longer needed */}
    {/*
    <div style={{
      position: 'absolute',
      top: '5px',
      left: '5px',
      fontSize: '12px',
      fontWeight: 'bold',
      color: '#fff',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      padding: '2px 6px',
      borderRadius: '4px',
      zIndex: 1000,
      pointerEvents: 'none',
      display: 'flex',
      flexDirection: 'column',
      gap: '2px'
    }}>
      <span>{slotNumber}</span>
      <span style={{ fontSize: '9px', textTransform: 'uppercase' }}>Opponent Flank</span>
    </div>
    */}
    {/* ========== END DEBUGGING ========== */}
  </>
  </div>
               );
             })}

            </div>
          </div>
        </div>
        {/* Your Battlefield Zones */}
        <div 
          className="battlefield-container viewport-scaled" 
          style={{ 
            display: "flex", 
            flexDirection: "row", 
            justifyContent: "space-between", 
            // MOBILE FIX: Parent battlefield-and-carousel already extends -24px, so children just use 100%
            width: isContainerMobile ? "100%" : "calc(100% + 48px)",
            marginLeft: isContainerMobile ? "0" : "-24px",
            marginRight: isContainerMobile ? "0" : "-24px",
            padding: "0", 
            marginBottom: 4, 
            gap: "0px", 
            maxWidth: "none",
            boxSizing: "border-box",
            ...(isContainerMobile ? {
              height: 'calc(50% - 2px)',
              minHeight: 0
            } : {})
          }}
        >
          {/* Frontline */}

  <div
  data-zone-key="front"
  className={`battle-zone viewport-scaled front ${(() => {
    const selectedOrder = selectedOrderInstanceId ? you?.hand?.find(o => o.instanceId === selectedOrderInstanceId) : null;
    const isStrategemSelected = selectedOrder?.type === 'Strategem';
    const shouldHighlightForDeployment = (((selectedOrderInstanceId && yourTurn) || selectedCounterStrikeOrder) && !isStrategemSelected) || isDeployDragActive;
    const isStrategemZoneTargeting = effectiveStrategemTargetMode && effectiveStrategemTargetInfo?.type === 'zone';
    const targetingRules = effectiveStrategemTargetInfo?.targetingOptions?.[0];
    const playerRestriction = targetingRules?.player || 'opponent';
    const isFriendlyZoneTargeting = isStrategemZoneTargeting && (playerRestriction === 'friendly' || playerRestriction === 'both');
    
    return getTargetingBorderClassName(shouldHighlightForDeployment || isFriendlyZoneTargeting);
  })()}`}
  style={{
    position: 'relative',
    zIndex: 1, // Keep constant to prevent position shifts
    ...getSlideInStyles(animationsStarted, 'left', 300),
    borderRight: '1px solid rgba(255,255,255,0.5)',
    ...(isContainerMobile ? { width: 'auto', flex: '1 1 0', minWidth: 0, height: '100%', minHeight: 0 } : {}),
    ...(() => {
      // Check if we should highlight for unit deployment (carousel, counter strike, or drag)
      // ✅ FIX: Exclude strategems from deployment highlighting
      // ✅ FIX: Allow counterstrike highlighting even when it's not the player's turn (counterstrike happens during opponent's turn)
      const selectedOrder = selectedOrderInstanceId ? you?.hand?.find(o => o.instanceId === selectedOrderInstanceId) : null;
      const isStrategemSelected = selectedOrder?.type === 'Strategem';
      const shouldHighlightForDeployment = (((selectedOrderInstanceId && yourTurn) || selectedCounterStrikeOrder) && !isStrategemSelected) || isDeployDragActive;
      
      if (shouldHighlightForDeployment) {
        // Use unified targeting border style
        return getTargetingBorderStyles(true);
      }
      
      // Check for strategem zone targeting mode (from Request button or drag)
      if (effectiveStrategemTargetMode && effectiveStrategemTargetInfo?.type === 'zone') {
        const targetingRules = effectiveStrategemTargetInfo?.targetingOptions?.[0];
        const playerRestriction = targetingRules?.player || 'opponent';
        // Highlight friendly zones if targeting allows friendly
        if (playerRestriction === 'friendly' || playerRestriction === 'both') {
          return getTargetingBorderStyles(true);
        }
      }
      
      return getZoneTargetingStyles(effectiveStrategemTargetMode, effectiveStrategemTargetInfo, 'zone', 'front');
    })()
  }}
  onDragOver={e => e.preventDefault()}
  onDrop={e => {
    if (!yourTurn || !draggedOrderId) return;
    playOrder(draggedOrderId, "front", null, draggedOrderInstanceId);
    setDraggedOrderId(null);
    setDraggedOrderInstanceId(null);
    setSelectedOrderId(null);
    setSelectedOrderInstanceId(null);
  }}
  onClick={() => {
    // ✅ FIX: Allow counterstrike zone clicks even when it's not the player's turn (counterstrike happens during opponent's turn)
    // Handle counter strike deployment FIRST (before turn check) - separate from carousel
    if (selectedCounterStrikeOrder) {
      playCounterStrike(selectedCounterStrikeOrder.id, "front", selectedCounterStrikeOrder.instanceId);
      setSelectedCounterStrikeOrder(null);
      return;
    }
    
    // Allow zone clicks during counter strike deploy targeting (e.g. Bulwark needs to target a unit)
    const isCounterStrikeDeployTargetingFront = deployTargetMode && selectedDeployOrderId && !yourTurn;
    if (!yourTurn && !isCounterStrikeDeployTargetingFront) return;
    
    // Handle strategem zone targeting (from Request button flow) - CHECK THIS FIRST
    const frontAnimationConfig = {
      registerAnimation: strategemTargetAnimations.registerAnimation,
      strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
      strategemId: selectedStrategemId,
      gameState,
      userId
    };
    if (handleZoneClick("front", strategemTargetMode, selectedStrategemId, strategemTargetInfo, targetStrategem, ws, gameId, setError, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, targetingData, frontAnimationConfig)) {
      return;
    }
    
    // ✅ FIX: Check if selected order is a strategem - prevent zone deployment
    // Only block if NOT in strategem targeting mode
    if (selectedOrderInstanceId && !strategemTargetMode) {
      const selectedOrder = you?.hand?.find(o => o.instanceId === selectedOrderInstanceId);
      if (selectedOrder?.type && (selectedOrder.type.toLowerCase() === 'strategem' || selectedOrder.type.toLowerCase() === 'stratagem')) {
        setError(t('battleUI.useRequestButton'));
        setTimeout(() => setError(''), 2000);
        return;
      }
    }
    
    // ✅ FIX: Also block zone clicks when strategem is in unit targeting mode
    if (strategemTargetMode && strategemTargetInfo?.type === 'unit') {
      setError(t('battleUI.clickUnitNotZone'));
      setTimeout(() => setError(''), 2000);
      return;
    }
    
    // Handle deploy targeting: place the unit we're in targeting mode for (same instanceId backend will set)
    if (deployTargetMode && selectedDeployOrderId && selectedDeployInstanceId) {
      const frontUnitsCount = you.playedOrders.front.filter(u => u !== null).length;
      if (frontUnitsCount >= 5) {
        setError(t('battleUI.zoneFull'));
        setTimeout(() => setError(''), 2000);
        return;
      }
      playOrder(selectedDeployOrderId, "front", null, selectedDeployInstanceId);
      return;
    }
    
    // Handle normal unit deployment
    if (selectedOrderInstanceId) {
      // Check if selected order is a strategem
      const selectedOrder = you?.hand?.find(o => o.instanceId === selectedOrderInstanceId);
      if (selectedOrder?.type && (selectedOrder.type.toLowerCase() === 'strategem' || selectedOrder.type.toLowerCase() === 'stratagem')) {
        setError(t('battleUI.useRequestButton'));
        setTimeout(() => setError(''), 2000);
        return;
      }
      
      // ✅ Check if zone is full - enter replacement mode
      const frontUnitsCount = you.playedOrders.front.filter(u => u !== null).length;
      if (frontUnitsCount >= 5) {
        setReplaceTargetMode(true);
        setSelectedReplaceUnitId(selectedOrderId);
        setSelectedReplaceInstanceId(selectedOrderInstanceId);
        setReplaceTargetZone('front');
        setError(t('battleUI.zoneFull'));
        return;
      }
      
      // ✅ FIX: Just deploy the unit - don't set targeting mode immediately
      // The backend will mark the unit with needsDeployTarget flag
      // The frontend will detect this from the updated game state and set up targeting
      // with the CORRECT instanceId that was assigned by the backend
      playOrder(selectedOrderId, "front", null, selectedOrderInstanceId);
      setSelectedOrderId(null);
      setSelectedOrderInstanceId(null);
    }
  }}
>

{/* ========== DEBUGGING: Always render all 5 slots ========== */}
{/* TODO: Change back to yourFront.slice(0, 5).map((order, idx) => { */}
<div style={{ display: "flex", gap: 0, justifyContent: "center", alignItems: "center", width: "100%", height: "100%", minHeight: 0 }}>
  {Array.from({length: 5}).map((_, idx) => {
    // Always render all 5 slots - get order from array if it exists
    const order = yourFront[idx];
    const visibleOrder = getVisibleUnit('your_front', idx, order);
    // COMMENTED OUT - slot number calculation no longer needed
    // const slotNumber = getGlobalSlotNumber('front', idx); // DEBUGGING: Slot number calculation
    
    if (!visibleOrder) {
      // ========== DEBUGGING: Empty slot box with number and zone label ==========
      // COMMENTED OUT - no longer needed
      /*
      const isHighlighted = slotNumber === 4 || slotNumber === 18; // DEBUGGING: Highlight slots 4 and 18
      return (
        <React.Fragment key={`your_front_empty_${idx}`}>
          <SlotDeathAnimations zoneName="your_front" index={idx} leftOffset={0} />
          <div
            style={{
              position: 'relative',
              flex: '0 0 auto',
              marginLeft: idx > 0 ? (isContainerMobile ? '-5px' : '-15px') : '0',
              backgroundColor: isHighlighted ? 'rgba(255, 255, 0, 0.4)' : 'rgba(100, 100, 100, 0.3)',
              border: isHighlighted ? '4px solid #ffff00' : '1px dashed rgba(102, 102, 102, 0.35)',
              borderRadius: '8px',
              opacity: 0.8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '-1px',
              padding: '0px',
              boxSizing: 'border-box',
              boxShadow: isHighlighted ? '0 0 20px rgba(255, 255, 0, 0.8)' : 'none'
            }}
          >
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px'
            }}>
              <span style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#fff',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
              }}>
                {slotNumber}
              </span>
              <span style={{
                fontSize: '10px',
                fontWeight: 'bold',
                color: '#fff',
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                textTransform: 'uppercase'
              }}>
                Player Front
              </span>
            </div>
          </div>
        </React.Fragment>
      );
      */
      // ========== END DEBUGGING ==========
      return (
        <React.Fragment key={`your_front_empty_${idx}`}>
          <SlotDeathAnimations zoneName="your_front" index={idx} leftOffset={0} />
          <div
            className="empty-slot-container"
            data-slot-zone="your_front"
            data-slot-index={idx}
            style={{
              position: 'relative',
              // MOBILE FIX: Let CSS control flex, margins on mobile
              ...(isContainerMobile ? {} : { flex: '0 0 auto', marginLeft: idx > 0 ? '-15px' : '0' }),
            }}
          >
            <div
              style={{
                position: 'relative',
              }}
            >
              <div
                style={{
                  ...ordersStyles.container,
                  backgroundColor: 'transparent',
                width: '200px',
                height: '100px',
                  margin: '-1px',
                  padding: '0px',
                  fontSize: '0.9em',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '0px solid #ccc',
                  borderRadius: '8px',
                  boxSizing: 'border-box',
                  overflow: 'visible',
                  transform: 'none', // Removed to allow proper vertical centering
                  pointerEvents: 'none',
                }}
              >
                {/* Border matching the image size (80% width, 140% height) */}
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%', // Match image width
                    height: '140%', // Match image height
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '8px',
                    pointerEvents: 'none',
                  }}
                />
              </div>
            </div>
          </div>
        </React.Fragment>
      );
    }
    
    const isHovered = hoveredPlayed.zone === "front" && hoveredPlayed.idx === idx;
    const isSelectedAttacker = selectedAttacker && selectedAttacker.zone === "front" && selectedAttacker.idx === idx;
    
    // Check if this unit is currently animating an attack
    // OLD CODE DISABLED - using new attack-initiated flow
    const isAttackingFront = false;
    const isBeingAttackedFront = false;
    const attackerStillExistsFront = true;
    const deploymentStylesFront = getDeploymentAnimationStyles(userId, 'front', idx);
    // ========== DEBUGGING: Highlight slots 4 and 18 ==========
    // COMMENTED OUT - no longer needed
    // const isHighlighted = slotNumber === 4 || slotNumber === 18; // TODO: Remove highlighting
    // ========== END DEBUGGING ==========
    
    return (
      <div
        key={`your_front_${visibleOrder?.id || order?.id || idx}_${idx}`}
        data-slot-zone="your_front"
        data-slot-index={idx}
        data-unit-key={visibleOrder?.instanceId ? `${userId}_${visibleOrder.instanceId}` : null}
          style={{
            position: 'relative',
            // MOBILE FIX: Let CSS control flex, margins, transform on mobile
            ...(isContainerMobile ? {} : {
              flex: '0 0 auto',
              marginLeft: idx > 0 ? '-15px' : '0',
              transform: isHovered ? "scale(1.05)" : "scale(1)",
              transformOrigin: "center",
            }),
            marginTop: 0,
            marginRight: isContainerMobile ? 0 : 0,
            marginBottom: 0,
          cursor: yourTurn ? "pointer" : "default",
          zIndex: isHovered ? 100 : 1,
          transition: isContainerMobile ? 'none' : (deploymentStylesFront.animation ? "opacity 0.2s ease" : "transform 0.2s ease"),
          ...(isContainerMobile ? {} : deploymentStylesFront),
          opacity: (isAttackingFront && !attackerStillExistsFront) ? 0 : (isAttackingFront ? 0 : 1),
        }}
        onMouseEnter={() => {
          // Don't trigger hover if replacement buttons are showing for this unit
          const hasReplaceButtons = replaceTargetMode && clickedUnitForReplace && clickedUnitForReplace.instanceId === visibleOrder.instanceId;
          if (!hasReplaceButtons) {
            playSoundEffect('/sounds/hoverover.mp3', 0.3);
            setHoveredPlayed({ zone: "front", idx });
          }
          sendOrderHover(visibleOrder?.instanceId);
        }}
        onMouseLeave={() => {
          setHoveredPlayed({ zone: null, idx: null });
          sendOrderHover(null);
        }}
        onMouseDown={(e) => {
          if (isContainerMobile || !yourTurn || !visibleOrder) return;
          startAttackMouseDrag('front', idx, e.clientX, e.clientY);
        }}
        // Mobile touch handlers for attack dragging
        onTouchStart={(e) => {
          if (!isMobile || !yourTurn || !visibleOrder) return;
          handleAttackTouchStart(e, visibleOrder, 'front', idx);
        }}
        onTouchMove={(e) => {
          if (!isMobile || !yourTurn) return;
          handleAttackTouchMove(e);
        }}
        onTouchEnd={(e) => {
          if (!isMobile || !yourTurn) return;
          const { wasAttackDrag, attacker, target } = handleAttackTouchEnd(e);
          
          if (wasAttackDrag && attacker && target) {
            e.preventDefault();
            e.stopPropagation();
            
            if (target.type === 'general') {
              // Attack opponent general
              attackOrder(attacker.zone, attacker.index, 'general', null);
            } else if (target.type === 'unit') {
              // Attack opponent unit
              attackOrder(attacker.zone, attacker.index, target.zone, target.index);
            }
          } else if (!wasAttackDrag) {
            // Not a drag - let click handler process it on next event cycle
            // Do nothing here, onClick will handle it
          }
        }}
        onClick={(e) => {
          // On mobile, skip onClick to prevent double-firing
          if (isMobile) return;
          
          
          // Check for blocking overlays
          const allBlockOverlays = [
            'front-block-overlay',
            'flank-block-overlay',
            'end-turn-block-overlay',
            'forsaken-sentinel-block-overlay',
            'counter-strike-player-front-block-overlay',
            'counter-strike-opponent-front-block-overlay',
            'counter-strike-keep-button-block-overlay',
            'isabela-block-overlay',
            'tranquilizer-block-overlay'
          ];
          const blockingOverlays = allBlockOverlays.filter(id => {
            const overlay = document.getElementById(id);
            return overlay && overlay.style.display !== 'none' && window.getComputedStyle(overlay).display !== 'none';
          });
          if (blockingOverlays.length > 0) {
            console.warn('⚠️ BLOCKING OVERLAYS DETECTED:', blockingOverlays);
            blockingOverlays.forEach(id => {
              const overlay = document.getElementById(id);
              if (overlay) {
                const rect = overlay.getBoundingClientRect();
                const clickX = e.clientX;
                const clickY = e.clientY;
                const isOverOverlay = clickX >= rect.left && clickX <= rect.right && clickY >= rect.top && clickY <= rect.bottom;
                console.warn(`⚠️ Overlay ${id}:`, {
                  display: overlay.style.display,
                  computedDisplay: window.getComputedStyle(overlay).display,
                  pointerEvents: overlay.style.pointerEvents,
                  computedPointerEvents: window.getComputedStyle(overlay).pointerEvents,
                  zIndex: overlay.style.zIndex,
                  computedZIndex: window.getComputedStyle(overlay).zIndex,
                  rect: { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom },
                  clickPos: { x: clickX, y: clickY },
                  isOverOverlay
                });
              }
            });
          } else {
          }
          
          // Allow clicks during counter strike deploy targeting (e.g. Bulwark +2 target)
          const isCounterStrikeDeployTargeting = deployTargetMode && selectedDeployOrderId && !yourTurn;
          if (!yourTurn && !isCounterStrikeDeployTargeting) {
            return;
          }
          
          // Handle replacement mode (for full zones)
          if (replaceTargetMode && selectedReplaceUnitId && replaceTargetZone === "front") {
            // Clear hover state to force minimal view
            setHoveredPlayed({ zone: null, idx: null });
            
            // Store clicked unit info to trigger button rendering
            setClickedUnitForReplace({
              unit: visibleOrder,
              zone: "front",
              index: idx,
              instanceId: visibleOrder.instanceId
            });
            return;
          }
          
          // Handle unit click when unit is selected in hand and zone is full (immediate replace mode)
          if (selectedOrderInstanceId && !replaceTargetMode) {
            const selectedOrder = you?.hand?.find(o => o.instanceId === selectedOrderInstanceId);
            // Skip if strategem is selected
            if (selectedOrder?.type && (selectedOrder.type.toLowerCase() === 'strategem' || selectedOrder.type.toLowerCase() === 'stratagem')) {
              // Let other handlers process this
            } else {
              // Check if front zone is full
              const frontUnitsCount = you.playedOrders.front.filter(u => u !== null).length;
              if (frontUnitsCount >= 5) {
                // Clear hover state to force minimal view
                setHoveredPlayed({ zone: null, idx: null });
                
                // Enter replace mode and immediately show buttons on this unit
                setReplaceTargetMode(true);
                setSelectedReplaceUnitId(selectedOrderId);
                setSelectedReplaceInstanceId(selectedOrderInstanceId);
                setReplaceTargetZone('front');
                setClickedUnitForReplace({
                  unit: visibleOrder,
                  zone: "front",
                  index: idx,
                  instanceId: visibleOrder.instanceId
                });
                return;
              }
            }
          }
          
          // Handle drone targeting
          if (droneTargetMode && selectedDroneId) {
            targetDrone("front", idx, ws, setError, setDroneTargetMode, setSelectedDroneId, setSelectedDroneInstanceId, selectedDroneId, selectedDroneInstanceId, gameId, gameState, userId);
            return;
          }
          
          // Handle weapon targeting
          if (weaponTargetMode && selectedWeaponId) {
            const targetUnit = {
              ...visibleOrder,
              zone: "front",
              index: idx
            };
            attachWeapon(targetUnit, ws, setError, setWeaponTargetMode, selectedWeaponId, setSelectedWeaponId, selectedWeaponInstanceId, gameId, userId, gameState, setWeaponDeployTargetMode, setWeaponDeployTargetInfo);
            return;
          }
          
          // Handle weapon deploy targeting
          if (weaponDeployTargetMode && weaponDeployTargetInfo) {
            const targetUnit = {
              ...visibleOrder,
              zone: "front",
              index: idx
            };
            weaponDeployTarget(targetUnit, ws, setError, weaponDeployTargetInfo, gameId);
            return;
          }
          
          // Handle confuse targeting
          if (confuseTargetMode && selectedConfuseWeaponId) {
            targetConfuse("front", idx, ws, setError, setConfuseTargetMode, setSelectedConfuseWeaponId);
            return;
          }
          
          // Handle strategem targeting
          if (strategemTargetMode && selectedStrategemId) {
            const animationConfig = {
              registerAnimation: strategemTargetAnimations.registerAnimation,
              strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
              strategemId: selectedStrategemId,
              gameState,
              userId
            };
            
            if (strategemTargetInfo?.type === 'zone') {
              // Target the zone itself
              targetStrategem("zone", "front", null, null, null, ws, gameId, setError, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, animationConfig);
            } else {
              // Target a unit in the zone
              const targetUnit = visibleOrder;
              targetStrategem("unit", "front", idx, "friendly", targetUnit?.instanceId, ws, gameId, setError, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, animationConfig);
            }
            return;
          }
          
          // Handle deploy targeting
          if (deployTargetMode && selectedDeployOrderId) {
            handleTargetDeploy("front", idx, "friendly");
            return;
          }
          
          // Handle attack selection
          if (
            selectedAttacker &&
            selectedAttacker.zone === "front" &&
            selectedAttacker.idx === idx
          ) {
            setSelectedAttacker(null);
            setAttackMode(false);
            setLastAttackedTarget(null); // Clear when canceling attack mode
          } else {
            // ✅ VALIDATION: Check if unit has 0 attack rating - cannot attack
            const attackRating = visibleOrder?.attack_rating ?? 0;
            if (attackRating === 0) {
              setError('This unit has 0 attack rating and cannot attack');
              return;
            }
            
            setSelectedAttacker({ zone: "front", idx, id: visibleOrder?.id || order?.id });
            setAttackMode(true);
            setLastAttackedTarget(null); // Clear when selecting new attacker
          }
        }}
      >
        {isNewlyDeployedLegendaryUnique(userId, 'front', idx) && (
          <LegendaryUniqueDeployRay />
        )}
        {(() => {
          const computedIsTarget = isTarget({
            strategemTargetMode: effectiveStrategemTargetMode,
            selectedStrategemId: effectiveSelectedStrategemId,
            strategemTargetInfo: effectiveStrategemTargetInfo,
            immobilizeTargetMode: false,
            confuseTargetMode,
            weaponTargetMode: effectiveWeaponTargetMode,
            weaponDeployTargetMode,
            deployTargetMode,
            selectedDeployOrderId,
            selectedDeployInstanceId,
            selectedDeployZone,
            zone: "front",
            targetingData,
            gameState,
            playerId: userId,
            attackMode: effectiveAttackMode,
            selectedAttacker: effectiveSelectedAttacker,
            droneTargetMode: effectiveDroneTargetMode,
            selectedDroneId: effectiveSelectedDroneId,
            unit: visibleOrder
          });
          // Use the computed targeting result (database-driven logic handles restrictions)
          const guardedIsTarget = computedIsTarget;
          // Check if replacement buttons are showing for this unit
          const hasReplaceButtons = replaceTargetMode && clickedUnitForReplace && clickedUnitForReplace.instanceId === visibleOrder.instanceId;
          return (
            <>
              <div
                className={`deployed-unit-slot ${!isNewlyDeployed(userId, 'front', idx) && !visibleOrder?._isRepositioning && !isImmobilizedUI(visibleOrder, "your_front") ? 'deployed-unit-slot-idle' : ''}`.trim()}
                data-slot-zone="your_front"
                data-slot-index={idx}
                style={{ position: 'relative' }}
              >
                {isNewlyDeployedLegendary(userId, 'front', idx) && (
                  <LegendaryDeployGlow />
                )}
                <OrderDesign
                  order={visibleOrder}
                  count={0} 
                  minimal={!isHovered || hasReplaceButtons}
                  scale={viewportScale}
                  isOOR={visibleOrder.isOOR}
                  isSelected={(selectedAttacker && selectedAttacker.zone === "front" && selectedAttacker.idx === idx) || selectedOrderInstanceId === visibleOrder.instanceId}
                  isHighlighted={highlightedInstanceIds.some(id => String(id).trim() === String(visibleOrder.instanceId).trim())}
                  isTarget={guardedIsTarget}
                  targetMode={getTargetingMode(strategemTargetMode, false, confuseTargetMode, false, deployTargetMode, selectedDeployOrderId, selectedDeployZone)}
                  zoneName="your_front"
                  slotIndex={idx}
                  gameState={gameState}
                  isHiddenDuringAnimation={attackAnimations.isUnitHidden(visibleOrder.instanceId)}
                  isUnitTargetInPendingAttack={attackAnimations.isUnitTargetInPendingAttack}
                  isViewedByOpponent={!!(opponentLookingAtInstanceId && visibleOrder?.instanceId && String(opponentLookingAtInstanceId) === String(visibleOrder.instanceId))}
                />
              </div>
              {/* ========== DEBUGGING: Slot number and zone label overlay ========== */}
              {/* COMMENTED OUT - no longer needed */}
              {/*
              <div style={{
                position: 'absolute',
                top: '5px',
                left: '5px',
                fontSize: '12px',
                fontWeight: 'bold',
                color: '#fff',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                padding: '2px 6px',
                borderRadius: '4px',
                zIndex: 1000,
                pointerEvents: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px'
              }}>
                <span>{slotNumber}</span>
                <span style={{ fontSize: '9px', textTransform: 'uppercase' }}>Player Front</span>
              </div>
              */}
              {/* ========== END DEBUGGING ========== */}
              {/* Inline Replace/Cancel buttons */}
              {hasReplaceButtons && (
                <div className="replace-buttons-container">
                  <button
                    className="replace-button replace-button-confirm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReplaceUnit(clickedUnitForReplace);
                    }}
                  >
                    Replace
                  </button>
                  <button
                    className="replace-button replace-button-cancel"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelReplace();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
              {/* Portal displacement (War Effort): one charge to switch zone (front → flank) */}
              {gameState?.defenderPortalActive && String(userId) === String(gameState.warEffortDefenderPlayerId) && visibleOrder?.portalDisplacementCharges > 0 && (
                <button
                  type="button"
                  className="portal-displacement-button"
                  title="Switch zone (Portal)"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePortalDisplacement(visibleOrder, 'flank');
                  }}
                >
                  Portal (→flank)
                </button>
              )}
            </>
          );
        })()}
      </div>
    );
  })}
</div>

          </div>
          {/* Flank */}
<div
  data-zone-key="flank"
  className={`battle-zone viewport-scaled flank ${(() => {
    const selectedOrder = selectedOrderInstanceId ? you?.hand?.find(o => o.instanceId === selectedOrderInstanceId) : null;
    const isStrategemSelected = selectedOrder?.type === 'Strategem';
    const shouldHighlightForDeployment = (((selectedOrderInstanceId && yourTurn) || selectedCounterStrikeOrder) && !isStrategemSelected) || isDeployDragActive;
    const isStrategemZoneTargeting = effectiveStrategemTargetMode && effectiveStrategemTargetInfo?.type === 'zone';
    const targetingRules = effectiveStrategemTargetInfo?.targetingOptions?.[0];
    const playerRestriction = targetingRules?.player || 'opponent';
    const isFriendlyZoneTargeting = isStrategemZoneTargeting && (playerRestriction === 'friendly' || playerRestriction === 'both');

    return getTargetingBorderClassName(shouldHighlightForDeployment || isFriendlyZoneTargeting);
  })()}`}
  style={{
    position: 'relative',
    zIndex: 1, // Keep constant to prevent position shifts
    ...getSlideInStyles(animationsStarted, 'right', 400),
    borderLeft: '1px solid rgba(255,255,255,0.5)',
    ...(isContainerMobile ? { width: 'auto', flex: '1 1 0', minWidth: 0, height: '100%', minHeight: 0 } : {}),
    ...(() => {
      // Check if we should highlight for unit deployment (carousel, counter strike, or drag)
      // ✅ FIX: Exclude strategems from deployment highlighting
      // ✅ FIX: Allow counterstrike highlighting even when it's not the player's turn (counterstrike happens during opponent's turn)
      const selectedOrder = selectedOrderInstanceId ? you?.hand?.find(o => o.instanceId === selectedOrderInstanceId) : null;
      const isStrategemSelected = selectedOrder?.type === 'Strategem';
      const shouldHighlightForDeployment = (((selectedOrderInstanceId && yourTurn) || selectedCounterStrikeOrder) && !isStrategemSelected) || isDeployDragActive;

      if (shouldHighlightForDeployment) {
        // Use unified targeting border style
        return getTargetingBorderStyles(true);
      }

      // Check for strategem zone targeting mode (from Request button or drag)
      if (effectiveStrategemTargetMode && effectiveStrategemTargetInfo?.type === 'zone') {
        const targetingRules = effectiveStrategemTargetInfo?.targetingOptions?.[0];
        const playerRestriction = targetingRules?.player || 'opponent';
        // Highlight friendly zones if targeting allows friendly
        if (playerRestriction === 'friendly' || playerRestriction === 'both') {
          return getTargetingBorderStyles(true);
        }
      }
      
      return getZoneTargetingStyles(effectiveStrategemTargetMode, effectiveStrategemTargetInfo, 'zone', 'flank');
    })()
  }}
  onDragOver={e => e.preventDefault()}
  onDrop={e => {
    if (!yourTurn || !draggedOrderId) return;
    playOrder(draggedOrderId, "flank", null, draggedOrderInstanceId);
    setDraggedOrderId(null);
    setDraggedOrderInstanceId(null);
    setSelectedOrderId(null);
    setSelectedOrderInstanceId(null);
  }}
  onClick={() => {
    // ✅ FIX: Allow counterstrike zone clicks even when it's not the player's turn (counterstrike happens during opponent's turn)
    // Handle counter strike deployment FIRST (before turn check) - separate from carousel
    if (selectedCounterStrikeOrder) {
      playCounterStrike(selectedCounterStrikeOrder.id, "flank", selectedCounterStrikeOrder.instanceId);
      setSelectedCounterStrikeOrder(null);
      return;
    }
    
    // Allow zone clicks during counter strike deploy targeting (e.g. Bulwark needs to target a unit)
    const isCounterStrikeDeployTargetingFlank = deployTargetMode && selectedDeployOrderId && !yourTurn;
    if (!yourTurn && !isCounterStrikeDeployTargetingFlank) return;
    
    // Handle strategem zone targeting (from Request button flow) - CHECK THIS FIRST
    const flankAnimationConfig = {
      registerAnimation: strategemTargetAnimations.registerAnimation,
      strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
      strategemId: selectedStrategemId,
      gameState,
      userId
    };
    if (handleZoneClick("flank", strategemTargetMode, selectedStrategemId, strategemTargetInfo, targetStrategem, ws, gameId, setError, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, targetingData, flankAnimationConfig)) {
      return;
    }
    
    // ✅ FIX: Check if selected order is a strategem - prevent zone deployment
    // Only block if NOT in strategem targeting mode
    if (selectedOrderInstanceId && !strategemTargetMode) {
      const selectedOrder = you?.hand?.find(o => o.instanceId === selectedOrderInstanceId);
      if (selectedOrder?.type && (selectedOrder.type.toLowerCase() === 'strategem' || selectedOrder.type.toLowerCase() === 'stratagem')) {
        setError(t('battleUI.useRequestButton'));
        setTimeout(() => setError(''), 2000);
        return;
      }
    }
    
    // ✅ FIX: Also block zone clicks when strategem is in unit targeting mode
    if (strategemTargetMode && strategemTargetInfo?.type === 'unit') {
      setError(t('battleUI.clickUnitNotZone'));
      setTimeout(() => setError(''), 2000);
      return;
    }
    
    // Handle deploy targeting: place the unit we're in targeting mode for (same instanceId backend will set)
    if (deployTargetMode && selectedDeployOrderId && selectedDeployInstanceId) {
      const flankUnitsCount = you.playedOrders.flank.filter(u => u !== null).length;
      if (flankUnitsCount >= 5) {
        setError(t('battleUI.zoneFull'));
        setTimeout(() => setError(''), 2000);
        return;
      }
      playOrder(selectedDeployOrderId, "flank", null, selectedDeployInstanceId);
      return;
    }
    
    // Handle normal unit deployment
    if (selectedOrderInstanceId) {
      // Check if selected order is a strategem
      const selectedOrder = you?.hand?.find(o => o.instanceId === selectedOrderInstanceId);
      if (selectedOrder?.type && (selectedOrder.type.toLowerCase() === 'strategem' || selectedOrder.type.toLowerCase() === 'stratagem')) {
        setError(t('battleUI.useRequestButton'));
        setTimeout(() => setError(''), 2000);
        return;
      }
      
      // ✅ Check if zone is full - enter replacement mode
      const flankUnitsCount = you.playedOrders.flank.filter(u => u !== null).length;
      if (flankUnitsCount >= 5) {
        setReplaceTargetMode(true);
        setSelectedReplaceUnitId(selectedOrderId);
        setSelectedReplaceInstanceId(selectedOrderInstanceId);
        setReplaceTargetZone('flank');
        setError(t('battleUI.zoneFull'));
        return;
      }
      
      // ✅ FIX: Just deploy the unit - don't set targeting mode immediately
      // The backend will mark the unit with needsDeployTarget flag
      // The frontend will detect this from the updated game state and set up targeting
      // with the CORRECT instanceId that was assigned by the backend
      playOrder(selectedOrderId, "flank", null, selectedOrderInstanceId);
      setSelectedOrderId(null);
      setSelectedOrderInstanceId(null);
    }
  }}
>


{/* ========== DEBUGGING: Always render all 5 slots ========== */}
{/* TODO: Change back to yourFlank.slice(0, 5).map((order, idx) => { */}
<div style={{ display: "flex", gap: 0, justifyContent: "center", alignItems: "center", width: "100%", height: "100%", minHeight: 0 }}>
  {Array.from({length: 5}).map((_, idx) => {
    // Always render all 5 slots - get order from array if it exists
    const order = yourFlank[idx];
    const visibleOrder = getVisibleUnit('your_flank', idx, order);
    // COMMENTED OUT - slot number calculation no longer needed
    // const slotNumber = getGlobalSlotNumber('flank', idx); // DEBUGGING: Slot number calculation
    
    if (!visibleOrder) {
      // ========== DEBUGGING: Empty slot box with number and zone label ==========
      // COMMENTED OUT - no longer needed
      /*
      const isHighlighted = slotNumber === 4 || slotNumber === 18; // DEBUGGING: Highlight slots 4 and 18
      return (
        <React.Fragment key={`your_flank_empty_${idx}`}>
          <SlotDeathAnimations zoneName="your_flank" index={idx} leftOffset={0} />
          <div
            style={{
              position: 'relative',
              flex: '0 0 auto',
              marginLeft: idx > 0 ? (isContainerMobile ? '-5px' : '-15px') : '0',
              backgroundColor: isHighlighted ? 'rgba(255, 255, 0, 0.4)' : 'rgba(100, 100, 100, 0.3)',
              border: isHighlighted ? '4px solid #ffff00' : '1px dashed rgba(102, 102, 102, 0.35)',
              borderRadius: '8px',
              opacity: 0.8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '-1px',
              padding: '0px',
              boxSizing: 'border-box',
              boxShadow: isHighlighted ? '0 0 20px rgba(255, 255, 0, 0.8)' : 'none'
            }}
          >
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px'
            }}>
              <span style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#fff',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
              }}>
                {slotNumber}
              </span>
              <span style={{
                fontSize: '10px',
                fontWeight: 'bold',
                color: '#fff',
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                textTransform: 'uppercase'
              }}>
                Player Flank
              </span>
            </div>
          </div>
        </React.Fragment>
      );
      */
      // ========== END DEBUGGING ==========
      return (
        <React.Fragment key={`your_flank_empty_${idx}`}>
          <SlotDeathAnimations zoneName="your_flank" index={idx} leftOffset={0} />
          <div
            className="empty-slot-container"
            data-slot-zone="your_flank"
            data-slot-index={idx}
            style={{
              position: 'relative',
              // MOBILE FIX: Let CSS control flex, margins on mobile
              ...(isContainerMobile ? {} : { flex: '0 0 auto', marginLeft: idx > 0 ? '-15px' : '0' }),
            }}
          >
            <div
              style={{
                position: 'relative',
              }}
            >
              <div
                style={{
                  ...ordersStyles.container,
                  backgroundColor: 'transparent',
                width: '200px',
                height: '100px',
                  margin: '-1px',
                  padding: '0px',
                  fontSize: '0.9em',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '0px solid #ccc',
                  borderRadius: '8px',
                  boxSizing: 'border-box',
                  overflow: 'visible',
                  transform: 'none', // Removed to allow proper vertical centering
                  pointerEvents: 'none',
                }}
              >
                {/* Border matching the image size (80% width, 140% height) */}
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%', // Match image width
                    height: '140%', // Match image height
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '8px',
                    pointerEvents: 'none',
                  }}
                />
              </div>
            </div>
          </div>
        </React.Fragment>
      );
    }
    
    const isHovered = hoveredPlayed.zone === "flank" && hoveredPlayed.idx === idx;
    const isSelectedAttacker = selectedAttacker && selectedAttacker.zone === "flank" && selectedAttacker.idx === idx;
    
    // Check if this unit is currently animating an attack
    // OLD CODE DISABLED - using new attack-initiated flow
    const isAttackingFlankPlayer = false;
    const isBeingAttackedFlankPlayer = false;
    const attackerStillExistsFlankPlayer = true;
    const deploymentStylesFlankPlayer = getDeploymentAnimationStyles(userId, 'flank', idx);
    // ========== DEBUGGING: Highlight slots 4 and 18 ==========
    // COMMENTED OUT - no longer needed
    // const isHighlighted = slotNumber === 4 || slotNumber === 18; // TODO: Remove highlighting
    // ========== END DEBUGGING ==========
    
    return (
      <div key={`your_flank_${visibleOrder.id}_${idx}`} data-slot-zone="your_flank" data-slot-index={idx} data-unit-key={visibleOrder?.instanceId ? `${userId}_${visibleOrder.instanceId}` : null} style={{ 
        position: "relative", 
        // MOBILE FIX: Let CSS control flex, margins on mobile
        ...(isContainerMobile ? {} : { flex: '0 0 auto', marginLeft: idx > 0 ? '-15px' : '0' }),
        zIndex: 1 
      }}>
        <div
          style={{
            position: 'relative',
            marginTop: 0,
            marginRight: isContainerMobile ? 0 : 0,
            marginBottom: 0,
            cursor: yourTurn ? "pointer" : "default",
            zIndex: 1,
            transition: isContainerMobile ? 'none' : (deploymentStylesFlankPlayer.animation ? "opacity 0.2s ease" : "transform 0.2s ease"),
            // MOBILE FIX: Let CSS control transform on mobile
            ...(isContainerMobile ? {} : {
              transform: isHovered ? "scale(1.05)" : "scale(1)",
              transformOrigin: "center",
            }),
          ...(isContainerMobile ? {} : deploymentStylesFlankPlayer),
            opacity: 1,
          }}
          onMouseEnter={() => {
            // Don't trigger hover if replacement buttons are showing for this unit
            const hasReplaceButtons = replaceTargetMode && clickedUnitForReplace && clickedUnitForReplace.instanceId === visibleOrder.instanceId;
            if (!hasReplaceButtons) {
              playSoundEffect('/sounds/hoverover.mp3', 0.3);
              setHoveredPlayed({ zone: "flank", idx });
            }
            sendOrderHover(visibleOrder?.instanceId);
          }}
          onMouseLeave={() => {
            setHoveredPlayed({ zone: null, idx: null });
            sendOrderHover(null);
          }}
          onMouseDown={(e) => {
            if (isContainerMobile || !yourTurn || !visibleOrder) return;
            startAttackMouseDrag('flank', idx, e.clientX, e.clientY);
          }}
          // Mobile touch handlers for attack dragging
          onTouchStart={(e) => {
            if (!isMobile || !yourTurn || !visibleOrder) return;
            handleAttackTouchStart(e, visibleOrder, 'flank', idx);
          }}
          onTouchMove={(e) => {
            if (!isMobile || !yourTurn) return;
            handleAttackTouchMove(e);
          }}
          onTouchEnd={(e) => {
            if (!isMobile || !yourTurn) return;
            const { wasAttackDrag, attacker, target } = handleAttackTouchEnd(e);
            
            if (wasAttackDrag && attacker && target) {
              e.preventDefault();
              e.stopPropagation();
              
              if (target.type === 'general') {
                // Attack opponent general
                attackOrder(attacker.zone, attacker.index, 'general', null);
              } else if (target.type === 'unit') {
                // Attack opponent unit
                attackOrder(attacker.zone, attacker.index, target.zone, target.index);
              }
            } else if (!wasAttackDrag) {
              // Not a drag - let click handler process it on next event cycle
              // Do nothing here, onClick will handle it
            }
          }}
          onClick={() => {
            // On mobile, skip onClick to prevent double-firing
            if (isMobile) return;
            
            // Allow clicks during counter strike deploy targeting (e.g. Bulwark +2 target)
            const isCounterStrikeDeployTargeting = deployTargetMode && selectedDeployOrderId && !yourTurn;
            if (!yourTurn && !isCounterStrikeDeployTargeting) return;
            
            // Handle replacement mode (for full zones)
            if (replaceTargetMode && selectedReplaceUnitId && replaceTargetZone === "flank") {
              // Clear hover state to force minimal view
              setHoveredPlayed({ zone: null, idx: null });
              
              // Store clicked unit info to trigger button rendering
              setClickedUnitForReplace({
                unit: visibleOrder,
                zone: "flank",
                index: idx,
                instanceId: visibleOrder.instanceId
              });
              return;
            }
            
            // Handle unit click when unit is selected in hand and zone is full (immediate replace mode)
            if (selectedOrderInstanceId && !replaceTargetMode) {
              const selectedOrder = you?.hand?.find(o => o.instanceId === selectedOrderInstanceId);
              // Skip if strategem is selected
              if (selectedOrder?.type && (selectedOrder.type.toLowerCase() === 'strategem' || selectedOrder.type.toLowerCase() === 'stratagem')) {
                // Let other handlers process this
              } else {
                // Check if flank zone is full
                const flankUnitsCount = you.playedOrders.flank.filter(u => u !== null).length;
                if (flankUnitsCount >= 5) {
                  // Clear hover state to force minimal view
                  setHoveredPlayed({ zone: null, idx: null });
                  
                  // Enter replace mode and immediately show buttons on this unit
                  setReplaceTargetMode(true);
                  setSelectedReplaceUnitId(selectedOrderId);
                  setSelectedReplaceInstanceId(selectedOrderInstanceId);
                  setReplaceTargetZone('flank');
                  setClickedUnitForReplace({
                    unit: visibleOrder,
                    zone: "flank",
                    index: idx,
                    instanceId: visibleOrder.instanceId
                  });
                  return;
                }
              }
            }
            
            // Handle drone targeting
            if (droneTargetMode && selectedDroneId) {
              targetDrone("flank", idx, ws, setError, setDroneTargetMode, setSelectedDroneId, setSelectedDroneInstanceId, selectedDroneId, selectedDroneInstanceId, gameId, gameState, userId);
              return;
            }
            
            // Handle weapon targeting
            if (weaponTargetMode && selectedWeaponId) {
              const targetUnit = {
                ...visibleOrder,
                zone: "flank",
                index: idx
              };
              attachWeapon(targetUnit, ws, setError, setWeaponTargetMode, selectedWeaponId, setSelectedWeaponId, selectedWeaponInstanceId, gameId, userId, gameState, setWeaponDeployTargetMode, setWeaponDeployTargetInfo);
              return;
            }
            
            // Handle confuse targeting
            if (confuseTargetMode && selectedConfuseWeaponId) {
              targetConfuse("flank", idx, ws, setError, setConfuseTargetMode, setSelectedConfuseWeaponId);
              return;
            }
            
            // Handle strategem targeting
            if (strategemTargetMode && selectedStrategemId) {
              const animationConfig = {
                registerAnimation: strategemTargetAnimations.registerAnimation,
                strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
                strategemId: selectedStrategemId,
                gameState,
                userId
              };
              
              if (strategemTargetInfo?.type === 'zone') {
                // Target the zone itself
                targetStrategem("zone", "flank", null, null, null, ws, gameId, setError, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, animationConfig);
              } else {
                // Target a unit in the zone
                const targetUnit = visibleOrder;
                targetStrategem("unit", "flank", idx, "friendly", targetUnit?.instanceId, ws, gameId, setError, setStrategemTargetMode, setSelectedStrategemId, setStrategemTargetInfo, animationConfig);
              }
              return;
            }
            
            // Handle deploy targeting
            if (deployTargetMode && selectedDeployOrderId) {
              handleTargetDeploy("flank", idx, "friendly");
              return;
            }
            
            // Handle attack selection
            if (
              selectedAttacker &&
              selectedAttacker.zone === "flank" &&
              selectedAttacker.idx === idx
            ) {
              setSelectedAttacker(null);
              setAttackMode(false);
              setLastAttackedTarget(null); // Clear when canceling attack mode
            } else {
              // ✅ VALIDATION: Check if unit has 0 attack rating - cannot attack
              const attackRating = visibleOrder?.attack_rating ?? 0;
              if (attackRating === 0) {
                setError('This unit has 0 attack rating and cannot attack');
                return;
              }
              
              setSelectedAttacker({ zone: "flank", idx, id: visibleOrder?.id || order?.id });
              setAttackMode(true);
              setLastAttackedTarget(null); // Clear when selecting new attacker
            }
          }}
        >
          {isNewlyDeployedLegendaryUnique(userId, 'flank', idx) && (
            <LegendaryUniqueDeployRay />
          )}
          {(() => {
            // Check if replacement buttons are showing for this unit
            const hasReplaceButtons = replaceTargetMode && clickedUnitForReplace && clickedUnitForReplace.instanceId === visibleOrder.instanceId;
            return (
              <>
                <div
                  className={`deployed-unit-slot ${!isNewlyDeployed(userId, 'flank', idx) && !visibleOrder?._isRepositioning && !isImmobilizedUI(visibleOrder, "your_flank") ? 'deployed-unit-slot-idle' : ''}`.trim()}
                  data-slot-zone="your_flank"
                  data-slot-index={idx}
                  style={{ position: 'relative' }}
                >
                  {isNewlyDeployedLegendary(userId, 'flank', idx) && (
                    <LegendaryDeployGlow />
                  )}
                  <OrderDesign
                    order={visibleOrder}
                    count={0} 
                    minimal={!isHovered || hasReplaceButtons}
                    scale={viewportScale}
                    isOOR={visibleOrder.isOOR}
                    isSelected={(selectedAttacker && selectedAttacker.zone === "flank" && selectedAttacker.idx === idx) || selectedOrderInstanceId === visibleOrder.instanceId}
                    isHighlighted={highlightedInstanceIds.some(id => String(id).trim() === String(visibleOrder.instanceId).trim())}
                    zoneName="your_flank"
                    slotIndex={idx}
                    gameState={gameState}
                    isHiddenDuringAnimation={attackAnimations.isUnitHidden(visibleOrder.instanceId)}
                    isUnitTargetInPendingAttack={attackAnimations.isUnitTargetInPendingAttack}
                    isViewedByOpponent={!!(opponentLookingAtInstanceId && visibleOrder?.instanceId && String(opponentLookingAtInstanceId) === String(visibleOrder.instanceId))}
                  isTarget={isTarget({
                    strategemTargetMode: effectiveStrategemTargetMode,
                    selectedStrategemId: effectiveSelectedStrategemId,
                    strategemTargetInfo: effectiveStrategemTargetInfo,
                    immobilizeTargetMode: false,
                    confuseTargetMode,
                    weaponTargetMode: effectiveWeaponTargetMode,
                    weaponDeployTargetMode,
                    deployTargetMode,
                    selectedDeployOrderId,
                    selectedDeployInstanceId,
                    selectedDeployZone,
                    zone: "flank",
                    targetingData,
                    gameState,
                    playerId: userId,
                    attackMode: effectiveAttackMode,
                    selectedAttacker: effectiveSelectedAttacker,
                    droneTargetMode: effectiveDroneTargetMode,
                    selectedDroneId: effectiveSelectedDroneId,
                    unit: visibleOrder
                  })}
                  targetMode={getTargetingMode(strategemTargetMode, false, confuseTargetMode, weaponTargetMode, deployTargetMode, selectedDeployOrderId, selectedDeployZone)}
                />
                </div>
                {/* ========== DEBUGGING: Slot number and zone label overlay ========== */}
                {/* COMMENTED OUT - no longer needed */}
                {/*
                <div style={{
                  position: 'absolute',
                  top: '5px',
                  left: '5px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#fff',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  zIndex: 1000,
                  pointerEvents: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px'
                }}>
                  <span>{slotNumber}</span>
                  <span style={{ fontSize: '9px', textTransform: 'uppercase' }}>Player Flank</span>
                </div>
                */}
                {/* ========== END DEBUGGING ========== */}
                {/* Inline Replace/Cancel buttons */}
                {hasReplaceButtons && (
            <div className="replace-buttons-container">
              <button
                className="replace-button replace-button-confirm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleReplaceUnit(clickedUnitForReplace);
                }}
              >
                Replace
              </button>
              <button
                className="replace-button replace-button-cancel"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelReplace();
                }}
              >
                Cancel
              </button>
            </div>
                )}
                {/* Portal displacement (War Effort): one charge to switch zone (flank → front) */}
                {gameState?.defenderPortalActive && String(userId) === String(gameState.warEffortDefenderPlayerId) && visibleOrder?.portalDisplacementCharges > 0 && (
                  <button
                    type="button"
                    className="portal-displacement-button"
                    title="Switch zone (Portal)"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePortalDisplacement(visibleOrder, 'front');
                    }}
                  >
                    Portal (→front)
                  </button>
                )}
              </>
            );
          })()}
        </div>
      </div>
    );
  })}
</div>


          </div>
        </div>
        </div>
		<div

>

</div>
{/* Player General Section - Removed full GeneralStatus, now using compact mode on avatar */}

{/* Turn Countdown Timer and First Turn Info - Below Player Intelligence Orb */}
<div style={{
  position: "absolute",
  left: "50%",
  transform: "translateX(-50%)",
  top: "540px", // Below the GeneralStatus container (which is around 520px from top, + 20px gap)
  zIndex: 1000,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px"
}}>
  <TurnTimerDisplay turnTimeLeft={needsMulligan && mulliganTimeLeft !== null ? mulliganTimeLeft : turnTimeLeft} />
  {playerGoesFirst !== null && !needsMulligan && showFirstTurnMessage && (
    <div style={{
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      color: "white",
      padding: "10px 20px",
      borderRadius: "8px",
      fontSize: "16px",
      fontWeight: "bold",
      textAlign: "center",
      border: "2px solid",
      borderColor: playerGoesFirst ? "#4CAF50" : "#FFA500",
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.4)",
      animation: "fadeIn 0.5s ease-in"
    }}>
      {playerGoesFirst ? t('battleUI.shouldActNow') : t('battleUI.haveTimeToPrepare')}
    </div>
  )}
</div>

{/* Targeting Cancel Dialog - Center Screen */}
<TargetingCancelDialog
  deployMode={deployTargetMode && selectedDeployOrderId}
  strategemMode={strategemTargetMode && selectedStrategemId}
  weaponMode={weaponTargetMode && selectedWeaponId}
  weaponDeployMode={weaponDeployTargetMode && weaponDeployTargetInfo}
  weaponDeployTargetInfo={weaponDeployTargetInfo}
  droneMode={droneTargetMode && selectedDroneId}
  strategemTargetInfo={strategemTargetInfo}
  selectedDeployOrderId={selectedDeployOrderId}
  deployTargetInfo={deployTargetInfo}
  onCancelDeploy={createCancelDeployHandler(
    setDeployTargetMode,
    setSelectedDeployOrderId,
    setSelectedDeployInstanceId,
    setSelectedDeployZone,
    setDeployTargetInfo,
    selectedDeployOrderId,
    selectedDeployInstanceId,
    selectedDeployZone
  )}
  onCancelStrategem={createCancelStrategemHandler(
    setStrategemTargetMode,
    setSelectedStrategemId,
    setStrategemTargetInfo
  )}
  onCancelWeapon={createCancelWeaponHandler(
    setWeaponTargetMode,
    setSelectedWeaponId
  )}
  onCancelWeaponDeploy={createCancelWeaponDeployHandler(
    setWeaponDeployTargetMode,
    setWeaponDeployTargetInfo
  )}
  onCancelDrone={createCancelDroneHandler(
    setDroneTargetMode,
    setSelectedDroneId,
    setSelectedDroneInstanceId
  )}
  ws={ws}
  gameId={gameId}
/>

{/* Player dock: general + O/S/C left, advisor + drones right, eye-to-eye above buttons */}
{/* Mobile version is rendered outside battlefield-and-carousel for proper positioning */}
{!isContainerMobile && (
<div className="player-dock-row" style={{ position: "absolute", bottom: "148px", left: "20px", right: "20px", zIndex: 1000, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
  <div className="player-general-column" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
    <div className="player-avatar-container" style={{}}>
  <div style={{ position: "relative", display: "inline-block" }}>
    {/* Player Compact Health Bar and Orb */}
    <GeneralStatus
      health={you.health}
      intelligenceLeft={you.intelligence}
      intelligenceTotal={you.maxIntelligence}
      barriersRemoved={you.barriersRemoved ?? []}
      damageFlash={playerDamageFlash}
      playerCompact={true}
      factionName={calculateFactionFromStrategy(you.strategy)}
      translatedFactionName={getTranslatedFactionName(calculateFactionFromStrategy(you.strategy))}
      isActiveTurn={yourTurn}
    />
    
    <div style={{ position: "relative", width: "180px", height: "180px", display: "inline-block", overflow: "visible" }}>
      {(() => {
        const isGeneralTarget = (
          (effectiveStrategemTargetMode && effectiveSelectedStrategemId && getStrategemTargetingRules(effectiveSelectedStrategemId, targetingData)?.general === true) ||
          (deployTargetMode && selectedDeployOrderId && getDeployTargetingRules(selectedDeployOrderId, targetingData)?.general === true)
        );
        return (
          <div
            className={getTargetingBorderClassName(isGeneralTarget)}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              borderRadius: '8px',
              pointerEvents: 'none',
              zIndex: 1
            }}
          >
            <img 
              src={getAvatarImage(selectedGeneral)} 
              alt="Player General"
              data-zone="player_general"
              onClick={(e) => {
                e.stopPropagation();
                // ✅ SPECTATOR FIX: Spectators cannot open emote menu
                if (isSpectating) {
                  return;
                }
                
                // Handle strategem targeting the player general
                if (strategemTargetMode && selectedStrategemId) {
                  const targetingRules = getStrategemTargetingRules(selectedStrategemId, targetingData);
                  
                  // Check if strategem can target generals and if it can target friendly generals
                  if (targetingRules?.general === true) {
                    const playerRestriction = targetingRules?.player || 'opponent'; // Default to opponent only
                    // Allow targeting if player is 'friendly' or 'both'
                    if (playerRestriction === 'friendly' || playerRestriction === 'both') {
                      targetStrategem(
                        'general',
                        null,
                        null,
                        'friendly',
                        null,
                        ws,
                        gameId,
                        setError,
                        setStrategemTargetMode,
                        setSelectedStrategemId,
                        setStrategemTargetInfo,
                        // Animation config
                        {
                          registerAnimation: strategemTargetAnimations.registerAnimation,
                          strategemInstanceId: strategemTargetInfo?.strategemInstanceId,
                          strategemId: selectedStrategemId,
                          gameState,
                          userId
                        }
                      );
                      return;
                    }
                  }
                }
                
                // Only show emote buttons if not in any targeting mode
                // ✅ FIX: Don't show if emotes are still loading
                if (!deployTargetMode && !weaponTargetMode && !strategemTargetMode && !attackMode && !emotesLoading) {
                  setShowEmoteButtons(!showEmoteButtons);
                } else if (emotesLoading) {
                }
              }}
              style={{ 
                width: "100%", 
                height: "100%", 
                objectFit: "contain",
                display: "block",
                pointerEvents: 'auto', // ✅ FIX: Ensure clicks reach the img despite parent's pointerEvents: 'none'
                ...getDamageFlashStyles(playerDamageFlash),
                transform: shouldMirrorAvatar(selectedGeneral, 'left', avatars) ? 'scaleX(-1)' : 'none',
                cursor: (!isSpectating && !deployTargetMode && !weaponTargetMode && !strategemTargetMode && !attackMode) ? 'pointer' : 'default',
                border: (() => {
                  if (isGeneralTarget) {
                    const targetingStyle = getTargetingBorderStyles(true);
                    return targetingStyle ? targetingStyle.border : '3px solid #ffff00';
                  }
                  return 'none';
                })(),
                borderRadius: '8px',
                position: 'relative',
                zIndex: 2
              }} 
            />
          </div>
        );
      })()}
      
      {/* Your turn: 3 animated arrows toward the right. Not your turn: pause (waiting) indicator. */}
      {yourTurn ? (
        <div className="player-your-turn-arrows" aria-hidden="true">
          <span className="player-your-turn-arrow" style={{ animationDelay: '0s' }} />
          <span className="player-your-turn-arrow" style={{ animationDelay: '0.2s' }} />
          <span className="player-your-turn-arrow" style={{ animationDelay: '0.4s' }} />
        </div>
      ) : (
        <div className="player-waiting-pause" aria-hidden="true" title={t('battleUI.notYourTurn') || "It's not your turn"}>
          <span className="player-waiting-pause-bar" />
          <span className="player-waiting-pause-bar" />
        </div>
      )}
      
      {/* Player Rank Display - Lower Right Corner of Avatar */}
      {playerRank !== null && playerRank !== undefined && (
        <div
          style={{
            ...getRankStyle(playerRank),
            position: "absolute",
            bottom: "2px",
            right: "8px",
            zIndex: 10002,
            fontSize: "22px",
            fontWeight: "bold",
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            padding: "4px 8px",
            borderRadius: "6px",
            pointerEvents: "none",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.8)"
          }}
        >
          #{playerRank}
        </div>
      )}
    </div>
    
  </div>
  
  {/* Emote Buttons - Show in center of screen */}
  {showEmoteButtons && (
    <div
      data-emote-buttons
      style={{
        position: "fixed",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, calc(-50% - 20px))", // 1 cell (20px) higher
        zIndex: 10000, // On top of everything
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        maxHeight: "400px",
        overflowY: "auto",
        padding: "0px",
        backgroundColor: "transparent",
        border: "none",
        borderRadius: "0px",
        minWidth: "250px"
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => {
        // Clear hovered order index when hovering over emotes to prevent order expansion
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }
        setHoveredIndex(null);
      }}
    >
      {(() => {
        
        // ✅ FIX: Show loading state
        if (emotesLoading) {
          return (
            <div style={{ color: '#aaa', textAlign: 'center', padding: '10px' }}>
              Loading emotes...
            </div>
          );
        }
        
        // ✅ FIX: Show error state with retry option
        if (emotesError) {
          return (
            <div style={{ color: '#ff6b6b', textAlign: 'center', padding: '10px' }}>
              <div style={{ marginBottom: '8px' }}>Failed to load emotes</div>
              <button
                onClick={() => {
                  setEmotesError(null);
                  fetchEmotes();
                }}
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  backgroundColor: '#444',
                  color: '#fff',
                  border: '1px solid #666',
                  borderRadius: '4px'
                }}
              >
                Retry
              </button>
            </div>
          );
        }
        
        if (playerEmotes.length === 0) {
          return (
            <div style={{ color: '#aaa', textAlign: 'center', padding: '10px' }}>
              No emotes available
            </div>
          );
        }
        
        // Filter to only show selected emotes from playerEmoteSelections
        // playerEmoteSelections format: { "Greet": "emote_greet_variant1", "Taunt": "emote_taunt_variant2", ... }
        const selectedEmotes = playerEmotes.filter(emote => {
          // Must be unlocked
          if (!emote.isUnlocked) {
            return false;
          }
          
          // Must have a name (emote type)
          if (!emote.name) {
            return false;
          }
          
          // Must be in playerEmoteSelections and match the selected unlockKey
          const selectedUnlockKey = playerEmoteSelections[emote.name];
          if (!selectedUnlockKey) {
            return false;
          }
          
          if (emote.unlockKey !== selectedUnlockKey) {
            return false;
          }
          
          return true;
        });
        
        
        if (selectedEmotes.length === 0) {
          return (
            <div style={{ color: '#aaa', textAlign: 'center', padding: '10px' }}>
              No emotes selected. Select emotes in your Profile.
            </div>
          );
        }
        
        // Group by type (though each type should only have one emote now)
        const grouped = selectedEmotes.reduce((acc, emote) => {
          const type = emote.name; // "Greet", "Taunt", "Thanks", etc.
          if (!acc[type]) acc[type] = [];
          acc[type].push(emote);
          return acc;
        }, {});
        
        const groupedEntries = Object.entries(grouped);
        
        return groupedEntries.map(([emoteType, emotes]) => (
          <div key={emoteType} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {emotes.map((emote) => (
              <button
                key={emote.unlockKey}
                onClick={() => {
                  // Backend returns speechBubble (camelCase) field, not metadata.speech_bubble
                  const emoteText = emote.speechBubble || emote.metadata?.speech_bubble || emote.name;
                  
                  if (emoteText) {
                    // Display emote bubble immediately
                    setCurrentEmoteBubble({
                      text: emoteText,
                      timestamp: Date.now()
                    });
                    
                    // Hide bubble after 3 seconds
                    setTimeout(() => {
                      setCurrentEmoteBubble(null);
                    }, 3000);
                    
                    // Send emote to opponent via WebSocket (if available)
                    if (ws && ws.readyState === WebSocket.OPEN) {
                      ws.send(JSON.stringify({
                        type: "emote",
                        gameId,
                        emoteType,
                        emoteKey: emote.unlockKey,
                        text: emoteText
                      }));
                    }
                    
                    // Close buttons after selection
                    setShowEmoteButtons(false);
                  } else {
                    console.warn('🎭 No emote text found for:', emote);
                  }
                }}
                style={{
                  backgroundColor: '#2a2a2a',
                  color: 'white',
                  border: '1px solid #4a90e2',
                  borderRadius: '4px',
                  padding: '6px 10px',
                  fontSize: '11px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#3a3a3a';
                  e.target.style.borderColor = '#5aa0f2';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#2a2a2a';
                  e.target.style.borderColor = '#4a90e2';
                }}
                title={emote.speechBubble || emote.metadata?.speech_bubble || emote.name}
              >
                {emote.speechBubble || emote.metadata?.speech_bubble || emote.name}
              </button>
            ))}
          </div>
        ));
      })()}
    </div>
  )}
  
  {/* Speaking Bubble - Show current emote */}
  {currentEmoteBubble && (
    <div style={{
      position: "absolute",
      top: "-70px",
      left: "70px",
      zIndex: 10002,
      pointerEvents: "none",
      maxWidth: "400px",
      minWidth: "120px"
    }}>
      <div style={{
        backgroundColor: "rgba(0, 0, 0, 0.85)", // Black with opacity
        border: "2px solid #40E0D0", // Turquoise border
        borderRadius: "12px", // Rounded borders
        padding: "12px 16px",
        boxShadow: "0 0 10px rgba(64, 224, 208, 0.6), 0 0 20px rgba(64, 224, 208, 0.4)", // Glowing turquoise effect
        color: "white", // White text
        fontSize: "18px",
        fontWeight: "bold",
        textAlign: "center",
        lineHeight: "1.4",
        wordWrap: "break-word",
        whiteSpace: "pre-wrap",
        display: "inline-block",
        width: "auto",
        minHeight: "40px"
      }}>
        {(() => {
          // Wrap text after 2 words
          const words = currentEmoteBubble.text.split(' ');
          const wrappedLines = [];
          for (let i = 0; i < words.length; i += 2) {
            wrappedLines.push(words.slice(i, i + 2).join(' '));
          }
          return wrappedLines.join('\n');
        })()}
      </div>
      {/* Speech bubble tail pointing downward toward general */}
      <div style={{
        position: "absolute",
        bottom: "-8px", // Position below the bubble
        left: "20px", // Position on the left side (toward general)
        width: 0,
        height: 0,
        borderLeft: "10px solid transparent",
        borderRight: "10px solid transparent",
        borderTop: "10px solid rgba(0, 0, 0, 0.85)", // Match bubble background
        filter: "drop-shadow(0 0 5px rgba(64, 224, 208, 0.6))", // Glow effect
        zIndex: 10003
      }} />
      {/* Speech bubble tail border (turquoise outline) */}
      <div style={{
        position: "absolute",
        bottom: "-10px", // Slightly below the main tail
        left: "20px",
        width: 0,
        height: 0,
        borderLeft: "11px solid transparent",
        borderRight: "11px solid transparent",
        borderTop: "11px solid #40E0D0", // Turquoise border
        zIndex: 10002
      }} />
    </div>
  )}
</div>

{/* Player Stats (O:, S:, C:) - below general in dock */}
  <div className="player-stats-counters" style={{ 
  display: 'flex', 
  gap: "20px",
  fontSize: "16px",
  fontWeight: 'bold',
  color: 'white'
}}>
  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
    <span style={{ color: '#4a90e2', cursor: 'help' }} data-tooltip={t('battleUI.ordersReady')}>O:</span>
    <span>{needsMulligan ? 0 : (you.hand?.length ?? 0)}</span>
  </div>
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <span style={{ color: '#4a90e2', cursor: 'help', fontSize: '0.9em' }} data-tooltip={t('battleUI.strategySize')}>S:</span>
    <button
      className="stat-badge"
      onClick={() => {
        playSoundEffect('/sounds/click.mp3');
        setShowStrategy(true);
      }}
      data-tooltip={t('battleUI.clickToViewStrategy') || 'Click to view strategy'}
      style={{
        background: (gameState?.playerDiplomaticAccords?.[userId] === 'command_directive' ||
                     gameState?.opponentDiplomaticAccords?.[userId] === 'command_directive') &&
                     !(you?.commandDirectiveSelected)
          ? 'rgba(255, 215, 0, 0.3)' // Yellow background when command directive is active
          : 'rgba(74, 144, 226, 0.15)',
        border: (gameState?.playerDiplomaticAccords?.[userId] === 'command_directive' ||
                 gameState?.opponentDiplomaticAccords?.[userId] === 'command_directive') &&
                 !(you?.commandDirectiveSelected)
          ? '2px solid rgba(255, 215, 0, 0.8)' // Yellow border when command directive is active
          : '1px solid rgba(74, 144, 226, 0.3)',
        borderRadius: '12px',
        padding: '4px 12px',
        color: (gameState?.playerDiplomaticAccords?.[userId] === 'command_directive' ||
                gameState?.opponentDiplomaticAccords?.[userId] === 'command_directive') &&
                !(you?.commandDirectiveSelected)
          ? '#FFD700' // Yellow text when command directive is active
          : '#4a90e2',
        cursor: 'pointer',
        fontSize: '0.9em',
        fontWeight: '600',
        transition: 'all 0.2s ease',
        userSelect: 'none',
        fontFamily: 'inherit',
        animation: (gameState?.playerDiplomaticAccords?.[userId] === 'command_directive' ||
                   gameState?.opponentDiplomaticAccords?.[userId] === 'command_directive') &&
                   !(you?.commandDirectiveSelected)
          ? 'blink-yellow 1s infinite' // Blinking animation
          : 'none',
        boxShadow: (gameState?.playerDiplomaticAccords?.[userId] === 'command_directive' ||
                    gameState?.opponentDiplomaticAccords?.[userId] === 'command_directive') &&
                    !(you?.commandDirectiveSelected)
          ? '0 0 10px rgba(255, 215, 0, 0.6)' // Yellow glow
          : 'none'
      }}
      onMouseEnter={(e) => {
        if (!((gameState?.playerDiplomaticAccords?.[userId] === 'command_directive' ||
               gameState?.opponentDiplomaticAccords?.[userId] === 'command_directive') &&
               !(you?.commandDirectiveSelected))) {
          e.target.style.background = 'rgba(74, 144, 226, 0.25)';
          e.target.style.borderColor = 'rgba(74, 144, 226, 0.5)';
        }
        e.target.style.transform = 'scale(1.05)';
      }}
      onMouseLeave={(e) => {
        if (!((gameState?.playerDiplomaticAccords?.[userId] === 'command_directive' ||
               gameState?.opponentDiplomaticAccords?.[userId] === 'command_directive') &&
               !(you?.commandDirectiveSelected))) {
          e.target.style.background = 'rgba(74, 144, 226, 0.15)';
          e.target.style.borderColor = 'rgba(74, 144, 226, 0.3)';
        }
        e.target.style.transform = 'scale(1)';
      }}
    >
      {you.deck?.length ?? you.strategy?.orders?.reduce((sum, order) => sum + (order.count || 1), 0) ?? 0}
    </button>
  </div>
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <span style={{ color: '#4a90e2', cursor: 'help', fontSize: '0.9em' }} data-tooltip={t('battleUI.completedOrders')}>C:</span>
    <button
      className="stat-badge"
      onClick={() => {
        playSoundEffect('/sounds/click.mp3');
        setShowCompletedOrders(true);
      }}
      data-tooltip={t('battleUI.clickToViewCompletedOrders') || 'Click to view completed orders'}
      style={{
        background: 'rgba(74, 144, 226, 0.15)',
        border: '1px solid rgba(74, 144, 226, 0.3)',
        borderRadius: '12px',
        padding: '4px 12px',
        color: '#4a90e2',
        cursor: 'pointer',
        fontSize: '0.9em',
        fontWeight: '600',
        transition: 'all 0.2s ease',
        userSelect: 'none',
        fontFamily: 'inherit'
      }}
      onMouseEnter={(e) => {
        e.target.style.background = 'rgba(74, 144, 226, 0.25)';
        e.target.style.borderColor = 'rgba(74, 144, 226, 0.5)';
        e.target.style.transform = 'scale(1.05)';
      }}
      onMouseLeave={(e) => {
        e.target.style.background = 'rgba(74, 144, 226, 0.15)';
        e.target.style.borderColor = 'rgba(74, 144, 226, 0.3)';
        e.target.style.transform = 'scale(1)';
      }}
    >
      {you.completedOrders?.length || 0}
    </button>
  </div>
</div>
  </div>
  <div className="player-advisor-column" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8 }}>
    <div className="player-advisor-container" style={{}}>
      <div style={{ position: "relative" }}>
        <GeneralStatus
          health={you.health}
          intelligenceLeft={you.intelligence}
          intelligenceTotal={you.maxIntelligence}
          barriersRemoved={you.barriersRemoved ?? []}
          damageFlash={playerDamageFlash}
          playerCompactIntelligence={true}
        />
        <img
          src={getAvatarImage(selectedAdvisor)}
          alt="Player Advisor"
          style={{
            width: "180px",
            height: "180px",
            objectFit: "contain",
            transform: shouldMirrorAvatar(selectedAdvisor, 'right', avatars) ? 'scaleX(-1)' : 'none'
          }}
        />
        <div style={{
          position: "absolute",
          bottom: "0px",
          left: "90px",
          transform: "translateX(-50%)",
          width: "fit-content",
          textAlign: "center",
          color: getTitleColorHex(activeGameState?.playerTitleColors?.[activeUserId]),
          fontSize: "16px",
          fontWeight: "bold",
          textShadow: "1px 1px 2px rgba(0, 0, 0, 0.8)",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          padding: "4px 8px",
          borderRadius: "4px",
          whiteSpace: "nowrap",
          zIndex: 10001,
          pointerEvents: "none"
        }}>
          {activeGameState?.playerTitles?.[activeUserId] || 'The undecided'}
        </div>
      </div>
    </div>
  </div>
  </div>
)}

{/* Preparation Bonus Button - Show for plan_ahead, black_market, and team_leader - Only for player who goes second - Hidden in tutorial missions */}
{/* Desktop only - Mobile version rendered outside battlefield-and-carousel */}
{/* Positions/sizes scale with viewport base (1920x1080 vs 2560x1440) so icons stay aligned at all resolutions */}
{!isContainerMobile && !isTutorialMission && (preparationBonus === 'plan_ahead' || preparationBonus === 'black_market' || preparationBonus === 'team_leader') && playerGoesFirst === false && (() => {
  const DESIGN_BASE_WIDTH = 1920;
  const DESIGN_BASE_HEIGHT = 1080;
  const scaleX = containerBaseWidth / DESIGN_BASE_WIDTH;
  const scaleY = containerBaseHeight / DESIGN_BASE_HEIGHT;
  const prepBonusRight = Math.round(198 * scaleX);
  const prepBonusTop = Math.round(798 * scaleY);
  const prepBonusSize = Math.round(84 * Math.min(scaleX, scaleY));
  return (
  <div 
    style={{ 
      position: "absolute", 
      right: `${prepBonusRight}px`,
      top: `${prepBonusTop}px`,
      zIndex: 1001,
      width: `${prepBonusSize}px`,
      height: `${prepBonusSize}px`,
      pointerEvents: (preparationBonusUsed || preparationBonusPendingRef.current || !yourTurn) ? 'none' : 'auto'
    }}
    className={(preparationBonusUsed || preparationBonusPendingRef.current || !yourTurn) ? 'preparation-bonus-disabled-wrapper' : ''}
  >
    <button
      className={`preparation-bonus-button ${(preparationBonusUsed || preparationBonusPendingRef.current || !yourTurn) ? 'disabled' : ''}`}
      onClick={(e) => {
        // Check if button should be disabled
        if (preparationBonusUsed || preparationBonusPendingRef.current || !yourTurn || (preparationBonus !== 'plan_ahead' && preparationBonus !== 'black_market' && preparationBonus !== 'team_leader')) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          setError(t('battleUI.websocketNotConnected') || 'WebSocket not connected');
          return;
        }
        
        // ✅ IMMEDIATE VISUAL DISABLE: Update button style directly (synchronous, before React re-renders)
        const buttonElement = e.currentTarget;
        buttonElement.style.filter = 'grayscale(100%)';
        buttonElement.style.cursor = 'not-allowed';
        buttonElement.style.pointerEvents = 'none';
        buttonElement.style.background = 'rgba(30, 30, 30, 0.7)';
        buttonElement.disabled = true;
        
        // ✅ IMMEDIATE DISABLE: Use ref for synchronous access (works before React re-renders)
        preparationBonusPendingRef.current = true;
        setPreparationBonusUsed(true);
        
        // Send activation action
        // Map 'team_leader' to 'team_leader_meeting' for backend compatibility
        const bonusTypeForBackend = preparationBonus === 'team_leader' ? 'team_leader_meeting' : preparationBonus;
        ws.send(
          JSON.stringify({
            type: "play-turn",
            gameId,
            action: {
              type: "activatePreparationBonus",
              bonusType: bonusTypeForBackend
            },
            stateVersion: lastStateVersionRef.current
          })
        );
      }}
      disabled={isAutobattler || isAutobattlerSpectator || preparationBonusUsed || preparationBonusPendingRef.current || !yourTurn || (preparationBonus !== 'plan_ahead' && preparationBonus !== 'black_market' && preparationBonus !== 'team_leader')}
      aria-disabled={isAutobattler || isAutobattlerSpectator || preparationBonusUsed || preparationBonusPendingRef.current || !yourTurn || (preparationBonus !== 'plan_ahead' && preparationBonus !== 'black_market' && preparationBonus !== 'team_leader')}
      data-tooltip={
        preparationBonusUsed ? (t('battleUI.preparationBonusUsed') || 'Preparation bonus already used') :
        !yourTurn ? (t('battleUI.notYourTurn') || 'Not your turn') :
        preparationBonus === 'plan_ahead' ? t('profile.preparationBonus.planAhead') :
        preparationBonus === 'black_market' ? t('profile.preparationBonus.blackMarketDossier') :
        t('profile.preparationBonus.teamLeaderMeeting')
      }
      style={{
        width: "100%",
        height: "100%",
        border: "2px solid rgba(255, 255, 255, 0.2)",
        borderRadius: "8px",
        transition: "all 0.3s ease",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        margin: 0,
        ...(preparationBonusUsed || preparationBonusPendingRef.current || !yourTurn ? {
          filter: 'grayscale(100%)',
          cursor: 'not-allowed',
          pointerEvents: 'none',
          background: 'rgba(30, 30, 30, 0.7)',
          transform: 'scale(1)'
        } : {
          cursor: 'pointer',
          background: 'rgba(50, 50, 50, 0.5)',
          filter: 'none'
        })
      }}
      onMouseEnter={(e) => {
        if (preparationBonusUsed || preparationBonusPendingRef.current || !yourTurn || (preparationBonus !== 'plan_ahead' && preparationBonus !== 'black_market' && preparationBonus !== 'team_leader')) {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
          e.currentTarget.style.boxShadow = "none";
          return;
        }
        e.currentTarget.style.transform = "scale(1.1)";
        e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.5)";
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.5)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
        e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <img 
        src={`/images/${preparationBonus === 'plan_ahead' ? 'plan_ahead.jpg' : 
                      preparationBonus === 'black_market' ? 'black_market_dossier.jpg' : 
                      'team_leader_briefing.jpg'}`}
        alt={preparationBonus}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          pointerEvents: "none"
        }}
      />
    </button>
  </div>
  );
})()}

{/* Player Diplomatic Accord Icon - Always show if player has one - Hidden in tutorial missions */}
{/* Desktop only - Mobile version rendered outside battlefield-and-carousel */}
{/* Positions/sizes scale with viewport base (1920x1080 vs 2560x1440) so icons stay aligned at all resolutions */}
{!isContainerMobile && !isTutorialMission && (() => {
  if (!diplomaticAccord) return null;
  
  const DESIGN_BASE_WIDTH = 1920;
  const DESIGN_BASE_HEIGHT = 1080;
  const scaleX = containerBaseWidth / DESIGN_BASE_WIDTH;
  const scaleY = containerBaseHeight / DESIGN_BASE_HEIGHT;
  const diplomaticAccordRight = (preparationBonus === 'plan_ahead' || preparationBonus === 'black_market' || preparationBonus === 'team_leader') 
    ? Math.round(298 * scaleX)
    : Math.round(198 * scaleX);
  const accordTop = Math.round(798 * scaleY);
  const accordSize = Math.round(84 * Math.min(scaleX, scaleY));
  
  return (
    <div 
      style={{ 
        position: "absolute", 
        right: `${diplomaticAccordRight}px`,
        top: `${accordTop}px`,
        zIndex: 1001,
        width: `${accordSize}px`,
        height: `${accordSize}px`
      }}
    data-tooltip={
      diplomaticAccord === 'strategic_coordination' ? t('profile.diplomaticAccords.strategicCoordination') :
      diplomaticAccord === 'public_relations' ? t('profile.diplomaticAccords.publicRelations') :
      diplomaticAccord === 'emergency_protocol' ? t('profile.diplomaticAccords.emergencyProtocol') :
      t('profile.diplomaticAccords.commandDirective')
    }
    data-tooltip-position="bottom"
  >
    <div
      style={{
        width: "100%",
        height: "100%",
        border: "2px solid rgba(255, 255, 255, 0.2)",
        borderRadius: "8px",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        margin: 0,
        background: 'rgba(50, 50, 50, 0.5)',
        filter: 'none',
        opacity: 1
      }}
    >
      <img 
        src={`/images/${diplomaticAccord === 'strategic_coordination' ? 'strategic_coordination.jpg' : 
                      diplomaticAccord === 'public_relations' ? 'public_relations.jpg' : 
                      diplomaticAccord === 'emergency_protocol' ? 'emergency_protocol.jpg' : 
                      'command_directive.jpg'}`}
        alt={diplomaticAccord}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          pointerEvents: "none"
        }}
      />
    </div>
  </div>
  );
})()}

{/* Player Drones - below advisor; aligned right so they sit under advisor in dock */}
{/* Desktop only - Mobile version rendered outside battlefield-and-carousel */}
{!isContainerMobile && (
<div
  className="player-drone-zone"
  data-player-drone-zone="true"
  style={{
  position: "absolute",
  right: "20px",
  bottom: "78px",
  zIndex: 1002,
  display: 'flex',
  gap: "8px",
  ...(isDroneDragActive ? { outline: '3px solid #ffff00', outlineOffset: '0', cursor: 'pointer' } : {})
}}>
  {you.drones?.filter(drone => !drone.markedForRemoval).map((drone, index) => {
    // ✅ Unit 502 (Disruptor): Check if any Disruptor is deployed (deactivates all drones)
    const allPlayerIds = gameState?.players || (gameState?.state ? Object.keys(gameState.state) : []);
    let disruptorDeployed = false;
    for (const pid of allPlayerIds) {
      const p = gameState?.state?.[pid];
      if (p && p.playedOrders) {
        for (const zone of ['front', 'flank']) {
          if (p.playedOrders[zone]) {
            for (const unit of p.playedOrders[zone]) {
              if (unit && unit.id === 502 && unit.health > 0) {
                disruptorDeployed = true;
                break;
              }
            }
            if (disruptorDeployed) break;
          }
        }
        if (disruptorDeployed) break;
      }
    }
    
    const canActivate = yourTurn && (drone.charges > 0 || drone.charges === undefined) && !drone.usedThisTurn && !disruptorDeployed;
    
    return (
    <div
      key={`player-drone-${index}`}
      data-drone-index={index}
      data-drone-zone="your_drone"
      data-instance-id={drone.instanceId || ''}
      style={{
        width: '80px',
        height: '60px',
        backgroundColor: '#333',
        border: canActivate ? '2px solid #2c5aa0' : '2px solid #444',
        borderRadius: '4px',
        cursor: canActivate ? 'pointer' : 'default',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: canActivate ? 1 : 0.6,
        overflow: 'visible'
      }}
      data-tooltip={`${drone.name} - ${drone.description || 'Drone'}${drone.charges !== undefined ? ` (${drone.charges} charges)` : ' (permanent effect)'}${drone.usedThisTurn ? ' - Used this turn' : ''}${disruptorDeployed ? ' - Inactive (Disruptor deployed)' : ''}`}
      onMouseDown={(e) => {
        if (isContainerMobile || !canActivate) return;
        if (droneNeedsTargeting(drone.id, targetingData)) {
          startDroneTargetMouseDrag({ id: drone.id, instanceId: drone.instanceId }, e.clientX, e.clientY);
        }
      }}
      onClick={() => {
        // If deploy targeting is active, allow selecting friendly drones as targets
        if (deployTargetMode && selectedDeployOrderId) {
          handleTargetDeploy('drone', index, 'friendly');
          return;
        }
        
        // Handle replacement mode (when drone from hand selected and zone is full)
        if (replaceDroneTargetMode && selectedReplaceDroneInstanceId) {
          // Clicking on a drone in replace mode shows the buttons
          setClickedDroneForReplace({
            ...drone,
            index
          });
          return;
        }
        
        // Check if a drone is selected from hand (for replacement)
        if (selectedOrderInstanceId && !replaceDroneTargetMode) {
          const selectedOrder = you?.hand?.find(o => o.instanceId === selectedOrderInstanceId);
          // Only proceed if selected order is a drone
          if (selectedOrder?.type === 'Drone') {
            // Check if drone zone is full (4 slots)
            const dronesCount = you.drones?.filter(d => !d.markedForRemoval).length || 0;
            if (dronesCount >= 4) {
              // Enter replace mode and immediately show buttons on this drone
              setReplaceDroneTargetMode(true);
              setSelectedReplaceDroneId(selectedOrderId);
              setSelectedReplaceDroneInstanceId(selectedOrderInstanceId);
              setClickedDroneForReplace({
                ...drone,
                index
              });
              return;
            }
          }
        }
        
        // Normal drone activation (only if Disruptor is not deployed)
        if (canActivate) {
          activateDrone(drone.instanceId);
        }
      }}
    >
      {/* Drone image */}
      <img
        src={`/images/${drone.id}.jpg`}
        alt={drone.name}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1
        }}
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
      {/* Drone charge counter overlay */}
      {drone.charges !== undefined && drone.charges > 0 && (
        <div style={{
          color: 'white',
          fontSize: '16px',
          fontWeight: 'bold',
          textShadow: '1px 1px 1px rgba(0,0,0,0.8)',
          position: 'absolute',
          top: '2px',
          right: '2px',
          backgroundColor: 'rgba(0,0,0,0.7)',
          borderRadius: '50%',
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2
        }}>
          {drone.charges}
        </div>
      )}
      
      {/* Replace/Cancel icon buttons for drone replacement */}
      {replaceDroneTargetMode && clickedDroneForReplace && clickedDroneForReplace.instanceId === drone.instanceId && (
        <div style={{
          position: 'absolute',
          bottom: '3px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '6px',
          zIndex: 3
        }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleReplaceDrone(clickedDroneForReplace);
            }}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: '#4caf50',
              color: 'white',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
              padding: 0
            }}
            data-tooltip="Replace drone"
          >
            ✓
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCancelReplaceDrone();
            }}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: '#f44336',
              color: 'white',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
              padding: 0
            }}
            data-tooltip="Cancel replacement"
          >
            ✗
          </button>
        </div>
      )}
    </div>
    );
  })}
  {/* Empty drone slots */}
  {Array.from({ length: 4 - (you.drones?.filter(drone => !drone.markedForRemoval).length || 0) }).map((_, index) => (
    <div
      key={`empty-player-drone-${index}`}
      style={{
        width: '80px',
        height: '60px',
        backgroundColor: '#333',
        border: '2px solid #555',
        borderRadius: '4px',
        opacity: 0.3
      }}
    />
  ))}
</div>
)}

{/* Player Controls Section */}
<div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: 8 }}>
</div>

{/* End Turn Button moved outside battlefield-and-carousel for proper mobile positioning */}





        {/* Order Carousel - Hidden on mobile, mobile version rendered outside battlefield-and-carousel */}
        <div style={{ display: isContainerMobile ? "none" : "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="order-carousel">
{(() => {
  // ✅ CRITICAL: Filter out cards that are in completedOrders or deck (prevent desync display issues)
  // NOTE: This filtering is safe for effects that move cards from completedOrders to hand
  // (e.g., Unit 232 Ammit, Unit 339 Master Tactician, Unit 393 Essia Luhr) because:
  // 1. Backend removes card from completedOrders BEFORE adding to hand
  // 2. Frontend receives updated state where card is only in hand
  // 3. Filter checks current state - if card is not in completedOrders, it passes
  // 4. This prevents edge cases where desync might show card in both places
  const validHand = (you.hand || []).filter(order => {
    // ✅ DEFENSIVE: Explicitly check for null/undefined first
    if (order === null || order === undefined) {
      console.warn(`🚨 Filtering out null/undefined order from hand`);
      return false;
    }
    
    if (!order.instanceId) {
      console.warn(`🚨 Filtering out order ${order.name || 'Unknown'} (ID: ${order.id || 'Unknown'}) - missing instanceId`);
      return false;
    }
    
    // Check if this order is in completedOrders - if so, exclude it from hand display
    const inCompleted = you.completedOrders?.some(co => co && co.instanceId === order.instanceId);
    if (inCompleted) {
      console.warn(`🚨 Filtered out card from hand display: ${order.name} (${order.instanceId}) is in completedOrders`);
      return false;
    }
    
    // ✅ CRITICAL: Check if this order is in deck - if so, exclude it from hand display
    // This prevents showing cards that haven't been drawn yet (desync issue)
    const inDeck = you.deck?.some(d => d && d.instanceId === order.instanceId);
    if (inDeck) {
      console.warn(`🚨 Filtered out card from hand display: ${order.name} (${order.instanceId}) is in deck (not drawn yet)`);
      return false;
    }
    
    return true;
  });
  
  // ✅ During mulligan, hide hand so player only sees cards in MulliganDialog (avoids seeing cards twice)
  const handToDisplay = needsMulligan ? [] : validHand;
  
  return handToDisplay.map((order, idx) => {
    // ✅ DEFENSIVE: Check for null/undefined order (shouldn't happen after filter, but safety first)
    if (!order) {
      console.error(`🚨 CRITICAL: Null order found in validHand at index ${idx}`);
      return null; // Return null to skip rendering this slot
    }
    
    const isHovered = idx === hoveredIndex;
    const isBlockedByOrderRule = Array.isArray(blockedOrderIds) && blockedOrderIds.includes(order.id);
    const isBlocked = isBlockedByOrderRule || isDisorientationHandLocked;

    // ✅ FIXED: Validate instanceId instead of generating fallback
    if (!order.instanceId) {
      throw new Error(`Order ${order.name || 'Unknown'} (ID: ${order.id || 'Unknown'}) missing instanceId. This indicates a backend data integrity issue.`);
    }
    
    // Check if order can be played with current intelligence
    const orderCost = order.hasInstanceModifications && order.instanceCost !== undefined ? order.instanceCost : order.cost;
    const isPlayable = yourTurn && you.intelligence >= orderCost;
    
    // Get deployed unit info for tooltip (excludes Unit 134 - Beastcaller of the Rift)
    const deployedUnitInfo = getDeployedUnitInfo(order.id);
    
    // Use fetched order from DB (same method as deployment uses)
    let deployedUnitCard = null;
    if (deployedUnitInfo && gameState) {
      if (deployedUnitInfo.id) {
        const fetchedOrder = orderDBCache[deployedUnitInfo.id];
        if (fetchedOrder) {
          // Use order data directly from database - includes type, faction_name, description
          deployedUnitCard = {
            ...fetchedOrder,
            // Override with custom stats if specified (for Unit 137's Shadow Strike)
            attack_rating: deployedUnitInfo.customStats?.attack ?? fetchedOrder.attack_rating,
            health: deployedUnitInfo.customStats?.health ?? fetchedOrder.health,
            abilities: deployedUnitInfo.customStats?.abilities ?? fetchedOrder.abilities,
          };
        }
        // If not fetched yet, deployedUnitCard will be null and tooltip won't show until data loads
      } else if (deployedUnitInfo.isRandom) {
        // For random units, we can't show a specific card
        deployedUnitCard = {
          name: deployedUnitInfo.name,
          description: deployedUnitInfo.source ? `Random unit from ${deployedUnitInfo.source}` : 'Random unit'
        };
      }
    }
  

  return (
    <div
      key={order.instanceId || `${order.id}_${idx}`}
      data-instance-id={order.instanceId}
      data-order-id={order.id}
      className={`carousel-order-card${isHovered ? " expanded" : ""}${opponentLookingAtInstanceId && String(opponentLookingAtInstanceId) === String(order.instanceId) ? " order-viewed-by-opponent" : ""}`}
      style={{
        position: 'relative',
        background: "none",
        zIndex: isHovered ? 10000 : 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        margin: 0,
        border: "none",
        boxShadow: isHovered ? "none" : "none",
        padding: isHovered ? 0 : "8px 10px",
        cursor: isBlocked ? "not-allowed" : (yourTurn ? "pointer" : "default"),
        pointerEvents: isBlocked ? "none" : undefined,
        opacity: isBlocked ? 0.6 : 1,
        transition: "transform 0.2s ease",
        transform: isHovered ? "scale(1.05) translateY(-10px)" : "scale(1) translateY(-60px)",
        transformOrigin: "top",
        height: '100%',
        alignItems: 'flex-start',
        opacity: 1,
        touchAction: isMobile && yourTurn && !isBlocked ? 'none' : 'auto',
      }}
      onMouseEnter={() => {
        // Play hover sound when expanding order
        playSoundEffect('/sounds/hoverover.mp3', 0.3);
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }
        setHoveredIndex(idx);
        sendOrderHover(order.instanceId);
      }}
      onMouseLeave={() => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }
        hoverTimeoutRef.current = setTimeout(() => {
          setHoveredIndex(prevIndex => {
            if (prevIndex !== idx) return prevIndex;
            return null;
          });
          hoverTimeoutRef.current = null;
        }, 100);
        sendOrderHover(null);
      }}
      onMouseDown={(e) => {
        if (isBlocked || !yourTurn || isMobile || e.button !== 0) return;
        e.preventDefault();
        if (order.type === 'Weapon') {
          startWeaponMouseDrag(order, e.clientX, e.clientY);
        } else if (order.type === 'Strategem') {
          startStrategemMouseDrag(order, e.clientX, e.clientY);
        } else if (order.type === 'Drone') {
          startDroneMouseDrag(order, e.clientX, e.clientY);
        } else {
          startCarouselMouseDrag(order, e.clientX, e.clientY);
        }
      }}
      onClick={(e) => {
        if (isBlocked) return;
        if (isMobile) return;
        if (clickHandledByMouseupRef.current) {
          clickHandledByMouseupRef.current = false;
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        handleCardClick(order, e);
      }}
      // Mobile touch drag handlers for drag-to-deploy
      onTouchStart={(e) => {
        if (isBlocked || !isMobile || !yourTurn) return;
        handleTouchDragStart(e, order);
      }}
      onTouchMove={(e) => {
        if (!isMobile || !yourTurn) return;
        handleTouchDragMove(e);
      }}
      onTouchEnd={(e) => {
        if (isBlocked || !isMobile || !yourTurn) return;

        // Handle touch drag end - returns info about the drag
        const { wasDragging, order: dragOrder, zone } = handleTouchDragEnd(e);
        
        if (wasDragging) {
          // If it was a drag, handle deployment or cancellation
          e.preventDefault();
          e.stopPropagation();
          
          if (dragOrder && zone) {
            // Check if the order type is valid for zone deployment
            // Deployable units are anything that's NOT a Drone, Strategem, or Weapon
            const isDeployableToZone = dragOrder.type !== 'Drone' && 
                                        dragOrder.type !== 'Strategem' && 
                                        dragOrder.type !== 'Weapon';
            
            if (isDeployableToZone) {
              playOrder(dragOrder.id, zone, null, dragOrder.instanceId);
            } else {
              // For non-deployable types, fall through to click behavior
              handleCardTouch(dragOrder, e);
            }
          } else {
          }
        } else {
          // Not a drag, treat as tap (existing click-click behavior)
          e.preventDefault();
          e.stopPropagation();
          handleCardTouch(order, e);
        }
      }}
    >
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        width: '100%',
        height: '100%',
        justifyContent: 'space-between'
      }}>
        <OrderDesign
          order={order}
          count={0}
          minimal={!isHovered}
          scale={viewportScale}
          isOOR={order.isOOR}
          isSelected={selectedOrderInstanceId === order.instanceId}
          isPlayable={isPlayable}
          gameState={gameState}
          userId={activeUserId}
          imageDraggable={false}
          debugInfo={{
            orderName: order.name,
            orderId: order.id,
            orderInstanceId: order.instanceId,
            selectedInstanceId: selectedOrderInstanceId,
            isSelected: selectedOrderInstanceId === order.instanceId
          }}
          renderButtons={yourTurn && isHovered ? () => {
            const buttons = [];
            
            // Deploy button for drones
            if (order.type === 'Drone') {
              buttons.push(
                <button
                  key="deploy"
                  onClick={() => {
                    // Check if drone zone is full (max 4 drones)
                    const dronesCount = you.drones?.filter(d => !d.markedForRemoval).length || 0;
                    if (dronesCount >= 4) {
                      // Select the drone in hand to enable replacement mode
                      setSelectedOrderId(order.id);
                      setSelectedOrderInstanceId(order.instanceId);
                      setError(t('battleUI.droneZoneFull'));
                      // Existing replacement logic will handle the rest when user clicks a deployed drone
                    } else {
                      // Normal deployment when zone not full
                      playDrone(order.id);
                    }
                  }}
                  style={{
                    backgroundColor: '#4a90e2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}
                >
                  {t('battleUI.deploy')}
                </button>
              );
            }
            
            // Request button for strategems
            if (order.type === 'Strategem') {
              buttons.push(
                <button
                  key="request"
                  onClick={() => {
                    // Use the same playStrategem function for consistency
                    playStrategem(order.id, order.instanceId);
                  }}
                  style={{
                    backgroundColor: '#8B4513',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}
                >
                  {t('battleUI.request')}
                </button>
              );
            }
            
            // Equip button for weapons
            if (order.type === 'Weapon') {
              buttons.push(
                <button
                  key="equip"
                  onClick={() => {
                    // ✅ CRITICAL: Only equip weapon if intelligence check passes
                    // For weapons, use enriched cost (order.cost) which includes Engineering Overseer reduction
                    // Don't use instanceCost as it doesn't include battlefield modifiers
                    if (order) {
                      const orderCost = order.cost !== undefined ? order.cost : (order.hasInstanceModifications && order.instanceCost !== undefined ? order.instanceCost : order.originalCost || 0);
                      if (gameState?.state?.[userId] && gameState.state[userId].intelligence >= orderCost) {
                        // Set weapon targeting mode
                        setWeaponTargetMode(true);
                        setSelectedWeaponId(order.id);
                        setSelectedWeaponInstanceId(order.instanceId);
                      } else {
                        setError(`${t('battleUI.notEnoughIntelligence')} ${order.name}! ${t('battleUI.need')} ${orderCost}, ${t('battleUI.have')} ${gameState?.state?.[userId]?.intelligence || 0}`);
                      }
                    }
                  }}
                  style={{
                    backgroundColor: '#ff6b35',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}
                >
                  Equip
                </button>
              );
            }
            
            return buttons.length > 0 ? buttons : null;
          } : null}
        />
        
        {/* Deployed Unit Tooltip - Shows what unit will be deployed (excludes Unit 134) */}
        {isHovered && deployedUnitInfo && deployedUnitInfo.id && !deployedUnitInfo.isRandom && deployedUnitCard ? (
          <div 
            className="deployed-unit-tooltip"
            style={{
              position: 'absolute',
              top: '0',
              left: '410px', // Position to the right of expanded card (390px width + 20px gap)
              zIndex: 10001,
              pointerEvents: 'none',
              overflow: 'visible'
            }}
          >
            <OrderDesign
              order={deployedUnitCard}
              count={0}
              minimal={false}
              scale={viewportScale * 0.8} // Scale down by 20% (0.8 = 80% of original size)
              isOOR={false}
              isSelected={false}
              isPlayable={false}
              gameState={gameState}
            />
          </div>
        ) : null}
        
        {/* Strategem selected indicator */}
        {yourTurn && selectedOrderInstanceId === order.instanceId && order.type === 'Strategem' && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '4px',
            alignItems: 'center',
            margin: '8px 0'
          }}>
          </div>
        )}
        
        {/* Spacer to compensate for removed instructional text */}
        {yourTurn && isHovered && !selectedOrderInstanceId && (
          <div style={{ marginTop: 8, height: '20px' }}>
            {/* Empty spacer to maintain positioning */}
          </div>
        )}
      </div>
    </div>
  );
  });
})()}


          </div>
        </div>
      </div>
    </div>

    {/* Mobile Order Carousel - Outside battlefield-and-carousel for proper positioning */}
    {isContainerMobile && (
      <MobileOrderCarousel
        you={you}
        yourTurn={yourTurn}
        hoveredIndex={hoveredIndex}
        setHoveredIndex={setHoveredIndex}
        selectedOrderInstanceId={selectedOrderInstanceId}
        gameState={gameState}
        handleTouchDragStart={handleTouchDragStart}
        handleTouchDragMove={handleTouchDragMove}
        handleTouchDragEnd={handleTouchDragEnd}
        handleCardTouch={handleCardTouch}
        playOrder={playOrder}
        blockedOrderIds={blockedOrderIds}
        isDisorientationHandLocked={isDisorientationHandLocked}
        needsMulligan={needsMulligan}
      />
    )}

    {/* DEVELOPER CONSOLE - REMOVE BEFORE PRODUCTION (PLAYER 1 ONLY) */}
    {isDeveloperMode && (
      <DeveloperConsole
        ws={ws}
        gameId={gameId}
        devUnitSearch={devUnitSearch}
        setDevUnitSearch={setDevUnitSearch}
        showGridOverlay={showGridOverlay}
        setShowGridOverlay={setShowGridOverlay}
        showDebugOverlay={showDebugOverlay}
        setShowDebugOverlay={setShowDebugOverlay}
        showTargetedDebug={showTargetedDebug}
        setShowTargetedDebug={setShowTargetedDebug}
        showElementSelector={showElementSelector}
        setShowElementSelector={setShowElementSelector}
        showEditableOverlay={showEditableOverlay}
        setShowEditableOverlay={setShowEditableOverlay}
        gridSize={gridSize}
        setGridSize={setGridSize}
        targetElements={targetElements}
        setTargetElements={setTargetElements}
        handleElementSelected={handleElementSelected}
        handleElementMoved={handleElementMoved}
        handleElementResized={handleElementResized}
        handleSaveDesign={handleSaveDesign}
        savedDesign={savedDesign}
        setSavedDesign={setSavedDesign}
        actionLog={actionLog}
        setActionLog={setActionLog}
      />
    )}

    {/* OLD DEVELOPER CONSOLE REMOVED */}


    {/* Grid Overlay - Only in developer mode */}
    {isDeveloperMode && (
      <GridOverlay 
        isVisible={showGridOverlay} 
        gridSize={gridSize} 
        showCoordinates={true} 
      />
    )}

    {/* Debug Overlay - Only in developer mode */}
    {isDeveloperMode && (
      <DebugOverlay 
        isVisible={showDebugOverlay} 
        showElementBoundaries={true} 
      />
    )}

    {/* Targeted Debug Overlay - Only in developer mode */}
    {isDeveloperMode && (
      <TargetedDebugOverlay 
        isVisible={showTargetedDebug} 
        targetElements={targetElements}
        showLabels={true}
      />
    )}

    {/* Simple Element Selector - Only in developer mode */}
    {isDeveloperMode && (
      <SimpleElementSelector 
        isVisible={showElementSelector} 
        onElementSelected={handleElementSelected}
      />
    )}

    {/* Editable Element Overlay - Only in developer mode */}
    {isDeveloperMode && (
      <EditableElementOverlay 
        isVisible={showEditableOverlay} 
        targetElements={targetElements}
        onElementMoved={handleElementMoved}
        onElementResized={handleElementResized}
        onSaveDesign={handleSaveDesign}
      />
    )}

    {/* Counter Strike Dialog */}
    {/* ✅ FIX: Add key based on orders to force reset when orders change (e.g., after playing one) */}
    <CounterStrikeDialog
      key={counterStrikeOrders.length > 0 ? counterStrikeOrders[0]?.instanceId || counterStrikeOrders.length : 'no-orders'}
      orders={counterStrikeOrders}
      onSelectZone={playCounterStrike}
      onKeepOrder={(orderId, instanceId) => keepCounterStrike(orderId, instanceId)}
      onStrikeModeActivated={handleCounterStrikeModeActivated}
      playerId={userId}
      isMobile={isContainerMobile}
      processingCounterStrikeId={processingCounterStrikeId}
      handSize={gameState?.state?.[userId]?.hand?.length || 0}
    />

    {/* Opponent Counter Strike Dialog - Blocks player when opponent has pending counterstrike */}
    <OpponentCounterStrikeDialog 
      show={opponentHasCounterStrike} 
      ws={ws}
      gameId={gameId}
      gameState={gameState}
      userId={userId}
      isMobile={isContainerMobile}
    />

    {/* Mulligan Dialog */}
    {!isDeveloperMode && needsMulligan && mulliganOrders.length > 0 && (
      <MulliganDialog
        orders={mulliganOrders}
        onSubmitMulligan={submitMulligan}
        playerId={userId}
        isMobile={isContainerMobile}
      />
    )}

    {/* Touch Drag Overlay for mobile deployment */}
    <TouchDragOverlay
      isDragging={isTouchDragging}
      draggedOrder={touchDraggedOrder}
      dragPosition={touchDragPosition}
      targetZone={touchTargetZone}
      viewportScale={viewportScale}
    />
    
    {/* Attack Drag Overlay for mobile attacks */}
    <AttackDragOverlay
      isDragging={isAttackDragging}
      attackerInfo={attackDragAttacker}
      startPosition={attackDragStartPosition}
      dragPosition={attackDragPosition}
      hoveredTarget={attackDragTarget}
    />

    {/* Desktop drag-to-deploy / strategem drag: red line only (custom mouse drag); deploy by half-screen, strategem by drop-on-target. Portaled to body so laser is on top of expanded order cards (OrderDesign portal uses z-index 99999). */}
    {!isContainerMobile && (dragDeployState || strategemDragState || weaponDragState || droneDragState || attackDragState || droneTargetDragState) && createPortal(
      (() => {
        const lineState = dragDeployState || strategemDragState || weaponDragState || droneDragState || attackDragState || droneTargetDragState;
        return (
          <div
            className="deploy-drag-overlay"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 100002,
              pointerEvents: 'none',
            }}
          >
            {lineState ? (
              <svg
                className="deploy-drag-line"
                viewBox={`0 0 ${typeof window !== 'undefined' ? window.innerWidth : 1920} ${typeof window !== 'undefined' ? window.innerHeight : 1080}`}
                preserveAspectRatio="none"
                style={{ pointerEvents: 'none' }}
              >
                <defs>
                  <filter id="deploy-drag-glow">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                  <filter id="deploy-drag-glow-soft">
                    <feGaussianBlur stdDeviation="6" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                    </feMerge>
                  </filter>
                  <filter id="deploy-drag-target-glow">
                    <feGaussianBlur stdDeviation="4" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                {/* Soft glow layer (drawn first, behind main line) */}
                <line
                  className="deploy-drag-line-glow"
                  x1={lineState.originX}
                  y1={lineState.originY}
                  x2={lineState.currentX}
                  y2={lineState.currentY}
                />
                <line
                  x1={lineState.originX}
                  y1={lineState.originY}
                  x2={lineState.currentX}
                  y2={lineState.currentY}
                />
                {/* Lit target at cursor */}
                <circle
                  className="deploy-drag-line-target"
                  cx={lineState.currentX}
                  cy={lineState.currentY}
                  r={10}
                />
              </svg>
            ) : null}
          </div>
        );
      })(),
      document.body
    )}

    {/* Revealed Enemy Order Modal (Ghost Operative - Unit 295) */}
    {revealedEnemyOrder && (
      <RevealedOrderModal
        revealedOrder={revealedEnemyOrder.order}
        enemyId={revealedEnemyOrder.enemyId}
        onAcknowledge={acknowledgeGhostRevealedOrder}
      />
    )}

    {/* Completed Orders Modal */}
    <CompletedOrdersModal
      show={showCompletedOrders}
      onClose={() => {
        // Only allow closing if Essia Luhr selection is complete or not active
        if (you?.essiaLuhrNeedsSelection && !you?.essiaLuhrSelected) {
          // Don't close if Essia Luhr selection is still needed
          return;
        }
        setShowCompletedOrders(false);
      }}
      completedOrders={you?.completedOrders || []}
      gameState={gameState}
      userId={activeUserId}
      hoveredOrder={hoveredCompletedOrder}
      onHoverOrder={setHoveredCompletedOrder}
      essiaLuhrActive={
        (you?.essiaLuhrNeedsSelection && !you?.essiaLuhrSelected) || false
      }
      onEssiaLuhrSelect={async (instanceId, orderId) => {
        if (!ws || ws.readyState !== 1) {
          setError(t('battleUI.websocketNotConnected') || 'WebSocket not connected');
          return;
        }
        
        try {
          ws.send(JSON.stringify({
            type: "play-turn",
            gameId,
            action: {
              type: "essia_luhr_select",
              instanceId: instanceId,
              orderId: orderId
            }
          }));
          
          setShowCompletedOrders(false);
        } catch (error) {
          console.error('❌ Error selecting Essia Luhr strategem:', error);
          setError(t('battleUI.failedToSelectOrder') || 'Failed to select strategem');
        }
      }}
    />

    {/* Strategy Modal */}
    <StrategyModal
      show={showStrategy}
      onClose={() => setShowStrategy(false)}
      strategy={you?.strategy}
      gameState={gameState}
      userId={activeUserId}
      hoveredOrderIndex={hoveredStrategyOrder}
      onHoverOrder={setHoveredStrategyOrder}
      playerState={you}
      commandDirectiveActive={
        (gameState?.playerDiplomaticAccords?.[userId] === 'command_directive' ||
         gameState?.opponentDiplomaticAccords?.[userId] === 'command_directive') &&
        !(you?.commandDirectiveSelected)
      }
      onCommandDirectiveSelect={async (orderId) => {
        if (!ws || ws.readyState !== 1) {
          setError(t('battleUI.websocketNotConnected') || 'WebSocket not connected');
          return;
        }
        
        try {
          ws.send(JSON.stringify({
            type: "play-turn",
            gameId,
            action: {
              type: "command_directive",
              orderId: orderId
            }
          }));
          
          setShowStrategy(false);
        } catch (error) {
          console.error('❌ Error selecting command directive order:', error);
          setError(t('battleUI.failedToSelectOrder') || 'Failed to select order');
        }
      }}
    />

    {/* Strategic Forecast Dialog (Strategem 323) */}
    {you?.needsStrategicForecastSelection?.orders && you.needsStrategicForecastSelection.orders.length > 0 && (
      <StrategicForecastDialog
        orders={you.needsStrategicForecastSelection.orders}
        onSelectOrder={(orderId) => {
          // Selection handled inside dialog component
        }}
        ws={ws}
        gameId={gameId}
      />
    )}


    {/* Repositioning Overlays - Rendered via portal to document.body for correct fixed positioning */}
    {activeRepositioningOverlays && activeRepositioningOverlays.size > 0 && createPortal(
      <>
        {Array.from(activeRepositioningOverlays.entries()).map(([key, data]) => (
          <RepositioningOverlay
            key={key}
            reposition={data.reposition}
            unit={data.unit}
            gameState={activeGameState}
            userId={activeUserId}
          />
        ))}
      </>,
      document.body
    )}

    {/* Decommission Overlays - Rendered via portal to document.body for correct fixed positioning */}
    {activeDecommissionOverlays && activeDecommissionOverlays.size > 0 && createPortal(
      <>
        {Array.from(activeDecommissionOverlays.entries()).map(([key, data]) => (
          <DecommissionOverlay
            key={key}
            decommission={data.decommission}
            unit={data.unit}
            gameState={activeGameState}
            userId={activeUserId}
          />
        ))}
      </>,
      document.body
    )}

    {/* Attack Animations - Rendered via portal to document.body for correct fixed positioning */}
    {createPortal(
      <>
        {attackAnimations.pendingAttacks.map(([attackId, pendingAttack]) => {
      const outcome = pendingAttack.outcome;
      if (!outcome) {
        console.warn(`⚠️ Attack animation: No outcome for attackId ${attackId}`);
        return null;
      }
      
      // ✅ NEW: Check if this is a general attack
      const isGeneralAttack = outcome.isGeneralAttack || outcome.targetZone === 'general';
      
      // ✅ SPECTATOR FIX: Use activeGameState instead of gameState for spectator support
      // Find attacker and target units in activeGameState
      const attackerPlayer = activeGameState?.state?.[outcome.attackerPlayerId];
      const targetPlayer = activeGameState?.state?.[outcome.targetPlayerId];
      
      if (!attackerPlayer) {
        console.warn(`⚠️ Attack animation: Attacker player not found`, {
          attackId,
          attackerPlayerId: outcome.attackerPlayerId,
          availablePlayers: activeGameState?.state ? Object.keys(activeGameState.state) : 'no state',
          isSpectating,
          hasActiveGameState: !!activeGameState
        });
        return null;
      }
      
      if (!targetPlayer) {
        console.warn(`⚠️ Attack animation: Target player not found`, {
          attackId,
          targetPlayerId: outcome.targetPlayerId,
          availablePlayers: activeGameState?.state ? Object.keys(activeGameState.state) : 'no state',
          isSpectating,
          hasActiveGameState: !!activeGameState
        });
        return null;
      }
      
      // ✅ FIX: Find attacker by instanceId first (more reliable than index)
      let attacker = null;
      if (attackerPlayer.playedOrders?.[outcome.attackerZone]) {
        if (outcome.attackerInstanceId) {
          attacker = attackerPlayer.playedOrders[outcome.attackerZone].find(
            u => u && u.instanceId === outcome.attackerInstanceId
          );
        }
        // Fallback to index if instanceId lookup fails
        if (!attacker && outcome.attackerIndex >= 0) {
          attacker = attackerPlayer.playedOrders[outcome.attackerZone][outcome.attackerIndex];
        }
      }
      
      // ✅ NEW: For general attacks, target is null (general is not in playedOrders)
      const target = isGeneralAttack ? null : (targetPlayer.playedOrders?.[outcome.targetZone]?.[outcome.targetIndex]);
      
      if (!attacker) {
        console.warn(`⚠️ Attack animation: Attacker unit not found`, {
          attackId,
          attackerPlayerId: outcome.attackerPlayerId,
          attackerZone: outcome.attackerZone,
          attackerIndex: outcome.attackerIndex,
          attackerInstanceId: outcome.attackerInstanceId,
          playedOrders: attackerPlayer.playedOrders?.[outcome.attackerZone]?.map((u, i) => ({
            index: i,
            name: u?.name,
            instanceId: u?.instanceId
          }))
        });
        return null;
      }
      
      
      // ✅ SPECTATOR FIX: Use activeUserId instead of userId for zone name determination
      // For spectators, activeUserId is the player they're watching, not their own userId
      const attackerPlayerId = outcome.attackerPlayerId === activeUserId ? 'your' : 'opponent';
      const targetPlayerId = outcome.targetPlayerId === activeUserId ? 'your' : 'opponent';
      
      return (
        <MemoizedAttackAnimation
          key={attackId}
          attackId={attackId}
          attacker={attacker}
          outcome={outcome}
          attackerPlayerId={attackerPlayerId}
          targetPlayerId={targetPlayerId}
          attackAnimations={attackAnimations}
          attackInProgressRef={attackInProgressRef}
          setDamageAnimationTrigger={setDamageAnimationTriggers}
          damageAnimationTriggeredRef={damageAnimationTriggeredRef}
          animationSpeedMultiplier={speedControl.animationSpeedMultiplier}
        />
      );
    })}

    {/* ✅ FIX: Render damage animations OUTSIDE of attack animation components */}
    {/* This ensures they persist even after attack components unmount */}
    {Array.from(damageAnimationTriggers.entries()).map(([attackId, trigger]) => {
      // ✅ CRITICAL FIX: Validate trigger position before rendering
      // Skip rendering if position is invalid (0, NaN, undefined, or out of bounds)
      // ✅ BUG FIX: Also reject positions that are too close to screen edges (likely invalid)
      // Valid damage animations should be within reasonable screen bounds
      // ✅ FIX: Allow y < 50 for general damage animations (generals are positioned at top of screen)
      const isGeneralAnimation = attackId?.includes('general') || 
                                  trigger?.slotElement?.getAttribute?.('data-zone')?.includes('general') ||
                                  trigger?.isGeneralAnimation === true;
      const minY = isGeneralAnimation ? 0 : 50; // Generals can be at y < 50 (top of screen)
      if (!trigger || typeof trigger.x !== 'number' || typeof trigger.y !== 'number' ||
          isNaN(trigger.x) || isNaN(trigger.y) || trigger.x <= 0 || trigger.y < minY ||
          trigger.x > 3000 || trigger.y > 2000 ||
          trigger.x < 50) {
        console.warn(`⚠️ Skipping invalid damage animation for ${attackId} (outside valid bounds):`, {
          x: trigger?.x,
          y: trigger?.y,
          isGeneralAnimation,
          trigger
        });
        return null;
      }
      
      // ✅ DEBUG: Log every damage animation being rendered
      
      return (
      <div
        key={`damage-container-${attackId}-${trigger.timestamp}`}
        style={{
          position: 'fixed',
          left: `${trigger.x}px`,
          top: `${trigger.y}px`,
          transform: 'translate(-50%, -50%)',
          zIndex: 10001, // Above attack animation
          pointerEvents: 'none',
          width: '200px',
          height: '200px',
        }}
      >
        <DamageAnimation
          key={`damage-${attackId}-${trigger.timestamp}`}
          x={50} // Center (50% of parent container)
          y={50} // Center (50% of parent container)
          onComplete={() => {
            // Remove from Map after animation completes
            setDamageAnimationTriggers(prev => {
              const next = new Map(prev);
              next.delete(attackId);
              return next;
            });
            // ✅ BUG FIX: Clean up tracking ref after animation completes
            damageAnimationTriggeredRef.current.delete(attackId);
          }}
          animationKey={`attack-damage-${attackId}-${trigger.timestamp}`}
          animationSpeedMultiplier={speedControl.animationSpeedMultiplier}
        />
      </div>
    );
    })}

    {/* Deploy Target Animations */}
    {deployTargetAnimations.activeAnimations.map((animation) => (
      <DeployTargetAnimation
        key={animation.key}
        fromSlot={animation.fromSlot}
        toSlot={animation.toSlot}
        animationType={animation.animationType}
        deployUnitId={animation.deployUnitId}
        deployAnimationVariant={animation.deployAnimationVariant}
        onComplete={() => {
          deployTargetAnimations.completeAnimation(animation.key);
        }}
        animationSpeedMultiplier={speedControl.animationSpeedMultiplier}
      />
    ))}

    {/* Strategem Target Animations - Black balls for damage, yellow balls for positive effects */}
    {strategemTargetAnimations.activeAnimations.map((animation) => (
      <StrategemTargetAnimation
        key={animation.key}
        fromHand={animation.fromHand}
        toSlot={animation.toSlot}
        targetType={animation.targetType}
        effectType={animation.effectType || 'damage'} // Default to 'damage' (black), 'heal' (yellow) for positive effects
        strategemId={animation.strategemId}
        isOpponentStrategem={animation.isOpponentStrategem || false} // Pass isOpponentStrategem flag
        animationSpeedMultiplier={speedControl.animationSpeedMultiplier}
        onComplete={() => {
          // ✅ STRATEGEM 219: Check if zone should be marked complete BEFORE removing animation
          // When strategem 219 (Plague of Darkness) animation completes, allow immobilize indicators to show
          if (animation.strategemId === 219 && animation.toSlot?.zone) {
            const zone = animation.toSlot.zone.replace('your_', '').replace('opponent_', '');
            
            // For zone-targeting animations (targetType === 'zone'), mark complete immediately
            // For unit-targeting animations, check if all animations for this strategem instance and zone have completed
            if (animation.targetType === 'zone') {
              strategem219CompletedZonesRef.current.add(zone);
            } else {
              // Check remaining animations BEFORE calling completeAnimation (which removes this one)
              const strategemInstanceId = animation.strategemInstanceId;
              const remainingAnimations = strategemTargetAnimations.activeAnimations.filter(anim => 
                anim.strategemId === 219 && 
                anim.strategemInstanceId === strategemInstanceId &&
                anim.toSlot?.zone?.replace('your_', '').replace('opponent_', '') === zone &&
                anim.key !== animation.key // Exclude the current animation (which will be removed)
              );
              
              // If no remaining animations for this strategem instance and zone, mark zone as complete
              if (remainingAnimations.length === 0) {
                strategem219CompletedZonesRef.current.add(zone);
              } else {
              }
            }
          }
          
          // ✅ TWO-PHASE STRATEGEM SYSTEM: Send strategem-animation-complete message to backend
          // This tells the backend to apply the strategem effect now that animation is done
          // Only send if we have a pending animation ID for this strategem instance
          // Skip for spectators and autobattler spectators
          const strategemInstanceId = animation.strategemInstanceId;
          const pendingAnimInfo = strategemInstanceId ? pendingStrategemAnimationIdsRef.current.get(strategemInstanceId) : null;
          
          if (pendingAnimInfo && ws && ws.readyState === 1 && !isSpectating && !isAutobattlerSpectator) {
            // Check if this is the last animation for this strategem instance
            // (zone-targeting strategems may have multiple animations)
            const remainingAnimationsForStrategem = strategemTargetAnimations.activeAnimations.filter(anim => 
              anim.strategemInstanceId === strategemInstanceId &&
              anim.key !== animation.key // Exclude current animation
            );
            
            if (remainingAnimationsForStrategem.length === 0) {
              ws.send(JSON.stringify({
                type: 'strategem-animation-complete',
                strategemAnimationId: pendingAnimInfo.strategemAnimationId,
                strategemInstanceId: strategemInstanceId,
                gameId: gameId
              }));
              
              // Clean up the mapping
              pendingStrategemAnimationIdsRef.current.delete(strategemInstanceId);
            } else {
            }
          } else if (pendingAnimInfo && (isSpectating || isAutobattlerSpectator)) {
          }
          
          strategemTargetAnimations.completeAnimation(animation.key);
        }}
        onDamageAtTarget={(targetPosition) => {
          // Only trigger damage animation for damage effects, not heal effects
          // Check effectType first - if it's 'heal', don't trigger damage explosion
          if (animation.effectType === 'heal') {
            return;
          }
          
          // Trigger damage animation if the strategem deals damage, destroys units, or shrinks units
          const strategemId = animation.strategemId;
          if (!strategemId) {
            return;
          }
          
          const dealsDamage = doesStrategemDealDamageById(strategemId, gameState);
          const destroysUnits = doesStrategemDestroyUnitsById(strategemId, gameState);
          const shrinksUnits = doesStrategemShrinkUnitsById(strategemId, gameState);
          
          if (!dealsDamage && !destroysUnits && !shrinksUnits) {
            return;
          }
          
          // Repurposed Warhead (54): explosions are shown at each unit slot via slot death detection, not here
          if (strategemId === 54) {
            return;
          }
          
          // Trigger damage animation at the exact target position when black ball arrives
          // This works for damage-dealing strategems, unit-destroying strategems, and shrink strategems
          // Shrink strategems need damage animations for both lethal (kills unit) and non-lethal (reduces stats) cases
          const reason = dealsDamage ? 'deals damage' : (destroysUnits ? 'destroys units' : 'shrinks units');
          const animationKey = `strategem-damage-${animation.key}-${Date.now()}`;
          
          // Check if this is a general animation (for proper validation)
          const isGeneralAnim = animation.targetType === 'general' || targetPosition.targetType === 'general';
          
          setDamageAnimationTriggers(prev => {
            const next = new Map(prev);
            next.set(animationKey, {
              attackId: animationKey,
              x: targetPosition.x,
              y: targetPosition.y,
              timestamp: Date.now(),
              isGeneralAnimation: isGeneralAnim // Mark as general animation for validation
            });
            return next;
          });
        }}
      />
    ))}

    {/* Strategem 113 Shockwave Effects */}
    {strategemShockwave.activeShockwaves.map((shockwave) => (
      <ShockwaveEffect
        key={shockwave.key}
        x={shockwave.x}
        y={shockwave.y}
        animationKey={shockwave.key}
        onComplete={() => {
          strategemShockwave.completeShockwave(shockwave.key);
        }}
      />
    ))}

    {/* Inferno Drop (319) napalm effect – all 4 zones */}
    {infernoNapalm.activeNapalms.map((napalm) => (
      <InfernoNapalmEffect
        key={napalm.key}
        animationKey={napalm.key}
        onComplete={() => infernoNapalm.completeNapalm(napalm.key)}
      />
    ))}

    {/* Strategem 114, 115, 221 & 225 Explosion Effects */}
    {Array.from(explosionEffects.values()).map((explosion) => (
      <ExplosionEffect
        key={explosion.key}
        x={explosion.x}
        y={explosion.y}
        animationKey={explosion.key}
        color={explosion.color || 'orange'}
        onComplete={() => {
          setExplosionEffects(prev => {
            const next = new Map(prev);
            next.delete(explosion.key);
            return next;
          });
        }}
      />
    ))}
      </>,
      document.body
    )}

    {/* DESYNC FIX: Loading overlay - only shows if action takes >300ms */}
    {showLoadingOverlay && (
      <div className="state-updating-overlay">
        <div className="state-updating-message">
          <div className="loading-spinner"></div>
          <p>Processing action...</p>
        </div>
      </div>
    )}
    
    {/* Speed control for autobattler spectators */}
    {isAutobattlerSpectator && (
      <SpectateSpeedControl
        speed={speedControl.speed}
        onSpeedChange={speedControl.updateSpeed}
        isPaused={speedControl.isPaused}
        onTogglePause={speedControl.togglePause}
      />
    )}
    
      </div>
    
    {/* War Effort fortification - outside battle-ui-background so visible (background has z-index -1). Shown on defender's side: opponent top-right (red box) or player bottom-left. Includes completion building bonuses (80 collected + 30 orders of faction). */}
    {(activeGameState?.warEffortBattleType === 'fortified_country' || activeGameState?.warEffortBattleType === 'other_country' || activeGameState?.warEffortBattleType === 'enemy_hq') && (() => {
      const fortUnitIds = {
        health_boost: 227, attack_boost: 77, intel_boost: 237, order_boost: 103,
        disorientation: 59, defend_twice: 187, instakill: 60, deploy_random: 279, ability_relay_drone: 399
      };
      const isOtherCountry = activeGameState?.warEffortBattleType === 'other_country';
      const fortTypes = isOtherCountry
        ? (activeGameState?.warEffortFortificationTypes || [])
        : (activeGameState?.warEffortFortificationType ? [activeGameState.warEffortFortificationType] : []);
      const completionBonuses = [
        { key: 'lavender', active: activeGameState?.defenderLavenderFarmActive, image: '/images/lavender.png', name: 'Lavender Farm', desc: 'Units costing <4 intel have Blitz (80 lavender, 30+ Exolot Dominion)' },
        { key: 'cattle', active: activeGameState?.defenderCattleFarmActive, image: '/images/cattle.png', name: 'Cattle Farm', desc: 'Remnants +1 health (80 stone, 30+ Remnants of Humanity)' },
        { key: 'quarry', active: activeGameState?.defenderQuarryActive, image: '/images/stone.png', name: 'Quarry', desc: 'Shield restore at turn start (80 stone, 30+ Ra\'s Children)' },
        { key: 'sewer', active: activeGameState?.defenderSewerActive, image: '/images/sewers.png', name: 'Sewer', desc: 'All units have Mend Wounds (80 biomass, 30+ Asrah)' },
        { key: 'portal', active: activeGameState?.defenderPortalActive, image: '/images/erray.png', name: 'Portal', desc: 'One zone-switch charge per unit (80 erray, 30+ League of Shadows)' }
      ].filter((b) => b.active);
      const hasFortTypes = fortTypes.length > 0;
      const hasCompletionBonuses = completionBonuses.length > 0;
      if (!hasFortTypes && !hasCompletionBonuses) return null;
      const defenderId = activeGameState.warEffortDefenderPlayerId != null
        ? activeGameState.warEffortDefenderPlayerId
        : (activeGameState.players && activeGameState.players[1]);
      const defenderState = defenderId && activeGameState.state ? activeGameState.state[defenderId] : null;
      const isDefender = userId === defenderId;
      const cellPx = 20;
      const cellsLeftPx = 4 * cellPx;
      const iconSize = isContainerMobile ? { width: '32px', height: '32px' } : { width: '36px', height: '36px' };
      const position = isContainerMobile
        ? (isDefender ? { left: `${12 - cellsLeftPx}px`, bottom: '72px' } : { right: `${12 + cellsLeftPx}px`, top: '72px' })
        : (isDefender ? { left: `${20 - cellsLeftPx}px`, top: '848px' } : { right: `${65 + cellsLeftPx}px`, top: '80px' });
      return (
        <div
          style={{
            position: 'absolute',
            ...position,
            zIndex: 1001,
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: '4px',
            alignItems: 'center',
            maxWidth: isContainerMobile ? '200px' : '320px'
          }}
        >
          {fortTypes.map((fortType, idx) => {
            const unitId = fortUnitIds[fortType];
            if (!unitId) return null;
            const oneTimeUsed = defenderState && (
              (fortType === 'instakill' && defenderState.fortificationInstakillUsed) ||
              (fortType === 'deploy_random' && defenderState.fortificationDeployRandomUsed)
            );
            const tooltipKey = oneTimeUsed ? 'battleUI.fortification.alreadyUsed' : `battleUI.fortification.${fortType}`;
            return (
              <div
                key={`${fortType}-${idx}`}
                className={`battle-fortification-indicator ${oneTimeUsed ? 'battle-fortification-used' : ''}`}
                style={{
                  ...iconSize,
                  borderRadius: '4px',
                  border: '1px solid rgba(78, 205, 196, 0.5)',
                  overflow: 'hidden',
                  background: 'rgba(30, 30, 30, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  ...(oneTimeUsed ? { filter: 'grayscale(1)', opacity: 0.6 } : {})
                }}
                data-tooltip={t(tooltipKey)}
                data-tooltip-position="top"
              >
                <img
                  src={`/images/${unitId}.jpg`}
                  alt=""
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    pointerEvents: 'none'
                  }}
                />
              </div>
            );
          })}
          {completionBonuses.map((bonus) => (
            <div
              key={`completion-${bonus.key}`}
              className="battle-fortification-indicator battle-completion-bonus-indicator"
              style={{
                ...iconSize,
                borderRadius: '4px',
                border: '1px solid rgba(78, 205, 196, 0.5)',
                overflow: 'hidden',
                background: 'rgba(30, 30, 30, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
              data-tooltip={`${bonus.name}: ${bonus.desc}`}
              data-tooltip-position="top"
            >
              <img
                src={bonus.image}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  pointerEvents: 'none'
                }}
              />
            </div>
          ))}
        </div>
      );
    })()}
    
      </div>{/* Close viewport-container */}
    </div>{/* Close viewport-wrapper */}
    
    {/* Battle End Transition Overlay - Rendered via portal to cover full screen */}
    {battleEndTransition.isTransitioning && battleEndTransition.type && createPortal(
      <div 
        className={`battle-end-overlay ${battleEndTransition.type}`}
        style={battleEndTransition.type === 'win' ? {
          '--victory-white': victoryImageWhite
        } : battleEndTransition.type === 'loss' ? {
          '--defeat-gray': defeatImageGray
        } : {}}
      >
        {battleEndTransition.type === 'win' && showVictoryImage && (
          <div className="victory-image-container">
            <img
              src="/images/victory.jpg"
              alt="Victory"
              className="victory-image"
            />
          </div>
        )}
        {battleEndTransition.type === 'loss' && showDefeatImage && (
          <div className="defeat-image-container">
            <img
              src="/images/defeat.jpg"
              alt="Defeat"
              className="defeat-image"
            />
          </div>
        )}
      </div>,
      document.body
    )}
  </>
  );
}

// Memoized BattleUI to prevent re-renders when tutorial changes
// Note: We can't use deep comparison for gameState, so we rely on reference equality
// BattleUI will re-render when gameState reference changes (which is expected)
const MemoizedBattleUI = React.memo(BattleUI);

// Wrapper component to provide damage animation context and repositioning context
const BattleUIWrapper = React.memo(function BattleUIWrapper(props) {
  // We need to get isUnitRepositioning from the hook, but hooks can't be called conditionally
  // So we'll create a nested component that uses the hook
  function BattleUIWithRepositioning(innerProps) {
    const { isUnitRepositioning } = useUnitRepositionAnimations(innerProps.gameState || null, innerProps.userId || null);
    
    return (
      <RepositioningProvider isUnitRepositioning={isUnitRepositioning}>
        <MemoizedBattleUI {...innerProps} />
      </RepositioningProvider>
    );
  }
  
  return (
    <DamageAnimationProvider>
      <BattleUIWithRepositioning {...props} />
      <CustomCursor />
    </DamageAnimationProvider>
  );
});

export default BattleUIWrapper;
